/**
 * Typed client for the Key Dials API.
 *
 * In later phases the bulk of this is GENERATED by NSwag from the API's OpenAPI
 * document. For now it provides a small hand-written surface for diagnostics,
 * authentication, and the current-user endpoint.
 */

export interface HealthResponse {
  status: string;
}

export interface RequestCodeRequest {
  email: string;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
  /** Selects the refresh-token lifetime: "admin" => short, capped session; omitted => default. */
  client?: string;
}

export interface TokenResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
}

export interface ImpersonatorInfo {
  userId: string;
  email: string;
}

export interface MeResponse {
  userId: string;
  email: string;
  /** The user's full name, when set; falls back to the email locally. */
  displayName: string | null;
  organisationId: string | null;
  /** The organisation this SESSION is scoped to — a multi-org account signs in against exactly one. */
  organisationName?: string | null;
  organisationType?: string | null;
  capabilities: string[];
  /** Set when this session is an impersonation: the real operator acting as this user. */
  impersonator?: ImpersonatorInfo | null;
  /** Set when this session was bought with a kiosk unlock PIN (KEY-268): the ONE dial instance it may
   * touch. The server confines it regardless; this exists so the UI can stop offering doors it knows are
   * locked — no "My Dials", no other-instance links, no Home. */
  pinnedToInstanceId?: string | null;
  /** Set when this is an external guest's session (KEY-297): the one dial instance the invite covers.
   * Same contract as pinnedToInstanceId — a marker for the shell, not a permission. */
  guestInstanceId?: string | null;
  /** Set when this account's card-free trial ended without a subscription (KEY-431) — only then; a cancellation
   * sets `subscriptionEnded` instead (KEY-911). Same contract again —
   * the shell shows the "trial ended" screen instead of a dial list the server would only refuse, and the
   * endpoints refuse it regardless, which is what covers a trial running out mid-session. */
  trialExpired?: boolean;
  /** The number this account's SMS notifications go to (KEY-453), or null when none is set. Without one
   * the server refuses an SMS subscription and drops every SMS, so the channel is offered only when this
   * is set — a switch that turns nothing on is worse than an absent one. */
  phoneNumber?: string | null;
  /** KEY-731: the white-label brand for this session's organisation — its (or an ancestor's) header logo, else a
   * published partner page logo. Null/absent means draw the Key Dials header, including whenever
   * Features:WhiteLabelHeader is off. */
  brand?: MeBrand | null;
  /** KEY-743: this session's organisation still has the name we guessed from the buyer's email address, and this
   * person may rename it — the user app asks them to check it. Always false/absent while Features:OrgNamePrompt is
   * off, and for kiosk, guest and impersonated sessions. A prompt marker, not a permission. */
  organisationNameIsGuessed?: boolean;
  /** KEY-892: Features:AdminShell — the admin app draws its redesigned shell. UI-only. */
  adminShell?: boolean;
  /** KEY-911: this account's subscription has ended, for either reason — a card-free trial that ran out, or a
   * cancellation. The shell shows the "ended" screen; the server refuses gated routes with 402 regardless.
   * `trialExpired` stays true only for the trial case. */
  subscriptionEnded?: boolean;
  /** KEY-911: why it ended — "trial" or "cancelled" — or null while it has not. */
  endedReason?: "trial" | "cancelled" | null;
}

/** A white-label header brand (KEY-731): whose it is, and its landscape logo (a short-lived URL, or null for a wordmark). */
export interface MeBrand {
  name: string;
  logoUrl: string | null;
}

// --- Your own account (the personal Settings page) ---------------------------------------------------

/** What a person may see and change about their own account. Email is shown but not editable here. */
export interface ProfileView {
  email: string;
  displayName: string | null;
  phoneNumber: string | null;
  /** Live (non-muted) SMS subscriptions on the number. Non-zero means removing it will silence them. */
  smsSubscriptionCount: number;
}

/** Both fields are "set to this", not "change if present": blank CLEARS. */
export interface UpdateProfileRequest {
  displayName: string | null;
  phoneNumber: string | null;
}

// --- Guest access (KEY-297): time-limited, email-verified, instance-bound outside access ---

/** One scope a guest holds, with the role it carries. Null sectionKey = the whole instance. */
export interface GuestScopeView {
  sectionKey: string | null;
  segmentKey: string | null;
  role: string;
  /** KEY-852: set when the grant is one item of the action point — that item alone, never its siblings. */
  itemKey?: string | null;
}

export interface GuestGrantView {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  scopes: GuestScopeView[];
  createdAtUtc: string;
  expiresAtUtc: string;
  revokedAtUtc: string | null;
  requireIpMatch: boolean;
  firstLandAtUtc: string | null;
  lastActiveAtUtc: string | null;
  lastIp: string | null;
}

export interface GuestScopeRequest {
  sectionKey: string | null;
  segmentKey: string | null;
  /** KEY-852: narrows the action point to one item. Only when the dial offers it
   * (`InstanceDetail.offersItemScopedGuests`); the server refuses it otherwise. */
  itemKey?: string | null;
  /** KEY-852: this row's own role (Editor / Reviewer / Reader); absent takes the invite's. */
  role?: string | null;
}

/** One field the inviter is turning on for a guest, at invite time (KEY-488). Same (target, attributeKey)
 * identity as {@link SetMemberAttributeAccessEntry}. "See" or "Edit" only — a guest defaults to Hidden
 * with no override, so there's no Hidden entry to send; leave a field off this list to keep it hidden. */
export interface GuestAttributeAccessRequest {
  target: string;
  attributeKey: string;
  access: string;
}

export interface InviteGuestRequest {
  email: string;
  displayName: string | null;
  /** Editor, Reviewer or Reader — guests are never Admin. */
  role: string;
  /** Null or empty means the whole instance. */
  scopes: GuestScopeRequest[] | null;
  /** Defaults to 7, clamped to 30 server-side. */
  expiresInDays: number | null;
  requireIpMatch?: boolean;
  /** The guest's initial field visibility (KEY-488) — everything not listed stays Hidden. */
  attributeAccess?: GuestAttributeAccessRequest[] | null;
}

/** The invite response. `linkToken` is returned exactly ONCE and never stored server-side — build the
 * link, show it, and let it go; losing it means revoking and re-inviting. */
export interface InviteGuestResult {
  grant: GuestGrantView;
  linkToken: string;
  /** Whether we managed to email the link to the guest (KEY-536). False means the invite stands and the
   * link works, but the inviter has to pass it on themselves — show it, and say so. The reason is logged
   * server-side, never returned. */
  emailSent: boolean;
}

// ---- Feeds: consensual cross-organisation sharing (KEY-308) ----

/** Mint a connect code on YOUR dial for a counterparty to redeem. The label is for your own tray — it is
 * never matched against and never shown to whoever redeems it. */
export interface MintFeedCodeRequest {
  expectedCounterparty: string | null;
  /** Defaults to 15, clamped to 60 server-side. */
  lifetimeMinutes: number | null;
}

/** A freshly minted code. `code` is returned exactly ONCE — only its hash is stored, so a lost code is
 * re-minted, never recovered. */
export interface MintedFeedCode {
  id: string;
  code: string;
  expectedCounterparty: string | null;
  expiresAtUtc: string;
}

/** A code in the minter's tray. Deliberately carries no code value. */
export interface FeedCodeView {
  id: string;
  expectedCounterparty: string | null;
  createdAtUtc: string;
  expiresAtUtc: string;
  consumedAtUtc: string | null;
  consumedByOrganisationName: string | null;
}

/** What redeeming buys: who you are about to disclose to, and nothing else. No access exists yet. */
export interface RedeemedFeed {
  feedId: string;
  counterpartyOrganisationName: string;
  counterpartyDialName: string;
}

/** One thing a feed discloses. `sectionKey` is required — there is no whole-dial entry, and a feed with
 * no entries discloses nothing. Items and attributes are refused until they can be enforced. */
export interface FeedScopeEntry {
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
  attributeKey: string | null;
  includeFutureChildren: boolean;
}

/** A feed from one side's point of view. `direction` is "out" (you send it) or "in" (you receive it);
 * `awaitingYou` says whose move it is. */
export interface FeedView {
  id: string;
  direction: "out" | "in";
  state: "Draft" | "Offered" | "Accepted" | "Declined" | "Revoked";
  sourceInstanceId: string;
  sourceDialName: string;
  sourceOrganisationName: string;
  targetInstanceId: string;
  targetDialName: string;
  targetOrganisationName: string;
  scopes: FeedScopeEntry[];
  offeredAtUtc: string | null;
  acceptedAtUtc: string | null;
  expiresAtUtc: string | null;
  revokedAtUtc: string | null;
  awaitingYou: boolean;
}

/** One line of a feed's history. Both parties read the same trail. */
export interface FeedEventView {
  kind: string;
  atUtc: string;
  actorOrganisationName: string | null;
  detail: string | null;
}

// ---- Feed mappings: wiring a fed slice into your own dial (KEY-358, Mode B) ----

/** One end of a wire. The source end carries the OTHER organisation's own keys — never the namespaced
 * `f-…` section key a fed slice takes on your face, which is a rendering detail, not a name. */
export interface FeedMappingNode {
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
  dimensionKey: string | null;
  attributeKey: string | null;
}

/** How a wire derives what it writes: `Mirror` (one fed node through a status map), `Aggregate` (a fed
 * scope through a condition ladder) or `Attribute` (a fed field, like-for-like). */
export type FeedMappingKind = "Mirror" | "Aggregate" | "Attribute";

/**
 * Why a wire cannot run right now. Recomputed on every read, never stored: the other organisation
 * republishes their definition without telling you, so a saved verdict would be stale from that moment.
 * `SourceOutOfScope` is deliberately survivable — a narrowed feed keeps the wire and skips it, rather
 * than throwing away work you authored.
 */
export type FeedMappingHealth =
  | "Ok"
  | "FeedNotLive"
  | "SourceOutOfScope"
  | "SourceMissing"
  | "TargetMissing"
  | "StatusUnmapped"
  | "AttributeIncompatible"
  | "Invalid";

/** Author or replace one wire. `config` carries the status map or condition ladder as JSON for the two
 * status kinds and is ignored for `Attribute`, which has nothing to translate. */
export interface SaveFeedMappingRequest {
  feedId: string;
  kind: FeedMappingKind;
  name: string;
  source: FeedMappingNode;
  target: FeedMappingNode;
  config: string | null;
  /** False unless you say otherwise: you authored this against the source AS IT WAS, and they can change
   * what it says without asking you. */
  autoApply?: boolean;
}

/** A wire as its author sees it, with its live health and the sentences behind it. */
export interface FeedMappingView {
  id: string;
  feedId: string;
  feedSourceOrganisationName: string;
  feedSourceDialName: string;
  kind: FeedMappingKind;
  name: string;
  source: FeedMappingNode;
  target: FeedMappingNode;
  config: string | null;
  autoApply: boolean;
  isPaused: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  health: FeedMappingHealth;
  /** Named, human problems — the same sentences that would have refused the save. Empty when Ok. */
  problems: string[];
}

/** Pause or resume one wire, independently of stopping the feed: you may want the view without the writes. */
export interface PauseFeedMappingRequest {
  paused: boolean;
}

/**
 * What the recipient may address when wiring a feed in (KEY-359) — the source's *vocabulary*, pruned to
 * exactly what the feed discloses.
 *
 * Deliberately a different question from what the fed sections on your own dial answer. Those carry what
 * is needed to *draw* their slice; this carries what may be *named* in a rule — above all `dimensions`,
 * which a mirror or an aggregate on an itemised source must name and which a face has no reason to hold.
 * Keys are the other organisation's own, never the namespaced `f-…` form a fed section takes on yours.
 */
export interface FeedWiring {
  feedId: string;
  sourceOrganisationName: string;
  sourceDialName: string;
  sourceKind: "Simple" | "Itemised";
  sections: FeedWiringSection[];
  /** Empty for a simple source; otherwise the axes that score at least one item the feed shares. */
  dimensions: FeedWiringDimension[];
  /** The fields the feed named, each on the node it was named for. Files and people never appear. */
  fields: FeedWiringField[];
}

export interface FeedWiringSection {
  key: string;
  label: string;
  segments: FeedWiringSegment[];
}

export interface FeedWiringSegment {
  key: string;
  label: string;
  /** Carried inline, not by name: their palette names mean nothing on your dial. */
  palette: GeneratedStatusSet;
  items: FeedWiringItem[];
}

export interface FeedWiringItem {
  key: string;
  name: string;
}

export interface FeedWiringDimension {
  key: string;
  label: string;
  palette: GeneratedStatusSet;
  /** "sectionKey/segmentKey/itemKey" paths this axis scores; empty = every item. */
  appliesToItemKeys: string[];
}

export interface FeedWiringField {
  key: string;
  label: string;
  kind: "Text" | "Date" | "RichText";
  target: "Section" | "Segment" | "Item";
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
}

/**
 * One change a feed wants to make to your dial, waiting on you (KEY-361).
 *
 * You wrote the wire against their dial *as it was*, and they can change what it says at any time without
 * telling you — so an incoming change is shown before it lands, unless you opted that wire in to applying
 * by itself. `Blocked` means your own status gate is holding it: it lands by itself once the field named
 * in `blockedFieldLabel` has a value.
 */
export interface FeedProposalView {
  id: string;
  feedId: string;
  mappingId: string;
  mappingName: string;
  /** Whose change this is, as they were called when they made it. */
  fromOrganisationName: string;
  fromDialName: string;
  /** The node on THEIR dial that drove it, in their own keys. */
  fromNodePath: string;
  /** Your node, spelled out — "Structure · Foundations". */
  targetLabel: string;
  targetSectionKey: string;
  targetSegmentKey: string | null;
  targetItemKey: string | null;
  targetDimensionKey: string | null;
  targetAttributeKey: string | null;
  proposedStatusKey: string | null;
  proposedValue: string | null;
  /** What your node said when this was worked out, so you see the move and not only where it ends up. */
  currentStatusKey: string | null;
  currentValue: string | null;
  sourceStatusKey: string | null;
  state: "Pending" | "Blocked";
  blockedFieldLabel: string | null;
  computedAtUtc: string;
}

/**
 * What stopping a feed would actually do. Watching one vanishes cleanly; wiring one in copies values into
 * your own rows, and `valuesRemaining` is how many of those stay behind, frozen at what they last said.
 */
export interface FeedRevokeImpact {
  valuesRemaining: number;
  liveWires: number;
}

/** A minted guest session: the token pair plus where the session is bound and when access ends. */
export interface GuestSessionResponse extends TokenResponse {
  instanceId: string;
  accessExpiresAtUtc: string;
  displayName: string | null;
}

/** The ticket a locked kiosk screen holds (KEY-268): capability-free, bound to one dial, no refresh token.
 * It buys exactly two calls — list who can unlock this dial, and try a PIN. */
export interface LockResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  instanceId: string;
}

/** One name on the unlock screen. Deliberately minimal — this list renders on an unattended device. */
export interface PinCandidate {
  userId: string;
  displayName: string;
}

/** A successful unlock: a short-lived token for the selected user's OWN identity, scoped to the one dial. */
export interface PinVerifyResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  userId: string;
  displayName: string | null;
  instanceId: string;
}

/** The caller's PIN state. `available` is false when none of their dials auto-locks, so a PIN would have
 * nothing to unlock and the setup screen explains rather than offering a pointless field. */
export interface PinStatusResponse {
  hasPin: boolean;
  available: boolean;
  setAtUtc: string | null;
}

/** Result of starting an impersonation — a short-lived access token scoped to the target user. */
export interface ImpersonationResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  userId: string;
  email: string;
  displayName: string | null;
}

export interface GenerateDefinitionRequest {
  description: string;
  goal?: string;
  industry?: string;
  audience?: string;
  model?: string;
  /** "Simple" (sections + segments) or "Itemised" (segments contain items). Default Itemised. */
  kind?: string;
  /**
   * Generate a Group Key Dial: a scaffolded Simple overview plus child-dial proposals (no child
   * links — the children don't exist yet). Only honoured where the server reports
   * aiFeatures.groupGeneration; elsewhere the endpoint 404s.
   */
  isGroup?: boolean;
  /**
   * Text extracted from an uploaded seed document (KEY-183) — obtained from extractSourceDocument().
   * The model bases the dial on it, following any build instructions it contains. Only honoured where
   * the server reports aiFeatures.documentSeed.
   */
  sourceDocument?: string;
  /**
   * Id of a reusable library palette (see listPalettes) to use for the generated dial instead of an
   * AI-invented one. When set and it resolves, the server applies that palette verbatim and tells the
   * model to use it. Omit/undefined to let the AI choose its own palette.
   */
  paletteId?: string;
  /**
   * Groups only (KEY-224): the kind of group dial to plan — "Authored" (the parent has its own sections,
   * each banded by a rollup of one child) or "Mirror" (the parent's face IS its children's sections,
   * shown live). Omit to let the model pick the shape that fits the description.
   */
  groupFace?: "Authored" | "Mirror";
}

/** One message of a planning conversation as the UI shows it (KEY-233). */
export interface PlanConversationMessage {
  role: "user" | "assistant";
  text: string;
  /** Answers the model offered, rendered as one-click chips. */
  suggestions?: string[] | null;
  /** Option sketches Milo offered in this turn (single-dial conversations, KEY-904), drawn as cards. */
  options?: PlanOption[] | null;
}

/**
 * One sketch of a dial Milo could build (KEY-904): enough to draw a ring, a scale strip and a "day to day"
 * card. `sections` is the real structure, so the ring always matches the card. Only the chosen one is built.
 */
export interface PlanOption {
  key: string;
  title: string;
  idea: string;
  dayToDay: string;
  tradeOff: string;
  sections: PlanOptionSection[];
  scales: PlanOptionScale[];
  recommended?: boolean;
  /** The option that follows the admin's own description exactly (always offered for precise input). */
  asDescribed?: boolean;
  /** Extra fields people would fill in, as short labels. */
  fields?: string[] | null;
}

export interface PlanOptionSection {
  label: string;
  segments: PlanOptionSegment[];
}

export interface PlanOptionSegment {
  label: string;
  /** Individual checks inside the segment; 0 when it is rated as a whole. */
  checkCount: number;
}

export interface PlanOptionScale {
  name: string;
  steps: { label: string; colour: string }[];
}

/**
 * A multi-turn Group Key Dial planning conversation. `plan` is the working dial as it stands — null until
 * the model first emits one — and is what the existing two-pass build consumes when the admin is happy.
 * `turnStatus` is "Idle" (your move), "Running" (poll again) or "Failed".
 */
export interface PlanConversation {
  id: string;
  turnStatus: "Idle" | "Running" | "Failed";
  messages: PlanConversationMessage[];
  plan?: GeneratedDefinition | null;
  /** Admin turns left before the conversation must be built or abandoned. */
  turnsRemaining: number;
  error?: string | null;
  failureStatusCode?: number | null;
  /** "Group" (KEY-233) or "Single" (a dial designed with Milo, KEY-904). */
  kind?: "Group" | "Single";
  /** The most recent option sketches (single-dial conversations), so the chosen one can be shown at full size. */
  options?: PlanOption[] | null;
}

/** Returned by POST /api/definitions/generate-jobs — the id of the queued generation to poll. */
export interface GenerationJobAccepted {
  jobId: string;
}

export type GenerationJobState = "Pending" | "Running" | "Succeeded" | "Failed";

/** KEY-531: which dial workspace a person is shown. "Classic" is the workspace as it has always been;
 * "Dynamic" is the selection-projection redesign. */
export type WorkspaceMode = "Classic" | "Dynamic" | "Dynamic3D";

export interface WorkspaceModeView {
  /** Whether `Features:DynamicWorkspace` is on for this server. False ⇒ hide the toggle entirely; the mode
   * is always "Classic" and any stored preference is ignored rather than deleted. */
  enabled: boolean;
  mode: WorkspaceMode;
  /** False when this is the server's default rather than a pick of their own — so the UI can show it as a
   * default instead of as a settled choice. */
  chosen: boolean;
  /** Whether `Features:LookBackCompare` is on: Look back as a Then/Now compare mode instead of a card tab.
   * Independent of `enabled`. Optional only for a client built against an older API. */
  lookBackCompare?: boolean;
  /** KEY-888: whether `Features:LookAhead` is on — Look ahead is offered. Absent (an older API) means off. */
  lookAhead?: boolean;
}

/** Whether a person's workspace tab strips show the tabs' names or an icon each. One choice for every strip. */
export type TabStripMode = "Labels" | "Icons";

/** How a guided-help tour ended. Both mean "don't offer it again" — see `TourCompletion`. */
export type TourOutcome = "Completed" | "Cancelled";

/**
 * KEY-535: a guided-help tour this person is done with. The ABSENCE of an entry is what makes a tour offer
 * itself, so a cancel is recorded exactly as a completion is — otherwise "allow cancel" would mean
 * "re-ambush on the next dial".
 */
export interface TourCompletion {
  /** The tour's id, e.g. "first-run-dial". */
  id: string;
  outcome: TourOutcome;
  atUtc: string;
}

/**
 * A generation job's current state (GET /api/definitions/generate-jobs/{id}). `definition` is set only when
 * succeeded; `error` + `failureStatusCode` (502/503) only when failed.
 */
export interface GenerationJobStatus {
  status: GenerationJobState;
  definition?: GeneratedDefinition;
  error?: string;
  failureStatusCode?: number;
}

// --- Self-service authoring (KEY-560) ---------------------------------------------------------------------------------

/**
 * Whether the caller can build a Key Dial here. The user app's builder gates on `selfService` alone: `enabled` is also true
 * for platform staff, who build the catalogue in the admin app.
 */
export interface AuthoringStatus {
  enabled: boolean;
  checkingEnabled: boolean;
  selfService: boolean;
  /** Whether a document can be added to an AI draft (Features:AiDocumentSeed). Never true when `enabled` is false. */
  documentSeed: boolean;
}

/** Where one meter stands. A null `allowed` means unlimited; a null `used` means the meter isn't counted here. */
export interface MeterPosition {
  used: number | null;
  allowed: number | null;
}

export interface AllowancePlan {
  family: string;
  tier: string;
}

/**
 * Where the caller's organisation stands on every meter its plan enforces. `cannotCreateReason` is the refusal a create
 * would meet, word for word, so the builder can say it before anyone presses Create.
 */
export interface OrganisationAllowance {
  governed: boolean;
  plan: AllowancePlan | null;
  definitions: MeterPosition;
  instancesPerDefinition: MeterPosition;
  users: MeterPosition;
  aiGenerations: MeterPosition;
  aiResetsAtUtc: string | null;
  canCreateDefinition: boolean;
  cannotCreateReason: string | null;
}

/** A platform dial offered as a starting point. */
export interface DefinitionTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  industry: string | null;
  kind: string;
  publishedVersion: number;
}

/** Which part of a dial a readiness problem points at. `level` travels as a word. */
export interface DefinitionProblemTarget {
  level: "Dial" | "Section" | "Segment" | "Dimension" | "Attribute" | "Status";
  sectionKey?: string | null;
  segmentKey?: string | null;
  key?: string | null;
}

export interface DefinitionProblem {
  code: string;
  message: string;
  target: DefinitionProblemTarget;
}

export type ReadinessPurpose = "publish" | "checking";

/** What stands between a dial and publishing it, or sending it for checking. Problems arrive in no fixed order. */
export interface DefinitionReadiness {
  definitionId: string;
  versionNumber: number;
  lifecycle: string;
  purpose: string;
  ready: boolean;
  problems: DefinitionProblem[];
  warnings: string[];
}

/** One of the caller's own dials, for the "Being built" shelf. */
export interface AuthoredDesign {
  id: string;
  name: string;
  kind: string;
  state: "Building" | "TestCopy" | "Running";
  /** The latest version's own state: a running dial can have a draft on top. */
  lifecycle: string;
  latestVersion: number;
  checkingInstanceId: string | null;
  liveInstanceId: string | null;
}

/** What going live did: the running dial, or the refusal the builder shows. */
export type GoLiveOutcome =
  | { ok: true; instanceId: string }
  | { ok: false; status: 400 | 409; message: string; problems: DefinitionProblem[]; instanceId: string | null };

/**
 * Who a test dial goes to (KEY-545). A customer author leaves it out: their test copy lands in their own organisation for them
 * alone, and "Organisation" is refused.
 */
export type CheckingAudience = "Organisation" | "SenderOnly";

/** For the definition writes a customer author shares with the admin app. */
export interface HomeOrgOption {
  /** Send no `X-KeyDials-Org`, whatever org is active: a customer author writes only in the organisation they signed in to. */
  homeOrg?: boolean;
}

/** Options for the polled async generation helper. */
export interface GenerateDefinitionAsyncOptions {
  /** Called after each poll while the job is still running — e.g. to update a progress message. */
  onPoll?: (elapsedMs: number) => void;
  /**
   * Stops polling when aborted, rejecting with an AbortError (KEY-560): the builder stops waiting when the author leaves. The job
   * itself carries on server-side and is still charged.
   */
  signal?: AbortSignal;
}

/** Which flag-gated dial-creation features this server has enabled (drives UI visibility). */
export interface AiFeaturesStatus {
  groupGeneration: boolean;
  /** Features:AiDocumentSeed — upload a document to seed generation (KEY-183). */
  documentSeed: boolean;
  /** Features:SpreadsheetImport — build a dial deterministically from a filled-in template (no AI). */
  spreadsheetImport?: boolean;
  /** Features:DialLinks — cross-dial propagation rules inside Group Key Dials (no AI; carried here so the editor learns every flag from one fetch). */
  dialLinks?: boolean;
  /** Features:GroupMirrorFace — mirror-mode group faces (compose a group's face from child sections, live). */
  groupMirrorFace?: boolean;
  /**
   * Features:GroupChildContainment — a group's children are created and owned by the group (KEY-226).
   * The same flag gates the server's publish and allocation guards, so a UI that greys out a dial with
   * live instances is never showing a rule the server won't enforce.
   */
  groupChildContainment?: boolean;
  /** Features:GroupSegmentRules — per-segment overrides of a group's rollup strategy (KEY-252). */
  groupSegmentRules?: boolean;
  /** Features:ItemSegmentRules — per-segment display rules on an itemised dial. */
  itemSegmentRules?: boolean;
  /** Features:GroupStatusRules — status rules on a group's own segments and sections (KEY-509). */
  groupStatusRules?: boolean;
  /** Features:ResetSchedules — authoring scheduled status resets (KEY-276). The same flag registers the
   * server's reset sweep; whether that sweep writes anything is ResetSweep:DryRun, which this does not report. */
  resetSchedules?: boolean;
  /** Features:DialPeople — a dial's people list and the Person attribute type (KEY-PEOPLE): whether the
   * editor offers the type at all. */
  dialPeople?: boolean;
  /** Features:DateLadders — the due-date ladder on a palette's statuses (KEY-LADDER): whether the editor
   * offers the rung settings. The same flag registers the server's sweep. */
  dateLadders?: boolean;
  /** Features:AiGroupConversation — design a group dial through a multi-turn conversation (KEY-231). */
  groupConversation?: boolean;
  /** Features:VisualConfig — the visual configuration canvas in the admin editor (Map structure view; later scope painting and the group canvas). */
  visualConfig?: boolean;
  /** Features:DefinitionChecking — try a draft out in the real end-user app on a throwaway root-org test dial before publishing it (KEY-283). */
  definitionChecking?: boolean;
  /** Features:ConfigWorkspace — the relations-first editing surfaces (the coverage-matrix "Grid" scope view now, the lens workspace shell later). */
  configWorkspace?: boolean;
  /** Features:DefinitionsRedesign — the redesigned definitions editor (one editor with a flow spine, KEY-777). */
  definitionsRedesign?: boolean;
  /** Features:SectionColourRules — a section's own edge colour: its palette and completion rule (KEY-795). */
  sectionColourRules?: boolean;
  /** Features:AiDialConversation — design a single dial by talking it through with Milo (KEY-904). */
  dialConversation?: boolean;
}

/** A single problem found while importing a template spreadsheet. */
export interface SpreadsheetImportDiagnostic {
  /** "Error" (blocks creation) or "Warning" (informational). */
  severity: "Error" | "Warning";
  message: string;
  /** Human-readable pointer such as "Structure!row 12", when known. */
  location?: string | null;
}

/** Quick counts for the import preview. */
export interface SpreadsheetImportSummary {
  sections: number;
  segments: number;
  items: number;
  /** How many rating scales the workbook defined (at least 1 — the dial's default). */
  scales: number;
  /** Per-item status axes ("checks"); 1 on an itemised dial that didn't describe any, 0 on a simple one. */
  checks: number;
  /** Extra fields ("extra details") the workbook set up. */
  details: number;
}

/**
 * The three answers that decide which sheets the downloaded workbook contains, so an author is never
 * handed a tab they have to ignore.
 *  - `items`: does each thing being rated break down into a checklist? Decides the Structure sheet, and
 *    is what the dial's kind is read back from.
 *  - `scales`: "library" (pick a saved scale — no Rating scales tab), "own" (define one), or "varied"
 *    (different parts rated differently — the Structure sheet also gets its Rating scale column).
 */
export interface DefinitionTemplateOptions {
  items?: boolean;
  scales?: "library" | "own" | "varied";
  /** Adds the Item checks tab — an item has to clear more than one check. Itemised dials only. */
  checks?: boolean;
  /** Adds the Extra details tab — an owner, a review date, a document alongside the rating. */
  details?: boolean;
}

/** Result of parsing a filled-in template (POST /api/definitions/import-spreadsheet). */
export interface SpreadsheetImportResult {
  /** The draft to create — null when the workbook couldn't be turned into a definition (see diagnostics). */
  definition: GeneratedDefinition | null;
  diagnostics: SpreadsheetImportDiagnostic[];
  summary: SpreadsheetImportSummary | null;
}

/** One change a re-import would make to an existing dial, matched by node key. */
export interface ReimportDiffEntry {
  level: "Section" | "Segment" | "Item" | "Rating scale" | "Check" | "Extra detail";
  label: string;
  change: "Added" | "Changed" | "Removed";
}

/** Preview of applying a re-completed spreadsheet to an existing dial (nothing is persisted). */
export interface ReimportPreviewResult {
  dialName: string;
  /** False when the sheet couldn't be read into a structure (see diagnostics). */
  canApply: boolean;
  diff: ReimportDiffEntry[];
  added: number;
  changed: number;
  removed: number;
  diagnostics: SpreadsheetImportDiagnostic[];
}

/** One "what's new" line in a version-upgrade notice (Spreadsheet-import Phase 2). */
export interface UpgradeDriftEntry {
  level: "Section" | "Segment" | "Item" | "Dimension" | "Attribute";
  key: string;
  label: string;
  change: "Added" | "Changed" | "Removed";
}

/**
 * Something a version upgrade strands, with a count of the instance data it holds.
 *
 * For every `nodeType` but one this is what an apply would CLEAR — a removed section/segment/item, or a removed
 * status dimension. The exception is `"Attribute"` (KEY-280), which carries `attributeKey`: a declared field the
 * new version drops. Its values survive the upgrade untouched and simply stop matching any declaration, so
 * nothing renders them. Surfaces must word the two cases differently — "cleared" is wrong for an attribute.
 */
export interface AtRiskNode {
  nodeType: string;
  label: string;
  sectionKey: string;
  segmentKey?: string | null;
  itemKey?: string | null;
  dimensionKey?: string | null;
  statusCount: number;
  historyCount: number;
  attributeCount: number;
  documentCount: number;
  total: number;
  /** Set only when `nodeType` is "Attribute". Absent on snapshots frozen before KEY-280. */
  attributeKey?: string | null;
  /**
   * Discussion threads under the node that hold at least one message, and those messages (KEY-803). Not deleted on
   * apply, but nothing shows a thread whose node is gone. `messageCount` is included in `total`. Absent on snapshots
   * frozen before KEY-803.
   */
  conversationCount?: number;
  messageCount?: number;
}

/** Whether an at-risk entry is a dropped field declaration — orphaned values, not deleted ones. */
export function isOrphanedAttribute(n: AtRiskNode): boolean {
  return n.nodeType === "Attribute";
}

/** Which kind of row holds a stranded status key: an item's status on one check, an action point's own status, or a hand-set section status. */
export type StrandedStatusHolder = "Item" | "Segment" | "Section";

/** A status the upgrade's target palette offers. */
export interface PaletteStatusOption {
  key: string;
  name: string;
}

/**
 * A status key the target version's palette doesn't have. `statusCount` rows hold it as their status and
 * `revertCount` as a review-date revert target. Both can be zero for a key the old palette offered that nobody
 * holds yet. `oldName` is null for a key that was already in neither palette.
 */
export interface StrandedStatusKey {
  oldKey: string;
  oldName?: string | null;
  statusCount: number;
  revertCount: number;
  suggestedKey?: string | null;
  suggestedBy?: "Name" | "Position" | null;
}

/**
 * One palette an upgrade strands keys on. `paletteRef` is the check's dimension key for "Item", "default" or
 * "{section}/{segment}" for "Segment", and "section" for "Section". `label` names the check or action point, and is
 * null for the dial-wide default and for section statuses. `options` is the target palette, in order.
 */
export interface StrandedStatusPalette {
  holder: StrandedStatusHolder;
  paletteRef: string;
  label?: string | null;
  keys: StrandedStatusKey[];
  options: PaletteStatusOption[];
}

/** How many rows on this palette hold a status the target version doesn't have, as their status or as a revert target. */
export function strandedHeldCount(palette: StrandedStatusPalette): number {
  return palette.keys.reduce((n, k) => n + k.statusCount + k.revertCount, 0);
}

/**
 * One line of the old→new status map an upgrade applies. It names the palette as a `StrandedStatusPalette` does, plus the
 * definition it belongs to, because a group rollout maps each child definition separately. Rows holding `oldKey` move to
 * `newKey`; null means the target palette's starting status, or no status at all for a hand-set section status.
 */
export interface StatusRemapEntry {
  definitionId: string;
  holder: StrandedStatusHolder;
  paletteRef: string;
  oldKey: string;
  newKey: string | null;
}

/** A stranded status key an upgrade's map leaves unanswered: the palette, named as a `StatusRemapEntry` names it, and the key. */
export interface MissingStatusRemap {
  definitionId: string;
  holder: StrandedStatusHolder;
  paletteRef: string;
  oldKey: string;
}

/** The 409 body of a queue refused because its status map leaves stranded keys unanswered. */
export interface StatusRemapRefusal {
  message: string;
  missing: MissingStatusRemap[];
}

/** The drift + at-risk snapshot behind a version-upgrade notice. */
export interface UpgradeDriftReport {
  instanceId: string;
  instanceName: string;
  fromVersion: number;
  toVersion: number;
  whatsNew: UpgradeDriftEntry[];
  atRisk: AtRiskNode[];
  /** Group instances only: what the upgrade does to each child dial (absent on non-groups and on
   * snapshots frozen before the group cascade existed). */
  children?: GroupChildUpgradeSummary[] | null;
  /**
   * Whether this dial will be held at the door after the upgrade (KEY-434) — the target version asks for
   * a dial-level value it does not yet carry, so whoever opens it next is asked for that first.
   *
   * It matters most on a MANDATORY rollout, which applies itself on its effective date: without this the
   * rollout screen could schedule a stop that nobody chose the moment for. Derived from the values that
   * are actually present, so a dial that already answered is not counted.
   */
  willNeedFirstRunCapture?: boolean;
  /**
   * The status keys this upgrade would strand, per palette. The dial's rows keep them, but the target version's
   * palettes don't have them, so whatever holds one shows no status and stops counting towards colours. Absent when
   * nothing strands, and on snapshots frozen before this existed.
   */
  strandedStatuses?: StrandedStatusPalette[] | null;
  /**
   * The lists and items this upgrade removes that hold data, one entry per node, built-in and custom alike (KEY-803).
   * Absent when none does, and on snapshots frozen before this existed.
   */
  structureAtRisk?: StructureAtRiskNode[] | null;
  /**
   * Subscriptions, dial memberships and visibility grants scoped to a whole section this upgrade removes (KEY-805). They
   * name no list, so a structure map can't reach them: on apply they follow the section's lists only when every list the map
   * moves or keeps lands in the same new section, and are otherwise left behind and stop applying. Absent when there are none.
   */
  droppedSectionScopes?: SectionScopeAtRisk[] | null;
}

/** The rows scoped to one removed section (see `UpgradeDriftReport.droppedSectionScopes`). `grantCount` is dial memberships. */
export interface SectionScopeAtRisk {
  sectionKey: string;
  label: string;
  subscriptionCount: number;
  grantCount: number;
  visibilityCount: number;
}

/** What a structure map entry does with a node the upgrade removes (KEY-805). */
export type StructureRemapAction = "MoveTo" | "KeepAsCustomUnder" | "Discard";

/**
 * One line of a dial admin's structure map (KEY-805), naming a `StructureAtRiskNode` by its path (`itemKey` null for a list).
 * - `MoveTo`: merge into an existing node of the new version at the same level. An item's target has all three keys, a
 *   list's has no item key. A list carries its items.
 * - `KeepAsCustomUnder`: keep it as the dial's own node under a parent of the new version: a list (section + segment) for an
 *   item, a section for a list. `newName` optionally renames it.
 * - `Discard`: let the upgrade clear it. No target.
 */
export interface StructureRemapEntry {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  action: StructureRemapAction;
  targetSectionKey?: string | null;
  targetSegmentKey?: string | null;
  targetItemKey?: string | null;
  newName?: string | null;
}

/** A node holding data that a saved structure map doesn't answer yet. */
export interface MissingStructureRemap {
  nodeType: "Segment" | "Item";
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  label: string;
}

/** Why a structure map entry was refused. */
export type StructureRemapProblemReason =
  | "NotAtRisk"
  | "Duplicate"
  | "UnknownAction"
  | "MissingTarget"
  | "TargetNotInNewVersion"
  | "LevelMismatch"
  | "ManyToOne"
  | "Conflict";

export interface StructureRemapProblem {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  reason: StructureRemapProblemReason;
  detail?: string | null;
}

/** The 400 (invalid entries) / 409 (the data can't follow) body of a refused structure map. */
export interface StructureRemapRefusal {
  message: string;
  problems: StructureRemapProblem[];
}

/**
 * The 409 body of an accept or apply held for a structure map (KEY-806). `unanswered` is what the dial's own saved map leaves out,
 * rebuilt from the dial as it is now; it is empty when the hold is for a map that can't be carried out, or for a group's child
 * dials. `dials` is set only on a group: child dials whose lists or items hold data, which no map can answer in v1.
 */
export interface StructureRemapRequiredRefusal {
  message: string;
  unanswered: MissingStructureRemap[];
  dials?: GroupChildStructureAtRisk[] | null;
}

/** One child dial of a group upgrade whose lists or items the upgrade removes while they hold data (KEY-806). */
export interface GroupChildStructureAtRisk {
  childInstanceId?: string | null;
  name: string;
  nodes: MissingStructureRemap[];
}

/** The 409 body of a group upgrade refused at queue because a child dial would lose lists or items holding data (KEY-806). */
export interface GroupStructureRemapRefusal {
  message: string;
  dials: GroupChildStructureAtRisk[];
}

/** A saved structure map, and the nodes holding data it doesn't answer yet (empty when complete). */
export interface StructureRemapSaved {
  missing: MissingStructureRemap[];
}

/** Whether a removed node comes from the definition or was added on the dial itself. */
export type StructureNodeSource = "BuiltIn" | "Custom";

/**
 * One list ("Segment") or item ("Item") an upgrade removes, with the data it holds ITSELF: a list's counts never include
 * its items', which have their own entries. `segmentKey` is the list's own key on a list entry and the parent list's on
 * an item entry. `changedStatusCount` counts statuses off their palette's starting status. `subscriptionCount` and
 * `grantCount` are list-scoped, so zero on items. `holdsData` is false only on a list listed because its items hold data.
 */
export interface StructureAtRiskNode {
  nodeType: "Segment" | "Item";
  source: StructureNodeSource;
  label: string;
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  changedStatusCount: number;
  reviewDateCount: number;
  historyCount: number;
  fieldCount: number;
  documentCount: number;
  conversationCount: number;
  messageCount: number;
  subscriptionCount: number;
  grantCount: number;
  holdsData: boolean;
  /** A feed this dial shares discloses the node. Feeds are never re-keyed, so it leaves the feed whatever the map does (KEY-805). */
  leavesFeeds?: boolean;
  /** The section the node lived in, by name (absent on snapshots written before it was added). */
  sectionLabel?: string | null;
  /** On an item, the list it lived in, by name; null on a list. */
  listLabel?: string | null;
  /** The one status every changed status on the node went to ("N/A"); null when they differ or it can't be read. */
  changedStatusName?: string | null;
}

/** What a group upgrade does to one child dial. "Removed" archives the child instance — its data is
 * retained (and restored if a later version re-adds the child), not cleared. */
export interface GroupChildUpgradeSummary {
  childDefinitionId: string;
  childInstanceId?: string | null;
  name: string;
  change: "Added" | "Removed" | "Upgraded";
  /** The child's own what's-new + at-risk for "Upgraded" (recursively for nested groups). */
  drift?: UpgradeDriftReport | null;
}

/** Whether a queued upgrade will move the instance regardless (Mandatory) or is merely offered (Optional). */
export type InstanceUpgradeKind = "Optional" | "Mandatory";

/** A platform admin's request to queue a version rollout for a set of live instances. The definition id
 * travels in the route (the server overwrites any body value). */
export interface QueueUpgradeRequest {
  targetVersionId: string;
  instanceIds: string[];
  kind: InstanceUpgradeKind;
  effectiveDateUtc: string;
  adminNote?: string | null;
  /** The old→new status map to apply, stored with each queued upgrade. Omit when the version strands no statuses. */
  statusRemap?: StatusRemapEntry[] | null;
}

/** An instance a queue request skipped, with a short machine-readable reason (e.g. "already-on-target"). */
export interface SkippedInstance {
  instanceId: string;
  reason: string;
}

/** The outcome of a queue request: which instances were queued, and which were skipped and why. */
export interface QueueUpgradeResult {
  status:
    | "Queued"
    | "TargetNotFound"
    | "TargetNotPublished"
    | "EffectiveDateTooSoon"
    | "Conflict"
    | "StatusRemapRequired"
    | "GroupStructureRemapUnsupported";
  queuedInstanceIds: string[];
  skipped: SkippedInstance[];
  /** Only on "StatusRemapRequired": the stranded keys the request's map didn't answer. Nothing was queued. */
  missingStatusRemap?: MissingStatusRemap[] | null;
  /** Only on "GroupStructureRemapUnsupported" (KEY-806): child dials that would lose lists or items holding data. Nothing was queued. */
  groupStructureAtRisk?: GroupChildStructureAtRisk[] | null;
}

/** One queued upgrade of a definition's live instance, as the admin Rollout tab lists them. */
export interface PendingUpgradeAdminRow {
  id: string;
  instanceId: string;
  instanceName: string;
  organisationName: string;
  fromVersion: number;
  toVersion: number;
  kind: string;
  status: string;
  effectiveDateUtc: string;
  queuedAtUtc: string;
  adminNote?: string | null;
  /** The upgrade strands statuses its stored map doesn't answer, so it should be mapped and queued again rather than applied. */
  needsStatusMapping?: boolean;
  /** KEY-806: waiting on the dial admin to say where some removed lists or items holding data go. A saved complete map releases it. */
  needsStructureMapping?: boolean;
}

/** The open version upgrade a dial admin sees on their live dial (Phase 2b). */
export interface PendingUpgradeView {
  id: string;
  kind: "Mandatory" | "Optional";
  status: string;
  effectiveDateUtc: string;
  adminNote?: string | null;
  targetVersionId: string;
  queuedAtUtc: string;
  drift?: UpgradeDriftReport | null;
  /** The update is waiting on Key Dials to say what the statuses it strands become; it can't be accepted or applied yet. */
  needsStatusMapping?: boolean;
  /** KEY-806: the dial admin has to say where some removed lists or items go before the update can be accepted or applied. */
  needsStructureMapping?: boolean;
  /** KEY-807: the mapping table, read from the dial as it is now. Absent when nothing removed holds data and no map is saved. */
  structureMapping?: PendingStructureMapping | null;
}

/**
 * The dial admin's mapping table for one open upgrade (KEY-807), all of it live: `atRisk` is what needs an answer now (a save
 * is checked against this list, not the frozen snapshot), `map` what is already saved, `missing` what that map leaves out, and
 * `targets` every node of the new version a map may name.
 */
export interface PendingStructureMapping {
  atRisk: StructureAtRiskNode[];
  map: StructureRemapEntry[];
  missing: MissingStructureRemap[];
  targets: StructureTargetSection[];
}

/** A section of the new version a structure map may name, with its lists. `custom` marks the dial's own. */
export interface StructureTargetSection {
  key: string;
  label: string;
  custom: boolean;
  segments: StructureTargetSegment[];
}

/** A list a structure map may name: a target for a moved list, a parent for a kept item. */
export interface StructureTargetSegment {
  key: string;
  label: string;
  custom: boolean;
  items: StructureTargetItem[];
}

/** An item a structure map may move an item into. */
export interface StructureTargetItem {
  key: string;
  label: string;
  custom: boolean;
}

/** A downloadable backup of the data an upgrade would clear (Phase 2a export-at-risk). */
export interface AtRiskExport {
  instanceId: string;
  instanceName: string;
  fromVersion: number;
  toVersion: number;
  statuses: Record<string, unknown>[];
  history: Record<string, unknown>[];
  attributes: Record<string, unknown>[];
  documents: Record<string, unknown>[];
}

/** Text extracted from an uploaded seed document (POST /api/definitions/extract-document, KEY-183). */
export interface ExtractedSourceDocument {
  text: string;
  charCount: number;
  /** True when the source exceeded the server's character cap and was clipped. */
  truncated: boolean;
  fileName: string;
}

/** Admin's intent for an AI portfolio blueprint (KEY-160): a cheap tree plan of the dials to build. */
export interface GenerateBlueprintRequest {
  description: string;
  industry?: string;
  audience?: string;
  model?: string;
}

/**
 * One proposed dial in a portfolio blueprint. The tree is formed via parentKey (absent = top level; a
 * non-null parent must be a node whose suggestedKind is "Group"). description/goal/suggestedKind are the
 * brief for generating this dial in full later — not a built definition.
 */
export interface BlueprintNode {
  key: string;
  name: string;
  description: string;
  suggestedKind: string;
  parentKey?: string | null;
  goal?: string | null;
  /** Group nodes only: AI-proposed named score tiers (segmentation) carried onto the assembled group (KEY-162). */
  classificationBands?: GeneratedClassificationBand[];
}

/** An AI-proposed portfolio (KEY-160): a flat list of nodes forming a tree via parentKey. Transient. */
export interface GeneratedBlueprint {
  name: string;
  nodes: BlueprintNode[];
  industry?: string | null;
}

export interface GeneratedStatus {
  key: string;
  name: string;
  colour: string;
  order: number;
  allowedRoles: string[];
  description?: string;
  isTerminal: boolean;
  /** True when a system admin has locked this status's colour so it can't be changed (carries into
   * definitions and dimensions). Optional on the wire; treat a missing value as false. */
  colourLocked?: boolean;
  /** "Not applicable": an item at this status is ignored by the completion rollup — it counts towards
   * neither the complete tally nor the total. Mutually exclusive with `isTerminal`. Optional on the wire;
   * treat a missing value as false. */
  isExcludedFromRollup?: boolean;
  /**
   * Node-attribute keys that must carry a value on the node before this status may be set there (KEY-248);
   * null/empty = no gate. Keys name attributes of the level this palette scores — Segment attributes for a
   * segment palette, Item attributes for an item dimension. The server enforces this and drops keys the
   * definition no longer declares; a library palette never carries any.
   */
  requiredAttributeKeys?: string[] | null;
  /**
   * Whether a node at this status may — or must — carry a review date (KEY-412): "Optional" (the default,
   * and what a missing value means), "NotOffered", or "Required". A property of the status, so it holds
   * wherever the status is applied — an action point's own status or an item's on one axis. Unlike
   * `requiredAttributeKeys` a library palette DOES carry this, since it names no definition-specific field.
   */
  reviewDatePolicy?: string | null;
  /**
   * "Needs a comment" (KEY-919): moving an action point or an item TO this status must come with a comment
   * saying why — sent as the status change's `note`. Re-setting the status a node already holds is exempt,
   * and sections aren't covered. The server refuses a blank note with a 400. Null/absent = false. Kept on a
   * library palette, like `reviewDatePolicy`.
   */
  requiresComment?: boolean | null;
  /**
   * Who may set it, beside `allowedRoles` (KEY-918): "Members" (never a guest), "Guests", or "Responsible"
   * (only the person the Responsible person field names at the node — read with inheritance — and, where it
   * names nobody, only a dial admin). Null/absent = "Everyone": the roles alone decide. A dial admin always
   * passes. On an instance read this is the dial's own audience where an admin has overridden it.
   */
  audience?: StatusAudience | null;
  /** KEY-916: picking this status hands the node on — it also allocates it to the next person, who must be named
   * with a due date (`handOnTo`). Null/absent = false. */
  handsOn?: boolean | null;
  /**
   * Due-date ladder, upper rungs (KEY-LADDER): the system moves a dated node to this status once its review
   * date is within this many days — 7 for "due soon", 0 for "today". Null/absent = not a rung. Kept on a
   * library palette, like `reviewDatePolicy`.
   */
  autoAtLeadDays?: number | null;
  /** Due-date ladder, bottom rung (KEY-LADDER): the system moves a dated node here once its date has passed.
   * The date is kept, so the node stays overdue until somebody records what happened. Absent = false. */
  autoWhenOverdue?: boolean;
  /**
   * Fields that start again with the next round (KEY-NEXTROUND): when a node reaches this status and its
   * review recurs, these node-attribute keys are cleared, old values kept in history. Terminal statuses only;
   * keys name attributes of the level this palette scores, and a library palette never carries any.
   */
  nextRoundClearAttributeKeys?: string[] | null;
  /** The due-date ladder holds off here (KEY-763): a node at this status keeps it whatever its review date
   * says. Lets a terminal status carry a date the ladder must not walk, e.g. an attended appointment's
   * "book the next one" date. Absent = false. */
  holdsLadder?: boolean;
  /**
   * The follow-up (KEY-764): moving a node here with no date of its own schedules it to move on to this
   * status (a key in the SAME palette) after `followUpAfterCount` `followUpAfterUnit`, with the usual
   * auto-revert notification. Null/absent = none. The server drops one that names no status in the palette.
   */
  followUpStatusKey?: string | null;
  /** "Days" | "Weeks" | "Months": the follow-up interval's unit. */
  followUpAfterUnit?: string | null;
  /** The follow-up interval's count (1 or more). */
  followUpAfterCount?: number | null;
}

/** A named, ordered set of statuses — the shape shared by a dial's default palette, an item status
 * axis's palette, and a definition-scoped custom palette (KEY-264). */
export interface GeneratedStatusSet {
  name: string;
  statuses: GeneratedStatus[];
}

/**
 * One author-provided guidance video on a node. The same shape serves authoring and viewing: on save the
 * client sends `key`/`title`/`kind`/`isDefault`/`order` plus `ref` (an uploaded blob's storage reference)
 * or `url` (an external link); on read the server additionally fills `embedKind` + `playbackUrl`.
 */
export interface GuidanceVideo {
  key: string;
  title: string;
  kind: "Upload" | "External";
  isDefault?: boolean;
  order?: number;
  /** Opaque blob storage reference (Upload) — round-trips on save; not used directly for playback. */
  ref?: string | null;
  /** The pasted URL (External). */
  url?: string | null;
  /** Resolved for the viewer: how to play it. */
  embedKind?: "youtube" | "vimeo" | "file" | null;
  /** Resolved for the viewer: a provider embed URL, a direct file URL, or a short-lived SAS URL. */
  playbackUrl?: string | null;
  /**
   * What it plays (KEY-565): "Video", or "Audio" — a native `<audio>` player, never provider-detected. Absent
   * or null reads as "Video", which is all a row written before audio existed can be.
   */
  mediaKind?: "Video" | "Audio" | null;
}

/** The storage reference to submit as a video's `ref` (`kind: "Upload"`) plus the short-lived URL to PUT
 * the bytes to — see {@link ApiClient.requestGuidanceVideoUpload}. */
/**
 * An author-provided document attached to a node's guidance (KEY-364) — the template, checklist or form
 * the dial ships with, as opposed to the evidence users upload against a running instance.
 *
 * Uploads only: a document that lives at a URL is a {@link GeneratedLink}, and two ways to say the same
 * thing would only split authoring.
 */
export interface GuidanceDocument {
  key: string;
  title: string;
  /** Opaque blob storage reference — round-trips on save; not used directly for download. */
  ref: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  order?: number;
  /** Resolved for the viewer: a short-lived SAS URL, or null when this server can't back the reference. */
  downloadUrl?: string | null;
}

export interface GuidanceDocumentUploadTicket {
  reference: string;
  uploadUrl: string;
}

export interface GuidanceVideoUploadTicket {
  reference: string;
  uploadUrl: string;
}

/**
 * A link to an external resource shown alongside a node's guidance. Authored in admin; the URL is
 * validated to an http/https/mailto scheme before it is stored and before it is rendered.
 */
export interface GeneratedLink {
  label: string;
  url: string;
}

export interface GeneratedItem {
  key: string;
  name: string;
  description?: string;
  guidance?: string;
  videos?: GuidanceVideo[];
  /** External-resource links shown with this item's guidance. */
  links?: GeneratedLink[];
  /** Author-provided documents shown with this item's guidance. */
  documents?: GuidanceDocument[];
  /** The per-item expiry switch (KEY-412): false means this item does no review dates on any axis, and a
   * "Required" status is treated as "Optional" here rather than making the item unsettable. Optional on
   * the wire; treat a missing value as true. */
  allowsReviewDate?: boolean;
}

export interface GeneratedSegment {
  key: string;
  label: string;
  name: string;
  items: GeneratedItem[];
  description?: string;
  guidance?: string;
  /**
   * Names a palette that overrides the definition's default for this segment; null/absent inherits
   * the default. Resolved against the definition's own `customPalettes` first and the reusable
   * library second, so a definition-scoped palette wins a name it shares with a library one.
   * Referenced by name (not server id) so export/import round-trips.
   */
  palette?: string | null;
  videos?: GuidanceVideo[];
  /** External-resource links shown with this segment's guidance. */
  links?: GeneratedLink[];
  /** Author-provided documents shown with this segment's guidance. */
  documents?: GuidanceDocument[];
  /** KEY-767: on a rolling-days section, when this time of day starts ("HH:mm"). */
  startsAt?: string | null;
  /**
   * KEY-853: this action point's own item-card setting, overriding its section's `simpleItemCards` — true Simple,
   * false Tabbed, absent/null follows the section. Carried by `ResolvedSegment` too; custom segments and day slots
   * never set it.
   */
  simpleItemCards?: boolean | null;
}

/** KEY-771: how a days section colours each time of day from its items — see `GeneratedSection.slotColouring`. */
export interface DaySlotColouring {
  dimensionKey: string;
  defaultStatusKey: string;
  importantStatusKey?: string | null;
  importantKeywords?: string[] | null;
  /** What a time of day with nothing in it that counts shows: a status on the check, or null for no colour ("Not set"). */
  emptyStatusKey?: string | null;
}

export interface GeneratedSection {
  key: string;
  label: string;
  segments: GeneratedSegment[];
  description?: string;
  guidance?: string;
  videos?: GuidanceVideo[];
  /** External-resource links shown with this section's guidance. */
  links?: GeneratedLink[];
  /**
   * When true the rendered dial shows the section's short `key` (its mnemonic) instead of the full
   * `label` — handy where space is tight (the outer ring band). Defaults false (show the label).
   */
  showMnemonic?: boolean;
  /** Author-provided documents shown with this section's guidance. */
  documents?: GuidanceDocument[];
  /**
   * KEY-736: `"Days"` makes this a rolling run of `dayCount` days, today first, generated when the dial is read in
   * the dial's time zone — so it authors no `segments`. Absent (or `"Standard"`) is an ordinary section.
   */
  kind?: "Days" | "Standard" | null;
  /** How many days a `"Days"` section shows (1–14, default 7). */
  dayCount?: number | null;
  /** How busy a day must be to take each colour: at least `minEvents` events shows `statusKey` from the palette. */
  busyBands?: { minEvents: number; statusKey: string }[] | null;
  /**
   * KEY-771: colour each time of day from what's IN it instead of how busy it is. Every item counts by its status on
   * the check `dimensionKey`, and the slot shows whichever comes first in that check's palette — so order it Missed,
   * Important, Coming up, Done. An unset item counts as `defaultStatusKey`; a calendar event the calendar flags, or
   * whose title or categories contain one of `importantKeywords`, counts as `importantStatusKey` until someone sets it.
   * Absent keeps the busyness colours.
   */
  slotColouring?: DaySlotColouring | null;
  /** KEY-767: on a ResolvedSection, the date (`yyyy-MM-dd`) this day of a rolling-days section stands for. */
  date?: string | null;
  /**
   * KEY-753: what this section's items are called — "appointment" makes the apps say "Add appointment". Absent or
   * null means "item". Only a complete pair (with `itemNounPlural`) is kept; read it through `itemNoun()`.
   */
  itemNounSingular?: string | null;
  /** KEY-753: the plural of `itemNounSingular` — stored, never derived. */
  itemNounPlural?: string | null;
  /**
   * KEY-755: the colour this section's band is drawn in, lower-case `#rrggbb`. Absent or null means the platform's
   * structural section colour. The server clears anything that isn't a hex colour. The status edge is drawn over it.
   */
  colour?: string | null;
  /** KEY-795: what this section's outer edge shows. Absent or null is `Auto` — the edge as it always was. */
  colouring?: SectionColouring | null;
  /**
   * KEY-762: what this section's segments (its lists) are called — "dentist" makes the apps say "Add dentist" in this
   * section. Overrides the definition's action-point noun (KEY-539) here only. Absent or null means the definition's
   * noun. Only a complete pair (with `segmentNounPlural`) is kept; read it through `segmentNoun(detail, sectionKey)`.
   */
  segmentNounSingular?: string | null;
  /** KEY-762: the plural of `segmentNounSingular` — stored, never derived. */
  segmentNounPlural?: string | null;
  /**
   * KEY-762: item cards in this section are a single pane — no Files or Discussion tabs, the Details fields shown
   * within Progress. Absent, null or false keeps the tabbed card.
   */
  simpleItemCards?: boolean | null;
}

/** KEY-795: what a section's outer edge shows. */
export type SectionColourSource = "Auto" | "None" | "HandSet" | "Busyness" | "Rule" | "GroupRollup";

/** KEY-795: one colour in a section's own palette. */
export interface SectionColourStatus {
  key: string;
  name: string;
  /** Lower-case `#rrggbb`. */
  colour?: string | null;
}

/**
 * KEY-795: how a section colours its outer edge. Under `Rule` the edge is worked out from the section's items on every
 * read: `overrides` (rungs may also be `anyMissed` / `allMissed`) then `bands`, naming colours in `palette`. Absent
 * overrides and bands use the default ladder (any missed → missed; 0 → not-started; above 0 → under-way; all → done);
 * an absent palette uses the default four colours.
 */
export interface SectionColouring {
  source: SectionColourSource;
  palette?: SectionColourStatus[] | null;
  overrides?: GeneratedItemSegmentRuleCondition[] | null;
  bands?: GeneratedSegmentProgressBand[] | null;
  /** What a section with nothing in it shows; absent draws no edge. */
  emptyStatusKey?: string | null;
  /** The check whose done statuses make an item complete; absent means every check that gates completion. */
  completionDimensionKey?: string | null;
  /** Statuses that mean an item was missed, whatever the time. */
  missedStatuses?: { dimensionKey: string; statuses: string[] }[] | null;
  /** Whether calendar events count as items. Defaults to true. */
  includeEvents?: boolean;
  /** When something not done counts as missed by itself; absent never. */
  autoMissed?: { enabled: boolean; graceMinutes: number; events: boolean } | null;
  split?: "Inherit" | "On" | "Off" | null;
  /** Whether a person may set the edge by hand over the rule. */
  allowHandSet?: boolean;
}

/** A status dimension applied to every item of an itemised dial; carries its own inline palette. */
export interface GeneratedDimension {
  key: string;
  label: string;
  order: number;
  palette: { name: string; statuses: GeneratedStatus[] };
  requiredForCompletion?: boolean;
  guidance?: string;
  /** External-resource links shown with this dimension's guidance. */
  links?: GeneratedLink[];
  /**
   * The items this dimension scores, as "sectionKey/segmentKey/itemKey" paths. Absent/empty = every item,
   * which is the default and how every dimension behaved before scoping existed. Use
   * {@link itemScopeApplies} rather than comparing entries by hand — legacy bare keys match differently.
   */
  appliesToItemKeys?: string[] | null;
  /** KEY-809: what an item's date is called on this check, e.g. "Target date". Absent/null = "Review date". */
  dateLabel?: string | null;
}

/**
 * Declares an extra metadata field carried by nodes of a given level (KEY-161) — e.g. an "owner" on
 * every segment. The declaration is template data; per-node values are set on the instance.
 * target is one of {@link NODE_ATTRIBUTE_TARGETS}; type is one of {@link NODE_ATTRIBUTE_TYPES}.
 */
export interface GeneratedNodeAttribute {
  key: string;
  label: string;
  target: string;
  type: string;
  order?: number;
  guidance?: string | null;
  /** Per-attribute permissions (KEY-299). Null/absent = no restriction: visible to everyone with the
   * scope / editable per the dial-wide AttributeEdit matrix. Admin is always retained when set. The
   * server prunes restricted VALUES and refuses restricted writes regardless of these. */
  visibleToRoles?: string[] | null;
  editableByRoles?: string[] | null;
  /** Document/Image only: whether the node may hold more than one file. Ignored for every other type. */
  allowMultiple?: boolean;
  /**
   * The nodes this attribute applies to; null/empty = every node at this attribute's `target` level.
   * Shaped per target (KEY-269): Section is a bare section key, Segment is a qualified
   * `"sectionKey/segmentKey"` pair, Item is a qualified `"sectionKey/segmentKey/itemKey"` path — see
   * {@link attributeScopeApplies}. Item keys are unique only within their segment (and segment keys only
   * within their section), so a bare key can't reliably name one node; Item-target entries saved before
   * the qualified scheme are bare keys and are still matched on the item key alone.
   */
  appliesToKeys?: string[] | null;
  /**
   * Another attribute on this version that pairs with this one (KEY-248) — "a target date and the document
   * that evidences it go together". Advisory: the apps warn when one of the pair is filled in and the other
   * isn't, but neither field is ever blocked. The pairing reads both ways, so declaring it on one side is
   * enough. A key naming no declared attribute (or this attribute itself) is dropped on save.
   */
  requiresAttributeKey?: string | null;
  /**
   * A suggested value the apps pre-fill this field with on a node that hasn't answered it yet (KEY-286).
   * Null/absent = no default; non-null IS the opt-in. It is a *display* default: nothing is stored until
   * someone presses Save on the field, so a default never satisfies a status gate, never appears in an
   * export, and never makes a freshly added custom node look used. Only Text/Date/RichText carry one —
   * the server drops it for every other type, and drops a Date default that isn't an ISO `yyyy-MM-dd`.
   */
  defaultValue?: string | null;
  /**
   * Whether a dial carrying this declaration cannot be opened until the value is supplied (KEY-434).
   * `target: "Dial"` only — the server drops it for every other target, because a per-node requirement
   * would gate the dial on a node the person at the door may not even be able to see.
   *
   * It rides the published version: switching it on leaves every dial already running untouched, and the
   * requirement arrives when a dial is created on, upgraded onto, or imported onto this version. The words
   * the capture screen shows are this attribute's `guidance`.
   */
  requiredAtFirstRun?: boolean;
  /**
   * Whether the action point carrying this field is held back until it is answered (KEY-460): its items
   * are out of reach, and their statuses unwritable, while the field is empty.
   *
   * `target: "Segment"` only — the server drops it for every other target, and for a field fed from a
   * child dial, which nobody on this dial could answer. Like `requiredAtFirstRun` it rides the published
   * version, so switching it on cannot lock an action point on a dial already running; the requirement
   * arrives when a dial is created on, upgraded onto or imported onto this version.
   *
   * Don't work the lock out from this flag alone — a value you cannot SEE is still a value (KEY-299). The
   * server says which action points are actually waiting, in {@link InstanceDetail.segmentRequirements}.
   */
  requiredForNode?: boolean;
  /**
   * The nodes this dial-wide field's single value is ALSO drawn on (KEY-378) — mixed-level and addressed
   * by part count: `"sectionKey"`, `"sectionKey/segmentKey"` or `"sectionKey/segmentKey/itemKey"`. Use
   * {@link attributeShownAt} rather than comparing entries by hand.
   *
   * `target: "Dial"` only, and never for the file-backed types (their values name documents validated
   * against one exact node); the server drops it otherwise. It is a DISPLAY list, not a scope, so
   * null/absent means the dial's own details only — the opposite of `appliesToKeys`, where empty means
   * *every* node. The value stays the single dial-wide row, so every placement reads and writes the same
   * cell and can never drift.
   */
  shownAtKeys?: string[] | null;
  /**
   * The status a file landing in THIS field sets (KEY-280), with the axis it sets it on. Null on every
   * field written before this, and on every field an author hasn't spoken about — a file kept in a field
   * moves nothing unless it was asked to.
   *
   * Honoured only on the file-backed types, and only on `Item` and `Segment` targets (a Section has no
   * status and a dial-wide field has no node to move); the server drops it otherwise. `Segment` bindings
   * carry no dimension — an action point has one status, not one per axis.
   *
   * Authored through a definition file for now: there is no editor for it yet, but the admin editor
   * preserves it across a save.
   */
  uploadSetsDimensionKey?: string | null;
  uploadSetsStatusKey?: string | null;
  /**
   * The answers a `"List"` field offers (KEY-481), in the order they are shown. The stored value IS the
   * option text — there is no key/label pair — so a list answer stays ordinary text to everything that
   * already reads an attribute value: status gates, feeds, history, export, the review table.
   *
   * A `"Table"` field (KEY-568) reuses this same list for its column headers, authored the same way; each
   * row a node saves must carry exactly this many cells, in this order — see
   * {@link parseTableAttributeValue}.
   *
   * Null/absent for every other type; the server drops it there. Normalised on save — see
   * {@link normaliseAttributeOptions}, which mirrors the server's rule so the editor shows what will
   * actually be persisted.
   */
  options?: string[] | null;
  /**
   * Whether somebody answering a `"List"` field may write an answer that isn't on the list (KEY-481).
   * False (the default, and what every field written before this means) closes the list: the server
   * refuses an off-list value. True opens it, and the picker additionally offers values already used
   * elsewhere on the same dial for the same field.
   *
   * A user's own answer is never written back into `options`. The declaration is version-owned template
   * data and one definition is licensed to many organisations, so adding a user's word to it would put one
   * tenant's data on every other tenant's dial — and an instance is pinned to a version, so there is no
   * write path from the user app into a published one anyway. The answer persists on the node's value.
   */
  allowUserValues?: boolean;
  /**
   * The child DEFINITION whose instance under this group answers this field (KEY-483), instead of it being
   * typed on the group. Null/absent — the default, and what every field written before this means — is an
   * ordinary field, answered where it is declared.
   *
   * Its presence is the whole gate: a field with a source renders READ-ONLY wherever it appears, with a
   * line saying where the answer came from, and the server refuses a write to it rather than merely
   * relying on the apps to hide the box. There is no feature flag — a declaration with no source behaves
   * exactly as it always did.
   *
   * Group definitions only, and only for the types whose value can cross dials at all
   * (Text/Date/RichText/Email/Phone/User): the file-backed types hold document ids scoped to one instance,
   * and two dials' `options` lists are unrelated. The server drops it otherwise.
   *
   * A definition id, not an instance id, because the declaration is version-owned template data shared by
   * every organisation licensed the group — each one's own allocated child resolves server-side at read
   * time. That also means the apps cannot name the child from this alone: the readable provenance rides
   * the VALUE, as {@link NodeAttributeValue.sourcedFromLabel}.
   */
  sourceChildDefinitionId?: string | null;
  /** The attribute key to read on that child. Set together with {@link sourceChildDefinitionId} — the
   *  server keeps the pair or neither, because half a source resolves to nothing. */
  sourceAttributeKey?: string | null;
  /**
   * Which node OF THE CHILD carries the answer, addressed by part count exactly as {@link shownAtKeys}
   * entries are: `"sectionKey"`, `"sectionKey/segmentKey"` or `"sectionKey/segmentKey/itemKey"`.
   * Null/absent reads the child's dial-level value, which is the common case.
   */
  sourceNodePath?: string | null;
  /**
   * The heading this field is shown under (KEY-436), or null/absent for a field that stands on its own —
   * which is every field authored before this. Fields at the same level sharing a title are drawn as one
   * bordered, collapsible card.
   *
   * The title IS the group's identity: there is no group key and no group record, so two fields are in the
   * same group exactly when this string matches, and a group stops existing when its last member stops
   * naming it. Renaming therefore means rewriting every member, which the admin editor does in one action
   * because a definition saves as a single payload.
   */
  fieldsetTitle?: string | null;
  /**
   * Whether that group arrives collapsed. Optional like its siblings, and safe to leave absent: `undefined`
   * is falsy, which reads as "starts expanded" — the same thing every field authored before this meant. It
   * is the ARRIVAL state, not a lock: the user app opens a group holding a field the status gate is waiting
   * on, or one with unsaved edits, whatever this says.
   */
  fieldsetStartsCollapsed?: boolean;
  /**
   * On a `"Contact"` field only (KEY-752): the key of a Text or RichText field at the same target whose EMPTY
   * draft the chosen contact's address pre-fills — "With" fills in "Where". A pre-fill, never a write:
   * nothing is stored until the person saves that field, and a value already there is never replaced. The
   * server drops it for every other type, and when it names no Text/RichText field at the same target.
   */
  fillsAttributeKey?: string | null;
}

/**
 * One dial-level attribute a dial may not be opened without (KEY-434), as carried on the 428 that refuses
 * the read. `canSupply` is about the person asking, not the dial: an attribute restricted to
 * administrators still gates everyone, and somebody who cannot fill it in needs to be told to ask rather
 * than shown a form that will refuse them.
 */
export interface FirstRunRequirement {
  attributeKey: string;
  label: string;
  kind: NodeAttributeType;
  guidance?: string | null;
  canSupply: boolean;
  /** A `"List"` requirement's authored answers (KEY-481), so the capture screen draws the same picker the
   *  dial would rather than a box whose typed answer the write path then refuses. */
  options?: string[] | null;
  /** Whether that list also accepts an answer of the person's own (KEY-481). */
  allowUserValues?: boolean;
}

/** One half-filled attribute pairing on a node (KEY-248): `filledLabel` has a value, `emptyLabel` doesn't. */
export interface NodeAttributePairingWarning {
  filledLabel: string;
  emptyLabel: string;
}

/**
 * The pairings (KEY-248) among one node level's declarations that are currently half-satisfied: exactly one
 * of the two fields carries a value. `valueOf` returns the node's current value for an attribute key.
 *
 * The link reads both ways, so a pairing declared on one side, on both sides, or on either, is one pairing
 * and is reported once. Only pairings whose two attributes are both in `decls` are considered — a pairing
 * across node levels can't be judged from a single node's values.
 */
export function nodeAttributePairingWarnings(
  decls: GeneratedNodeAttribute[],
  valueOf: (key: string) => string,
): NodeAttributePairingWarning[] {
  const byKey = new Map(decls.map((d) => [d.key, d]));
  const filled = (key: string) => valueOf(key).trim().length > 0;
  const seen = new Set<string>();
  const warnings: NodeAttributePairingWarning[] = [];
  for (const decl of decls) {
    const other = decl.requiresAttributeKey ? byKey.get(decl.requiresAttributeKey) : undefined;
    if (!other || other.key === decl.key) continue;
    const pairId = JSON.stringify([decl.key, other.key].sort());
    if (seen.has(pairId)) continue;
    seen.add(pairId);
    if (filled(decl.key) === filled(other.key)) continue;
    const [set, unset] = filled(decl.key) ? [decl, other] : [other, decl];
    warnings.push({ filledLabel: set.label, emptyLabel: unset.label });
  }
  return warnings;
}

/** Every declarable node-attribute type, in the order the admin editor offers them. */
export const NODE_ATTRIBUTE_TYPES = [
  "Text", "Date", "RichText", "Document", "Image", "Video", "User", "Email", "Phone", "List", "Table", "Person", "Contact",
  "ResponsiblePerson",
] as const;

/**
 * The levels an attribute can hang off. "Dial" (KEY-434) is a fact about the whole instance rather than
 * any part of it — an emergency contact, a site address — so it takes no `appliesToKeys`, and it is the
 * only target that may set `requiredAtFirstRun`. "Segment" is likewise the only target that may set
 * `requiredForNode` (KEY-460): an action point is the only node with items beneath it to hold back.
 */
export const NODE_ATTRIBUTE_TARGETS = ["Dial", "Section", "Segment", "Item"] as const;

export type NodeAttributeTarget = (typeof NODE_ATTRIBUTE_TARGETS)[number];

export type NodeAttributeType = (typeof NODE_ATTRIBUTE_TYPES)[number];

/**
 * Whether an attribute's value references uploaded files rather than holding text (KEY-247). These fields
 * are edited through the document pipeline, are excluded from AI extraction, and their value is JSON — see
 * {@link parseDocumentAttributeValue} and {@link parseVideoAttributeValue}.
 */
export function isFileAttributeType(type: string): boolean {
  return type === "Document" || type === "Image" || type === "Video";
}

/**
 * Whether an attribute's value names a person rather than holding text (KEY-267). The value is a bare user
 * id, and the only people it may name are the instance's own members — so the field is edited with a picker
 * over `InstanceDetail.members` and the server rejects any id that isn't one of them.
 */
export function isUserAttributeType(type: string): boolean {
  return type === "User";
}

/**
 * Whether an attribute's value names somebody from the dial's own people list (KEY-PEOPLE) — a person the
 * dial keeps track of who need not hold an account, unlike {@link isUserAttributeType}. The value is one
 * person id, or a JSON array of them when the declaration allows more than one; see
 * {@link parsePersonAttributeValue}. The picker draws from `InstanceDetail.people`, and the server rejects
 * any id that isn't one of them.
 */
export function isPersonAttributeType(type: string): boolean {
  return type === "Person";
}

/**
 * Whether an attribute's value names one entry from the dial's own contacts list (KEY-752) — the dentist,
 * the school office — saved once with a phone, address and email. Not the typed Email/Phone fields
 * (KEY-434), which hold one way of reaching somebody written in place. The value is one contact id; see
 * {@link parseContactAttributeValue}. The picker draws from `InstanceDetail.contacts`, and the server
 * rejects any id that isn't one of them.
 */
export function isContactAttributeType(type: string): boolean {
  return type === "Contact";
}

/**
 * Whether an attribute's value names who is responsible for a node (KEY-917): ONE dial member or ONE of the
 * dial's contacts, stored as `user:<id>` / `contact:<id>` — see {@link parseResponsibleAttributeValue}. Read
 * with inheritance: a node with no answer of its own takes the nearest one above it (item → segment →
 * section → dial → the parent group dial, carried as `InstanceDetail.parentResponsible`).
 */
export function isResponsibleAttributeType(type: string): boolean {
  return type === "ResponsiblePerson";
}

/** Who a Responsible-person value names. */
export interface ResponsibleRef {
  kind: "Member" | "Contact";
  id: string;
}

/** Reads a Responsible-person value, or null for an empty or malformed one (never throws). */
export function parseResponsibleAttributeValue(value: string | null | undefined): ResponsibleRef | null {
  const match = /^(user|contact):([0-9a-f-]{36})$/i.exec((value ?? "").trim());
  if (!match) return null;
  return { kind: match[1].toLowerCase() === "user" ? "Member" : "Contact", id: match[2].toLowerCase() };
}

/** The stored spelling of a Responsible-person value. */
export function formatResponsibleAttributeValue(ref: ResponsibleRef): string {
  return `${ref.kind === "Member" ? "user" : "contact"}:${ref.id}`;
}

/** The contact id a Contact value names, or null for an empty or malformed value (never throws). A
 * one-element array reads the same as a bare id, as a single-person value does. */
export function parseContactAttributeValue(value: string | null | undefined): string | null {
  const ids = parsePersonAttributeValue(value);
  return ids.length === 1 ? ids[0] : null;
}

/** Whether a declaration of this type may name a field it pre-fills (KEY-752) — only a Contact. */
export function attributeTypeFillsAnother(type: string): boolean {
  return isContactAttributeType(type);
}

/** The types a Contact field's address may pre-fill (KEY-752): somewhere text is typed. Mirrors the
 * server's `NodeAttributeKinds.IsFillableFromContact`. */
export function isFillableFromContactType(type: string): boolean {
  return type === "Text" || type === "RichText";
}

/**
 * The types that may hold more than one value when the author ticks "allow more than one": several files,
 * several images, or several people. Mirrors the server's `NodeAttributeKinds.SupportsMultiple`.
 */
export function attributeTypeSupportsMultiple(type: string): boolean {
  return type === "Document" || type === "Image" || type === "Person";
}

/**
 * The person ids a Person value names, in order (KEY-PEOPLE). A bare id and a one-element array read the
 * same; a malformed payload reads as naming nobody, so a bad value shows as empty rather than crashing the
 * card. Never throws.
 */
export function parsePersonAttributeValue(value: string | null | undefined): string[] {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return [];
  if (!trimmed.startsWith("[")) return [trimmed];
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string" && x.length > 0) : [];
  } catch {
    return [];
  }
}

/** The value to store for a Person field: a bare id for a single-person field, a JSON array otherwise; empty clears. */
export function serialisePersonAttributeValue(ids: readonly string[], allowMultiple: boolean): string {
  if (ids.length === 0) return "";
  return allowMultiple ? JSON.stringify(ids) : ids[0];
}

/**
 * Whether AI document analysis may propose a value for an attribute of this type. Mirrors the server's
 * `NodeAttributeKinds.IsAiExtractable` (`KeyDials.Domain/Definitions/NodeAttributeDefinition.cs`), which is
 * the write path's own gate: the bulk-add endpoint silently drops values for any other type, so offering
 * one on the import review screen would ask the user to approve something that is then thrown away.
 *
 * Deliberately an ALLOWLIST, like the server's. A denylist has to be remembered every time a new attribute
 * type is added, and forgetting it re-opens exactly that silent drop — which is how Email and Phone
 * (KEY-434), and then List (KEY-481), came to be offered for extraction.
 */
export function isAiExtractableAttributeType(type: string): boolean {
  return type === "Text" || type === "Date";
}

/**
 * Whether an attribute's value is one answer chosen from a list the author wrote out (KEY-481). The value
 * is still plain text — the option text itself — so nothing that merely reads a value needs to know this;
 * it is the field's *editor* that changes, from a box into a picker.
 */
export function isListAttributeType(type: string): boolean {
  return type === "List";
}

/**
 * Whether an attribute's value is rows of data against columns the author wrote out (KEY-568). The columns
 * are authored the same way a List's answers are — this field's `options` — but the value itself is JSON;
 * see {@link parseTableAttributeValue}.
 */
export function isTableAttributeType(type: string): boolean {
  return type === "Table";
}

/** Most options one list may hold, and the longest one option may be — mirrors `NodeAttributeOptions`. */
export const ATTRIBUTE_OPTION_LIMITS = { maxOptions: 200, maxLength: 200 } as const;

/**
 * The answer list as the server will persist it (KEY-481): each option trimmed, blanks dropped, over-long
 * ones clamped, duplicates removed case-insensitively (the FIRST spelling wins, because that is the one the
 * author typed first and the one a saved value is canonicalised to), and the whole thing capped. Author
 * order is kept — the order answers are offered in is a deliberate choice, not something to sort away.
 *
 * The twin of the server's `NodeAttributeOptions.Normalise`. Kept in step deliberately: the editor showing
 * a different list from the one that will be saved is how an author loses an option without being told.
 */
export function normaliseAttributeOptions(options: readonly string[] | null | undefined): string[] {
  if (!options) return [];
  const kept: string[] = [];
  const seen = new Set<string>();
  for (const raw of options) {
    const option = (raw ?? "").trim().slice(0, ATTRIBUTE_OPTION_LIMITS.maxLength);
    if (!option) continue;
    const folded = option.toLowerCase();
    if (seen.has(folded)) continue;
    seen.add(folded);
    kept.push(option);
    if (kept.length >= ATTRIBUTE_OPTION_LIMITS.maxOptions) break;
  }
  return kept;
}

/**
 * The authored spelling of `value` within `options`, or null when the list doesn't offer it — the twin of
 * the server's `NodeAttributeOptions.TryCanonicalise`. Matching ignores case and surrounding space, which
 * is what makes "north west" and "North West" one answer when the field is counted.
 */
export function attributeOptionMatch(options: readonly string[] | null | undefined, value: string): string | null {
  const wanted = value.trim();
  if (!wanted) return null;
  const folded = wanted.toLowerCase();
  return (options ?? []).find((o) => o.trim().toLowerCase() === folded) ?? null;}

/** How a Video attribute's value sources its footage. */
export type VideoAttributeValue =
  | { kind: "Upload"; documentId: string }
  | { kind: "Link"; url: string };

/** The document ids a Document/Image attribute value names, in order; empty when unset or malformed. */
export function parseDocumentAttributeValue(value: string): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string" && id.length > 0);
  } catch {
    return [];
  }
}

/** Serialises a Document/Image attribute value; an empty list serialises to "" so the server clears it. */
export function serialiseDocumentAttributeValue(documentIds: readonly string[]): string {
  return documentIds.length === 0 ? "" : JSON.stringify(documentIds);
}

/** Most rows one table value may hold, and the longest one cell may be — mirrors `NodeAttributeTableValue`. */
export const ATTRIBUTE_TABLE_LIMITS = { maxRows: 500, maxCellLength: 2000 } as const;

/**
 * The rows of a Table attribute value (KEY-568), or `null` when unset or malformed — the twin of the
 * server's `NodeAttributeTableValue.TryParse`. Each row is a cell per `columnCount`, in column order; a row
 * with the wrong cell count, a value over either cap, or JSON that isn't an array of arrays of strings is
 * malformed, same as the server treats it. `columnCount` is the declaration's (normalised) `options.length`
 * at the time of parsing — pass the CURRENT column count, not the one the value was saved against, so a
 * value shaped for a column set that has since been re-authored is reported as malformed rather than
 * silently misaligned.
 */
export function parseTableAttributeValue(value: string, columnCount: number): string[][] | null {
  if (!value) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length > ATTRIBUTE_TABLE_LIMITS.maxRows) return null;
  const rows: string[][] = [];
  for (const row of parsed) {
    if (!Array.isArray(row) || row.length !== columnCount) return null;
    if (row.some((cell) => typeof cell !== "string" || cell.length > ATTRIBUTE_TABLE_LIMITS.maxCellLength)) {
      return null;
    }
    rows.push(row as string[]);
  }
  return rows;
}

/** Serialises a Table attribute value; an empty list serialises to "" so the server clears it. */
export function serialiseTableAttributeValue(rows: readonly (readonly string[])[]): string {
  return rows.length === 0 ? "" : JSON.stringify(rows);
}

/** The parsed Video attribute value, or null when unset or malformed. */
export function parseVideoAttributeValue(value: string): VideoAttributeValue | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { kind, documentId, url } = parsed as Record<string, unknown>;
    if (kind === "Upload" && typeof documentId === "string" && documentId.length > 0) {
      return { kind: "Upload", documentId };
    }
    if (kind === "Link" && typeof url === "string" && isSafeHttpUrl(url)) {
      return { kind: "Link", url: url.trim() };
    }
    return null;
  } catch {
    return null;
  }
}

/** Serialises a Video attribute value; null serialises to "" so the server clears it. */
export function serialiseVideoAttributeValue(value: VideoAttributeValue | null): string {
  return value === null ? "" : JSON.stringify(value);
}

/** Mirrors the server's link check: an absolute http(s) URL and nothing else. */
export function isSafeHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url.trim());
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/** Separates the key parts of a qualified `appliesToKeys` entry. */
const ITEM_SCOPE_SEPARATOR = "/";

/** Builds the `appliesToKeys` entry naming exactly one item of a definition's structure. */
export function qualifyItemScope(sectionKey: string, segmentKey: string, itemKey: string): string {
  return `${sectionKey}${ITEM_SCOPE_SEPARATOR}${segmentKey}${ITEM_SCOPE_SEPARATOR}${itemKey}`;
}

/** The item-position marker meaning "every item under this action point, present or future" (KEY-SCOPE). */
export const ITEM_SCOPE_WILDCARD = "*";

/**
 * Builds the scope entry naming every item of one action point, INCLUDING items an instance adds later
 * (KEY-SCOPE) — the one thing an exact item path can never say, because an instance-added item's key is
 * freshly minted. Mirrors the server's `NodeAttributeItemScope.QualifySegmentWide`.
 */
export function qualifySegmentWideItemScope(sectionKey: string, segmentKey: string): string {
  return `${sectionKey}${ITEM_SCOPE_SEPARATOR}${segmentKey}${ITEM_SCOPE_SEPARATOR}${ITEM_SCOPE_WILDCARD}`;
}

/** Whether a scope entry is segment-wide — see {@link qualifySegmentWideItemScope}. */
export function isSegmentWideItemScope(entry: string): boolean {
  const parts = entry.split(ITEM_SCOPE_SEPARATOR);
  return parts.length === 3 && parts.every(Boolean) && parts[2] === ITEM_SCOPE_WILDCARD;
}

/** Whether a scope names the whole of this action point (a segment-wide entry for it). False for an empty scope. */
export function itemScopeCoversSegment(appliesToItemKeys: string[] | null | undefined, sectionKey: string, segmentKey: string): boolean {
  return (appliesToItemKeys ?? []).some((entry) => {
    const parts = entry.split(ITEM_SCOPE_SEPARATOR);
    return parts.length === 3 && parts[0] === sectionKey && parts[1] === segmentKey && parts[2] === ITEM_SCOPE_WILDCARD;
  });
}

/** Builds the `appliesToKeys` entry naming exactly one section of a definition's structure (KEY-269). */
export function qualifySectionScope(sectionKey: string): string {
  return sectionKey;
}

/** Builds the `appliesToKeys` entry naming exactly one segment of a definition's structure (KEY-269). */
export function qualifySegmentScope(sectionKey: string, segmentKey: string): string {
  return `${sectionKey}${ITEM_SCOPE_SEPARATOR}${segmentKey}`;
}

/** The section position of a day scope entry ("day-" plus the wildcard) — see {@link itemScopeApplies} (KEY-808). */
export const ITEM_SCOPE_ANY_DAY = "day-*";

/** A generated day's section key: `day-2026-10-10`. */
const DAY_SECTION_KEY = /^day-\d{4}-\d{2}-\d{2}$/;

/** True for a day scope entry, which the server derives on save and the editor never shows. */
export function isDayItemScope(entry: string): boolean {
  return entry.startsWith(`${ITEM_SCOPE_ANY_DAY}${ITEM_SCOPE_SEPARATOR}`);
}

/**
 * Whether a scope list covers the named item. An absent/empty scope means every item (including items an
 * instance admin adds later). Mirrors the server's `NodeAttributeItemScope`, legacy fallback included: a
 * bare entry (no separator) matches that item key wherever it appears, which is the breadth it had when it
 * was written.
 *
 * Shared by Item-target node attributes and item-scoped status dimensions — both use the same addressing.
 * A segment-wide entry (`section/segment/*`, KEY-SCOPE) matches every item under that action point,
 * whatever its key — which is how an instance-added item comes to carry the field or the scale.
 *
 * A day entry (section `day-*`, then a time of day, then the wildcard; KEY-808) matches every item in that time of day on every generated day of
 * a Days section (`day-2026-10-10` / `day-2026-10-10.morning`). The server derives these from the template
 * slot's own segment-wide entry when the definition is saved.
 */
export function itemScopeApplies(
  appliesToItemKeys: string[] | null | undefined,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): boolean {
  if (!appliesToItemKeys?.length) return true;
  return appliesToItemKeys.some((entry) => {
    const parts = entry.split(ITEM_SCOPE_SEPARATOR);
    if (parts.length === 3 && parts.every(Boolean)) {
      if (parts[0] === ITEM_SCOPE_ANY_DAY) {
        return parts[2] === ITEM_SCOPE_WILDCARD
          && DAY_SECTION_KEY.test(sectionKey)
          && segmentKey === `${sectionKey}.${parts[1]}`;
      }
      return parts[0] === sectionKey && parts[1] === segmentKey && (parts[2] === ITEM_SCOPE_WILDCARD || parts[2] === itemKey);
    }
    return parts.length === 1 && entry === itemKey;
  });
}

/** @deprecated Prefer {@link itemScopeApplies} — the scheme is no longer attribute-specific. */
export const nodeAttributeAppliesToItem = itemScopeApplies;

/**
 * Whether a Section-target attribute's scope list covers the named section (KEY-269). An absent/empty
 * scope means every section. Unlike {@link itemScopeApplies} there is no legacy bare-key fallback:
 * Section scoping didn't exist before KEY-269, so every entry is already qualified (a bare section key).
 */
export function sectionScopeApplies(appliesToKeys: string[] | null | undefined, sectionKey: string): boolean {
  if (!appliesToKeys?.length) return true;
  return appliesToKeys.includes(sectionKey);
}

/**
 * Whether a Segment-target attribute's scope list covers the named segment (KEY-269). An absent/empty
 * scope means every segment. Entries are qualified `"sectionKey/segmentKey"` pairs, with no legacy
 * bare-key fallback (this scoping is new, unlike the Item scheme's).
 */
export function segmentScopeApplies(
  appliesToKeys: string[] | null | undefined,
  sectionKey: string,
  segmentKey: string,
): boolean {
  if (!appliesToKeys?.length) return true;
  return appliesToKeys.some((entry) => {
    const parts = entry.split(ITEM_SCOPE_SEPARATOR);
    return parts.length === 2 && parts[0] === sectionKey && parts[1] === segmentKey;
  });
}

/**
 * Whether a node attribute's scope list covers the named node, dispatching on the attribute's own
 * `target` level (KEY-269): Section matches a bare section key, Segment a qualified
 * `"sectionKey/segmentKey"` pair, Item the existing qualified path (falling back to
 * {@link itemScopeApplies}'s legacy bare-key matching).
 */
export function attributeScopeApplies(
  target: string,
  appliesToKeys: string[] | null | undefined,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): boolean {
  switch (target) {
    case "Section":
      return sectionScopeApplies(appliesToKeys, sectionKey);
    case "Segment":
      return segmentScopeApplies(appliesToKeys, sectionKey, segmentKey);
    default:
      return itemScopeApplies(appliesToKeys, sectionKey, segmentKey, itemKey);
  }
}

/**
 * Whether a dial-wide field is also DRAWN on the named node (KEY-378). Deliberately separate from
 * {@link attributeScopeApplies}: that one answers "does this node have its own value", reads an empty
 * list as "every node", and falls through to {@link itemScopeApplies}'s legacy bare-key matching for any
 * unrecognised target — all three of which are wrong here.
 *
 * Placements are mixed-level in one list, so the depth of an entry is what picks the level, and matching
 * is strict: a 1-part entry names a SECTION and never an item that happens to share its key. An empty or
 * absent list draws the field nowhere but the dial's own details, which is where a dial-wide field has
 * always been shown.
 *
 * Pass `""` for the levels below the node being asked about — a section asks
 * `(keys, "s1", "", "")`, an action point `(keys, "s1", "sg1", "")`, an item all three.
 */
export function attributeShownAt(
  shownAtKeys: string[] | null | undefined,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): boolean {
  if (!shownAtKeys?.length) return false;
  const depth = itemKey ? 3 : segmentKey ? 2 : 1;
  return shownAtKeys.some((entry) => {
    const parts = entry.split(ITEM_SCOPE_SEPARATOR);
    if (parts.length !== depth || parts.some((p) => !p)) return false;
    return parts[0] === sectionKey
      && (depth < 2 || parts[1] === segmentKey)
      && (depth < 3 || parts[2] === itemKey);
  });
}

/**
 * The per-instance customisation envelope carried by a definition version: gates + caps bounding what a
 * licensee instance admin may change on an instance's structure. Null/absent means customisation is off.
 */
export interface CustomisationPolicy {
  enabled: boolean;
  /** Cap on total sections an instance may have (base + added). Must be ≥ the base section count. */
  maxSections: number;
  /** Cap on segments per section; null = unbounded. */
  maxSegmentsPerSection?: number | null;
  /** Cap on items per segment; null = unbounded. */
  maxItemsPerSegment?: number | null;
  allowAddSections: boolean;
  allowAddSegments: boolean;
  allowAddItems: boolean;
  allowEditWording: boolean;
  allowDisableBaseNodes: boolean;
  /** Base section keys that may never be re-worded or disabled. */
  lockedSectionKeys: string[];
}

/**
 * A Group Key Dial link: the child definition (server-local id) whose instance rolls up into the
 * overview segment named by overviewSegmentKey. When childSectionKey is set, only that one section of the
 * child rolls up into the segment (the per-section authoring model); null/absent rolls up the whole child.
 * weight feeds the weighted rollup strategy.
 */
export interface GeneratedGroupChild {
  childDefinitionId: string;
  overviewSegmentKey: string;
  weight?: number;
  order?: number;
  /** Section of the child whose segments roll up into this overview segment; null/absent = the whole child. */
  childSectionKey?: string | null;
  /** Read-only: the child version this mapping was pinned to when the group version published
   * (null/absent on drafts and pre-pinning rows). Ignored on save — publish stamps the pin. */
  childVersionId?: string | null;
  /** Read-only: the pinned child version's display number, resolved server-side. */
  childVersion?: number | null;
}

/** One row of a mirror dial link's status map: source palette key → target palette key. */
export interface GeneratedStatusMapEntry {
  from: string;
  to: string;
}

/**
 * One rung of an aggregate dial link's condition ladder. `when` is "allTerminal" | "allOf" | "anyOf" |
 * "percentTerminalGte"; `statusKeys` feeds allOf/anyOf and `percent` (0..100) feeds percentTerminalGte.
 */
export interface GeneratedAggregateCondition {
  when: string;
  statusKeys?: string[] | null;
  percent?: number | null;
  thenTargetStatusKey: string;
}

/**
 * An aggregate dial link's evaluation config: statuses excluded from the population (e.g.
 * "not-applicable"), the ordered first-match-wins condition ladder, and the fallback when nothing
 * matches (null/absent = leave the target unchanged).
 */
export interface GeneratedAggregateRule {
  excludeStatusKeys?: string[] | null;
  conditions: GeneratedAggregateCondition[];
  fallbackTargetStatusKey?: string | null;
}

/**
 * A cross-dial link rule on a Group Key Dial (dial links): when statuses change on the source child,
 * the rule computes and writes a status on one node of a sibling child. `kind` is "Mirror" (exact node →
 * exact node via `statusMap`) or "Aggregate" (a source scope — null section/segment keys widen —
 * evaluated through `aggregate`). Child references are server-local ids, so links are not exported.
 */
export interface GeneratedGroupLink {
  kind: string;
  name: string;
  sourceChildDefinitionId: string;
  targetChildDefinitionId: string;
  targetSectionKey: string;
  targetSegmentKey: string;
  sourceSectionKey?: string | null;
  sourceSegmentKey?: string | null;
  sourceItemKey?: string | null;
  sourceDimensionKey?: string | null;
  targetItemKey?: string | null;
  targetDimensionKey?: string | null;
  statusMap?: GeneratedStatusMapEntry[] | null;
  aggregate?: GeneratedAggregateRule | null;
  enabled?: boolean;
  order?: number;
}

/**
 * One rung of a group segment rule's condition ladder (KEY-252). `when` takes the same discriminators as
 * GeneratedAggregateCondition — "allTerminal" | "allOf" | "anyOf" | "percentTerminalGte" — but the
 * population is the overview segment's child BANDS, so "terminal" reads as Green. `bands` ("Red" | "Amber" |
 * "Green") feeds allOf/anyOf and `percent` (0..100) feeds percentTerminalGte.
 */
export interface GeneratedGroupSegmentRuleCondition {
  when: string;
  bands?: string[] | null;
  percent?: number | null;
  /** The band the overview segment shows when this rung matches. */
  thenBand: string;
}

/**
 * A per-segment override of a group's rollup strategy (KEY-252): `overviewSegmentKey`'s band is decided by
 * an ordered first-match-wins ladder over the child bands feeding it, instead of the version-wide strategy.
 * `fallbackBand` applies when nothing matches; null/absent leaves the segment to the version-wide strategy.
 * Authored group faces only — a mirror face never rolls anything up, and publish refuses a rule there.
 */
export interface GeneratedGroupSegmentRule {
  overviewSegmentKey: string;
  conditions: GeneratedGroupSegmentRuleCondition[];
  fallbackBand?: string | null;
  enabled?: boolean;
  order?: number;
}

/** One rung of a group status rule's OVERRIDE ladder (KEY-509): when it matches, the group node shows
 * `thenStatusKey` — a status in the GROUP's own rating scale. Only "anyOf" / "allOf" are meaningful; the
 * population is the child statuses feeding the node and `statuses` are the CHILDREN's keys. `childKeys`
 * narrows the rung to particular children ("{childDefinitionId}" or "{childDefinitionId}/{childSectionKey}")
 * and carries the claimed-child rule: a child named by any rung is answered only by the rungs naming it. */
export interface GeneratedGroupStatusRuleCondition {
  when: string;
  statuses: string[];
  thenStatusKey: string;
  childKeys?: string[] | null;
}
/** One rung of a group status rule's BAND ladder: the segment shows `statusKey` once its composite score
 * reaches `minScore` (0..1). Segments only — a section has no score. */
export interface GeneratedGroupScoreBand {
  statusKey: string;
  minScore: number;
}
/** A status rule on one node of a group's own face (KEY-509): `scope` is "Segment" or "Section", `key` the
 * group's own segment or section key. Overrides first (scoped rungs ahead of unscoped), then bands, then
 * the worst-of selection the colouring-from-children source makes — which must be switched on for the rule
 * to apply at all. */
export interface GeneratedGroupStatusRule {
  scope: string;
  key: string;
  overrides: GeneratedGroupStatusRuleCondition[];
  bands: GeneratedGroupScoreBand[];
  enabled?: boolean;
  order?: number;
}

/**
 * One rung of an itemised segment's OVERRIDE ladder: when it matches, the segment shows `thenStatusKey`
 * whatever its progress. Only "anyOf" and "allOf" are meaningful — the population is the segment's live items
 * and the test is status membership against `statuses`. Anything threshold-based belongs in a progress band.
 * `dimensionKey` narrows the test to one status axis; null/absent tests every axis.
 */
export interface GeneratedItemSegmentRuleCondition {
  when: string;
  statuses?: string[] | null;
  dimensionKey?: string | null;
  /** The status key the segment shows when this rung matches. */
  thenStatusKey: string;
  /**
   * Bare item keys (within this rule's segment) that narrow the rung to named items; null/empty leaves it
   * unscoped. Carries the claimed-item rule: an item named by any rung of the ladder is answered only by the
   * rungs naming it, and scoped rungs are evaluated ahead of unscoped ones.
   */
  itemKeys?: string[] | null;
  /**
   * Per-axis status choices (KEY-293), superseding the flat `statuses` + single `dimensionKey` pair — which
   * cannot tell one palette's "complete" from another's, since a status key is unique only within its own
   * palette. Each entry names an axis and the statuses an item must sit at ON THAT AXIS. Wins over the
   * legacy pair when present; only the axes named here are tested, so an axis added to the dial later
   * cannot silently stop an existing rung firing.
   */
  dimensionStatuses?: GeneratedDimensionStatusMatch[] | null;
  /**
   * How many of the named axes an item must satisfy: "any" (default) or "all". Orthogonal to `when`, which
   * quantifies over the segment's items — together they give any/every item x any/every named axis.
   */
  dimensionMatch?: string | null;
}

/** One axis's share of a rung's test (KEY-293): the statuses an item must sit at on this axis. */
export interface GeneratedDimensionStatusMatch {
  dimensionKey: string;
  statuses: string[];
}

/** One rung of an itemised segment's BAND ladder: show `statusKey` once progress reaches `minProgress` (0..1). */
export interface GeneratedSegmentProgressBand {
  statusKey: string;
  minProgress: number;
  /** KEY-795: a strict threshold — progress must be ABOVE `minProgress` ("some done"). */
  exclusive?: boolean;
}

/**
 * A per-segment display rule on an itemised dial: the segment resolves to a palette status rather than its
 * coarse completion band. `overrides` is an ordered first-match-wins ladder evaluated first (the exception
 * channel — a band cannot say "needs review"); `bands` is the progress ladder used when no override applies.
 * When neither yields a status the segment keeps its RAG band, so a rule is purely additive.
 * Both keys are needed: an itemised dial's segment keys are unique only within their section.
 */
/**
 * A repeating schedule in the vocabulary a user says out loud (KEY-276) — "every 2 hours",
 * "every Monday at 06:00". There is deliberately no cron anywhere in this product.
 *
 * `unit` "Hour" advances in ABSOLUTE time (2 hours stays 2 hours across a clock change); "Day"/"Week"/
 * "Month" advance in LOCAL WALL-CLOCK time against the dial's own time zone (06:00 stays 06:00).
 * Describe and preview it client-side with `describeRecurrence` / `previewRecurrence` in web-admin's
 * `recurrence.ts`, which is pinned to the server's engine by a shared fixture.
 */
export interface GeneratedRecurrence {
  /** How many units between occurrences; 1 or more. */
  every: number;
  unit: "Hour" | "Day" | "Week" | "Month";
  /** Local time of day as strictly zero-padded 24-hour "HH:mm". Absent = midnight. Ignored by "Hour". */
  atTimeOfDay?: string | null;
  /** "Week" only: English day names ("Monday"). Absent/empty = a plain seven-day step. */
  onDaysOfWeek?: string[] | null;
  /** "Month" only: 1–31, clamped to a short month's last day without the clamp sticking. */
  onDayOfMonth?: number | null;
}

/**
 * A standing instruction that one node goes back to a known status on a repeating schedule (KEY-276).
 *
 * Addresses ONE node on ONE status axis: `itemKey` absent targets the action point itself (on an itemised
 * dial, every item in it); `dimensionKey` absent targets a no-items dial's own segment status. One schedule
 * per (section, segment, item, dimension).
 */
export interface GeneratedNodeResetSchedule {
  sectionKey: string;
  segmentKey: string;
  recurrence: GeneratedRecurrence;
  itemKey?: string | null;
  dimensionKey?: string | null;
  /** The status to reset to, or absent/null for the node's STARTING status — the normal case. */
  resetToStatusKey?: string | null;
  /** False suspends the schedule without losing how it was set up. */
  enabled?: boolean;
  /** Attribute keys this reset also CLEARS on the node (KEY-276 phase 4). Absent/empty clears nothing.
   * Named rather than all-or-nothing: only the author knows which fields are this-period's working notes
   * and which are standing facts. Cleared values are kept in the node's attribute history. */
  clearAttributeKeys?: string[] | null;
  /** KEY-RESETTIME: a dial's own admins may move WHEN this runs on their dial — the day and time only.
   * Absent/false keeps the template's timing for every dial. An hourly schedule can't allow it. */
  allowInstanceOverride?: boolean;
}

export interface GeneratedItemSegmentRule {
  sectionKey: string;
  segmentKey: string;
  overrides: GeneratedItemSegmentRuleCondition[];
  bands: GeneratedSegmentProgressBand[];
  enabled?: boolean;
  order?: number;
}

/**
 * A proposed top-level section of a yet-to-be-generated child dial. overviewSegmentKey names the group
 * overview segment scaffolded from it, so the real groupChildren link can be wired without re-deriving
 * the mapping once the child exists.
 */
export interface GeneratedChildSectionProposal {
  key: string;
  label: string;
  overviewSegmentKey?: string | null;
  /**
   * The parent segment this child section feeds (KEY-224) — e.g. a child's "Initial Notice" section
   * feeding the parent segment of the same name, or several "parts" sections feeding one "overall
   * compliance" segment. Advisory: it drives labelling and the mapping review, not the wiring.
   */
  parentSegmentKey?: string | null;
  /**
   * The parent SECTION that segment belongs to — a theme of the parent's own face
   * ("regulatory-approvals"), not a child's name. This is what gives a segment fed by several children
   * somewhere honest to sit. Null falls back to the child's own section.
   */
  parentSectionKey?: string | null;
  /** Mirror plans only: this section is on the parent's face (read when the child's
   * mirrorOnParentFace is "SelectedSections"). */
  showOnParentFace?: boolean;
}

/** How a proposed child contributes to a Mirror-mode parent face (KEY-224). */
export type GroupChildMirrorMode = "None" | "AllSections" | "SelectedSections";

/**
 * An AI-proposed child dial of a generated group. The child does not exist yet — description/goal/
 * suggestedKind are the brief for generating it in full later (the two-pass flow), and
 * overviewSectionKey names the group overview section scaffolded from it.
 */
export interface GeneratedGroupChildProposal {
  key: string;
  name: string;
  description: string;
  suggestedKind: string;
  sections: GeneratedChildSectionProposal[];
  goal?: string | null;
  overviewSectionKey?: string | null;
  /**
   * Mirror plans only (KEY-224): how much of this child is drawn on the parent's face. The face ROWS
   * can't come from the server — they key off child definition ids, and the children don't exist until
   * the two-pass build creates them — so the client turns this into real rows once each child is
   * published and its REAL section keys are known.
   */
  mirrorOnParentFace?: GroupChildMirrorMode;
  /**
   * Metadata fields the planner suggests this child should declare (an "owner" per segment, say).
   * Advisory: folded into the brief when the child is generated, so the child generation — whose
   * schema already supports nodeAttributes — is explicitly prompted for them.
   */
  suggestedAttributes?: GeneratedNodeAttribute[] | null;
}

/**
 * One rung of a planned link's condition ladder. Unlike GeneratedAggregateCondition it names no real
 * status keys — the children's palettes don't exist at plan time — so `then` is a semantic level
 * ("best" | "middle" | "worst") resolved against the target segment's real palette at build time.
 * `when` is "allTerminal" or "percentTerminalGte"; `percent` (0..100) feeds percentTerminalGte.
 */
export interface GeneratedGroupLinkProposalCondition {
  when: string;
  then: string;
  percent?: number | null;
}

/**
 * An AI-planned cross-dial link between two proposed children of a generated group. The children don't
 * exist yet, so it references plan child KEYS and predicted section keys, not definition ids — the
 * two-pass build reconciles it against each child's real structure (key, then label, like the
 * mirror-face reconciliation) and wires the survivors as real GeneratedGroupLink Aggregate rules.
 * Links that don't reconcile are dropped. Transient like groupChildProposals: never persisted.
 */
export interface GeneratedGroupLinkProposal {
  key: string;
  name: string;
  sourceChildKey: string;
  targetChildKey: string;
  targetSectionKey: string;
  conditions: GeneratedGroupLinkProposalCondition[];
  /** The source scope within the source child; null/absent aggregates the whole child. */
  sourceSectionKey?: string | null;
  /** Optional key/label hint naming which segment of the target section receives the status. */
  targetSegmentHint?: string | null;
  /** Semantic level applied when no condition matches; null/absent leaves the target unchanged. */
  otherwise?: string | null;
}

/**
 * The per-definition rule that auto-sets an item's status when a file is uploaded against it (itemised
 * dials). Null/absent = the feature is off. When `enabled`, `dimensionKey` + `statusKey` name the item
 * status axis and the status to set; the status must belong to that dimension's palette.
 */
export interface UploadStatusAutomation {
  enabled: boolean;
  dimensionKey?: string | null;
  statusKey?: string | null;
}

export interface GeneratedDefinition {
  /**
   * The dial's own intro video (KEY-374) — "what is this dial and how do I use it", shown on the dial
   * card's Overview tab beside the description. Distinct from a node's guidance videos, which answer
   * "how do I do this bit". At most one.
   */
  introVideo?: GuidanceVideo | null;

  /**
   * How to work this whole dial (KEY-534) — the definition-level counterpart of a node's guidance, and a
   * different question from `introVideo`'s "what is this dial". On an update an undefined/null value means
   * "not specified" and the stored value is kept; send an empty string to clear it.
   */
  guidance?: string | null;

  /** The one video that goes with that guidance (KEY-534). At most one. */
  guidanceVideo?: GuidanceVideo | null;

  /**
   * Documents that go with that guidance (KEY-565) — the dial-level counterpart of a section's `documents`.
   * On an update undefined/null means "not specified" and the stored set is kept; send `[]` to clear.
   */
  guidanceDocuments?: GuidanceDocument[] | null;

  /** External-resource links that go with that guidance (KEY-565). The same three-state rule: `[]` clears. */
  guidanceLinks?: GeneratedLink[] | null;

  /** KEY-814: the dial's welcome page. Null/absent keeps the stored page on an update; a page with no prose and no
   * buttons clears it. */
  welcome?: DialWelcome | null;

  name: string;
  statusSet: { name: string; statuses: GeneratedStatus[] };
  sections: GeneratedSection[];
  description?: string;
  category?: string;
  industry?: string;
  /** Server-local storage reference for the central hub image (set via uploadCentralImage). Not exported/imported. */
  centralImage?: string | null;
  /** "Simple" or "Itemised". A Simple dial's segments carry no items. Default Itemised. */
  kind?: string;
  /** Per-item status dimensions (itemised dials). */
  dimensions?: GeneratedDimension[];
  /** Whether the rendered dial shows the central completion ring + percentage. Default true. */
  showCentralProgress?: boolean;
  /**
   * How many days before a node's review date to send a reminder, one entry per rung (KEY-413), e.g.
   * `[30, 7, 0]`. `null`/absent = this dial has no opinion, so the server's default ladder applies; an
   * EMPTY array is the explicit "this dial sends no reminders". The two are not interchangeable.
   * Each entry 0-365, at most five, no duplicates; stored descending.
   */
  reviewReminderLeadDays?: number[] | null;
  /** Per-instance customisation envelope. Null/absent = customisation disabled. */
  customisation?: CustomisationPolicy | null;
  /** True for a Group Key Dial — its overview segments roll up mapped child definitions. */
  isGroup?: boolean;
  /** "WorstOf" | "Latest" | "Weighted" — how a group's overview segments roll up. Null on a non-group. */
  rollupStrategy?: string | null;
  /**
   * KEY-482: the scoring axis whose item statuses colour a with-items dial's action points, and how those
   * items combine ("WorstOf" — as good as its worst item — is the only strategy implemented). Null/absent
   * means no derivation: an action point keeps its display rule or its completion band.
   *
   * On an UPDATE, omitting the key keeps what the dial has, exactly like the group-wiring fields; send an
   * empty string to turn the derivation off.
   */
  statusRollupDimensionKey?: string | null;
  statusRollupStrategy?: string | null;
  /**
   * KEY-485: the axis the dial's SECTION edges are coloured from, when that should not be the same one its
   * action points use — an action point saying how far its work has got, the section saying how much trouble
   * the area is in. Null/absent means the sections follow `statusRollupDimensionKey`.
   *
   * On an UPDATE, omitting it keeps what the dial has; send an empty string to hand the sections back to the
   * action points' axis. Combined by `statusRollupStrategy`, the same as the action points — what differs is
   * which check is read, not how several of them are reconciled.
   */
  sectionStatusRollupDimensionKey?: string | null;
  /**
   * KEY-483: where a GROUP dial's section edges are fed from — `"ChildSectionStatuses"` rolls each group
   * section up from the child sections mapped onto it — and how those children combine ("WorstOf", the
   * only strategy implemented). Null/absent means no rollup: the group's sections carry only what people
   * set on them.
   *
   * On an UPDATE, omitting the source keeps what the dial has, exactly like `statusRollupDimensionKey`;
   * send an empty string to turn the rollup off.
   */
  groupSectionStatusSource?: string | null;
  groupSectionStatusStrategy?: string | null;
  /**
   * KEY-509: where a GROUP dial's overview SEGMENTS take their status from — `"ChildSegmentStatuses"`
   * shows on each segment the worst of the statuses its mapped child action points are displaying, in
   * that child's own name and colour — and how they combine ("WorstOf", the only strategy implemented).
   * Null/absent means band-only colouring, as every group had before. Configured and carried exactly like
   * the section pair above; publishing refuses it unless `rollupStrategy` is also "WorstOf".
   */
  groupSegmentStatusSource?: string | null;
  groupSegmentStatusStrategy?: string | null;
  /**
   * Child-definition links mapped onto a group's overview segments. Empty on a non-group.
   *
   * Every group-wiring field below (`groupChildren`, `classificationBands`, `groupLinks`, `groupFaceMode`,
   * `groupFaceSections`) follows the same rule when updating an existing group: OMITTING it keeps what the
   * server has — so a payload that doesn't model group wiring can't erase it — while an explicit `[]`
   * clears it. Send `[]`, not `undefined`, to remove the last child / band / link / face row.
   */
  groupChildren?: GeneratedGroupChild[];
  /**
   * AI-proposed children of a generated group (the brief for the two-pass flow). Transient: returned
   * by AI generation but not persisted with a draft. Null/absent except on AI-generated groups.
   */
  groupChildProposals?: GeneratedGroupChildProposal[];
  /**
   * AI-planned cross-dial links between a generated group's proposed children. Transient like
   * `groupChildProposals`: the two-pass build reconciles them against the real children and wires
   * the survivors as `groupLinks`.
   */
  groupLinkProposals?: GeneratedGroupLinkProposal[];
  /** Extra metadata fields declared on this definition's nodes (KEY-161). Null/absent when none. */
  nodeAttributes?: GeneratedNodeAttribute[];
  /** Author-named score tiers for a group dial's composite score (KEY-162). Group dials only. */
  classificationBands?: GeneratedClassificationBand[];
  /** The rule that auto-sets an item's status on file upload. Null/absent = off. */
  uploadStatusAutomation?: UploadStatusAutomation | null;
  /** Cross-dial link rules between a group's children (dial links). Null/absent on a non-group. */
  groupLinks?: GeneratedGroupLink[];
  /** "Authored" (default) or "Mirror" — how a group's face is composed. Groups only. */
  groupFaceMode?: string;
  /** Mirror-mode face rows, in display order. Null/absent except on Mirror-mode groups. */
  groupFaceSections?: GeneratedGroupFaceSection[];
  /** Per-segment overrides of a group's rollup strategy (KEY-252). Null/absent on a non-group. */
  groupSegmentRules?: GeneratedGroupSegmentRule[];
  /** Status rules on a GROUP's own segments and sections (KEY-509). Null/absent when it has none; on a
   * save, absent = "not specified" (the server keeps what it has) and [] clears. */
  groupStatusRules?: GeneratedGroupStatusRule[];
  /** Per-segment display rules on an itemised version. */
  itemSegmentRules?: GeneratedItemSegmentRule[];
  /** Standing scheduled resets on this version's nodes (KEY-276). Absent/null is "not specified" on a save:
   * the server keeps what the version already has, so a client that doesn't model schedules can't erase
   * them. An explicit empty array deletes them all. */
  resetSchedules?: GeneratedNodeResetSchedule[];
  /**
   * Palettes authored inside this definition and reusable across its own segments, without ever being
   * published to the shared library (KEY-264). A segment names one through `GeneratedSegment.palette`
   * exactly as it names a library palette, and these are resolved FIRST — a definition-scoped palette
   * beats a library palette of the same name.
   *
   * One persists whether or not a segment currently uses it, so an author can create a palette before
   * wiring it up. Editing its statuses changes every segment pointing at it. Follows the same
   * omit-keeps / `[]`-clears rule as the group-wiring fields above.
   */
  customPalettes?: GeneratedStatusSet[];
}

/** One ordered row of a Mirror-mode group face: show `childSectionKey` of the child's dial on the group
 * face, or — when null/absent — ALL of that child's sections (the whole-dial mirror, live). */
export interface GeneratedGroupFaceSection {
  childDefinitionId: string;
  childSectionKey?: string | null;
  order?: number;
}

/** One named score tier (KEY-162): the label shown when the composite score is at or above `minScore` (0..1). */
export interface GeneratedClassificationBand {
  label: string;
  minScore: number;
}

/** One version of a definition, for the detail-page version switcher and as the rollout tab's
 * target-version picker (queue/preview address versions by `id`). */
export interface DefinitionVersionSummary {
  id: string;
  versionNumber: number;
  lifecycle: string;
  publishedAtUtc?: string | null;
}

/** KEY-785: a catalogue thumbnail's worth of a dial — sections with their action points' short labels, and the
 * dial scale's colours in order. */
export interface DefinitionSummaryFace {
  sections: { label: string; actionPoints: string[] }[];
  colours: string[];
}

export interface DefinitionSummary {
  id: string;
  name: string;
  category?: string | null;
  industry?: string | null;
  latestVersion: number;
  lifecycle: string;
  /** A group dial aggregates child dials; a single dial is one standalone tracker. */
  isGroup: boolean;
  /** "Simple" (sections + segments) or "Itemised" (segments contain items). */
  kind: string;
  /** True when any version is published, even if a newer draft sits on top. */
  hasPublishedVersion: boolean;
  /** The published version number, or null when no version is published yet. */
  publishedVersion?: number | null;
  /** Display name (or email) of who last edited the latest version; null when unknown (KEY-178). */
  lastEditedByName?: string | null;
  /** ISO timestamp of the last edit to the latest version; null when unknown (KEY-178). */
  lastEditedAtUtc?: string | null;
  /** Catalogue folder this definition is filed in; null = the catalogue root. */
  folderId?: string | null;
  /**
   * Group definitions whose latest version claims this one as a child. Empty/absent for a standalone
   * dial. When non-empty the catalogue hides it from the grid and routes access through the group's
   * "Child dials" tab — a definition may be shared by more than one group.
   */
  groupParentIds?: string[] | null;
  /**
   * How many child dials this group's latest version maps. 0 on a standalone dial, and on a group whose
   * children aren't wired up yet — so the catalogue can say "Group · 4 dials" without a second request.
   */
  groupChildCount?: number;
  /** Offered as a template in the self-service builder (KEY-560). */
  isTemplate?: boolean;
  /**
   * How many LIVE STANDALONE instances this dial has: root instances that aren't archived, across every
   * organisation. Instances a group allocated as its own children carry a parent and don't count.
   * Non-zero means the dial can't be adopted into a group — a group's children are created and owned by
   * the group (KEY-226) — so the child pickers grey it out with the count as the reason.
   */
  standaloneInstanceCount?: number;
  /** KEY-785: the definition's description, for the catalogue row's second line. */
  description?: string | null;
  /** KEY-785: the latest version's size. */
  sectionCount?: number;
  actionPointCount?: number;
  itemCount?: number;
  checkCount?: number;
  /** KEY-785: every live dial running it, group children included (unlike standaloneInstanceCount). */
  liveInstanceCount?: number;
  /** KEY-785: a thumbnail's worth of the latest version's face. */
  face?: DefinitionSummaryFace | null;
}

/**
 * One folder in the admin catalogue tree (e.g. Education > Ofsted > V2). Folders nest via
 * `parentFolderId` and organise the definition catalogue only — they carry no entitlement
 * or visibility meaning, and are independent of the free-text `category`/`industry` metadata.
 */
export interface DefinitionFolder {
  id: string;
  name: string;
  /** Parent folder, or null for a top-level folder. */
  parentFolderId?: string | null;
  /** Definitions filed directly in this folder (descendants are not counted). */
  definitionCount: number;
}

/**
 * The catalogue metadata that lives on the definition itself rather than on one of its versions, so it can
 * be corrected in place on a published dial (see `updateDefinitionMetadata`). Nothing keys off these fields:
 * they drive the catalogue's display, search and filters only.
 */
export interface DefinitionMetadata {
  name: string;
  description?: string | null;
  category?: string | null;
  industry?: string | null;
}

export interface DefinitionDetail {
  introVideo?: GuidanceVideo | null;

  /**
   * How to work this whole dial (KEY-534), authored on the definition-details form. Version-owned, so it
   * freezes at publish — unlike `description`, which lives on the definition and is editable in place.
   */
  guidance?: string | null;

  /** That guidance's video, with `embedKind`/`playbackUrl` already resolved for preview. */
  guidanceVideo?: GuidanceVideo | null;

  /** That guidance's documents (KEY-565), each with its `downloadUrl` resolved. */
  guidanceDocuments?: GuidanceDocument[] | null;

  /** That guidance's external-resource links (KEY-565). */
  guidanceLinks?: GeneratedLink[] | null;

  /** KEY-814: the dial's welcome page. On an instance, only for its editors and admins. */
  welcome?: DialWelcome | null;

  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  industry?: string | null;
  version: number;
  lifecycle: string;
  statusSet: { name: string; statuses: GeneratedStatus[] };
  sections: GeneratedSection[];
  /** Storage reference for the central hub image (round-tripped on save). */
  centralImage?: string | null;
  /** Short-lived display URL for the central hub image. */
  centralImageUrl?: string | null;
  /** "Simple" (sections + segments) or "Itemised" (segments contain items). */
  kind: string;
  /** Per-item status dimensions (itemised dials). */
  dimensions?: GeneratedDimension[];
  /** Whether the rendered dial shows the central completion ring + percentage. Default true. */
  showCentralProgress?: boolean;
  /** This dial's review-reminder ladder (KEY-413), descending. Null = no opinion, so the server default
   * applies; empty = reminders explicitly off for this dial. */
  reviewReminderLeadDays?: number[] | null;
  /** Per-instance customisation envelope (gates + caps). Null/absent = disabled. */
  customisation?: CustomisationPolicy | null;
  /** Master switch on the definition root: when false no instance may be customised. */
  instanceCustomisationEnabled?: boolean;
  /** Offered as a template in the self-service builder (KEY-560). Set by a platform admin; needs a published version. */
  isTemplate?: boolean;
  /** Master switch: when true, a dial admin may override the central logo of this definition's instances
   * (subject to each instance's own gate). */
  allowInstanceLogoOverride?: boolean;
  /** KEY-744: whether a dial with no logo-override setting of its own has it on. Inert while
   * `allowInstanceLogoOverride` is off. */
  instanceLogoOverrideDefault?: boolean;
  /** Master switch for section statuses (KEY-479): when true, people on this definition's dials may set a
   * status on a whole section, drawn from the default palette and shown as a coloured edge on the outer
   * ring. Root-level, so it takes effect on published dials without a new version. */
  sectionStatusEnabled?: boolean;
  /** Whether this definition's dials offer Milo, the assistant (KEY-714). Off by default; root-level, so it takes
   * effect on published dials without a new version. */
  miloEnabled?: boolean;
  /** Whether this definition's dials offer Look back (KEY-735). On unless an author switched it off; root-level,
   * so it takes effect on published dials without a new version. Absent (an older server) means on. */
  lookBackEnabled?: boolean;
  /** KEY-939: the instance demos of this definition are copied from, or null for none (a demo is then empty). */
  demoSourceInstanceId?: string | null;
  /** KEY-539: what this definition's dials call an action point, singular — e.g. "segment". Absent means
   * the platform's own word, "action point". Display only; `segmentKey` and every other key are untouched.
   * Always absent or present together with `segmentNounPlural`: the server makes the half state
   * unrepresentable. Read it through `segmentNoun()` in @keydials/dial-workspace rather than directly. */
  segmentNounSingular?: string | null;
  /** KEY-539: the plural of `segmentNounSingular`. Stored, never derived — never append an "s". */
  segmentNounPlural?: string | null;
  /** KEY-670: this definition's own names for its dials' fixed tab headings, `{ key: label }` — e.g.
   * `{ "guidance.segment": "Deliverables" }`. Absent/null when it renames none. Keys are `tab` (every level) or
   * `tab.level` (one level, winning over the bare key there), from the server's allowlist, mirrored as
   * `DIAL_LABEL_KEYS` in @keydials/dial-workspace. Read it through `dialLabel()` there rather than directly. */
  labelOverrides?: Record<string, string> | null;
  /** Master switch for kiosk auto-lock (KEY-268): when true, instances of this definition may be put into
   * shared-device mode (idle -> locked screen -> name + PIN), subject to each instance's own gate. */
  autoLockEnabled?: boolean;
  /** The idle timeout instances inherit unless they override it, in seconds. */
  defaultAutoLockTimeoutSeconds?: number;
  /** The role-action matrix (KEY-298): who may edit attributes / upload documents / manage document
   * versions / post messages on this definition's dials, unless an instance overrides it. */
  attributeEditRoles?: string[];
  documentUploadRoles?: string[];
  documentVersionRoles?: string[];
  messagePostRoles?: string[];
  /** Who may post RESTRICTED messages (KEY-285), and who they may address. A null/absent
   * `messagePostRestrictedTo` means no restriction — anyone who can read the action point. */
  messagePostRestrictedRoles?: string[];
  messagePostRestrictedTo?: string[] | null;
  /** The two READ masks of the same matrix (KEY-537): who may SEE that a file exists on this definition's
   * dials, and who may fetch its bytes. The first read actions in a matrix that was otherwise all writes. */
  documentListRoles?: string[];
  documentDownloadRoles?: string[];
  /** True for a Group Key Dial — its overview segments roll up mapped child definitions. */
  isGroup?: boolean;
  /** "WorstOf" | "Latest" | "Weighted" — how a group's overview segments roll up. Null on a non-group. */
  rollupStrategy?: string | null;
  /**
   * KEY-482: the scoring axis whose item statuses colour a with-items dial's action points, and how those
   * items combine ("WorstOf" — as good as its worst item — is the only strategy implemented). Null/absent
   * means no derivation: an action point keeps its display rule or its completion band.
   *
   * On an UPDATE, omitting the key keeps what the dial has, exactly like the group-wiring fields; send an
   * empty string to turn the derivation off.
   */
  statusRollupDimensionKey?: string | null;
  statusRollupStrategy?: string | null;
  /**
   * KEY-485: the axis the dial's SECTION edges are coloured from, when that should not be the same one its
   * action points use — an action point saying how far its work has got, the section saying how much trouble
   * the area is in. Null/absent means the sections follow `statusRollupDimensionKey`.
   *
   * On an UPDATE, omitting it keeps what the dial has; send an empty string to hand the sections back to the
   * action points' axis. Combined by `statusRollupStrategy`, the same as the action points — what differs is
   * which check is read, not how several of them are reconciled.
   */
  sectionStatusRollupDimensionKey?: string | null;
  /**
   * KEY-483: where a GROUP dial's section edges are fed from — `"ChildSectionStatuses"` rolls each group
   * section up from the child sections mapped onto it — and how those children combine ("WorstOf", the
   * only strategy implemented). Null/absent means no rollup: the group's sections carry only what people
   * set on them.
   *
   * On an UPDATE, omitting the source keeps what the dial has, exactly like `statusRollupDimensionKey`;
   * send an empty string to turn the rollup off.
   */
  groupSectionStatusSource?: string | null;
  groupSectionStatusStrategy?: string | null;
  /**
   * KEY-509: where a GROUP dial's overview SEGMENTS take their status from (`"ChildSegmentStatuses"` or
   * null for band-only), and how they combine. On an UPDATE, omitting the source keeps what the dial has;
   * send an empty string to turn it off — the same contract as the section pair above.
   */
  groupSegmentStatusSource?: string | null;
  groupSegmentStatusStrategy?: string | null;
  /** Child-definition links mapped onto this group's overview segments. Null/absent on a non-group. */
  groupChildren?: GeneratedGroupChild[];
  /** Extra metadata fields declared on this definition's nodes (KEY-161). Null/absent when none. */
  nodeAttributes?: GeneratedNodeAttribute[];
  /** Author-named score tiers for a group dial's composite score (KEY-162). Group dials only. */
  classificationBands?: GeneratedClassificationBand[];
  /** The rule that auto-sets an item's status on file upload. Null/absent = off. */
  uploadStatusAutomation?: UploadStatusAutomation | null;
  /** Set when the dial is awaiting a re-completed spreadsheet: editing is paused until it's applied or cancelled. */
  reimportArmedAtUtc?: string | null;
  /** Cross-dial link rules between a group's children (dial links). Null/absent on a non-group. */
  groupLinks?: GeneratedGroupLink[];
  /** "Authored" (default) or "Mirror" — how a group's face is composed. Groups only. */
  groupFaceMode?: string;
  /** Mirror-mode face rows, in display order. Null/absent except on Mirror-mode groups. */
  groupFaceSections?: GeneratedGroupFaceSection[];
  /** Per-segment overrides of a group's rollup strategy (KEY-252). Null/absent on a non-group. */
  groupSegmentRules?: GeneratedGroupSegmentRule[];
  /** Status rules on a GROUP's own segments and sections (KEY-509). Null/absent when it has none; on a
   * save, absent = "not specified" (the server keeps what it has) and [] clears. */
  groupStatusRules?: GeneratedGroupStatusRule[];
  /** KEY-515: the test dial allocated when this version went out for checking, for a one-click deep link
   * into the user app. Read-only, and absent unless the version's lifecycle is "Checking". */
  checkingInstanceId?: string | null;
  /** KEY-545: who the current checking round's test dial went to — "Organisation" or "SenderOnly". Read-only
   * and never authored: it describes a checking round, not the dial, so it is not carried by a save, a copy
   * or an export. Null/absent (a version never sent for checking, or one predating the choice) reads as
   * "Organisation". */
  checkingAudience?: string | null;
  /** KEY-575: who sent the current checking round (or made its fresh test dial), their display name, and when.
   * Read-only, and all absent unless the version is "Checking"; the id and name are also null on a round nobody
   * could be traced for. Shown to everyone who opens the definition, so a colleague can see whose round it is
   * before discarding it. */
  checkingSentByUserId?: string | null;
  checkingSentByName?: string | null;
  checkingSentAtUtc?: string | null;
  /** KEY-667: days a lapsed customer's data is kept for this definition's dials; null uses the platform setting.
   * A root setting made by a platform admin — not versioned, exported or copied. */
  retentionDaysAfterLapse?: number | null;
  /** Per-segment display rules on an itemised version. */
  itemSegmentRules?: GeneratedItemSegmentRule[];
  /** Standing scheduled resets on this version's nodes (KEY-276). Absent/null is "not specified" on a save:
   * the server keeps what the version already has, so a client that doesn't model schedules can't erase
   * them. An explicit empty array deletes them all. */
  resetSchedules?: GeneratedNodeResetSchedule[];
  /**
   * Definition-scoped custom palettes this version owns (KEY-264), including any no segment currently
   * uses. Null/absent when the version has none. See `GeneratedDefinition.customPalettes`.
   */
  customPalettes?: GeneratedStatusSet[];
}

// --- Status palettes (reusable library) ---

export interface PaletteDetail {
  id: string;
  name: string;
  /** Read-only system palette — can't be edited or deleted. */
  isSystem: boolean;
  statuses: GeneratedStatus[];
}

export interface SavePaletteRequest {
  name: string;
  statuses: GeneratedStatus[];
}

/**
 * A portable dial definition for moving between servers (no instance data). Format 2 adds a group's
 * wiring, whose child dials are named by portable reference rather than by server-local id, plus
 * `sourceDefinitionId` — the exporting server's id, carried only so a catalogue restored as a set can
 * re-resolve those references exactly. The admin app treats the payload as opaque: it reads a file,
 * posts it back, and never builds one by hand, so only the fields it displays are typed here.
 */
/** What a welcome-page button does (KEY-814). */
export type WelcomeActionKind = "guide" | "dialSettings" | "accountSettings" | "dialTab" | "link";

/** One welcome-page button (KEY-814): start a walkthrough, open a dial Settings section or one of the person's own
 * settings, switch the dial to a tab, or open a web page. */
export interface WelcomeAction {
  kind: WelcomeActionKind | string;
  target: string;
  label: string;
}

/** A dial's welcome page (KEY-814): rich text and up to {@link WELCOME_MAX_ACTIONS} buttons. */
export interface DialWelcome {
  html?: string | null;
  actions?: WelcomeAction[] | null;
}

/** The server's limits and choices for a welcome page — mirrors `DialWelcome` in the domain. */
export const WELCOME_MAX_ACTIONS = 6;
export const WELCOME_MAX_LABEL = 60;
export const WELCOME_DIAL_SETTINGS_SECTIONS = [
  "members", "whoswho", "contacts", "calendars", "guests", "licensing", "family", "roles",
  "structure", "display", "device", "feeds", "insights",
] as const;
export const WELCOME_DIAL_TABS = [
  "overview", "about", "guidance", "sections", "comingUp", "lookBack", "discussion", "files", "activity",
] as const;

/** Whether a welcome page has anything on it. */
export function welcomeHasContent(welcome: DialWelcome | null | undefined): boolean {
  if (!welcome) return false;
  const text = (welcome.html ?? "").replace(/<[^>]*>/g, "").trim();
  return text.length > 0 || (welcome.actions?.length ?? 0) > 0;
}

export interface ExportedDefinition {
  formatVersion: number;
  name: string;
  description?: string | null;
  category?: string | null;
  industry?: string | null;
  statusSet: { name: string; statuses: GeneratedStatus[] };
  sections: GeneratedSection[];
  generationPrompt?: string | null;
  generationModel?: string | null;
  sourceLifecycle?: string | null;
  /** Format 2+. The exporting server's id for this definition. */
  sourceDefinitionId?: string;
  isGroup?: boolean;
}

/** One dial produced by a duplicate — the group itself, or one of its copied children. */
export interface DuplicatedDefinition {
  id: string;
  name: string;
  isGroup: boolean;
  /** The dial this one was copied FROM, so a caller can pair a copy with its original. */
  sourceId: string;
}

/**
 * What a duplicate produced. `children` is empty unless a group was copied together with its children,
 * and lists them deepest-first — the order they were created in.
 */
export interface DefinitionDuplicateResult {
  id: string;
  name: string;
  isGroup: boolean;
  children: DuplicatedDefinition[];
}

/** The result of importing a definition: the new draft, plus any group children this server couldn't match. */
export interface DefinitionImportResult {
  id: string;
  /** Names (or ids) of child dials whose wiring was dropped because they aren't on this server. */
  unresolvedChildren: string[];
  /**
   * Uploaded guidance videos and central images the file referenced whose bytes this server doesn't hold —
   * dropped rather than imported as a dead link, and named so they can be re-uploaded. Empty when the file
   * came from this server (duplicating, or restoring a backup after a reset).
   */
  skippedAssets?: string[] | null;
}

/** The result of importing a definition onto a dial that already exists here, as a new version of it. */
export interface DefinitionVersionImportResult {
  /** The target dial — unchanged by the import; only a new draft version hangs off it. */
  id: string;
  /** The version the file landed on: the next one up, or the replaced draft's own number. */
  versionNumber: number;
  replacedDraft: boolean;
  unresolvedChildren: string[];
  /** Media this server couldn't back, dropped rather than imported as a dead reference. */
  skippedAssets?: string[] | null;
}

/** Which slice of the catalogue a backup covers. The two are always separate downloads. */
export type DefinitionBackupScope = "live" | "archived";

/** One definition's row in a restore preview: what the manifest says, plus any same-kind definitions
 * already here under the same name — the conflicts a restore has to decide how to handle. */
export interface DefinitionRestorePreviewEntry {
  sourceDefinitionId: string;
  name: string;
  isGroup: boolean;
  childCount: number;
  lifecycle: string;
  /** Ids of existing same-kind definitions sharing this name; empty when there's no conflict. */
  conflictingDefinitionIds: string[];
  /**
   * This group's child dials that are also in the file. Lets the picker show the zip as the tree it is
   * and keep a group selected together with its children — `childCount` alone says a group HAS children
   * but not WHICH rows they are. Absent on manifests written before this existed, where the list simply
   * reads flat as it always did.
   */
  childSourceIds?: string[] | null;
}

/** The result of previewing a backup zip before a selective restore. */
export interface DefinitionRestorePreviewResult {
  formatVersion: number;
  generatedAtUtc: string;
  scope: DefinitionBackupScope;
  count: number;
  definitions: DefinitionRestorePreviewEntry[];
}

/** What to do with one manifest entry on restore, when it was selected to be included. */
export type DefinitionRestoreAction = "Skip" | "Duplicate" | "Version";

/** The caller's choice for one selected manifest entry. */
export interface DefinitionRestoreSelection {
  sourceDefinitionId: string;
  action: DefinitionRestoreAction;
  /** Required when action is "Version" — which existing dial the file becomes a new version of. */
  targetDefinitionId?: string;
  replaceDraft?: boolean;
}

/** How one selected entry landed. */
export type DefinitionRestoreOutcome = "Imported" | "ImportedAsVersion" | "Skipped" | "DraftPending" | "KindMismatch" | "TargetNotFound";

/** The outcome for one selected manifest entry. */
export interface DefinitionRestoreItemResult {
  sourceDefinitionId: string;
  name: string;
  outcome: DefinitionRestoreOutcome;
  id?: string;
  versionNumber?: number;
  unresolvedChildren?: string[];
  /** Media this server couldn't back, dropped rather than restored as a dead reference. */
  skippedAssets?: string[] | null;
  /** The catalogue folder this dial was filed back into, recreated from the manifest. Null for the root. */
  folderPath?: string | null;
}

/** The result of applying a selective restore. */
export interface DefinitionRestoreApplyResult {
  items: DefinitionRestoreItemResult[];
}

// --- Organisations ---

/**
 * Which organisation types may be created under each parent type (KEY-362), already intersected with
 * what THIS caller may create. Keyed by parent type name.
 *
 * Fetched rather than mirrored in the frontend: a second copy of the rule table would drift, and the
 * copy that drifts is the one offering a choice the server then refuses.
 */
export interface OrganisationPlacementRules {
  allowedChildTypes: Record<string, string[]>;
}

export interface OrganisationSummary {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
  parentName: string | null;
  subEntityLabel: string | null;
  /** True when the node (and its subtree) is archived — hidden from normal listings. */
  isArchived: boolean;
  /** Place among its siblings, lowest first, ties broken on name. 0 on anything nobody has reordered. */
  childOrder?: number;
  /**
   * On a `Tier`, the plan family of the rung it sells — the ladder it belongs to, which is how the admin
   * tree files Top Co's tiers under a Personal and a Commercial heading (KEY-443). Null or absent on
   * every other type, and on a tier whose binding could not be read; such a node is shown ungrouped
   * rather than hidden. Only the list endpoint populates it.
   */
  tierFamily?: string | null;
}

export interface OrganisationDetail extends OrganisationSummary {
  children: OrganisationSummary[];
  /** Per-tenant opt-in for the AI insight layer (KEY-163); off by default. */
  aiInsightsEnabled?: boolean;
  /** Who may hand this organisation's dials out (KEY-546) — its own overrides and what they resolve to. */
  sharePolicy?: SharePolicyView | null;
  /** On a `Tier` organisation, the first rung of the price list it sells (KEY-614). Null on every other type. */
  tierBinding?: TierBindingView | null;
  /** Every rung a `Tier` organisation sells (KEY-625) — a tier may sell several of one family. */
  tierBindings?: TierBindingView[] | null;
}

/**
 * The rung a tier organisation is bound to (KEY-614): one billing-plan row. `period` is the period the
 * price is authored in — a rung sold monthly and annually is two rows, so this names which one.
 */
export interface TierBindingView {
  billingPlanId: string;
  family: string;
  tierName: string;
  amountPence: number;
  currency: string;
  period: string;
  isRetired: boolean;
}

/**
 * An organisation's share restrictions (KEY-546): what it states itself, and what that resolves to.
 *
 * The two halves mean different things and are both needed. `own*` is this organisation's own decision, with
 * null meaning "inherit" — a real choice an editor must be able to show and to restore. `effective*` is what
 * actually governs its dials, which for an org that states nothing comes from the nearest ancestor that
 * does, or from the platform default.
 */
export interface SharePolicyView {
  ownMember: ShareAuthority | null;
  ownGuest: ShareAuthority | null;
  ownGrant: ShareAuthority | null;
  ownFeed: ShareAuthority | null;
  effectiveMember: ShareAuthority;
  effectiveGuest: ShareAuthority;
  effectiveGrant: ShareAuthority;
  effectiveFeed: ShareAuthority;
}

/**
 * Set (or clear) an organisation's share restrictions (KEY-546). Each field is independent; null or omitted
 * means inherit. There is deliberately no "apply to descendants" — inheritance is resolved on read, so a
 * sub-organisation created later is born as restricted as its parent rather than silently open.
 */
/**
 * Who below an organisation may change dial logos (KEY-718), least to most strict. The effective policy is the
 * strictest stated anywhere up the tree; the platform default applies only where nobody states one.
 */
export type ChildLogoPolicy = "DialAdmins" | "PermittedInstances" | "OrganisationManagers" | "Locked";

/** Which of an organisation's two logos (KEY-719 dial centre / KEY-731 white-label header). */
export type OrganisationLogoKind = "dial" | "header";

/** An organisation's branding (KEY-718/719/731): its own logos, what it inherits, and its logo policy. */
export interface OrganisationBrandingView {
  organisationId: string;
  dialLogoUrl: string | null;
  headerLogoUrl: string | null;
  /** The dial logo the nearest ancestor passes down, and who that is. Null when nobody above sets one. */
  inheritedDialLogoUrl: string | null;
  inheritedDialLogoFrom: string | null;
  inheritedHeaderLogoUrl: string | null;
  inheritedHeaderLogoFrom: string | null;
  /** The policy this organisation states, or null for inherit. */
  ownChildLogoPolicy: ChildLogoPolicy | null;
  /** What governs this organisation's dials now. */
  effectiveChildLogoPolicy: ChildLogoPolicy;
  /** The strictest policy stated strictly above this organisation, or null. Locked = its managers may change nothing. */
  policyAbove: ChildLogoPolicy | null;
  platformDefaultChildLogoPolicy: ChildLogoPolicy;
  /** Whether the caller may change this organisation's logos and policy. */
  canEdit: boolean;
  /** The least strict policy the caller may set here, or null/absent when any. */
  loosestAllowedChildLogoPolicy?: ChildLogoPolicy | null;
}

export interface SetSharePolicyRequest {
  member?: ShareAuthority | null;
  guest?: ShareAuthority | null;
  grant?: ShareAuthority | null;
  feed?: ShareAuthority | null;
}

/** One category of data that currently prevents an organisation from being hard-deleted. */
export interface OrganisationBlocker {
  kind: string;
  count: number;
}

/** Whether an organisation is safe to hard-delete, and what (if anything) is blocking it. */
export interface OrganisationDeletionInfo {
  canDelete: boolean;
  blockers: OrganisationBlocker[];
}

export interface CreateOrganisationRequest {
  name: string;
  type: string;
  parentId?: string | null;
  subEntityLabel?: string | null;
  /** The rung a `Tier` organisation sells. Required for that type (or `billingPlanIds`), ignored for every other. */
  billingPlanId?: string | null;
  /** Every rung a new `Tier` organisation sells (KEY-625), combined with `billingPlanId`. */
  billingPlanIds?: string[] | null;
}

export interface UpdateOrganisationRequest {
  name: string;
  parentId?: string | null;
  subEntityLabel?: string | null;
}

// --- Organisation transport (KEY-227) ---

/** A portable organisation subtree: the organisations, the people who hold memberships in them, and the
 * roles those memberships name. Dial instances, entitlements and licences, sharing grants, saved views,
 * notifications and conversations are NOT carried, and neither is any credential material — an imported
 * person who had no account arrives as the same passwordless shell "Add member" creates. */
export interface ExportedOrganisationTree {
  formatVersion: number;
  generatedAtUtc: string;
  sourceRootOrganisationId: string;
  /** Root first, every parent before its children. */
  organisations: ExportedOrganisationNode[];
  users: ExportedOrganisationUser[];
  roles: ExportedRoleReference[];
}

export interface ExportedOrganisationNode {
  sourceOrganisationId: string;
  sourceParentId: string | null;
  name: string;
  type: string;
  subEntityLabel: string | null;
  isArchived: boolean;
  aiInsightsEnabled: boolean;
  members: ExportedOrganisationMembership[];
}

export interface ExportedOrganisationMembership {
  email: string;
  cascadeToDescendants: boolean;
  roles: string[];
}

export interface ExportedOrganisationUser {
  email: string;
  displayName: string | null;
  isArchived: boolean;
}

/** A role the memberships reference. `capabilities` records what the role meant on the source server and
 * is never read on import — roles resolve by name against the roles the target already defines. */
export interface ExportedRoleReference {
  name: string;
  isSystem: boolean;
  capabilities: string[];
}

/** What an import would do to one organisation. `RefusedType` always covers a Key Dials organisation:
 * that type carries platform-wide authority and an import can never create one. */
export type OrganisationImportMatch = "Create" | "Existing" | "Ambiguous" | "RefusedType" | "ParentSkipped";

export type OrganisationImportUserMatch = "Create" | "Existing" | "ExistingArchived";

export interface OrganisationImportOrgRow {
  sourceOrganisationId: string;
  name: string;
  type: string;
  parentName: string | null;
  match: OrganisationImportMatch;
  matchedOrganisationId: string | null;
  memberCount: number;
}

export interface OrganisationImportUserRow {
  email: string;
  displayName: string | null;
  match: OrganisationImportUserMatch;
  matchedUserId: string | null;
  membershipCount: number;
}

export interface OrganisationImportRoleRow {
  name: string;
  existsHere: boolean;
  platformOnly: boolean;
  membershipCount: number;
}

/** One thing the import declined to do, and why. */
export interface OrganisationImportSkip {
  kind: string;
  subject: string;
  reason: string;
}

/** What an import would do, worked out without writing anything. */
export interface OrganisationImportPreviewResult {
  formatVersion: number;
  generatedAtUtc: string;
  organisations: OrganisationImportOrgRow[];
  users: OrganisationImportUserRow[];
  roles: OrganisationImportRoleRow[];
  problems: OrganisationImportSkip[];
}

/** Import an exported subtree beneath `targetParentOrganisationId`. `createMissingUsers` is off by
 * default: without it, a membership naming an email with no account here is skipped and reported. */
export interface ImportOrganisationRequest {
  targetParentOrganisationId: string;
  payload: ExportedOrganisationTree;
  createMissingUsers?: boolean;
}

/** What the import actually did. Running the same file twice leaves every `*Created` count at zero the
 * second time — that is what "guards against duplication" means here. */
export interface OrganisationImportReport {
  organisationsCreated: number;
  organisationsMatched: number;
  usersCreated: number;
  usersMatched: number;
  membershipsCreated: number;
  membershipsMatched: number;
  roleAssignmentsCreated: number;
  skipped: OrganisationImportSkip[];
}

// --- Instance entitlements (two-tier quotas) ---

export interface DefinitionEntitlement {
  definitionId: string;
  definitionName: string;
  /** Null = unlimited. */
  maxInstances: number | null;
  /** Live count across the governing licensee's subtree (the shared, grant-wide usage). */
  usedInstances: number;
  /** Instances allocated directly to the viewed organisation (0 until it assigns one). */
  directInstances: number;
  /** True when the viewed org isn't the grant holder — the grant is inherited from a licensee ancestor. */
  isInherited: boolean;
  /** Licensee grant override for customisation: true = allow, false = block, null = inherit the definition default. */
  allowsCustomisation?: boolean | null;
  /** A Group Key Dial: allocating it brings its child dials with it, and the whole group still draws one licence. */
  isGroup: boolean;
  /**
   * A child of a Group Key Dial (KEY-226): created and owned by its group rather than allocated in its
   * own right. The grant row survives so existing instances keep working, but it can't be assigned —
   * the definition is absent from `availableDefinitions` and the server refuses a root allocation.
   */
  isGroupChild?: boolean;
}

export interface DefinitionOption {
  definitionId: string;
  name: string;
  /** A Group Key Dial — see {@link DefinitionEntitlement.isGroup}. */
  isGroup: boolean;
}

export interface OrganisationEntitlements {
  organisationId: string;
  organisationType: string;
  /** "Pool" (partner), "Grant" (licensee), "Inherited" (carried through from a licensee above), or "None". */
  tier: string;
  supportsEntitlements: boolean;
  entitlements: DefinitionEntitlement[];
  availableDefinitions: DefinitionOption[];
}

export interface SetEntitlementRequest {
  definitionId: string;
  /** Null = unlimited. */
  maxInstances: number | null;
  /** Licensee grant only: true = allow customisation, false = block, null/absent = inherit the definition default. */
  allowsCustomisation?: boolean | null;
}

export interface AllocateInstanceRequest {
  definitionId: string;
  organisationId: string;
  name: string;
  /** Organisation member to seed as the instance Admin. */
  adminUserId?: string | null;
  /** KEY-938: allocate a demo dial — stores no files, is never usage, may stand beside a real copy of the same dial. */
  isDemo?: boolean;
}

// --- Roles, members, users ---

// --- Inviting somebody straight onto a dial (KEY-220) ---

export interface InviteInstanceMemberRequest {
  email: string;
  role: string;
  displayName?: string | null;
  sectionKey?: string | null;
  segmentKey?: string | null;
  /** Emails the recipient about the grant, if it succeeds. Best-effort — see `InviteInstanceMemberResult.emailSent`. */
  sendEmail?: boolean;
}

/** What the "dial.access-granted" email would say, rendered server-side. Mirrors `MemberEmailPreviewResult`. */
export interface MemberEmailPreview {
  toEmail: string;
  subject: string;
  bodyHtml: string;
}

export interface PreviewMemberEmailRequest {
  userId: string;
  role: string;
}

export interface PreviewInviteEmailRequest {
  email: string;
  role: string;
  displayName?: string | null;
}

/** A section as a grant scope: keys and display names only. Mirrors `InstanceScopeSectionDto`. */
export interface InstanceScopeSection {
  key: string;
  label: string;
  segments: { key: string; name: string }[];
}

/**
 * A dial's members and the structure its grants are scoped to (KEY-578): what a members panel needs
 * without the whole dial behind it. Mirrors `InstanceMembersView` on the server.
 */
export interface InstanceMembersView {
  instanceId: string;
  name: string;
  members: InstanceMember[];
  /** The effective structure (pinned version plus customisation), as keys and display names only. */
  sections: InstanceScopeSection[];
}

/**
 * Mirrors `InstanceSeatsView` on the server.
 *
 * `allowance` being null means two different things depending on `enforced`: unenforced, nothing is being
 * counted at all; enforced, the plan includes unlimited users. Everything below `used` is the terms the
 * figure came from (KEY-497), and is null whenever `enforced` is false — there is no plan to describe.
 */
export interface InstanceSeatsView {
  /** Whether adding a member can be refused for want of a seat. When false the two counts are null. */
  enforced: boolean;
  /** How many users the governing plan covers. */
  allowance: number | null;
  /** How many of them are already taken across every dial the plan pays for. */
  used: number | null;
  /** The governing plan, family and tier together — "Commercial Silver". */
  planName?: string | null;
  /** What `usersPerUnit` is counted against: "Instance", "Definition" or "School". */
  allowanceUnit?: string | null;
  /** Users the plan includes per unit. Null on a plan that includes unlimited users. */
  usersPerUnit?: number | null;
  /** How many units the subscriber has, so `usersPerUnit × units` reproduces `includedAllowance`. */
  units?: number | null;
  /** The plan's own contribution to the pool, before anything bought on top. */
  includedAllowance?: number | null;
  /** Places bought as add-ons. Zero, not null, when none were. */
  purchasedAllowance?: number | null;
  /** How many of `used` are archived accounts — archiving leaves memberships, so they keep their place. */
  archivedInUse?: number | null;
}

export interface InviteInstanceMemberResult {
  userId: string;
  email: string;
  /** False when the address already had an account and was reused rather than duplicated. */
  userCreated: boolean;
  /** True when they also had to be let into the organisation to hold the grant. */
  addedToOrganisation: boolean;
  role: string;
  /** Only meaningful when the request set `sendEmail` — false both when it wasn't asked for and when it failed. */
  emailSent: boolean;
}

// --- Onboarding one person in a single step (KEY-218) ---

export interface OnboardingOrganisation {
  /** Pick one… */
  existingId?: string | null;
  /** …or create one. Exactly one side, or the request is refused. */
  newName?: string | null;
  newType?: string | null;
  newParentId?: string | null;
}

export interface OnboardingInstanceGrant {
  instanceId: string;
  role: string;
  sectionKey?: string | null;
  segmentKey?: string | null;
}

export interface OnboardPersonRequest {
  email: string;
  organisation: OnboardingOrganisation;
  organisationRole: string;
  displayName?: string | null;
  instances?: OnboardingInstanceGrant[];
  cascadeToChildren?: boolean;
}

export interface OnboardPersonResult {
  userId: string;
  userCreated: boolean;
  organisationId: string;
  organisationName: string;
  organisationCreated: boolean;
  instancesGranted: string[];
  /** Dials that did NOT work, each with why — the membership still stands. */
  instancesSkipped: string[];
}

export interface RoleSummary {
  id: string;
  name: string;
  isSystem: boolean;
  capabilities: string[];
  /** Platform-only roles (the Administrator) may only be assigned to the Key Dials organisation. */
  platformOnly: boolean;
}

export interface OrgMemberSummary {
  userId: string;
  email: string;
  displayName: string | null;
  roles: string[];
  /** True when this membership also enrols the user into every sub-organisation (existing and future). */
  cascadesToChildren: boolean;
  /**
   * The dials this organisation owns on which the member holds a grant — the grants that removing them from
   * the organisation also removes (KEY-747). Archived dials carry an " (archived)" suffix. Absent from older APIs.
   */
  dialNames?: string[] | null;
  /** Ticked "Can build dials" here (KEY-928): may build their own dials, whatever their role. Absent from older APIs. */
  canBuildDials?: boolean;
}

/** Tick or untick "Can build dials" for a member (KEY-928). */
export interface SetMemberBuilderRequest {
  canBuildDials: boolean;
}

export interface AddOrgMemberRequest {
  email: string;
  roleName: string;
  displayName?: string | null;
  password?: string | null;
  /** Also add the member, with the same role, to every sub-organisation — now and any added later. */
  cascadeToChildren?: boolean;
}

export interface AddOrgMemberResult {
  userId: string;
  userCreated: boolean;
}

export interface UpdateOrgMemberRequest {
  roleName: string;
}

/** One person in a People search (KEY-895). The organisation shown is the first by name of their memberships
 * inside the searched level; `otherOrganisationCount` is how many more they hold there. */
export interface PersonSearchRow {
  id: string;
  email: string;
  displayName: string | null;
  archivedAtUtc: string | null;
  isExternal: boolean;
  primaryOrganisationId: string | null;
  primaryOrganisationName: string | null;
  otherOrganisationCount: number;
  dialCount: number;
}

export interface PeopleSearchResult {
  items: PersonSearchRow[];
  hasMore: boolean;
}

export interface PeopleSearchQuery {
  q: string;
  /** Narrow to this organisation and everything below it (the admin's working level). */
  orgId?: string | null;
  status?: "active" | "archived" | "any";
  skip?: number;
  take?: number;
}

export interface UserSummary {
  id: string;
  email: string;
  displayName: string | null;
  /** Set (ISO timestamp) when the account is archived — sign-in blocked, hidden from the active directory. */
  archivedAtUtc?: string | null;
  /**
   * True for an external guest (KEY-297), whose dial access is held by a `GuestGrant` alongside the
   * membership rows. Deleting those rows strips access while the grant stays Active, so the user card
   * offers no per-grant revoke for a guest (KEY-544).
   */
  isExternal?: boolean;
}






/**
 * A user's footprint across the platform, used to decide between a permanent delete and a
 * (reversible) archive. `canHardDelete` is true only when removing the user would orphan or block
 * nothing and they are not the last platform administrator.
 */
export interface UserUsage {
  organisationMemberships: number;
  instanceMemberships: number;
  statusChanges: number;
  documents: number;
  entitlementGrants: number;
  /**
   * Nodes naming this user in a User attribute (KEY-267). No foreign key backs the reference, so a hard
   * delete would leave those fields pointing at nobody. Independent of `instanceMemberships`: revoking a
   * grant on a dial leaves the assignments behind on purpose.
   */
  attributeAssignments: number;
  isLastPlatformAdmin: boolean;
  canHardDelete: boolean;
}

// --- Dashboard / admin ---

export interface OrgTypeCount {
  type: string;
  count: number;
}

export interface DashboardStats {
  organisations: number;
  users: number;
  definitions: number;
  instances: number;
  /** Definitions that are standalone (single) dials. */
  singleDefinitions: number;
  /** Definitions that are group dials (aggregating child dials). */
  groupDefinitions: number;
  /** Dial instances whose definition is a single dial. */
  singleInstances: number;
  /** Dial instances whose definition is a group dial. */
  groupInstances: number;
  orgTypeBreakdown: OrgTypeCount[];
  /**
   * Definitions built by a user rather than the platform (KEY-212 Phase A). They are private to whoever
   * built them and so never appear in the catalogue list — this count is the only place the platform sees
   * them, and `definitions` above stays the unfiltered total. 0 until user-built authoring ships (Phase B).
   */
  userBuiltDefinitions: number;
  /** Non-archived organisations created in the trailing 30 days (KEY-562). */
  newOrganisations: number;
  /** Dial instances created in the trailing 30 days. */
  newInstances: number;
  /**
   * Definitions whose first version was published in the trailing 30 days — publication is the proxy for
   * "created", since a definition carries no creation timestamp of its own.
   */
  newDefinitions: number;
}

export interface UserMembershipView {
  organisationId: string;
  organisationName: string;
  organisationType: string;
  roles: string[];
  /**
   * The membership also enrols the user into every organisation beneath this one. Removing it does NOT
   * unwind that cascade — the descendant memberships are rows of their own, listed separately.
   */
  cascadesToChildren?: boolean;
}

/** One in-dial grant. A null `sectionKey` means the whole instance; a section with no segment narrows it to that section. */
export interface UserInstanceGrantView {
  role: string;
  sectionKey: string | null;
  segmentKey: string | null;
  /** Display names resolved from the instance's structure; null for whole-instance grants or keys the structure no longer contains. */
  sectionName: string | null;
  segmentName: string | null;
  /** The grant row's own id, so one grant can be revoked without disturbing the others (KEY-544). */
  grantId?: string;
}

/** A dial a user is allocated to, with every grant they hold on it (a user may hold several, at different scopes). */
export interface UserInstanceView {
  instanceId: string;
  instanceName: string;
  definitionName: string;
  organisationId: string;
  organisationName: string;
  grants: UserInstanceGrantView[];
  /**
   * True when the dial's definition is a group. A whole-dial grant on a group is inherited by every dial
   * beneath it, so revoking it reaches further than the one row (KEY-544).
   */
  isGroup?: boolean;
}

// --- Roles & capabilities ---

export interface CapabilityView {
  key: string;
  description: string;
  isPlatform: boolean;
  domain: string;
}

export interface RoleDetailView {
  id: string;
  name: string;
  isSystem: boolean;
  isLocked: boolean;
  capabilities: string[];
}

export interface CreateRoleRequest {
  name: string;
}

/** A user who holds a role, with the organisations in which they hold it. */
export interface RoleMemberView {
  userId: string;
  email: string;
  displayName: string | null;
  organisations: string[];
}

export interface SetRoleCapabilitiesRequest {
  capabilities: string[];
}

// --- System settings ---

export interface SettingView {
  key: string;
  label: string;
  category: string;
  isSecret: boolean;
  isSet: boolean;
  value: string | null;
  description: string | null;
  /** True when the value comes from deployment config (env vars / Key Vault) rather than the admin UI. */
  isFromEnvironment: boolean;
  /** KEY-546: the closed list of values this setting accepts, when it has one. Render a picker rather than a
   * text box — the server refuses anything outside it. Absent for free-text settings, which is most. */
  allowedValues?: string[] | null;
}

export interface UpdateSettingRequest {
  value: string;
}

/** A Claude model available to the configured API key (drives the settings model pickers). */
export interface AiModelOption {
  id: string;
  displayName: string;
}

// --- Data admin (dev/test only) ---

/** Whether the destructive database-reset tools are enabled in this environment. */
export interface DataToolsStatus {
  resetEnabled: boolean;
}

/** Which slices of data a reset should clear (the platform baseline is always preserved). */
export interface DataResetOptions {
  clearOrganisations: boolean;
  clearUsers: boolean;
  clearDefinitions: boolean;
}

export interface DataResetSummary {
  organisationsDeleted: number;
  usersDeleted: number;
  definitionsDeleted: number;
  instancesDeleted: number;
}

// --- Dial instances ---

export interface InstanceSummary {
  id: string;
  name: string;
  definitionName: string;
  version: number;
  memberCount: number;
  /** The definition this instance was allocated from. */
  definitionId: string;
  /** The owning organisation. */
  organisationId: string;
  organisationName: string;
  /** The owning organisation's parent, when it has one — lets the client nest child orgs under their parent. */
  organisationParentId: string | null;
  createdAtUtc: string;
  /** False on the normal listing (archived instances are filtered out); true only on the archived view. */
  isArchived: boolean;
  archivedAtUtc?: string | null;
  /** True when this dial runs a version that is only out for CHECKING (KEY-283) — a throwaway test dial
   * whose contents are discarded when checking ends. The end-user app badges it so nobody mistakes it for
   * a live dial and enters real work into it. */
  isTesting?: boolean;
  /** True when this dial asks for a dial-level value before it opens (KEY-434). Badge it in a list, so a
   * card with a blank ring reads as "not set up yet" rather than as still loading — and so a group shows
   * which of its children needs attention, a group never being blocked by one. */
  needsFirstRunCapture?: boolean;
  /** True when this dial is a GROUP — its action points are child dials rather than work of its own. The
   * portfolio marks a group's tile so a group is recognisable in a grid of ordinary dials (KEY-520). */
  isGroup?: boolean;
  /** KEY-575: on a test dial (`isTesting`), the name of whoever sent it for checking or made it fresh, so a list can
   * say whose it is. Null/absent on every other dial, and on a round sent before senders were recorded. */
  testingSentByName?: string | null;
  /** KEY-938: a demo dial — shown with a Demo badge; files are never stored in it. */
  isDemo?: boolean;
}

/** One organisation node in the cross-org portfolio tree (KEY-131). */
export interface PortfolioOrganisationNode {
  id: string;
  name: string;
  parentId: string | null;
  type: string;
}

/** The cross-org "My Dials" portfolio: every dial the user can read across all their orgs, plus the org tree. */
export interface PortfolioResponse {
  instances: InstanceSummary[];
  organisations: PortfolioOrganisationNode[];
  /**
   * Pre-computed render details (KEY-195) for the dials the server could safely batch — standalone,
   * non-group dials the user has full visibility of — so My Dials draws their cards from this one
   * response instead of a detail request per card. Dials absent here (scoped, group, or uncovered) are
   * fetched per-card as before. Undefined on older servers.
   */
  cards?: InstanceDetail[];
}

/** A saved dashboard view (KEY-134). `configJson` is opaque to the server — see {@link ViewConfig}. */
export interface SavedView {
  id: string;
  name: string;
  isDefault: boolean;
  /** Opaque JSON: the frontend's grouping/filter/sort configuration. */
  configJson: string;
  updatedAtUtc: string;
}

/** Create or replace a saved view. */
export interface SaveViewRequest {
  name: string;
  configJson: string;
  isDefault?: boolean;
}

export interface InstanceMember {
  /** Identifies this specific grant (a user may hold several at different scopes). */
  grantId: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: string;
  sectionKey: string | null;
  segmentKey: string | null;
  /** KEY-852: set when the grant is one item of the action point — it counts at that item only. */
  itemKey?: string | null;
  /**
   * True when the grant is not held on this dial but comes down from its parent Group Key Dial. It carries
   * the same authority here, but can only be edited or removed on the group it was granted on.
   */
  inherited?: boolean;
  /**
   * True when the user's account is archived: sign-in is blocked but the grant stands. Keep naming them on
   * records they already hold — a User attribute assignment, say — and leave them out of pickers (KEY-267).
   */
  archived?: boolean;
}

/**
 * One entry in a dial's people list (KEY-PEOPLE): somebody the dial keeps track of, who need not hold an
 * account. `userId` is the dial member this person IS, when they have one — the link that makes "Mila's
 * dentist" and "Mila, who reads the dial" the same Mila. Mirrors `InstancePersonDto`.
 */
/** KEY-RESETTIME: the scheduled resets on one dial, as its Overview shows them. */
export interface InstanceResets {
  /** The zone every time here is read in — the dial's own, or null for UTC. */
  timeZoneId?: string | null;
  /** Empty while the server's reset sweep is in dry run, so nothing is promised that won't happen. */
  resets: InstanceReset[];
}

/** One scheduled reset on a dial. */
export interface InstanceReset {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  dimensionKey?: string | null;
  /** When it runs on THIS dial — the dial's own timing where it has one, else the template's. */
  recurrence: GeneratedRecurrence;
  /** That, as the product's own sentence ("Every week on Monday at 04:00"). */
  description: string;
  /** The author's timing, for "Back to the dial's default". */
  templateRecurrence: GeneratedRecurrence;
  templateDescription: string;
  /** The dial runs it on its own day and time. */
  adjusted: boolean;
  /** The author lets a dial move it. */
  adjustable: boolean;
  /** This caller may move it: adjustable and a dial admin. */
  canChange: boolean;
  /** When it next runs (ISO instant), or null when that can't be worked out. */
  nextResetUtc?: string | null;
  /** The fields it also empties. */
  clearAttributeKeys: string[];
}

/** Moves one reset on one dial; `recurrence: null` puts it back to the dial's default. */
export interface SetInstanceResetTimingRequest {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  dimensionKey?: string | null;
  recurrence: GeneratedRecurrence | null;
}

// ---- Milo, the assistant (KEY-704) ----

/** Whether Milo is on, and where the household's pool stands. The pool fields are null when billing is off. */
/** One of the caller's connected calendars (KEY-736). An iCal link is never echoed back — `accountLabel` is its name. */
export interface CalendarConnectionView {
  id: string;
  provider: "google" | "microsoft" | "ics" | "caldav";
  accountLabel: string;
  connectedAtUtc: string;
  lastSyncedUtc: string | null;
  lastSyncError: string | null;
  upcomingEventCount: number;
  /** KEY-818: the account holds several calendars to choose from (Google, Outlook). False for an iCal link. */
  canChooseCalendars?: boolean;
  /** KEY-818: the calendars chosen, or empty for the account's main calendar alone. */
  calendarNames?: string[] | null;
  /** KEY-819: dials could add appointments to this account at all (not an iCal link). */
  supportsWrites?: boolean;
  /** KEY-819: the person has let them. */
  writeAllowed?: boolean;
  /** KEY-819: the calendar they go into, or null for the account's main calendar. */
  writeCalendarName?: string | null;
  /** KEY-821: the person's words that send an appointment Milo adds to this account. */
  keywords?: string[] | null;
}

/** KEY-818: one calendar in a connected account. `colour` is the provider's own, as #rrggbb. */
export interface CalendarChoiceView {
  id: string;
  name: string;
  isPrimary: boolean;
  chosen: boolean;
  colour: string | null;
  /** KEY-819: the account may add events to it. */
  canWrite?: boolean;
  /** KEY-819: new appointments go into it. */
  writesHere?: boolean;
}

/** KEY-818: an account's calendars, and the most that may be chosen. */
export interface CalendarChoiceList {
  calendars: CalendarChoiceView[];
  maxChosen: number;
}

/** A calendar provider this server can connect, and whether it's configured here. */
export interface CalendarProviderOption {
  provider: "google" | "microsoft" | "ics" | "caldav";
  label: string;
  available: boolean;
}

/** The caller's calendars (KEY-736). `enabled: false` (and nothing else) when `Features:DialCalendars` is off. */
export interface CalendarConnectionList {
  enabled: boolean;
  connections?: CalendarConnectionView[];
  providers?: CalendarProviderOption[];
  /** How many days ahead a sync reads — the card says so, because it's how long titles are kept. */
  windowDays?: number;
  /** KEY-819: dials may be let add appointments (`Features:DialCalendarWrite`). */
  writeEnabled?: boolean;
}

/** One person's calendar shown on a dial (KEY-736). `accountLabel`/`provider` only for the caller's own. */
export interface InstanceCalendarSourceView {
  id: string;
  ownerUserId: string;
  ownerName: string;
  isMine: boolean;
  shareDetails: boolean;
  /** False once the owner has removed the connection itself. */
  connected: boolean;
  provider: "google" | "microsoft" | "ics" | "caldav" | null;
  accountLabel: string | null;
  personId: string | null;
  /** KEY-820: my own calendar, and it lets dials add appointments. Always false for anyone else's. */
  canAddAppointments?: boolean;
  /** KEY-820: the calendar appointments go into, or null for the account's main calendar. */
  writeCalendarName?: string | null;
}

/** KEY-823: whether I have a private feed link. The link itself is shown only when it is made. */
export interface CalendarFeedView {
  exists: boolean;
  createdAtUtc: string | null;
}

/** KEY-823: a new feed link — shown once, because only its hash is kept. */
export interface CalendarFeedLink {
  url: string;
  webcalUrl: string;
}

/** KEY-820: an appointment added to my calendar from a day. `startTime` is "HH:mm" in the dial's time zone. */
export interface AddCalendarAppointmentRequest {
  segmentKey: string;
  title: string;
  startTime?: string | null;
  durationMinutes?: number | null;
  allDay?: boolean;
  location?: string | null;
  sourceId?: string | null;
}

export interface InstanceCalendarSourceList {
  sources: InstanceCalendarSourceView[];
  /** The caller's own connections that aren't on this dial yet. */
  myOtherConnections: CalendarConnectionView[];
  /** Editors and admins may add their own calendar. */
  canAdd: boolean;
}

/** KEY-767: how a dial lays out its rolling days, and the times of day its definition offers. */
export interface DaysLayout {
  allDay: boolean;
  hiddenSlots: string[];
  slots: { key: string; name: string; startsAt: string }[];
}

export interface MiloStatus {
  enabled: boolean;
  /** Whether the person can talk to Milo here. Since KEY-846 the same as `enabled`: free Milo needs no pack. */
  canUse: boolean;
  /** Whether Milo Plus — the AI, for anything free Milo can't place — answers here (KEY-846): a pack, top-up credits, or
   * billing off. False is when the apps show the Milo Plus note. Absent from older servers: treat as true. */
  plus?: boolean;
  packs?: number | null;
  allowance?: number | null;
  used?: number | null;
  resetsAtUtc?: string | null;
  /** What one Milo pack costs on this household's rate card, in pence, for the introduction (KEY-715). Null with
   * billing off or when nothing governs the organisation. */
  packPence?: number | null;
  /** True when `packPence` is a yearly price (an annual plan), false when monthly. */
  packAnnual?: boolean;
  /** The rung of the Milo ladder the household holds (KEY-716), in requests a month; null with none. */
  tierRequests?: number | null;
  /** The ladder the household's plan sells, smallest first, for the tier picker; null with billing off. */
  tiers?: MiloTier[] | null;
  /** Top-up credits not yet spent (KEY-717). They outlive the month. Null with billing off. */
  topUpRemaining?: number | null;
  /** What 300 more requests cost, in pence per top-up; null where a top-up cannot be bought. */
  topUpPence?: number | null;
  /** Whether this environment has Milo at all (Features:Milo), whoever asks — unlike `enabled`, which is whether he is
   * offered to this caller. The admin app hides Milo's tier and top-up controls when false. Absent from older servers. */
  available?: boolean;
}

/** What buying a Milo top-up came to (KEY-717). */
export interface MiloTopUpReceipt {
  id: string;
  requests: number;
  /** What was charged; zero when nothing bills the organisation. */
  paidPence: number;
  /** Whether the credits are spendable now. False while a card is still being confirmed. */
  granted: boolean;
  note?: string | null;
}

/** One rung of the Milo ladder (KEY-716): so many requests a month for so many pence, in the plan's period. */
export interface MiloTier {
  requests: number;
  pence: number;
}

export interface MiloTurnRequest {
  message: string;
  /** The dial the person is looking at, if any, so "the MOT" needs no "on which dial?". */
  instanceId?: string | null;
}

/** A write Milo wants to make and is waiting on the person for. The server holds it; confirming runs it. */
export interface MiloProposedAction {
  id: string;
  /** `set_status`, `add_item`, `post_comment` or `assign_person`. */
  kind: string;
  /** What will happen, in words. */
  summary: string;
  instanceId: string;
  /** Where it could go when that's the person's to pick (an appointment, more than one calendar): a button each.
   *  Confirming with none takes the first. */
  choices?: MiloChoice[] | null;
}

/** One place a proposal could go — a calendar. */
export interface MiloChoice {
  id: string;
  label: string;
}

export interface MiloTurnResponse {
  conversationId: string;
  reply: string;
  proposedAction?: MiloProposedAction | null;
  turnsLeft: number;
}

export interface MiloMessage {
  role: "user" | "milo";
  text: string;
}

export interface MiloConversation {
  id: string;
  messages: MiloMessage[];
  proposedAction?: MiloProposedAction | null;
  turnsLeft: number;
  expiresAtUtc: string;
}

export interface InstancePerson {
  id: string;
  name: string;
  order: number;
  userId?: string | null;
  userEmail?: string | null;
  userDisplayName?: string | null;
  /** The linked account has been archived: keep naming them, leave them out of link pickers. */
  archived?: boolean;
}

export interface AddInstancePersonRequest {
  name: string;
  userId?: string | null;
}

export interface UpdateInstancePersonRequest {
  name: string;
  userId?: string | null;
}

/**
 * One entry in a dial's contacts list (KEY-752): somebody the dial deals with — the dentist, the school
 * office — saved once and picked by a Contact field. Every detail but the name is optional; `address` may
 * run over several lines. Mirrors `InstanceContactDto`.
 */
export interface InstanceContact {
  id: string;
  name: string;
  order: number;
  phone?: string | null;
  address?: string | null;
  email?: string | null;
  notes?: string | null;
}

/** Add or replace a contact. An update replaces every detail: send the current value of anything to keep. */
export interface InstanceContactRequest {
  name: string;
  phone?: string | null;
  address?: string | null;
  email?: string | null;
  notes?: string | null;
}

/** One field that is currently empty because it was cleared (KEY-LASTTIME). `source` is the change source
 * of the clear: "ScheduledReset", "NextRound", "User", … */
export interface ClearedField {
  nodeType: string;
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  attributeKey: string;
  clearedAtUtc: string;
  source: string;
}

/**
 * The days a dial changed (KEY-LOOKBACK timeline): one row per UTC day with at least one change the caller
 * may see, oldest first. `createdAtUtc` is where the timeline starts.
 */
export interface InstanceChangeDays {
  createdAtUtc: string;
  days: { day: string; count: number }[];
}

/** The value a field held before it was last cleared (KEY-LASTTIME), in its stored form. */
export interface PreviousAttributeValue {
  value: string;
  clearedAtUtc: string;
  source: string;
  clearedByEmail: string;
}

/**
 * The dial as it stood at an instant (KEY-LOOKBACK): statuses replayed from the change history, rolled up
 * under today's rules. Structure is today's; a node with no status yet shows its starting status. `rollup`
 * is null for a scoped viewer, as on the detail.
 */
/** KEY-889 (Look ahead): the dial on a later day if nothing is done, what changes before then, and a days dial's
 * days from that date. */
export interface InstanceForecast {
  atUtc: string;
  ahead: InstanceSnapshot;
  changes: ForecastChange[];
  days?: ResolvedSection[] | null;
}

/** One thing that happens by itself before the day — `on` is the dial-local `yyyy-MM-dd`. */
export interface ForecastChange {
  on: string;
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  dimensionKey?: string | null;
  fromStatusKey: string;
  toStatusKey: string;
  reason: "dueDate" | "changeBack" | "reset";
}

export interface InstanceSnapshot {
  atUtc: string;
  /** `reviewDate` is the date the last status change up to then RECORDED — approximate, because a date the
   * server kept or re-armed on its own is written without an event (see InstanceSnapshotService). */
  statuses: { sectionKey: string; segmentKey: string; statusKey: string; reviewDate?: string | null }[];
  itemStatuses: { sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string; statusKey: string; reviewDate?: string | null }[];
  rollup: InstanceRollup | null;
  /** The sections' own statuses then (KEY-479); null when the dial doesn't offer them. */
  sectionStatuses?: { sectionKey: string; statusKey: string }[] | null;
  /** Every field's value then, keyed as {@link NodeAttributeValue} is. `value` null = empty then. */
  fields?: SnapshotField[] | null;
  /** KEY-795: the section colours as they were then — absent when every section is Auto. */
  sectionColours?: SectionColour[] | null;
}

/** One field's value on the Then day (Look back). Pruned server-side by the same rules as the live value. */
export interface SnapshotField {
  nodeType: string;
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  attributeKey: string;
  value: string | null;
}

/** Who may set a status, beside its roles (KEY-918). "Everyone" is the default and travels as null/absent
 * on a status; the per-dial role editor spells it out. */
export type StatusAudience = "Everyone" | "Members" | "Guests" | "Responsible";

export const STATUS_AUDIENCES: readonly StatusAudience[] = ["Everyone", "Members", "Guests", "Responsible"];

/** The words for an audience, as the editors offer it. */
export const STATUS_AUDIENCE_LABELS: Record<StatusAudience, string> = {
  Everyone: "Everyone with the role",
  Members: "Members only",
  Guests: "Guests only",
  Responsible: "Responsible person only",
};

/** One status as the per-instance role editor sees it. */
export interface InstanceStatusRole {
  statusKey: string;
  name: string;
  colour: string;
  order: number;
  /** What the dial definition grants. Shown as the baseline to revert to. */
  definitionRoles: string[];
  /** What applies in this dial now. Always contains "Admin" — Admin bypasses the check. */
  effectiveRoles: string[];
  /** True when this dial has its own roles for the status rather than the definition's. */
  isOverridden: boolean;
  /** KEY-918: who may set it as the definition has it. */
  definitionAudience?: StatusAudience;
  /** KEY-918: who may set it in this dial now. */
  effectiveAudience?: StatusAudience;
}

/** One palette a dial draws on (default, a per-action-point override, or an item-status axis). */
export interface InstanceStatusRolePalette {
  statusSetId: string;
  name: string;
  /** Where this palette applies, in words — for the editor's heading. */
  usage: string;
  statuses: InstanceStatusRole[];
}

/** Who may set each status in a dial. `canManage` false ⇒ show it read-only. */
export interface InstanceStatusRolesView {
  canManage: boolean;
  palettes: InstanceStatusRolePalette[];
}

// --- Allocation (KEY-916): hand a section, action point or item to one person ---

/** One allocation. `state`: "Active" | "Completed" | "Withdrawn" | "HandedOn". `assigneeName` is null when they're no
 * longer on the dial. `contactRole` is a contact's access level; null for a member. */
export interface NodeAssignmentView {
  id: string;
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
  assigneeKind: "Member" | "Contact";
  assigneeId: string;
  assigneeName: string | null;
  contactRole: string | null;
  dueDate: string;
  state: "Active" | "Completed" | "Withdrawn" | "HandedOn";
  assignedByName: string | null;
  assignedAtUtc: string;
  endedAtUtc: string | null;
  handedOnToId: string | null;
}

/** Allocate a node. `dueDate` is required (yyyy-mm-dd) and becomes an action point's or item's review date;
 * `statusKey` optionally sets a status too; `contactRole` is a contact's access (default Editor). */
export interface AssignNodeRequest {
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
  assigneeKind: "Member" | "Contact";
  assigneeId: string;
  dueDate: string;
  contactRole?: string | null;
  statusKey?: string | null;
  dimensionKey?: string | null;
}

/** Who a hand-on status passes the node to. */
export interface HandOnRequest {
  assigneeKind: "Member" | "Contact";
  assigneeId: string;
  dueDate: string;
  contactRole?: string | null;
}

/** One status's new roles; `roles: null` reverts it to the definition's. */
export interface SetInstanceStatusRoleEntry {
  statusSetId: string;
  statusKey: string;
  roles: string[] | null;
  /** KEY-918: this dial's audience, or null/absent to keep the definition's. Rides on the same override as
   * the roles, so send the effective roles alongside it; `roles: null` reverts both. */
  audience?: StatusAudience | null;
}

// --- Role-action matrix (KEY-298): who may do what beyond statuses ---

/** One configurable action's roles: "AttributeEdit" | "DocumentUpload" | "DocumentVersions" |
 * "MessagePost" | "MessagePostRestricted". */
export interface InstanceActionRole {
  action: string;
  definitionRoles: string[];
  effectiveRoles: string[];
  isOverridden: boolean;
  /** The SECOND mask, on "MessagePostRestricted" only (KEY-285): who a restricted sender may address.
   * Null/absent means no restriction — anyone who can read the action point. */
  definitionTargetRoles?: string[] | null;
  effectiveTargetRoles?: string[] | null;
}

/** Who may perform each configurable action in a dial. `canManage` false ⇒ show it read-only. */
export interface InstanceActionRolesView {
  canManage: boolean;
  actions: InstanceActionRole[];
}

/** One action's new roles; `roles: null` reverts it to the definition's (deleting the override row, and
 * with it both masks). `targetRoles` is read only for "MessagePostRestricted" and only when a row is
 * written — null or empty means "may address anyone who can read the action point". */
export interface SetInstanceActionRoleEntry {
  action: string;
  roles: string[] | null;
  targetRoles?: string[] | null;
}

/** One of MY per-person attribute overrides (KEY-299 fine-tune), riding the detail payload. */
export interface MyAttributeOverride {
  target: string;
  attributeKey: string;
  /** "Hidden" | "See" | "Edit" — wins over the role rules in both directions. */
  access: string;
}

/** One declared attribute's access for one member: null = no override, the role rules decide. */
export interface MemberAttributeAccess {
  target: string;
  attributeKey: string;
  label: string;
  access: string | null;
}

export interface MemberAttributeAccessView {
  canManage: boolean;
  attributes: MemberAttributeAccess[];
}

/** One attribute's new access for a member; null deletes the override, restoring the role rules. */
export interface SetMemberAttributeAccessEntry {
  target: string;
  attributeKey: string;
  access: string | null;
}

/** The dial's effective action matrix, overrides applied — gate affordances on these exactly as on
 * per-status allowedRoles; the server re-checks every write. */
export interface InstanceRoleActions {
  attributeEdit: string[];
  documentUpload: string[];
  documentVersions: string[];
  messagePost: string[];
  /** Who may post RESTRICTED messages here (KEY-285). Absent on older payloads — treat as nobody. */
  messagePostRestricted?: string[] | null;
  /** Who a restricted sender may address; null = anyone who can read the action point. */
  messagePostRestrictedTo?: string[] | null;
  /** Who may SEE that a file exists on this dial (KEY-537). Absent on a payload from a server that predates
   * it — treat that as "everyone", the pre-KEY-537 behaviour, rather than hiding every dial's Files tab. */
  documentList?: string[] | null;
  /** Who may DOWNLOAD a file's bytes here (KEY-537). Same absent-means-older-server rule. */
  documentDownload?: string[] | null;
}

/** Add or update an instance member (role + optional section/segment scope). */
export interface AddInstanceMemberRequest {
  userId: string;
  role: string;
  sectionKey?: string | null;
  segmentKey?: string | null;
  /** Emails the recipient about the grant, if it succeeds. Best-effort — see `AddInstanceMemberResponse.emailSent`. */
  sendEmail?: boolean;
}

export interface AddInstanceMemberResponse {
  /** Only meaningful when the request set `sendEmail` — false both when it wasn't asked for and when it failed. */
  emailSent: boolean;
}

/** The current status of one segment within an instance. */
/** The status set on one whole section (KEY-479). Only sections that have one appear. */
export interface SectionStatus {
  sectionKey: string;
  statusKey: string;
  updatedAtUtc: string;
  updatedByEmail: string;
}

/**
 * One group section's rolled-up edge (KEY-483). The status is a CHILD's — worst-of selects an input rather
 * than synthesising one — so its display name and colour travel with the key: nothing on the group's own
 * payload could resolve them.
 */
export interface GroupSectionStatus {
  sectionKey: string;
  statusKey: string;
  statusName: string;
  statusColour: string;
}

/** KEY-523: one action point's share of a group section's rolled-up edge — what the ring shows for that
 * segment when the dial splits it. Same rollup as `GroupSectionStatus` over a narrower population: just the
 * child sections that one segment's mappings name. Absent for a section a group status RULE decided (one
 * statement about the whole section), on a Mirror face, and for segments the viewer cannot see. */
export interface GroupSectionEdgeSegment {
  sectionKey: string;
  segmentKey: string;
  statusKey: string;
  statusName: string;
  statusColour: string;
}

export interface SegmentStatus {
  sectionKey: string;
  segmentKey: string;
  statusKey: string;
  reviewDate: string | null;
  updatedAtUtc: string;
  updatedByEmail: string;
  /** When set, the segment auto-reverts to this status once `reviewDate` passes (Phase 6). */
  revertToStatusKey?: string | null;
  /** This node's own reminder horizon: how many days before `reviewDate` to speak up first. Null follows
   * the dial's authored ladder (KEY-413), which is what almost every node does. */
  reviewReminderLeadDays?: number | null;
  /** How often to repeat that reminder, in days, until the review date; null says it once. Refused
   * without a lead — there would be nothing for it to repeat from. */
  reviewReminderRepeatDays?: number | null;
  /**
   * How often this review comes round again (KEY-376) — `"Days"`, `"Weeks"` or `"Months"` with
   * {@link reviewRecurrenceCount}. The next round is measured from when the review was DONE, never from
   * the date it was due, so a lapsed one stays visible instead of quietly re-dating itself.
   *
   * On a REQUEST this has three values, not two: absent means "not talking about it" and leaves any
   * existing cycle alone (a plain status change must not cancel a yearly cycle on its way past), and
   * `"None"` ends it.
   */
  reviewRecurrenceUnit?: string | null;
  reviewRecurrenceCount?: number | null;
}

/** The current status of one item along one dimension within an instance (itemised dials). */
export interface ItemStatus {
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  dimensionKey: string;
  statusKey: string;
  updatedAtUtc: string;
  updatedByEmail: string;
  /** Optional date this item's status should be reviewed (KEY-376). */
  reviewDate?: string | null;
  /** When set, the item auto-reverts to this status once `reviewDate` passes (KEY-376). */
  revertToStatusKey?: string | null;
  /** This node's own reminder horizon: how many days before `reviewDate` to speak up first. Null follows
   * the dial's authored ladder (KEY-413), which is what almost every node does. */
  reviewReminderLeadDays?: number | null;
  /** How often to repeat that reminder, in days, until the review date; null says it once. Refused
   * without a lead — there would be nothing for it to repeat from. */
  reviewReminderRepeatDays?: number | null;
  /**
   * How often this review comes round again (KEY-376) — `"Days"`, `"Weeks"` or `"Months"` with
   * {@link reviewRecurrenceCount}. The next round is measured from when the review was DONE, never from
   * the date it was due, so a lapsed one stays visible instead of quietly re-dating itself.
   *
   * On a REQUEST this has three values, not two: absent means "not talking about it" and leaves any
   * existing cycle alone (a plain status change must not cancel a yearly cycle on its way past), and
   * `"None"` ends it.
   */
  reviewRecurrenceUnit?: string | null;
  reviewRecurrenceCount?: number | null;
}

/** The value of one declared node attribute (KEY-161) on one node. nodeType is "Section" | "Segment" |
 * "Item"; unused key levels are empty strings. */
export interface NodeAttributeValue {
  nodeType: string;
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  attributeKey: string;
  value: string;
  updatedAtUtc: string;
  updatedByEmail: string;
  /**
   * When this value was answered on one of a group's children rather than here (KEY-483), the child
   * instance it came from. Absent on an ordinary value — which is every value on every dial that feeds no
   * field — so the two are told apart by the presence of the provenance rather than by a flag.
   *
   * The value is computed on the read path and never stored, so there is nothing here to save, refresh or
   * reconcile: it simply reflects whatever the child currently says. A field can be fed and still have no
   * answer yet, in which case `value` is empty but the provenance is present — that is the difference
   * between "waiting on the Site Safety dial" and "nobody has filled this in".
   */
  sourcedFromInstanceId?: string | null;
  /** Where the answer came from, ready to show: the child dial's name and the field's label on it, e.g.
   *  `"Site Safety · Owner"`. Present exactly when {@link sourcedFromInstanceId} is. */
  sourcedFromLabel?: string | null;
}

/**
 * A segment's rolled-up completion band ("Red" | "Amber" | "Green") and its item tally. `progress` (0..1) is
 * the finer-grained companion to the band: the mean share of each item's gating dimensions that are finished,
 * so a one-item segment — whose band can only be Red or Green — still shows movement. Optional because an
 * older API may not send it; treat a missing value as "fall back to the band".
 */
export interface SegmentRollup {
  sectionKey: string;
  segmentKey: string;
  band: "Red" | "Amber" | "Green";
  completeItems: number;
  totalItems: number;
  progress?: number;
  /**
   * The status the segment's display rule resolved to. Absent when the segment has no rule, or its rule had
   * no opinion — colour from `band` in that case, exactly as before rules existed.
   */
  statusKey?: string | null;
  /**
   * KEY-485: this segment has a threshold ladder whose lowest rung its progress has not reached, so the
   * ladder resolved nothing on purpose. Only meaningful alongside an absent `statusKey`, and it exists so a
   * reader can be told that rather than shown the RAG band's wording — "some of the items are complete" and
   * "the ladder has not fired yet" are different sentences that were rendering as the same words.
   */
  belowFirstThreshold?: boolean;
  /**
   * KEY-494: the worst status this segment's items sit at on the SECTION's rollup axis — the same
   * worst-of the section edge derives from, taken one segment at a time so that edge can be drawn split
   * into per-segment arcs. Not interchangeable with `statusKey`, which answers in the segment's own
   * palette when a display rule fired; they coincide only where the dial shares one axis and has no
   * rules. Absent when the dial names no axis, or nothing on this segment resolves against it.
   */
  sectionAxisStatusKey?: string | null;
}

/**
 * The whole-instance completion rollup: per-segment bands plus the overall fractions (0..1). `completion`
 * counts fully-finished items only and stays the headline percentage; `progress` gives partial credit for
 * part-scored items.
 */
/** KEY-482: a section's status as derived from the dial's scoring axis. The key names a status in THAT
 *  axis's palette, not the dial's default one — see `statusRollupDimensionKey` on the instance detail. */
export interface SectionRollup {
  sectionKey: string;
  statusKey: string;
}

export interface InstanceRollup {
  completion: number;
  completeItems: number;
  totalItems: number;
  segments: SegmentRollup[];
  /** Each section's derived status (KEY-482). Absent when the dial derives nothing. A status someone set by
   *  hand is a different thing and lives in `sectionStatuses` on the detail. */
  sections?: SectionRollup[] | null;
  progress?: number;
}

/** Where a resolved structure node came from. */
/** Where a node came from. `"Calendar"` (KEY-736) is an event read from someone's connected calendar. */
export type NodeOrigin = "Base" | "Custom" | "Calendar";

/**
 * A dial instance's effective structure (pinned version ⊕ customisation overlay, Phase B). Extends the
 * {@link GeneratedSection} shapes so renderers that read `key`/`label`/`segments`/`items` keep working
 * unchanged; `origin`/`overridden` are additive per-node badging (optional for forward compatibility).
 */
export interface ResolvedItem extends GeneratedItem {
  origin?: NodeOrigin;
  overridden?: boolean;
  /** KEY-736: set on an event from a connected calendar. Its `name` is "09:15 · Busy" unless this viewer may read
   * the title (theirs, or shared with the dial), in which case `revealed` is true and `name` carries it. */
  calendar?: CalendarEventInfo | null;
  /** KEY-754: true only on a node that is hidden on this dial, sent because the dial admin reading it asked to see
   * the hidden parts for a while (`InstanceDetail.hiddenRevealExpiresAtUtc`). Nobody else ever receives it. */
  isHidden?: boolean;
  /** KEY-859: on an item added to a day, its time; absent otherwise (a calendar event's is in `calendar`). */
  span?: ItemSpan | null;
}

/** The calendar side of an event on a dial (KEY-736). */
export interface CalendarEventInfo {
  sourceId: string;
  ownerUserId: string;
  startUtc: string;
  endUtc: string;
  allDay: boolean;
  /** The time as the dial shows it — "09:15", or "All day". */
  when: string;
  location?: string | null;
  revealed?: boolean;
  /** Kept because somebody acted on it after it left the calendar. */
  frozen?: boolean;
  /** KEY-771: flagged important by the calendar, or by a word the days section looks for. */
  important?: boolean;
  /** KEY-821: this viewer may change or delete it from the dial — their own, one-off, live event in an account that
   * lets dials write. Only ever set on the owner's own read. */
  canEdit?: boolean;
}

export interface ResolvedSegment extends Omit<GeneratedSegment, "items"> {
  items: ResolvedItem[];
  origin?: NodeOrigin;
  overridden?: boolean;
  /** KEY-736: the date (`yyyy-MM-dd`) a rolling-days section's segment stands for; absent on any other segment. */
  date?: string | null;
  /** KEY-754: true only on a node that is hidden on this dial, sent because the dial admin reading it asked to see
   * the hidden parts for a while (`InstanceDetail.hiddenRevealExpiresAtUtc`). Nobody else ever receives it. */
  isHidden?: boolean;
  /** KEY-771 part B: on a time of day coloured from its items, how busy it is — a key in the DEFAULT palette — drawn as
   * its arc of the day's edge when the dial splits section edges. */
  edgeStatusKey?: string | null;
  /** KEY-859: on a time of day, what's listed earlier that day but runs on into it — counted here, shown as "continues". */
  spanning?: SpanningItem[] | null;
}

export interface ResolvedSection extends Omit<GeneratedSection, "segments"> {
  segments: ResolvedSegment[];
  origin?: NodeOrigin;
  overridden?: boolean;
  /** Set only on a section another organisation feeds onto this dial (KEY-257 phase 3). Its presence is
   * what keeps the section out of this dial's own completion totals and out of every edit affordance —
   * never infer "fed" from the key prefix. */
  fedFrom?: FedSectionOrigin | null;
  /** KEY-754: true only on a node that is hidden on this dial, sent because the dial admin reading it asked to see
   * the hidden parts for a while (`InstanceDetail.hiddenRevealExpiresAtUtc`). Nobody else ever receives it. */
  isHidden?: boolean;
  /** KEY-771 part B: on a day coloured from its items, how busy the whole day is — a key in the DEFAULT palette — drawn
   * on its single outer edge. A hand-set section status still wins. */
  edgeStatusKey?: string | null;
}

/** Whose work a fed section is. Names are carried, not looked up: the recipient is not a member of the
 * sharer's organisation and generally cannot resolve either name for themselves. */
export interface FedSectionOrigin {
  feedId: string;
  sourceInstanceId: string;
  organisationName: string;
  dialName: string;
  /** The section's key on the source dial, before namespacing — what the sharer calls it. */
  sourceSectionKey: string;
}

/** How much room the customisation envelope leaves on this instance (Phase B). */
export interface StructureHeadroom {
  enabled: boolean;
  /** Null when customisation is disabled. */
  sectionsRemaining?: number | null;
  /** Per-level caps (null = unlimited) so the UI can compute per-section / per-segment headroom. */
  maxSegmentsPerSection?: number | null;
  maxItemsPerSegment?: number | null;
}

/**
 * One overview segment of a Group Key Dial (Phase 7): its rolled-up traffic-light band (null when no
 * child segments feed it yet) and the child instance the segment drills into. Derived from the children's
 * live status, not stored.
 */
export interface GroupOverviewSegment {
  sectionKey: string;
  segmentKey: string;
  band: "Red" | "Amber" | "Green" | null;
  /** Composite score in 0..1 (KEY-162), or null until something feeds the segment. */
  score: number | null;
  /** Named health tier derived from the score (KEY-162), or null when there is no score. */
  classification: string | null;
  childInstanceId: string | null;
  /** The child status this segment is SHOWING, in the child's own palette. On a Mirror face, the mirrored
   * simple child segment's real status; on either face, once the version colours its segments from its
   * children (`groupSegmentStatusSource`, KEY-509), the worst status the mapped child action points are
   * displaying — an itemised child's display-rule or axis grade included. Null/absent otherwise, and the
   * segment colours by band. */
  statusKey?: string | null;
  statusName?: string | null;
  statusColour?: string | null;
  /** The child section this segment stands for, so a drill-in can land on it: the mirrored section on a
   * mirror face, or the one section an authored link is bound to. Null when the segment rolls up a whole
   * child dial — there is no single section to open. */
  childSectionKey?: string | null;
  /** Mirror-face groups only: the single child segment this face segment is a window onto. */
  childSegmentKey?: string | null;
}

/** One child instance of a Group Key Dial — the group workspace's child list (the only route to a child
 * a Mirror-mode face doesn't display). */
export interface GroupChildRef {
  childInstanceId: string;
  name: string;
  definitionId: string;
  /** KEY-529: how this child is doing as the GROUP sees it — the worst of the wedges it feeds. A child is
   * mapped to many wedges on a mirror face and on the per-section shape, and to none when it is allocated
   * but unlinked, so there is no single wedge to read instead. Absent when nothing feeds it. */
  band?: "Red" | "Amber" | "Green" | null;
  /** The child status behind that band, in the CHILD's own words. Folded server-side: ordering two
   * children needs each one's palette, which a group does not hold — so a client fold could only ever
   * have compared bands, and would show bands under a dial showing grades. Absent when the group colours
   * by band rather than from its children, in which case `band` alone is set. */
  statusKey?: string | null;
  statusName?: string | null;
  statusColour?: string | null;
}

/**
 * KEY-527: one palette a GROUP's wedges are showing statuses from, and the group sections it applies to.
 *
 * A group holds no palette its children's statuses live in — the children each grade on their own scale —
 * so unlike every other dial the legend cannot resolve these from `statusSet`. Without them the key can
 * only list the statuses currently PAINTED, which shows a group whose children agree a single word and
 * calls it the scale.
 *
 * `sectionKeys` is always populated, one palette or many: whether a single palette collapses to one
 * unlabelled row is the client's presentation call.
 */
export interface GroupPalette {
  palette: GeneratedStatusSet;
  sectionKeys: string[];
}

/** KEY-719: where a dial's centre image came from — its own override, an organisation's dial logo, the
 * definition's default, or nothing (the client draws the Key Dials mark). */
export type CentralImageSource = "instance" | "organisation" | "definition" | "none";

export interface InstanceDetail {
  introVideo?: GuidanceVideo | null;

  /** How to work this whole dial (KEY-534) — rendered on the workspace's own Guidance tab. */
  guidance?: string | null;

  /** That guidance's video, resolved for playback. */
  guidanceVideo?: GuidanceVideo | null;

  /** That guidance's documents (KEY-565), each with a short-lived `downloadUrl`. */
  guidanceDocuments?: GuidanceDocument[] | null;

  /** That guidance's external-resource links (KEY-565). */
  guidanceLinks?: GeneratedLink[] | null;

  /** KEY-814: the dial's welcome page. On an instance, only for its editors and admins. */
  welcome?: DialWelcome | null;

  id: string;
  name: string;
  definitionId: string;
  version: number;
  /** The definition default palette. */
  statusSet: { name: string; statuses: GeneratedStatus[] };
  /** The effective structure: pinned version merged with the instance customisation overlay. */
  sections: ResolvedSection[];
  members: InstanceMember[];
  /** The dial's people list (KEY-PEOPLE) — what a Person field picks from. Absent when empty or the
   * feature is off, and on the lock screen. */
  people?: InstancePerson[] | null;
  /** The dial's contacts list (KEY-752) — what a Contact field picks from. Absent when empty, and on the
   * lock screen. */
  contacts?: InstanceContact[] | null;
  /** KEY-LASTTIME: fields that are empty now because something cleared them, so the card can offer "last
   * time". Only fields the caller may see. Absent when none. */
  clearedFields?: ClearedField[] | null;
  statuses: SegmentStatus[];
  /**
   * Every distinct palette the instance uses (default + per-segment overrides), by name. A segment's
   * effective palette is its `palette` when set, otherwise `statusSet`.
   */
  palettes: { name: string; statuses: GeneratedStatus[] }[];
  /** Short-lived display URL for the dial's central hub image, if the definition has one. */
  centralImageUrl?: string | null;
  /** "Simple" | "Itemised". */
  kind: string;
  /** Per-item status axes (itemised dials only). */
  dimensions?: GeneratedDimension[];
  /** Current per-item statuses (itemised dials only). */
  itemStatuses?: ItemStatus[];
  /** Computed completion rollup: per-segment RAG bands + overall fraction (itemised dials only). */
  rollup?: InstanceRollup | null;
  /** KEY-482: the scoring axis this dial's action points are coloured from, or null/absent when nothing is
   *  derived. Needed to resolve a derived status key, which names a colour in that axis's palette. */
  statusRollupDimensionKey?: string | null;
  /** KEY-485: the axis the SECTION edges are coloured from, when that differs from the action points' one.
   *  Null/absent means they follow `statusRollupDimensionKey`. Sent for the same reason as that key: with
   *  the two separated, a section's edge and its action points may be speaking different palettes, and a
   *  status key cannot be read without knowing which. */
  sectionStatusRollupDimensionKey?: string | null;
  /** Customisation headroom for this instance (Phase B): remaining sections + per-level caps. */
  headroom?: StructureHeadroom | null;
  /** True for a Group Key Dial — its overview segments are coloured by a rollup of child instances. */
  isGroup?: boolean;
  /** Per overview-segment rollup band + drill-in child instance (group dials only, Phase 7). */
  overview?: GroupOverviewSegment[] | null;
  /** Current per-node attribute values (KEY-161). Null/absent when none set or none visible. */
  nodeAttributeValues?: NodeAttributeValue[];
  /** The attribute fields declared on this instance's nodes (what each level carries). */
  nodeAttributes?: GeneratedNodeAttribute[];
  /** The active upload-status-automation rule (item axis + target status), or null when the feature is off. */
  uploadAutomation?: UploadAutomation | null;
  /** Per-node on/off overrides of the rule; only switched nodes appear (everything else defaults on). */
  uploadAutomationSettings?: UploadAutomationSetting[];
  /** KEY-499: this dial's own upload tags, in creation order — offered in the Files picker after the three
   * built-ins. Absent/empty on dials that use the built-ins alone. A document's `category` may name a tag
   * that is NOT in here (one since deleted): this list drives the picker, never the reading of a file. */
  documentTags?: DocumentTag[];
  /** True when a dial admin may change this instance's central logo (definition master switch + this
   * instance's gate both on). The user app shows the click-to-change affordance only when this is true. */
  logoOverrideAllowed?: boolean;
  /** True when this instance currently has its own central image override set (vs. inheriting the
   * definition's image). Drives the "revert to default" affordance. */
  hasCentralImageOverride?: boolean;
  /** KEY-719: where `centralImageUrl` came from. Absent from older payloads. */
  centralImageSource?: CentralImageSource | null;
  /** KEY-719: what would show if this dial's own override were cleared (never "instance") — what Revert goes back to. */
  centralImageFallbackSource?: CentralImageSource | null;
  /** KEY-719: the organisation whose logo that fallback is, when it is "organisation". */
  centralImageFallbackOrganisationName?: string | null;
  /** KEY-718: why the centre logo may not be changed here (null when it may): "rebranding-off" (definition),
   * "not-switched-on" (this dial's switch), "managers-only" (an organisation's policy) or "locked" (a lock above). */
  centralImageEditBlock?: "rebranding-off" | "not-switched-on" | "managers-only" | "locked" | null;
  /** KEY-718: the organisation whose policy that is, for "managers-only" and "locked". */
  centralImageEditBlockedBy?: string | null;
  /** Group instances only: every child instance of this group, whether or not the face shows it. */
  children?: GroupChildRef[] | null;
  /** The dial-level description authored on the definition (rich text), shown on the workspace Overview
   * tab. Null/absent when the definition has none. */
  description?: string | null;
  /** Kiosk auto-lock (KEY-268). Absent/null when the definition's master switch is off — the shared-device
   * mode isn't offered for this dial at all, so the workspace says nothing about it. */
  autoLock?: InstanceAutoLock | null;
  /** True when this dial runs a version that is only out for CHECKING (KEY-283) — a throwaway test dial
   * whose contents are discarded when checking ends. The end-user app badges it so nobody mistakes it for
   * a live dial and enters real work into it. */
  isTesting?: boolean;
  /** KEY-524: true when this dial sits anywhere in a subtree whose ROOT is out for checking — including a
   * child of a checking group, which `isTesting` reports false for (a checking group allocates its children
   * at their own latest versions, so a child's own version is never the one being checked). Gate the testing
   * banner and role toggle on this; `isTesting` stays the narrower "this dial's own version is the one out
   * for checking". Always true wherever `isTesting` is. */
  inTestingSubtree?: boolean;
  /**
   * KEY-938: a demo dial. The workspace shows the Demo banner and turns a refused upload or download (a 409
   * carrying the demo's own words) into a notice rather than an error; the server refuses regardless.
   */
  isDemo?: boolean;
  /** KEY-498: the role the server ACTUALLY evaluated this caller as, when they are checking this dial and
   * asked to be treated as an Editor, Reviewer or Reader instead of the Admin checking makes everyone.
   * Null/absent whenever nothing was simulated — no request, not a checking dial, or the caller is not a
   * real Admin and so may not downgrade. Render the toggle from this: a refused request then shows up as
   * the toggle snapping back to "my own role" rather than a UI that disagrees with the server. */
  simulatedRole?: string | null;
  /** The dial's effective action matrix (KEY-298), overrides applied. Absent on older payloads — fall
   * back to the historical assumptions when missing. */
  roleActions?: InstanceRoleActions | null;
  /** MY per-person attribute overrides (KEY-299 fine-tune), when any exist. */
  myAttributeOverrides?: MyAttributeOverride[] | null;
  /** The IANA time zone this dial's SCHEDULES are read in (KEY-276); absent/null means UTC. Scheduling
   * only — no timestamp in this payload is localised by it, every `*AtUtc` field is still UTC. */
  timeZoneId?: string | null;
  /** True when the caller may change `timeZoneId` (an instance-wide dial admin, or an org instance-manager).
   * The server re-checks on write; this only decides who is shown the control. */
  canEditTimeZone?: boolean;
  /** True when the caller may change the centre logo on this dial (KEY-756): `logoOverrideAllowed` plus the
   * dial-admin role in the dial's OWN organisation, which is what the upload checks. Absent from older payloads. */
  canEditCentreLogo?: boolean;
  /** Whether this dial offers the "hide empty action points" filter at all (KEY-457). */
  hideEmptyEnabled?: boolean;
  /** Whether this dial draws its section status edges split per action point (KEY-494). Absent or false
   * means one colour across the whole section, which is what every dial did before. */
  sectionEdgeSplitEnabled?: boolean;
  /** Whether people may set a status on a whole section here (KEY-479) — the definition's master switch.
   * When true, `sectionStatuses` lists the sections that carry one and the palette is `statusSet`. */
  sectionStatusEnabled?: boolean;
  /** Whether this dial offers Milo at all (KEY-714) — the definition's switch. The apps still ask `miloStatus`
   * before showing him: the environment flag and the household's pool are not the dial's to know. */
  miloEnabled?: boolean;
  /** Whether somebody put Milo away on this dial (KEY-715): the intro's Dismiss, for the whole household. Only
   * meaningful while `miloEnabled` is true. A dial admin clears it from the dial's Display settings. */
  miloHidden?: boolean;
  /** Whether this dial offers Look back (KEY-735) — the definition's switch. False hides the Look back tab and
   * the compare mode, and the server refuses `as-of`. Absent (an older server) means on. */
  lookBackEnabled?: boolean;
  /** KEY-754: while the CALLER (a dial admin) is looking at the dial's hidden parts, when that stops (ISO UTC);
   * null otherwise — and always null except on web-user's own dial read. While set, `sections` includes the hidden
   * nodes, each marked `isHidden`. Re-read the dial
   * when it passes — the server stops honouring it then regardless. */
  hiddenRevealExpiresAtUtc?: string | null;
  /** KEY-539: what this definition's dials call an action point, singular — e.g. "segment". Absent means
   * the platform's own word, "action point". Display only; `segmentKey` and every other key are untouched.
   * Always absent or present together with `segmentNounPlural`: the server makes the half state
   * unrepresentable. Read it through `segmentNoun()` in @keydials/dial-workspace rather than directly. */
  segmentNounSingular?: string | null;
  /** KEY-539: the plural of `segmentNounSingular`. Stored, never derived — never append an "s". */
  segmentNounPlural?: string | null;
  /** KEY-670: this definition's own names for its dials' fixed tab headings, `{ key: label }` — e.g.
   * `{ "guidance.segment": "Deliverables" }`. Absent/null when it renames none. Keys are `tab` (every level) or
   * `tab.level` (one level, winning over the bare key there), from the server's allowlist, mirrored as
   * `DIAL_LABEL_KEYS` in @keydials/dial-workspace. Read it through `dialLabel()` there rather than directly. */
  labelOverrides?: Record<string, string> | null;
  /** KEY-527: GROUP dials only — every distinct palette this group's wedges draw their statuses from, each
   * naming the sections it applies to, in the order the face first uses them. Absent on non-groups, and on
   * a group nothing has coloured yet. */
  groupPalettes?: GroupPalette[] | null;
  /** The sections that currently carry a status (KEY-479); absent when none, or when not offered. */
  sectionStatuses?: SectionStatus[] | null;
  /**
   * KEY-483: the sections whose edge this GROUP dial rolls up from its children — one entry per section
   * something feeds. Absent when the group's version names no source, or when no child section carries an
   * edge yet.
   *
   * A channel of its own because the winning status belongs to a CHILD's palette: neither `statusSet` nor
   * the rolled-up axis palette holds it, so the colour is resolved server-side and travels with the key.
   * A hand-set row in `sectionStatuses` still wins — this is the fallback beneath it.
   */
  groupSectionStatuses?: GroupSectionStatus[] | null;
  /** KEY-523: the same edges at action-point grain, so a GROUP can split its section ring. The arcs read
   * these rather than the segments' displayed statuses, so a split ring goes on speaking the children's
   * section vocabulary instead of switching to the wedges' palette. */
  groupSectionEdgeSegments?: GroupSectionEdgeSegment[] | null;
  /** Whether the CALLER is hiding them — their own choice, kept per user rather than per browser. */
  hideEmptyForMe?: boolean;
  /** KEY-592: ask the caller, once, whether they want notifications from this dial. False once they have answered
   * either way, when they already follow it (here or through a group above it), on a test dial, and in Preview-as. */
  offerFollowPrompt?: boolean;
  /** KEY-606: the organisation that owns this dial. A group's child is never in the dial list, so this is the only place
   * its title bar can read whose dial it is. */
  organisationName?: string | null;
  /** KEY-606: the source definition's name — what kind of dial this is. */
  definitionName?: string | null;
  /** KEY-606: for a group's child, the group dial above it. Absent when the caller cannot read the group. */
  parentInstanceId?: string | null;
  parentInstanceName?: string | null;
  /** The days before a review date this dial sends reminders on (KEY-413) — the version's authored ladder,
   * or the server default beneath it. Resolved server-side so the review sheet never has to know the
   * inheritance rule. An EMPTY array is a real answer: this dial sends no reminders. */
  reviewReminderLeadDays?: number[] | null;
  /**
   * KEY-460: the required fields this dial's action points are still waiting on — one row per unanswered
   * field. Absent/empty is the common case: nothing is required, or everything required is answered.
   *
   * Server-computed, and it has to be: a field restricted away from the caller has its value pruned from
   * `nodeAttributeValues` (KEY-299), so a client deciding for itself would read an answered restricted
   * field as unanswered and lock the action point for ever. Use {@link segmentRequirementsFor}.
   */
  segmentRequirements?: SegmentRequirement[] | null;
  /** KEY-546: who may hand THIS dial out, per channel, with the caller's own verdict on each. Absent only
   * from older payloads; an unrestricted dial still reports every channel as `DialAdmin` and allowed. */
  sharePolicy?: InstanceSharePolicy | null;
  /** KEY-767: present on a dial with a rolling-days section. */
  daysLayout?: DaysLayout | null;
  /** KEY-795: the outer edge of every section whose colouring isn't Auto, worked out by the server just now. */
  sectionColours?: SectionColour[] | null;
  /** KEY-795: when a section colour next changes by itself (something falls due), so the dial can be read again. */
  nextColourChangeUtc?: string | null;
  /** KEY-814: show the welcome page now — the first time this person opens the dial, or after a new version changed it. */
  offerWelcome?: boolean;
  /** KEY-917: who the parent group dial names as Responsible person at dial level — the last fallback. */
  parentResponsible?: ResponsiblePersonView | null;
  /** KEY-918: the viewer is a guest, so a "Members" status isn't theirs to set and a "Guests" one is. */
  viewerIsGuest?: boolean;
  /** KEY-852: a guest invite may name single items here — the feature is on and this isn't a group dial. */
  offersItemScopedGuests?: boolean;
  /** KEY-916: the live allocations the viewer can see — who has each node, until when. Absent when allocation is off. */
  assignments?: NodeAssignmentView[] | null;
}

/**
 * KEY-917: who is responsible for a node and where that answer was set. `name` is null when the person is
 * no longer a member or on the contacts list.
 */
export interface ResponsiblePersonView {
  kind: "Member" | "Contact";
  id: string;
  name: string | null;
  level: "Item" | "Segment" | "Section" | "Dial" | "ParentDial";
  attributeKey: string;
  sectionKey?: string | null;
  segmentKey?: string | null;
  itemKey?: string | null;
}

/** KEY-917: one attempt to invite a dial contact, picked as Responsible person or re-sent. */
export interface ContactInvite {
  id: string;
  contactId: string;
  email: string | null;
  trigger: "Picked" | "Resent";
  outcome: "Sent" | "EmailFailed" | "NoEmail" | "HasAccount" | "Blocked" | "InvalidEmail";
  sentAtUtc: string;
  sentBy: string | null;
}

/**
 * KEY-795: one section's outer edge. For `Rule` the server worked the colour out (`statusKey`/`name`/`colour`, all null
 * for "no edge"), with one arc per action point in `segments` when the edge is split. For the other sources the apps
 * draw what they always drew for that source. `allowHandSet`: a hand-set status, when there is one, wins.
 */
export interface SectionColour {
  sectionKey: string;
  source: SectionColourSource;
  statusKey?: string | null;
  name?: string | null;
  colour?: string | null;
  segments?: { segmentKey: string; statusKey?: string | null; name?: string | null; colour?: string | null }[] | null;
  completeItems?: number;
  totalItems?: number;
  missedItems?: number;
  allowHandSet?: boolean;
}

/**
 * Who may hand a dial out (KEY-546), ordered by strictness.
 *
 * - `DialAdmin` — an instance-wide dial admin, or an organisation instance-manager. The default everywhere,
 *   and exactly the behaviour that existed before restrictions were introduced.
 * - `OrganisationManager` — only an organisation instance-manager; a dial admin is refused.
 * - `Nobody` — the channel is closed on this dial, with no platform-admin carve-out.
 */
export type ShareAuthority = "DialAdmin" | "OrganisationManager" | "Nobody";

/** Why a share was refused (KEY-546). Absent when it wasn't. */
export type ShareBlockReason = "OrganisationManagerOnly" | "Closed" | "InstanceNotFound" | "CheckingTestDial";

/**
 * One share channel's answer for the caller who asked for this dial (KEY-546).
 *
 * `allowed` is about the POLICY alone, never the caller's role: a Reader on an unrestricted dial sees
 * `allowed: true`, because nothing in the policy is stopping them. Whether they clear the ordinary
 * member-management bar is a separate question the rest of the payload already answers — which is what lets
 * a UI say "your organisation doesn't allow this" instead of the misleading "you can't do this".
 */
export interface ShareChannelPolicy {
  authority: ShareAuthority;
  allowed: boolean;
  blockReason?: ShareBlockReason | null;
  /** The refusal in words, ready to show. Null when allowed. */
  message?: string | null;
}

/** The four share channels governing one dial (KEY-546). */
export interface InstanceSharePolicy {
  /** Adding somebody to the dial — including the one-step invite that creates their account. */
  member: ShareChannelPolicy;
  /** Inviting a guest: dial access with no organisation membership at all. */
  guest: ShareChannelPolicy;
  /** A scoped read grant to a user or a whole organisation. */
  grant: ShareChannelPolicy;
  /** A cross-organisation feed. */
  feed: ShareChannelPolicy;
}

/** One required field an action point is still waiting on (KEY-460). */
export interface SegmentRequirement {
  sectionKey: string;
  segmentKey: string;
  attributeKey: string;
  /** The field's display label — sent so the lock can NAME what's missing even when the caller can't see
   *  the field's value. A lock that names nothing is a dead end. */
  label: string;
}

/** The required fields one action point is waiting on (KEY-460); empty when it is open for business. */
export function segmentRequirementsFor(
  detail: Pick<InstanceDetail, "segmentRequirements">,
  sectionKey: string,
  segmentKey: string,
): SegmentRequirement[] {
  return (detail.segmentRequirements ?? []).filter(
    (r) => r.sectionKey === sectionKey && r.segmentKey === segmentKey,
  );
}

/** The kiosk auto-lock state of one instance (KEY-268). `enabled` is this instance's own gate — the dial
 * locks only when the definition's master switch is on AND this is on. `timeoutSeconds` is the EFFECTIVE
 * timeout to count down (the override where set, otherwise the definition's default), so the idle timer
 * never has to resolve the inheritance; `timeoutSecondsOverride` is the raw override for the settings UI. */
export interface InstanceAutoLock {
  enabled: boolean;
  timeoutSeconds: number;
  timeoutSecondsOverride: number | null;
  definitionTimeoutSeconds: number;
}

/** One choice in the time-zone picker (KEY-276): the IANA id stored on the dial, a human name, and the
 * offset the zone is on right now. The offset is a display aid — scheduling re-resolves the zone (daylight
 * saving included) at every occurrence rather than trusting a cached number. */
export interface TimeZoneOption {
  id: string;
  displayName: string;
  /** "+01:00" / "-05:30" / "+00:00". */
  utcOffset: string;
}

/** One of a definition's instances in the admin logo-override management list. */
export interface InstanceLogoOverrideRow {
  instanceId: string;
  name: string;
  organisationName: string;
  /** The per-instance logo-override gate in force — the same as `effective`, kept for older callers. */
  enabled: boolean;
  /** KEY-640: the group a child instance sits in; absent for a top-level dial. A child has its own gate —
   * switching the group on doesn't switch its children on (unless IncludeSubdials is passed, KEY-669). */
  parentInstanceName?: string | null;
  /** KEY-744: the dial's own setting; null when it follows the definition's default. */
  override?: boolean | null;
  /** KEY-744: what is in force once the definition's default is applied. */
  effective?: boolean;
}

/** Result of a bulk logo-override set (KEY-669). */
export interface SetInstancesLogoOverrideResult {
  updated: number;
  /** Names of any child definitions whose master switch was off and got switched on as a side effect of
   * enabling with `includeSubdials` — empty when disabling, or when nothing needed switching. */
  definitionsEnabled: string[];
}

/** The active upload-status-automation rule for an instance: the dial-wide default status it sets on upload
 * and (itemised dials only) the dimension it applies to — null on a simple dial, where the segment's own
 * status is set. Nodes may override the target per segment/item. */
export interface UploadAutomation {
  dimensionKey?: string | null;
  statusKey: string;
}

/** One per-instance override of the rule. `itemKey` null = a segment-level (whole action point) row.
 * `statusKey` null = the node inherits the dial-wide default target. */
export interface UploadAutomationSetting {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  /** The status axis this row governs. Null on the action point's own row (a Simple dial, or an itemised
   * action point with no items). One row per item per axis (KEY-279). */
  dimensionKey?: string | null;
  enabled: boolean;
  statusKey?: string | null;
}

/** Set the rule's overlay for one segment or item: whether it's on and its target-status override. `itemKey`
 * null/absent targets the whole action point; `statusKey` null/absent inherits the dial-wide default. Send the
 * node's full desired state (current status alongside an on/off change, and vice versa). */
export interface SetUploadAutomationSettingRequest {
  sectionKey: string;
  segmentKey: string;
  itemKey?: string | null;
  /** The status axis this row governs. Null on the action point's own row (a Simple dial, or an itemised
   * action point with no items). One row per item per axis (KEY-279). */
  dimensionKey?: string | null;
  enabled: boolean;
  statusKey?: string | null;
}

// --- Instance-first group views ---
// A user-assembled group over instances they already own (referenced, not re-allocated), the complement of
// the admin-authored Group Key Dial. Rolled up by the same engine; members stay normal standalone dials.

/** A group view in a listing: rollup config + a composite of its accessible members' health. */
export interface InstanceGroupSummary {
  id: string;
  name: string;
  /** "WorstOf" | "Latest" | "Weighted". */
  rollupStrategy: string;
  /** The number of members the caller can currently see. */
  memberCount: number;
  /** Composite score in 0..1 across accessible members, or null when none feed it. */
  score: number | null;
  classification: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  /**
   * The instance ids of exactly those members the caller can see — the same set the consolidated view
   * lists as columns. My Dials uses these to drop grouped dials from the standalone card grid without
   * waiting for the (much heavier) per-group consolidated calls (KEY-238). Optional: an older API omits it.
   */
  memberInstanceIds?: string[] | null;
  /**
   * How many members the caller sees only part of (a section/segment-scoped grant). Those carry no band, so
   * {@link score} covers the fully-visible members only. Optional: an older API omits it.
   */
  partialMemberCount?: number | null;
}

/** One member of a group view: the referenced instance (the drill-in target) + its rolled-up health. */
export interface InstanceGroupMemberSlice {
  instanceId: string;
  name: string;
  definitionName: string;
  organisationId: string;
  organisationName: string;
  band: "Red" | "Amber" | "Green" | null;
  score: number | null;
  classification: string | null;
  /**
   * The caller holds only a section/segment-scoped grant on this dial, so `band`, `score` and
   * `classification` are withheld rather than computed from the part they can see — a composite over one
   * reader's slice would present itself as the dial's health. The dial still opens, pruned to those scopes.
   */
  partial?: boolean;
}

/** A group view's detail: config + one slice per accessible member, a composite, and a hidden-member count. */
export interface InstanceGroupDetail {
  id: string;
  name: string;
  rollupStrategy: string;
  members: InstanceGroupMemberSlice[];
  /** Members hidden because the caller can no longer see them (or they were deleted). */
  hiddenMemberCount: number;
  score: number | null;
  classification: string | null;
  classificationBands?: GeneratedClassificationBand[] | null;
  /**
   * How many of {@link members} are visible only in part — present but unscored, and left out of
   * {@link score}. Distinct from {@link hiddenMemberCount}, which counts members that don't appear at all.
   */
  partialMemberCount?: number | null;
}

/** Create a group view from instances the caller already owns (inaccessible ids are dropped). */
export interface CreateInstanceGroupRequest {
  name: string;
  memberInstanceIds: string[];
  /** "WorstOf" (default) | "Latest" | "Weighted". */
  rollupStrategy?: string | null;
}

/** Rename a group view and/or change its rollup strategy. A null field is left unchanged. */
export interface UpdateInstanceGroupRequest {
  name?: string | null;
  rollupStrategy?: string | null;
}

/** One member column in the consolidated (by-area) view. */
export interface ConsolidatedMember {
  instanceId: string;
  name: string;
  organisationName: string;
}

/** One member's cell for a segment row: its band/score, or null when that member has no status there. */
export interface ConsolidatedCell {
  instanceId: string;
  band: "Red" | "Amber" | "Green" | null;
  score: number | null;
}

/** One segment row of the consolidated view: a shared-definition segment aggregated across members. */
export interface ConsolidatedRow {
  sectionKey: string;
  sectionLabel: string;
  segmentKey: string;
  segmentLabel: string;
  aggregateBand: "Red" | "Amber" | "Green" | null;
  aggregateScore: number | null;
  /** One cell per member, in the same order as {@link InstanceGroupConsolidated.members}. */
  cells: ConsolidatedCell[];
}

/** The consolidated "by-area" view of a group; `uniform` is false (and `rows` empty) when members span definitions. */
export interface InstanceGroupConsolidated {
  id: string;
  name: string;
  rollupStrategy: string;
  uniform: boolean;
  definitionName: string | null;
  members: ConsolidatedMember[];
  /**
   * Only the segments the caller's grants reach on at least one member. A section they hold nothing on
   * anywhere in the group is absent rather than present-and-empty, so don't treat the row set as the
   * definition's full structure. Within a row, a member the caller can't read that segment on has a null
   * cell and is left out of the aggregate.
   */
  rows: ConsolidatedRow[];
}

// --- AI insight layer (KEY-163) ---

/** One AI observation about a node of an instance (advisory; nothing is written back). */
export interface InsightFinding {
  /** Human label of the section/segment the finding is about. */
  nodeRef: string;
  observation: string;
  /** "improving" | "stable" | "deteriorating". */
  trend: string;
  /** "info" | "watch" | "urgent". */
  severity: string;
  recommendedAction: string;
  owner?: string | null;
  targetDate?: string | null;
}

/** AI-generated, read-only analysis of an instance (KEY-163). */
export interface InstanceInsights {
  instanceId: string;
  instanceName: string;
  model: string;
  generatedAtUtc: string;
  findings: InsightFinding[];
}

// --- Instance customisation (Phase C) ---

/** POST body to add a custom node; the node type comes from the route (/sections | /segments | /items). */
export interface AddNodeBody {
  parentKey?: string | null;
  label?: string | null;
  name?: string | null;
  description?: string | null;
  guidance?: string | null;
  /** KEY-859: on an item added to a day, when it starts and ends, or all day. */
  span?: ItemSpan | null;
}

/** KEY-859: when an item on a day starts and ends ("HH:mm", in the dial's zone; an end of "00:00" is midnight), or
 * that it takes the whole day. A start with no end is a moment at that time. */
export interface ItemSpan {
  startsAt?: string | null;
  endsAt?: string | null;
  allDay?: boolean;
}

/** KEY-859: something listed in `segmentKey` (the same day) that runs on into this time of day. `until` is its end
 * that day ("HH:mm"), or null when it runs all day or past midnight. */
export interface SpanningItem {
  segmentKey: string;
  itemKey: string;
  until?: string | null;
}

/** PATCH body for a node: re-word a custom node, or re-word/disable a base node (`nodeType` required for a base node). */
export interface PatchNodeBody {
  nodeType?: string | null;
  label?: string | null;
  name?: string | null;
  description?: string | null;
  guidance?: string | null;
  disabled?: boolean | null;
  /** KEY-859: an item on a day's time. Sent on its own, it changes only the time. */
  span?: ItemSpan | null;
}

/** Body to move a custom item to another action point (segment). */
export interface MoveNodeBody {
  targetParentKey: string;
}

/** A base node currently hidden on an instance (so the editor can offer to re-enable it). */
export interface DisabledNode {
  nodeType: string;
  key: string;
  /** What the hidden node is called — a section's label (KEY-737), an action point's or item's name (KEY-754) —
   * for the admin's overview note. Absent from an older server for anything but a section. */
  label?: string | null;
}

/** Why customisation is off, in the order the server evaluates the gates (KEY-239). Explanatory only —
 * `canCustomise` is still the gate. `VersionEnvelopeOff` is the one that varies by version, so an instance
 * pinned to an older version reports it while a newer version of the same dial has customisation on. */
export type CustomisationBlockReason =
  | "None"
  | "VersionEnvelopeOff"
  | "DefinitionMasterSwitchOff"
  | "GrantBlocked"
  | "NotPermitted"
  | "InstanceNotFound";

/** The customisation editor view for an instance: gate result, effective envelope, headroom, resolved tree, disabled nodes. */
export interface InstanceCustomisationView {
  canCustomise: boolean;
  effectivePolicy: CustomisationPolicy;
  headroom: StructureHeadroom;
  sections: ResolvedSection[];
  disabled: DisabledNode[];
  /** Populated when `canCustomise` is false; `"None"` otherwise. Optional for tolerance of an older API. */
  blockReason?: CustomisationBlockReason;
}

/** Why one of the campaign owner's OWN tiers can't be put behind a QR code (KEY-440). Explanatory only —
 * being in `plans` is still the gate. Ownership is deliberately absent: a tier on somebody else's price
 * list never reaches the client at all, so there is no "not yours" to render. */
export type CampaignPlanBlockReason =
  | "None"
  | "Retired"
  | "FreeOfCharge"
  | "BilledByInvoice"
  | "ReferenceLadder"
  | "NoAgreedPartnerShare"
  | "PartnerPayoutsNotReady";

/** Why there is nothing at all to pick (KEY-440) — a fact about the list, not about any one tier. */
export type CampaignPlanListBlockReason =
  | "None"
  | "NoOrganisation"
  | "OrganisationCannotRunCampaigns"
  | "NoPriceListOfItsOwn"
  | "EveryTierBlocked";

/** One of the owner's own tiers that can't be offered, and why. `label` reads as the picker spells it. */
export interface BlockedCampaignPlanView {
  id: string;
  label: string;
  reason: CampaignPlanBlockReason;
  /** Whose tier it is — set only on an any-owner list, where several price lists share one picker. */
  ownerName?: string | null;
}

/** One price list in the any-owner picker: Key Dials' own catalogue or one partner's. */
export interface CampaignPlanOwnerView {
  /** The organisation a campaign on one of these tiers will belong to. */
  organisationId: string;
  name: string;
  /** False for Key Dials' own catalogue. */
  isPartner: boolean;
  /** Which of `plans` are this owner's. */
  planIds: string[];
}

/** What the campaign editor's tier picker needs to be honest (KEY-440): what can be sold, what can't and
 * why, and whose price list this is at all. `blocked` only ever holds the resolved owner's own tiers. */
export interface CampaignPlanOptionsView {
  plans: PurchasablePlanView[];
  blocked: BlockedCampaignPlanView[];
  /** Whose price list this is — null when the caller isn't entitled to be told. */
  ownerName: string | null;
  /** True when the owner is the caller's own organisation, so the UI can say "your tiers". */
  ownerIsMine: boolean;
  listBlockReason: CampaignPlanListBlockReason;
  /** True for Top Co creating with no organisation in focus: every price list at once, and the tier picked
   * decides whose campaign it is. `owners` says which tier is whose; `ownerName`/`ownerIsMine` mean nothing. */
  anyOwner?: boolean;
  owners?: CampaignPlanOwnerView[] | null;
}

/** A portable set of an instance's bespoke (custom-overlay) items — for copying into other instances of the
 * same dial, or re-using as an item template for later blank instances. No instance ids or statuses; importing
 * recreates the items fresh and seeds their initial status. Items re-attach by `segmentKey`. */
export interface ExportedInstanceItems {
  formatVersion: number;
  definitionId: string;
  definitionName: string;
  version: number;
  items: ExportedItem[];
}

/** One exported custom item. `segmentKey` is the stable parent-segment key used to re-attach it on import;
 * `sectionKey`/`segmentName` are carried for readability of the file only. */
export interface ExportedItem {
  sectionKey: string;
  segmentKey: string;
  segmentName: string;
  name: string;
  description?: string | null;
  guidance?: string | null;
}

/** The outcome of importing items into an instance: overall status plus per-item tallies. */
export interface ImportItemsReport {
  status: "Ok" | "InstanceNotFound" | "Forbidden" | "NotAllowed" | "DefinitionMismatch";
  added: number;
  replaced: number;
  skippedNoSegment: number;
  skippedCapReached: number;
}

/** A portable running dial instance, for moving one between servers (KEY-208). Carries the progress a team
 * built on top of the definition — overlay, current statuses, attribute values and status history — all
 * addressed by stable keys. Documents, members, conversations and notifications are NOT included. */
export interface ExportedInstance {
  formatVersion: number;
  definitionName: string;
  definitionVersionNumber: number;
  sourceDefinitionId: string;
  instanceName: string;
  customNodes: unknown[];
  overrides: unknown[];
  segmentStatuses: unknown[];
  itemStatuses: unknown[];
  attributes: unknown[];
  segmentEvents: unknown[];
  itemEvents: unknown[];
}

/** What the imported instance should become here: owning organisation, name and instance admin. */
export interface ImportInstanceRequest {
  organisationId: string;
  name?: string | null;
  adminUserId?: string | null;
  payload: ExportedInstance;
}

/** The outcome of importing an instance. A non-`Ok` status means nothing was created; on success the
 * skip tallies say what the target's published version had no home for. */
export interface ImportInstanceReport {
  status:
    | "Ok"
    | "DefinitionNotFound"
    | "DefinitionNotPublished"
    | "DefinitionAmbiguous"
    | "GroupNotSupported"
    | "EntitlementExceeded";
  instanceId: string;
  sourceVersion: number;
  targetVersion: number;
  customNodes: number;
  overrides: number;
  statuses: number;
  attributes: number;
  events: number;
  /** Rows naming a section/segment/item/dimension this server's version doesn't have. */
  skippedUnknownKey: number;
  /** Rows whose status key isn't in the palette the target uses for that node. */
  skippedUnknownStatus: number;
  /** Attribute values the target version doesn't declare at that level. */
  skippedUndeclaredAttribute: number;
  /** Custom nodes whose parent isn't on the target, so they'd never resolve into the tree. */
  skippedOrphanNode: number;
  /** Segments whose auto-revert was cleared — its review date had already passed, or its revert
   * target isn't in the target palette. */
  clearedAutoRevert: number;
  /** History whose author has no account here, left unattributed rather than credited to the importer. */
  unattributed: number;
  versionDiffers: boolean;
}

/** A proposed value for one of the definition's declared Item-target attributes (AI document import). */
export interface CandidateAttribute {
  key: string;
  value: string;
}

/** One image extracted from the source document during AI item-import (PDF only for now), PNG-encoded
 * and indexed in document order. Referenced by index elsewhere rather than re-carried. */
export interface ExtractedDocumentImage {
  index: number;
  base64: string;
  mediaType: string;
}

/** One image the analyzer matched to a candidate item. `imageIndex` resolves against
 * `DocumentItemAnalysisResult.images`. `category` is "Found" | "Resolution" | "Uncategorized" — the
 * reviewer may drop the image or override the category before confirming. */
export interface CandidateImage {
  imageIndex: number;
  category: string;
}

/** One AI-extracted candidate item from an uploaded document. `suggestedSegmentKey` is null when no
 * segment clearly fits — the review UI shows it as unassigned until the user picks one. */
export interface DocumentItemCandidate {
  name: string;
  description?: string | null;
  guidance?: string | null;
  suggestedSegmentKey?: string | null;
  confidence?: "high" | "medium" | "low" | null;
  sourceReference?: string | null;
  attributes?: CandidateAttribute[] | null;
  /** The reviewer's/inspector's own commentary from the document — posted to the item's discussion on confirm. */
  inspectorComment?: string | null;
  /** A status from the dial's primary item-status dimension, when the document clearly states the item's state. */
  suggestedStatusKey?: string | null;
  /** Photos from the source document the model matched to this item, if any. */
  images?: CandidateImage[] | null;
}

/** The outcome of one AI document analysis; `truncated` warns that only the extractor's character cap
 * of the source was analysed. `images` is every photo extracted from the source (regardless of whether
 * the model matched it to an item) — resolve a candidate's `CandidateImage.imageIndex` against this list
 * to render a thumbnail. */
export interface DocumentItemAnalysisResult {
  items: DocumentItemCandidate[];
  fileName: string;
  truncated: boolean;
  images?: ExtractedDocumentImage[] | null;
}

/** One image the reviewer kept for a bulk-added item, resolved server-side against the analysis job
 * named by `BulkAddItemsRequest.sourceJobId` — the bytes are never re-sent. `category` may be edited
 * from the model's guess. */
export interface BulkAddItemImage {
  imageIndex: number;
  category: string;
}

/** One item to create via the additive bulk add (the document-import confirm step). `comment` is posted
 * to the new item's discussion; `statusKey` sets its status along the request's status dimension —
 * both best-effort (a refusal never fails the batch). `images` are uploaded against the created item
 * once it exists (also best-effort). */
export interface BulkAddItem {
  segmentKey: string;
  name: string;
  description?: string | null;
  guidance?: string | null;
  attributes?: CandidateAttribute[] | null;
  comment?: string | null;
  statusKey?: string | null;
  images?: BulkAddItemImage[] | null;
}

/** Maps one created item back to its index in the request. */
export interface BulkAddCreatedItem {
  inputIndex: number;
  sectionKey: string;
  segmentKey: string;
  key: string;
}

/** The outcome of an additive bulk add. Unlike `ImportItemsReport` nothing is ever replaced. */
export interface BulkAddItemsReport {
  status: "Ok" | "InstanceNotFound" | "Forbidden" | "NotAllowed";
  added: number;
  skippedNoSegment: number;
  skippedCap: number;
  createdKeys?: string[] | null;
  createdItems?: BulkAddCreatedItem[] | null;
  commentsPosted: number;
  statusesApplied: number;
  photosAttached: number;
  /** Comments/statuses/photos that couldn't be applied (gates, unknown status, missing source job) —
   * the items themselves were still created. */
  extrasSkipped: number;
}

/** Set — or, with a null `statusKey`, clear — a whole section's status (KEY-479). */
export interface ChangeSectionStatusRequest {
  sectionKey: string;
  statusKey: string | null;
  note?: string | null;
}

export interface ChangeStatusRequest {
  sectionKey: string;
  segmentKey: string;
  statusKey: string;
  /** ISO date; optional review/expiry date for the new status. */
  reviewDate?: string | null;
  note?: string | null;
  /** Status to auto-revert to once `reviewDate` passes; requires `reviewDate` (Phase 6). */
  revertToStatusKey?: string | null;
  /** This node's own reminder horizon: how many days before `reviewDate` to speak up first. Null follows
   * the dial's authored ladder (KEY-413), which is what almost every node does. */
  reviewReminderLeadDays?: number | null;
  /** How often to repeat that reminder, in days, until the review date; null says it once. Refused
   * without a lead — there would be nothing for it to repeat from. */
  reviewReminderRepeatDays?: number | null;
  /**
   * How often this review comes round again (KEY-376) — `"Days"`, `"Weeks"` or `"Months"` with
   * {@link reviewRecurrenceCount}. The next round is measured from when the review was DONE, never from
   * the date it was due, so a lapsed one stays visible instead of quietly re-dating itself.
   *
   * On a REQUEST this has three values, not two: absent means "not talking about it" and leaves any
   * existing cycle alone (a plain status change must not cancel a yearly cycle on its way past), and
   * `"None"` ends it.
   */
  reviewRecurrenceUnit?: string | null;
  reviewRecurrenceCount?: number | null;
  /** KEY-916: who a hand-on status passes the node to — required when the target status `handsOn`. */
  handOnTo?: HandOnRequest | null;
}

/** Change one item's status along one dimension (itemised dials). */
export interface ChangeItemStatusRequest {
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  dimensionKey: string;
  statusKey: string;
  note?: string | null;
  /** ISO date; optional review/expiry date for the new status (KEY-376). */
  reviewDate?: string | null;
  /** Status to auto-revert to once `reviewDate` passes; requires `reviewDate` (KEY-376). */
  revertToStatusKey?: string | null;
  /** This node's own reminder horizon: how many days before `reviewDate` to speak up first. Null follows
   * the dial's authored ladder (KEY-413), which is what almost every node does. */
  reviewReminderLeadDays?: number | null;
  /** How often to repeat that reminder, in days, until the review date; null says it once. Refused
   * without a lead — there would be nothing for it to repeat from. */
  reviewReminderRepeatDays?: number | null;
  /**
   * How often this review comes round again (KEY-376) — `"Days"`, `"Weeks"` or `"Months"` with
   * {@link reviewRecurrenceCount}. The next round is measured from when the review was DONE, never from
   * the date it was due, so a lapsed one stays visible instead of quietly re-dating itself.
   *
   * On a REQUEST this has three values, not two: absent means "not talking about it" and leaves any
   * existing cycle alone (a plain status change must not cancel a yearly cycle on its way past), and
   * `"None"` ends it.
   */
  reviewRecurrenceUnit?: string | null;
  reviewRecurrenceCount?: number | null;
  /** KEY-916: who a hand-on status passes the node to — required when the target status `handsOn`. */
  handOnTo?: HandOnRequest | null;
}

/** Set (or, with an empty value, clear) one declared node attribute (KEY-161). target is "Section" |
 * "Segment" | "Item"; supply the key path down to that level. */
export interface SetNodeAttributeRequest {
  /** One of {@link NODE_ATTRIBUTE_TARGETS}. "Dial" needs no keys — send empty strings. */
  target: string;
  sectionKey: string;
  attributeKey: string;
  value: string;
  segmentKey?: string | null;
  itemKey?: string | null;
  /** Look back's "Bring back": the day (ISO) this value is copied from. Tags the history row BroughtBack;
   * otherwise an ordinary edit. */
  broughtBackFrom?: string | null;
}

/** One entry in a segment's status-change history (newest first). */
export interface SegmentStatusEvent {
  sectionKey: string;
  segmentKey: string;
  fromStatusKey: string | null;
  toStatusKey: string;
  reviewDate: string | null;
  note: string | null;
  changedByEmail: string;
  changedAtUtc: string;
  /**
   * What drove the change: "User" (a person), "AutoRevert" (the review-date scan, Phase 6), "Propagated"
   * (a dial-link write), or "AutomationSkipped" — an upload whose auto-set was held back by a status gate,
   * where `fromStatusKey` and `toStatusKey` are both the status the node KEPT and `note` names the field
   * that is still empty (KEY-278), "ScheduledReset" (a recurring reset, KEY-276), "DateLadder" (the due-date
   * ladder moving a dated node to due soon / today / overdue, KEY-LADDER), or "Fed" — a write that
   * came in over a feed from ANOTHER organisation (KEY-257 Mode B), in which case `fedFrom` says whose.
   */
  source?: string;
  /** Set only on a "Fed" event. See {@link FedWriteOrigin}. */
  fedFrom?: FedWriteOrigin | null;
}

/**
 * Where a change came from when another organisation's feed made it (KEY-360).
 *
 * Carried on the event rather than looked up, because the value outlives the feed that delivered it —
 * Mode B copies values into your own rows, so revoking leaves them standing, frozen, and by then the other
 * organisation may not be resolvable from here at all. It is a snapshot: if they rename themselves
 * tomorrow, this still says what was true when the value landed.
 *
 * Organisation-only by decision — "Bob's Builders set this", never a named individual. `nodePath` is the
 * node on THEIR dial that drove it, in their own keys.
 */
export interface FedWriteOrigin {
  organisationName: string;
  dialName: string;
  nodePath: string;
}

/** One entry in an item's status-change history (newest first). */
export interface ItemStatusEvent {
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  dimensionKey: string;
  fromStatusKey: string | null;
  toStatusKey: string;
  /** The review date set by this change, or — on an "AutoRevert" — the expiry that triggered it. */
  reviewDate?: string | null;
  note: string | null;
  changedByEmail: string;
  changedAtUtc: string;
  /** As {@link SegmentStatusEvent.source}. */
  source?: string;
  /** Set only on a "Fed" event. See {@link FedWriteOrigin}. */
  fedFrom?: FedWriteOrigin | null;
}

/** Which ledger an activity row came from. Phase 2 (KEY-489) adds uploads, items, membership, guests and
 *  structure edits; KEY-851 adds diary appointments added, changed or deleted from a dial (the title only
 *  where the viewer may read it, else "an appointment"); conversation messages are deliberately excluded and stay that way (KEY-285). */
export type InstanceActivityKind =
  | "SectionStatus" | "SegmentStatus" | "ItemStatus" | "Attribute"
  | "FileUploaded" | "FileDeleted" | "FileVersioned"
  | "ItemAdded" | "ItemHidden" | "ItemRestored"
  | "MemberAdded" | "MemberRemoved" | "MemberRoleChanged"
  | "GuestInvited" | "GuestExpired" | "GuestRevoked"
  | "StructureEdited"
  | "AppointmentAdded" | "AppointmentChanged" | "AppointmentDeleted";

/**
 * One line of a dial's activity log (KEY-464 phase 1) — the four ledgers this system already writes, merged
 * into one newest-first trail so the same feed can be shown at the dial, at a section or at an action point.
 *
 * `description` arrives rendered: resolving it needs the pinned version's palettes, dimension labels and
 * attribute labels, and a row may name a status or a field the CURRENT version no longer has, which the
 * client's own view model cannot resolve at all.
 */
export interface InstanceActivityEvent {
  kind: InstanceActivityKind;
  sectionKey: string;
  segmentKey: string | null;
  itemKey: string | null;
  /** Set on an "Attribute" row: which field changed. */
  attributeKey: string | null;
  /** Set on an "ItemStatus" row: which scoring axis moved. */
  dimensionKey: string | null;
  /** Where it happened, ready to read — "Fire safety / Weekly checks". Null for a dial-wide field. */
  nodePath: string | null;
  /** The change as a verb phrase, to follow the actor's name: "set the status to Complete". */
  description: string;
  /** All-zero guid for a system write, exactly as the ledgers store it. */
  actorUserId: string;
  /** Empty for a system write, or when the person's account has gone since. */
  actorEmail: string;
  occurredAtUtc: string;
  /** As {@link SegmentStatusEvent.source}, plus "Import" / "ItemImport" on a field row. */
  source: string;
  note: string | null;
  /** Set only on a "Fed" row. See {@link FedWriteOrigin}. */
  fedFrom?: FedWriteOrigin | null;
  /** A field row's before and after — only when the feed was asked for values (Look back's Changes tab);
   * the ordinary feed never quotes a field. Null `fromValue` = it was empty; null `toValue` = cleared. */
  fromValue?: string | null;
  toValue?: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /**
     * The server's named problems, when it answered with a list of them rather than one sentence — the
     * feed-mapping endpoints do this, because a refused wire has to say which end is wrong. Absent
     * everywhere else; fall back to `message`.
     */
    public readonly problems?: string[],
    /**
     * The dial-level values a dial must be given before it opens (KEY-434). Present only on the 428 from
     * `getInstance`; the capture screen renders these and then retries the read.
     */
    public readonly requirements?: FirstRunRequirement[],
    /**
     * The refusal's parsed JSON body, when it had one (KEY-560). Some refusals carry more than a sentence: going live says
     * which problems stand in the way, or where the dial already runs.
     */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * True when a request failed because the API couldn't be reached or couldn't answer (KEY-732): the network dropped
 * (fetch throws a TypeError), a 5xx while a deploy swaps the API, or a 408/429. A sign-in check that fails this way
 * says nothing about the tokens, so a client keeps them and tries again rather than sending someone to the login
 * screen. Any other failure is the server's answer, and a refused session still ends.
 */
export function isServiceUnavailable(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof ApiError && (error.status >= 500 || error.status === 408 || error.status === 429);
}

/**
 * The stranded statuses a refused queue request didn't answer, or null when `error` is anything else. The server rebuilds the
 * report as the dials are now, so a queue can be refused over a key that appeared after the preview was taken.
 */
export function statusRemapRefusal(error: unknown): StatusRemapRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as Partial<StatusRemapRefusal> | undefined;
  return Array.isArray(body?.missing) ? { message: body.message ?? error.message, missing: body.missing } : null;
}

/** The structure-hold refusal an accept / apply 409 carries, or null when `error` is anything else. */
export function structureRemapRequiredRefusal(error: unknown): StructureRemapRequiredRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as Partial<StructureRemapRequiredRefusal> | undefined;
  return Array.isArray(body?.unanswered)
    ? { message: body.message ?? error.message, unanswered: body.unanswered, dials: body.dials ?? null }
    : null;
}

/**
 * A refused structure-map save (KEY-805): the 400 (entries that can't be used) or 409 (the data can't follow) body, or null when
 * `error` is anything else. Each problem's `detail` is for engineers; surfaces word the reason themselves.
 */
export function structureRemapRefusal(error: unknown): StructureRemapRefusal | null {
  if (!(error instanceof ApiError) || (error.status !== 400 && error.status !== 409)) return null;
  const body = error.body as Partial<StructureRemapRefusal> | undefined;
  return Array.isArray(body?.problems) ? { message: body.message ?? error.message, problems: body.problems } : null;
}

/**
 * The 409 a group rollout's queue answers when a child dial would lose lists or items holding data (KEY-806), or null when
 * `error` is anything else. Told apart from `structureRemapRequiredRefusal` by having no `unanswered`.
 */
export function groupStructureRemapRefusal(error: unknown): GroupStructureRemapRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as Partial<GroupStructureRemapRefusal & { unanswered: unknown }> | undefined;
  return Array.isArray(body?.dials) && !Array.isArray(body?.unanswered)
    ? { message: body.message ?? error.message, dials: body.dials }
    : null;
}

/**
 * Builds an ApiError carrying the server's own explanation where there is one. ASP.NET problem
 * responses are JSON ({ detail, title }); minimal-API `Results.BadRequest("…")` sends the message as a
 * JSON-encoded string (quoted); validation failures are plain text. A 500 returns the dev exception HTML
 * page, which is useless to a user, so we fall back to a generic message there.
 * Reads the body once (it can only be consumed once).
 */
async function apiFailure(res: Response, fallback: string): Promise<ApiError> {
  let detail = "";
  let parsed: unknown;
  try {
    const text = await res.text();
    const trimmed = text.trim();
    if (trimmed.startsWith("{")) {
      const body = JSON.parse(text) as {
        detail?: string;
        title?: string;
        message?: string;
        requirements?: FirstRunRequirement[];
      };
      parsed = body;
      // KEY-560: some refusals are { message } rather than a problem document (the AI quota's reset date, going live).
      detail = body.detail || body.title || body.message || "";
      // KEY-434: the 428 carries what is outstanding in the problem document itself, so the capture
      // screen needs no second request to know what to ask for.
      if (Array.isArray(body.requirements) && body.requirements.length > 0) {
        return new ApiError(res.status, detail || fallback, undefined, body.requirements, body);
      }
    } else if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      // Unwrap the quoted string so the user reads the message, not its JSON encoding.
      detail = JSON.parse(trimmed) as string;
    } else if (text && !trimmed.startsWith("<")) {
      detail = text;
    }
  } catch {
    // unreadable/HTML body — fall through to the generic message
  }
  return new ApiError(res.status, detail || `${fallback} (HTTP ${res.status})`, undefined, undefined, parsed);
}

// --- Segment documents (KEY-36) ---

export interface RequestDocumentUploadRequest {
  sectionKey: string;
  segmentKey: string;
  /** Attaches the file to a single item (itemised dials); omit/null to attach it to the action point. */
  itemKey?: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** What phase of the work this file illustrates. Omit for Uncategorized. */
  category?: DocumentCategory | null;
  /** Makes the file the value of a Document/Image/Video node attribute rather than ordinary node evidence. */
  attributeKey?: string | null;
}

/**
 * The tag a file is filed under: "Found" (before) / "Resolution" (after) evidence, "Uncategorized" for
 * anything else — or, since KEY-499, one of the dial's OWN tag labels.
 *
 * The `(string & {})` arm widens this to any string while keeping the three built-ins as autocomplete
 * suggestions. It stopped being a closed union the moment a dial could name its own buckets; the server
 * still clamps an unrecognised value to "Uncategorized", so a typo never becomes a stored tag.
 */
export type DocumentCategory = "Found" | "Resolution" | "Uncategorized" | (string & {});

/** One upload tag a dial has added for itself (KEY-499), offered beside the three built-ins. */
export interface DocumentTag {
  id: string;
  /** Display text, and the exact string stored on a document's `category`. */
  label: string;
  createdAtUtc: string;
}

export interface DocumentUploadTicket {
  documentId: string;
  uploadUrl: string;
}

export interface DialDocument {
  id: string;
  sectionKey: string;
  segmentKey: string;
  /** Set when the file is attached to a specific item; null for an action-point (segment) file. */
  itemKey?: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** AV lifecycle: Pending (awaiting scan), Clean (downloadable), Quarantined (malware, blocked). */
  status: "Pending" | "Clean" | "Quarantined";
  uploadedByEmail: string;
  uploadedAtUtc: string;
  /** This file's place in its version history (1-based). */
  versionNumber: number;
  /** How many versions exist for this file (1 = no earlier versions). >1 offers the history dropdown. */
  versionCount: number;
  /** What phase of the work this file illustrates; Uncategorized for legacy uploads and untagged files. */
  category: DocumentCategory;
  /**
   * Whether THIS viewer may fetch these bytes (KEY-537): the dial's `documentDownload` role mask weighed at
   * this file's own scope, and false on anything not `Clean`.
   *
   * A row the viewer may see but not download is still listed — its existence is already disclosed, and a
   * download button that 403s is worse than none — so hide the affordance and leave the row. Optional only
   * for wire compatibility with a server that predates it; absent means "the server didn't say", which the
   * UI reads as the pre-KEY-537 behaviour (offer the download and let the server answer).
   */
  canDownload?: boolean;
  /** Set when the file is the value of a node attribute rather than ordinary evidence on the node — show it
   * under that field, not in the node's file list. */
  attributeKey?: string | null;
}

export interface DocumentDownloadTicket {
  downloadUrl: string;
}

/**
 * Omitted/null `documentIds` means "every visible, Clean document" on the instance.
 *
 * `sectionKey`/`segmentKey` narrow it the same way `browseDocuments` does, so "zip everything in this
 * section" works under pagination without the client enumerating rows it never loaded: it sends the INTENT
 * and the server re-derives the set under the identical visibility filter. Given both, the two intersect —
 * an id outside the scope is dropped exactly as an invisible one is.
 */
export interface DocumentZipRequest {
  documentIds?: string[] | null;
  sectionKey?: string | null;
  segmentKey?: string | null;
  /** The browse page's tag filter, applied identically here, so "Download as ZIP" beside a filtered table
   * zips what the table is showing rather than the whole scope. Several tags OR. */
  tags?: string[] | null;
  /** The browse page's free-text search, applied identically here. ANDs with `tags`. */
  q?: string | null;
  /**
   * A Group Key Dial's whole-dial zip (KEY-643): the group's own files AND every subdial's the caller can see,
   * each subdial's under a folder named after it — the set `browseDocuments({ includeChildren: true })` lists.
   * Send it on EVERY zip from a group's rolled-up table, ticked ids included: without it the server resolves
   * the group's own files only, and a ticked subdial file would be silently left out. Not combinable with
   * `sectionKey`/`segmentKey` (400). Send `false`, never `null`, to leave it off: the server's flag is a plain bool.
   */
  includeChildren?: boolean;
  /** With `includeChildren`, narrows the zip to that one dial of the group (400 without it). */
  dialId?: string | null;
}

/** What the dial file browser sorts on. `location` is the resolved display path (section → segment → item),
 * not the raw keys, so the server's order is the one the reader sees. */
export type DocumentBrowseSort = "name" | "location" | "size" | "uploaded" | "category" | "status";

/**
 * A {@link DialDocument} plus the display labels for where it lives, resolved server-side so the browser can
 * sort by location across pages without holding the dial's whole structure.
 *
 * Any label is null when the resolved structure no longer names that node — an attribute file orphaned by a
 * definition upgrade, say. Fall back to the corresponding key rather than rendering a blank.
 */
export interface DialDocumentRow extends DialDocument {
  sectionLabel?: string | null;
  segmentName?: string | null;
  itemName?: string | null;
  attributeLabel?: string | null;
  /** Which level the file hangs off, for grouping/iconography. An attribute's file is always "Attribute". */
  locationKind: "Section" | "Segment" | "Item" | "Attribute";
  /**
   * The dial this file belongs to (KEY-643). On a group's rolled-up Files tab a row may be a subdial's, and its
   * download, versions and every other per-file call go to THIS id — under the group's id they 404. Optional only
   * for wire compatibility with a server that predates it; absent means the dial that was browsed.
   */
  instanceId?: string;
  /** That dial's name, so a rolled-up list can say where each file came from. */
  dialName?: string;
}

/** One dial a group's Files tab can narrow to (KEY-643). `depth` is 0 for the group itself, 1 for its children,
 * and so on down to the nesting limit. */
export interface DocumentBrowseDial {
  instanceId: string;
  name: string;
  depth: number;
}

/** One page of the file browser. `totalCount` is the caller's whole PERMISSION-FILTERED set — never the raw
 * one — so the last page it implies is one they can actually reach. `page`/`pageSize` echo what the server
 * applied after clamping (pageSize is capped at 200). */
export interface DocumentBrowsePage {
  items: DialDocumentRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  /**
   * The tags actually present in this caller's scope-filtered set — the options a tag filter should offer.
   * Computed server-side BEFORE the tag/search filters, so selecting one tag doesn't collapse the list.
   *
   * ⚠️ Render the filter from THIS and never from a hard-coded list or `listDocumentTags`. A document's tag
   * is a denormalised label with no foreign key: a dial can withdraw a tag from its picker while every file
   * already filed under it keeps the label for ever. Options built from the tag table would make each of
   * those files unreachable — silently, and for evidence. A withdrawn label appears here under its own name.
   *
   * Order: the built-ins, then the dial's own tags in creation order, then withdrawn leftovers, then
   * "Uncategorized" last — matching how the action point's file list groups them.
   *
   * Absent on a payload from a server that predates it; treat that as "no filter available".
   */
  availableTags?: string[];
  /**
   * Only on a group's rolled-up browse (`includeChildren`, KEY-643): the dials this caller can see files on — the
   * group and its subdials — for a picker narrowing to one. Taken BEFORE the dial, tag and search filters, like
   * `availableTags`, so picking one dial doesn't collapse the picker. Nearest level first, then by name.
   */
  dials?: DocumentBrowseDial[] | null;
}

/** The resolved size of a zip request, returned on the 400 the server sends when it's over the cap — lets
 * the UI report the actual limit rather than a generic failure. */
export interface DocumentZipPlan {
  fileCount: number;
  totalBytes: number;
  withinLimits: boolean;
  maxFiles: number;
  maxTotalBytes: number;
}

// ---- User feedback → Linear ----

export type FeedbackType = "Bug" | "FeatureRequest";
export type FeedbackStatus = "Open" | "Investigating" | "Developing" | "UnderReview" | "Resolved" | "Declined";
export type FeedbackPriority = "Low" | "Normal" | "Urgent";
/** Which app a piece of feedback was raised from — so user-app, admin-app and phone-app feedback are distinguishable. */
export type FeedbackSource = "WebUser" | "WebAdmin" | "Mobile";

export interface SubmitBugReportRequest {
  whatWereYouDoing: string;
  whatDidYouExpect: string;
  whatActuallyHappened: string;
  priority?: FeedbackPriority;
}

export interface SubmitFeatureRequestRequest {
  description: string;
  priority?: FeedbackPriority;
}

export interface UpdateFeedbackRequest {
  status: FeedbackStatus;
  newNote?: string;
}

/**
 * Attachment envelope for feedback submissions (KEY-261). These mirror
 * `FeedbackAttachmentLimits` on the API — the server's copy is the one that counts; this exists so a
 * paste that can't be accepted is refused in the modal instead of after a round trip. Keep the two in
 * step: a type allowed here but not there fails only once the user has hit Send.
 */
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const FEEDBACK_ATTACHMENT_MAX_COUNT = 5;
export const FEEDBACK_ATTACHMENT_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

/** What `acceptFeedbackAttachments` decided: the files to keep, and why anything was turned away. */
export interface FeedbackAttachmentOutcome {
  accepted: File[];
  /** One message per rejected file (or per breached limit), ready to show as-is. */
  errors: string[];
}

/**
 * Decides which newly-offered files can join the ones already staged. Pure, so both modals share one
 * set of rules and both can test them: it never mutates its inputs and returns the full replacement
 * list. Rejections are reported per file — one unsupported paste shouldn't discard the rest.
 */
export function acceptFeedbackAttachments(existing: readonly File[], incoming: readonly File[]): FeedbackAttachmentOutcome {
  const accepted = [...existing];
  const errors: string[] = [];

  for (const file of incoming) {
    if (accepted.length >= FEEDBACK_ATTACHMENT_MAX_COUNT) {
      errors.push(`You can attach at most ${FEEDBACK_ATTACHMENT_MAX_COUNT} files.`);
      break;
    }
    if (!FEEDBACK_ATTACHMENT_TYPES.includes(file.type)) {
      errors.push(`“${file.name}” isn’t a supported attachment type.`);
      continue;
    }
    if (file.size > FEEDBACK_ATTACHMENT_MAX_BYTES) {
      errors.push(`“${file.name}” is larger than ${Math.round(FEEDBACK_ATTACHMENT_MAX_BYTES / (1024 * 1024))} MB.`);
      continue;
    }
    // Same name and size twice over is a double-paste, not two files worth keeping.
    if (accepted.some((f) => f.name === file.name && f.size === file.size)) {
      continue;
    }
    accepted.push(file);
  }

  return { accepted, errors };
}

/**
 * Pulls the image files out of a paste. Clipboard images arrive as `File`s with a generic name
 * ("image.png") or none at all, so each is renamed to something a triaging admin can tell apart in the
 * Linear issue's attachment list.
 *
 * Reads `DataTransfer.files` first and only falls back to `.items` (KEY-500): Safari and Firefox fill
 * `files` more reliably than `items` for an OS screenshot. The fallback is deliberately not additive —
 * both lists describe the same clipboard, so scanning them in turn would stage one screenshot twice.
 */
export function feedbackAttachmentsFromPaste(data: DataTransfer | null | undefined, stamp: string): File[] {
  const named = (file: File, index: number): File => {
    const extension = file.type.slice("image/".length).split("+")[0] || "png";
    return new File([file], `pasted-${stamp}-${index + 1}.${extension}`, { type: file.type });
  };

  const fromFiles = Array.from(data?.files ?? []).filter((file) => file.type.startsWith("image/"));
  if (fromFiles.length > 0) return fromFiles.map(named);

  const fromItems: File[] = [];
  for (const item of Array.from(data?.items ?? [])) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (file) fromItems.push(file);
  }
  return fromItems.map(named);
}

/** A field that owns its own paste: a screenshot pasted into one of these is never a feedback attachment. */
function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as (Partial<HTMLElement> & { tagName?: unknown }) | null;
  if (!element || typeof element.tagName !== "string") return false;
  if (element.isContentEditable) return true;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName.toUpperCase());
}

/**
 * Whether a `paste` seen at the document belongs to the feedback panel (KEY-500).
 *
 * A `paste` event dispatches at the focused node, so the old handler on the form's own element only ever
 * fired when focus was already inside it — and since KEY-445 the form lives in a floating dock whose whole
 * point is that you carry on clicking around the app while it is open. The listener therefore sits on
 * `document`, and this is the test that stops it stealing a paste meant for somewhere else: anything
 * inside the panel is ours, and outside it we claim only targets that are not themselves editable (the
 * body, a button, the dock's own chrome) — never a field belonging to the rest of the app.
 */
export function feedbackPasteBelongsToPanel(target: EventTarget | null, panel: Element | null): boolean {
  if (!panel) return false; // No surface mounted: the panel is closed, or showing its thank-you state.
  const node = target as Node | null;
  if (node && panel.contains(node)) return true;
  return !isEditableTarget(target);
}

export interface UserFeedbackSummary {
  userFeedbackId: string; // Linear issue UUID
  feedbackType: FeedbackType;
  status: FeedbackStatus;
  source: FeedbackSource;
  priority?: FeedbackPriority | null;
  submittedAt: string;
  submitterName: string;
  preview: string;
  adminNotes?: string | null;
  isArchived: boolean;
  isExpired: boolean;
  identifier: string; // e.g. KEY-123
  url: string; // Linear deep link
}

export interface UserFeedbackDetail {
  userFeedbackId: string;
  feedbackType: FeedbackType;
  status: FeedbackStatus;
  source: FeedbackSource;
  priority?: FeedbackPriority | null;
  submittedAt: string;
  submitterName: string;
  submitterEmail?: string | null;
  adminNotes?: string | null;
  isArchived: boolean;
  isExpired: boolean;
  whatWereYouDoing?: string | null;
  whatDidYouExpect?: string | null;
  whatActuallyHappened?: string | null;
  description?: string | null;
  identifier: string;
  url: string;
}

export interface FeedbackListResponse {
  items: UserFeedbackSummary[];
  totalCount: number;
}

export interface FeedbackQuery {
  type?: FeedbackType;
  status?: FeedbackStatus;
  source?: FeedbackSource;
  includeArchived?: boolean;
  includeExpired?: boolean;
  page?: number;
  pageSize?: number;
}

// ---- Feedback clarification threads (KEY-253) ----
// An admin asks the submitter a follow-up question; the submitter is notified and answers. Message bodies
// live in Linear as tagged issue comments — the API stores only the conversation metadata.

/** Which side of a clarification thread a message came from, or which side the caller is on. */
export type ClarificationParty = "Admin" | "Submitter";

export interface FeedbackClarificationMessage {
  from: ClarificationParty;
  body: string;
  createdAtUtc: string;
}

/** A clarification thread the caller is a party to, from their own side. */
export interface FeedbackClarificationDto {
  userFeedbackId: string;
  identifier: string;
  url: string;
  preview: string;
  /** Which side the caller is on — "Submitter" for their own feedback, "Admin" for threads they opened. */
  myRole: ClarificationParty;
  requestedAtUtc: string;
  lastMessageAtUtc: string;
  lastMessageBy: ClarificationParty;
  isUnread: boolean;
  messages: FeedbackClarificationMessage[];
}

/** `unreadCount` is derived server-side from local metadata, so it stays correct even when Linear is
 * unreachable — in that case `messages` come back empty rather than failing the whole call. */
export interface FeedbackClarificationListResponse {
  items: FeedbackClarificationDto[];
  unreadCount: number;
}

/** The clarification state of one feedback item, for the admin drawer. */
export interface FeedbackClarificationDetail {
  userFeedbackId: string;
  /** False when the issue has no submitter recorded (raised by hand in Linear) — there is nobody to ask. */
  canClarify: boolean;
  hasThread: boolean;
  requestedAtUtc?: string | null;
  lastMessageAtUtc?: string | null;
  lastMessageBy?: ClarificationParty | null;
  /** True when the admin had the last word — i.e. we're waiting on the submitter. */
  awaitingSubmitter: boolean;
  messages: FeedbackClarificationMessage[];
}

export interface RequestClarificationRequest {
  message: string;
}

export interface ReplyToClarificationRequest {
  message: string;
}

export interface ApiClient {
  health(): Promise<HealthResponse>;
  /** Passwordless step 1 — best-effort; resolves regardless of outcome (reveals nothing). */
  requestCode(request: RequestCodeRequest): Promise<void>;
  /** Passwordless step 2 — exchanges the code for tokens; throws ApiError on failure. */
  verifyCode(request: VerifyCodeRequest): Promise<TokenResponse>;
  refresh(refreshToken: string): Promise<TokenResponse>;
  logout(refreshToken: string): Promise<void>;
  me(accessToken: string): Promise<MeResponse>;
  /** The caller's own account details, for the personal Settings page. */
  getProfile(accessToken: string): Promise<ProfileView>;
  /** Save the caller's own name and phone number. Blank values clear them. */
  updateProfile(accessToken: string, request: UpdateProfileRequest): Promise<void>;
  /** KEY-743: rename the organisation this session is scoped to (needs org.edit there). Its name and nothing else.
   * Throws ApiError with the server's reason on a 400 (blank, or longer than 200 characters). */
  renameMyOrganisation(accessToken: string, name: string): Promise<void>;
  /** Which flag-gated AI generation features this server has enabled (drives UI visibility). */
  getAiFeatures(accessToken: string): Promise<AiFeaturesStatus>;
  /**
   * Extract text from an uploaded seed document (PDF/DOCX/XLSX/text) to pass as GenerateDefinitionRequest.sourceDocument
   * (KEY-183). Throws an ApiError (400) for an unsupported/empty/unreadable file, or 404 where the flag is off. `orgId` follows
   * the usual rule: undefined means the active org, null means no org header (a customer author's builder, KEY-560).
   */
  extractSourceDocument(accessToken: string, file: File, orgId?: string | null): Promise<ExtractedSourceDocument>;
  /** Download the blank definition-import template (.xlsx) as a Blob, shaped by the author's answers.
   * 404s where the flag is off. */
  downloadDefinitionTemplate(accessToken: string, options?: DefinitionTemplateOptions): Promise<Blob>;
  /** Download an EXISTING dial as a filled-in template (structure + hidden node keys) for re-import. */
  downloadDialTemplate(accessToken: string, id: string): Promise<Blob>;
  /**
   * Parse a filled-in template spreadsheet into a draft definition + validation diagnostics (deterministic,
   * no AI). Returns the parsed result for a preview; create the draft via createDefinition on confirm.
   */
  importDefinitionSpreadsheet(accessToken: string, file: File): Promise<SpreadsheetImportResult>;
  /** Preview applying a re-completed spreadsheet to an existing dial: what would be added / changed / removed. */
  previewReimport(accessToken: string, id: string, file: File): Promise<ReimportPreviewResult>;
  /** Apply a re-completed spreadsheet to the dial's draft (additive; removeAbsent applies deletions too). */
  applyReimport(accessToken: string, id: string, file: File, removeAbsent: boolean): Promise<void>;
  /**
   * AI-generate a draft definition asynchronously: starts a background job and polls until it finishes, so a
   * slow generation isn't bound by the inbound request timeout. Resolves to the definition, or throws an
   * ApiError carrying the same 502/503/404 the synchronous endpoint used.
   */
  generateDefinitionAsync(accessToken: string, request: GenerateDefinitionRequest, options?: GenerateDefinitionAsyncOptions): Promise<GeneratedDefinition>;
  /**
   * Opens a planning conversation and waits for the model's opening reply (KEY-234). Only honoured where
   * the server reports aiFeatures.groupConversation; elsewhere the endpoint 404s.
   */
  startPlanConversation(accessToken: string, request: { message: string; industry?: string; model?: string; kind?: "Group" | "Single"; sourceDocument?: string; sourceDocumentName?: string }): Promise<PlanConversation>;
  /** Sends the admin's next turn and waits for the reply. Throws 409 when a turn is in flight or the cap is reached. */
  sendPlanConversationMessage(accessToken: string, conversationId: string, message: string): Promise<PlanConversation>;
  /** Reads a conversation without sending anything (used to resume one). */
  getPlanConversation(accessToken: string, conversationId: string): Promise<PlanConversation>;
  /** AI-plan a whole portfolio (KEY-160): a tree of proposed dials to review before building. 404s where the flag is off. */
  generateBlueprint(accessToken: string, request: GenerateBlueprintRequest): Promise<GeneratedBlueprint>;
  createDefinition(accessToken: string, definition: GeneratedDefinition): Promise<{ id: string }>;
  listDefinitions(accessToken: string): Promise<DefinitionSummary[]>;
  getDefinition(accessToken: string, id: string, version?: number): Promise<DefinitionDetail>;
  /** Whether this caller can build here (KEY-560). Always answers, including when the feature is off. */
  getAuthoringStatus(accessToken: string): Promise<AuthoringStatus>;
  /** Where the caller's organisation stands on its plan's meters. 404 while user authoring is off. */
  getAuthoringAllowance(accessToken: string): Promise<OrganisationAllowance>;
  /** The platform dials offered as starting points. 404 while user authoring is off. */
  listAuthoringTemplates(accessToken: string): Promise<DefinitionTemplateSummary[]>;
  /** Copies a template into a new draft of the caller's own; resolves to the draft's id. */
  copyAuthoringTemplate(accessToken: string, templateId: string): Promise<{ id: string }>;
  /** The caller's own dials, for the "Being built" shelf. 404 for anyone who isn't a customer author. */
  listMyDesigns(accessToken: string): Promise<AuthoredDesign[]>;
  /** What stands between a dial and publishing or checking, as problems that point at its parts. 404 for somebody else's dial. */
  getDefinitionReadiness(accessToken: string, id: string, purpose: ReadinessPurpose): Promise<DefinitionReadiness>;
  /**
   * Publishes the author's own dial if it needs to be and creates its running dial. The refusals the builder shows (not ready,
   * already live) resolve; anything else throws an ApiError, including 402 when the trial has ended.
   */
  goLive(accessToken: string, id: string): Promise<GoLiveOutcome>;
  listDefinitionVersions(accessToken: string, id: string): Promise<DefinitionVersionSummary[]>;
  /** Preview the drift + at-risk of moving these live instances to a target version (platform admin).
   * 404 when `Features:VersionUpgrade` is off on the server. */
  previewUpgrade(accessToken: string, definitionId: string, body: { targetVersionId: string; instanceIds: string[] }): Promise<UpgradeDriftReport[]>;
  /** Queue a version rollout for a set of live instances (platform admin). */
  queueUpgrade(accessToken: string, definitionId: string, body: QueueUpgradeRequest): Promise<QueueUpgradeResult>;
  /** Apply one queued upgrade now instead of waiting for its effective date (platform admin). */
  applyUpgrade(accessToken: string, definitionId: string, pendingId: string): Promise<void>;
  /** Withdraw a queued upgrade before it applies (platform admin). */
  cancelUpgrade(accessToken: string, definitionId: string, pendingId: string): Promise<void>;
  /** The open (Pending/Accepted) upgrades queued against a definition's live instances (platform admin). */
  listPendingUpgrades(accessToken: string, definitionId: string): Promise<PendingUpgradeAdminRow[]>;
  updateDefinition(accessToken: string, id: string, definition: GeneratedDefinition, options?: HomeOrgOption): Promise<void>;
  /**
   * Correct a definition's catalogue metadata in place. Unlike `updateDefinition` this works on a published
   * dial — these fields live on the definition, not on a version, so fixing a typo needs no new draft. 409s
   * while the dial is paused awaiting a re-completed spreadsheet.
   */
  updateDefinitionMetadata(accessToken: string, id: string, metadata: DefinitionMetadata): Promise<void>;
  /** Upload a central hub image for a definition; returns a storage reference (to save on the definition) + a display URL. */
  uploadCentralImage(accessToken: string, id: string, file: File): Promise<{ reference: string; url: string | null }>;
  /**
   * Mints a direct-to-Blob write ticket for a guidance video upload — unlike `uploadCentralImage`, the bytes
   * never pass through the API. PUT the file to `uploadUrl` with {@link ApiClient.uploadDocumentBytes} (it's
   * content-agnostic), then submit `reference` as the video's `ref` (`kind: "Upload"`) on the next definition
   * save. There is no confirm step.
   */
  requestGuidanceVideoUpload(accessToken: string, id: string, contentType: string, sizeBytes: number): Promise<GuidanceVideoUploadTicket>;
  /** The audio twin of {@link requestGuidanceVideoUpload} (KEY-565): the same ticket, submitted as a video row's
   *  `ref` with `mediaKind: "Audio"`. Accepts MP3, M4A, WAV and Ogg. */
  requestGuidanceAudioUpload(accessToken: string, id: string, contentType: string, sizeBytes: number): Promise<GuidanceVideoUploadTicket>;
  /** Mints a direct-to-Blob write ticket for a guidance document (KEY-364). The file name travels so the
   *  server can keep the real extension on the blob, which is what makes a download open rather than save. */
  requestGuidanceDocumentUpload(accessToken: string, id: string, contentType: string, sizeBytes: number, fileName: string): Promise<GuidanceDocumentUploadTicket>;
  createDefinitionVersion(accessToken: string, id: string, options?: HomeOrgOption): Promise<{ id: string }>;
  publishDefinition(accessToken: string, id: string): Promise<void>;
  /**
   * Sends the latest draft for checking (KEY-283): the version is frozen and a test dial is created to try it on. `audience`
   * (KEY-545) says whose My Dials the test dial lands in, "Organisation" or "SenderOnly"; a platform send that leaves it out
   * goes to the organisation. A customer author's own dial (KEY-560) leaves it out: its test copy lands in their own
   * organisation for them alone, and "Organisation" is refused with a 400.
   */
  sendDefinitionForChecking(accessToken: string, id: string, audience?: CheckingAudience): Promise<void>;
  /** Takes the version back out of checking so it can be edited again — discards the test dial and its contents. */
  revertDefinitionToDraft(accessToken: string, id: string): Promise<void>;
  /** Throws away the current test dial and allocates a fresh one against the same checking version. Pass
   *  `audience` (KEY-545) to change who it goes to at the same time; omit it to keep the recorded one. */
  recreateCheckingDial(accessToken: string, id: string, audience?: CheckingAudience): Promise<void>;
  /** Toggle the master switch for per-instance customisation of this definition. */
  setDefinitionInstanceCustomisation(accessToken: string, id: string, value: boolean): Promise<void>;
  setDefinitionTemplate(accessToken: string, id: string, value: boolean): Promise<void>;
  /** Toggle the master switch that lets dial admins override an instance's central logo. */
  setDefinitionLogoOverride(accessToken: string, id: string, value: boolean): Promise<void>;
  /** KEY-744: whether dials with no logo-override setting of their own have it on. */
  setDefinitionLogoOverrideDefault(accessToken: string, id: string, value: boolean): Promise<void>;
  /** Sets (days) or clears (null) how long a lapsed customer's data is kept for this definition's dials (KEY-667). */
  setDefinitionRetention(accessToken: string, id: string, days: number | null): Promise<void>;
  /** Toggle whether this definition's dials offer a status on each section (KEY-479). Platform-only. */
  setDefinitionSectionStatus(accessToken: string, id: string, value: boolean): Promise<void>;
  /** Toggle whether this definition's dials offer Milo, the assistant (KEY-714). Platform-only. */
  setDefinitionMilo(accessToken: string, id: string, value: boolean): Promise<void>;
  /** KEY-735: switch Look back on or off for every dial of a definition (platform-only). */
  setDefinitionLookBack(accessToken: string, id: string, value: boolean): Promise<void>;
  /** KEY-939: names (or clears, with null) the instance demos of this definition are copied from. Rejects with
   *  ApiError(400) for an instance that cannot be a template (another definition's, a demo, a test dial, a group child). */
  setDefinitionDemoSource(accessToken: string, id: string, instanceId: string | null): Promise<void>;
  /** KEY-939: replaces a demo dial with a fresh copy of its template, carrying its members. The old id is gone;
   *  navigate to the returned one. Dial admins and organisation instance-managers. */
  resetDemoInstance(accessToken: string, instanceId: string): Promise<{ id: string }>;
  /** Name what this definition's dials call an action point (KEY-539): the platform, or a customer author on their own dial
   * (KEY-560). Pass both forms, or both null/blank to clear the override back to "action point" — exactly one is rejected with a 400. */
  setDefinitionSegmentNoun(
    accessToken: string,
    id: string,
    singular: string | null,
    plural: string | null,
    options?: HomeOrgOption,
  ): Promise<void>;
  /** Rename this definition's dials' fixed tab headings (KEY-670). Replaces the WHOLE map: a key left out goes back
   * to the platform's wording, a blank value is a removal, and null or `{}` clears them all. An unknown key or a label
   * over 40 characters is a 400 naming the problem, and nothing is saved. Same audience as `setDefinitionSegmentNoun`. */
  setDefinitionLabelOverrides(
    accessToken: string,
    id: string,
    overrides: Record<string, string | null> | null,
    options?: HomeOrgOption,
  ): Promise<void>;
  /** Toggle the kiosk auto-lock master switch (KEY-268) and, optionally, the definition-wide idle timeout. */
  setDefinitionAutoLock(accessToken: string, id: string, value: boolean, timeoutSeconds?: number): Promise<void>;
  /** Replace the definition-level role-action matrix (KEY-298). Omitted arrays leave that action alone. */
  setDefinitionRoleActions(
    accessToken: string,
    id: string,
    body: {
      attributeEdit?: string[];
      documentUpload?: string[];
      documentVersions?: string[];
      messagePost?: string[];
      messagePostRestricted?: string[];
      /** null/omitted leaves the target mask alone; an EMPTY array clears it (address anyone). */
      messagePostRestrictedTo?: string[];
      /** The two READ masks (KEY-537). Omitted leaves that action alone, as for every field above. */
      documentList?: string[];
      documentDownload?: string[];
    },
  ): Promise<void>;
  /** Pause editing while awaiting a re-completed spreadsheet from the client (arms the re-import lock). */
  armReimport(accessToken: string, id: string): Promise<void>;
  /** Cancel the awaiting-re-import wait so the dial can be edited manually again. */
  cancelReimport(accessToken: string, id: string): Promise<void>;
  /** List this definition's instances (across orgs) with their per-instance logo-override gate. */
  listInstanceLogoOverrides(accessToken: string, definitionId: string): Promise<InstanceLogoOverrideRow[]>;
  /** Bulk enable/disable the per-instance logo-override gate for a set of this definition's instances.
   * `includeSubdials` (KEY-669) cascades a group row to its whole subtree — children, and theirs — in one
   * action; enabling also switches on any descendant definition's own master switch where it was off.
   * `enabled: null` (KEY-744) resets each dial to follow the definition's default. */
  setInstancesLogoOverride(
    accessToken: string, definitionId: string, instanceIds: string[], enabled: boolean | null, includeSubdials?: boolean,
  ): Promise<SetInstancesLogoOverrideResult>;
  /** Export a portable copy of a definition (no instance data) for moving to another server. */
  exportDefinition(accessToken: string, id: string): Promise<ExportedDefinition>;
  /** Import a previously exported definition as a new draft in the caller's org. Accepts format 1 and 2. */
  importDefinition(accessToken: string, payload: ExportedDefinition): Promise<DefinitionImportResult>;
  /**
   * Copy a definition into a brand-new dial. The copy takes the source's latest version — draft or
   * published — and lands as version 1, Draft, so nothing is live until it is published deliberately.
   *
   * `includeChildren` applies to groups only: it copies every child dial, however deeply nested, and
   * wires the new group onto the copies. Without it the copy shares the SAME children as the original.
   * Duplicating a dial that is itself a group's child always yields a STANDALONE dial — warn first.
   */
  duplicateDefinition(
    accessToken: string,
    id: string,
    body?: { name?: string; includeChildren?: boolean },
  ): Promise<DefinitionDuplicateResult>;
  /**
   * Import a previously exported definition as a new *version* of a dial that already exists here —
   * the dev-to-live path, where a plain import would only make a duplicate. It always lands as a draft,
   * so the target's published version and its live instances are untouched until that draft is published.
   * 409 when the target's latest version is an unpublished draft, unless `replaceDraft` is set.
   */
  importDefinitionVersion(
    accessToken: string,
    id: string,
    payload: ExportedDefinition,
    replaceDraft?: boolean,
  ): Promise<DefinitionVersionImportResult>;
  /**
   * Back up the whole catalogue as a zip of one portable JSON file per definition — the snapshot to take
   * before a destructive database reset. Live and archived definitions are separate downloads. Requires
   * the platform-only `dial.create` capability.
   */
  downloadDefinitionsBackup(accessToken: string, scope: DefinitionBackupScope): Promise<Blob>;
  /**
   * Download one group and every dial beneath it as a single zip — the dev-to-live move for a portfolio.
   * Same format as the whole-catalogue backup, so it feeds the same restore preview; what differs is that
   * you don't have to find the children yourself. A dial with no children bundles as just itself.
   */
  downloadDefinitionBundle(accessToken: string, id: string): Promise<Blob>;
  /**
   * Read a backup zip's manifest and report every definition in it, plus any same-name/same-kind
   * conflicts already on this server — the first step of a selective restore.
   */
  previewDefinitionsRestore(accessToken: string, file: File): Promise<DefinitionRestorePreviewResult>;
  /**
   * Restore the chosen entries from a backup zip, in dependency order, threading a source-id map
   * through so a restored group's wiring resolves exactly rather than by name.
   */
  restoreDefinitions(accessToken: string, file: File, selections: DefinitionRestoreSelection[]): Promise<DefinitionRestoreApplyResult>;
  /** Delete a definition and all of its instances (across tenants) and their data. */
  deleteDefinition(accessToken: string, id: string, options?: HomeOrgOption): Promise<void>;

  // Catalogue folders — the nested tree that organises the definition catalogue in the admin app.
  /** Every catalogue folder, flat; the caller assembles the tree from `parentFolderId`. */
  listDefinitionFolders(accessToken: string): Promise<DefinitionFolder[]>;
  /** Create a folder. `parentFolderId` null/omitted creates it at the top level. */
  createDefinitionFolder(accessToken: string, name: string, parentFolderId?: string | null): Promise<{ id: string }>;
  renameDefinitionFolder(accessToken: string, id: string, name: string): Promise<void>;
  /** Re-parent a folder and its whole subtree. Null moves it to the top level. */
  moveDefinitionFolder(accessToken: string, id: string, parentFolderId: string | null): Promise<void>;
  /**
   * Delete a folder. Never deletes a definition: a folder with contents throws a 409 ApiError unless
   * `moveContents` is true, in which case its direct contents are lifted into its parent first.
   */
  deleteDefinitionFolder(accessToken: string, id: string, moveContents?: boolean): Promise<void>;
  /** File definitions into a folder (null = the catalogue root). Backs drag-drop and bulk move. */
  moveDefinitionsToFolder(accessToken: string, definitionIds: string[], folderId: string | null): Promise<void>;

  // Status palettes (reusable library)
  listPalettes(accessToken: string): Promise<PaletteDetail[]>;
  getPalette(accessToken: string, id: string): Promise<PaletteDetail>;
  createPalette(accessToken: string, request: SavePaletteRequest): Promise<{ id: string }>;
  updatePalette(accessToken: string, id: string, request: SavePaletteRequest): Promise<void>;
  deletePalette(accessToken: string, id: string): Promise<void>;

  // Dashboard / admin
  getStats(accessToken: string): Promise<DashboardStats>;
  /** Whether the dev/test database-reset tools are enabled here (drives danger-zone visibility). */
  getDataToolsStatus(accessToken: string): Promise<DataToolsStatus>;
  /** Run a destructive dev/test database reset. */
  resetData(accessToken: string, options: DataResetOptions): Promise<DataResetSummary>;
  getUserMemberships(accessToken: string, userId: string): Promise<UserMembershipView[]>;
  /** The dials a user is allocated to. 404s when the user is outside a scoped admin's subtree. */
  getUserInstances(accessToken: string, userId: string): Promise<UserInstanceView[]>;

  // Roles & capabilities
  listCapabilities(accessToken: string): Promise<CapabilityView[]>;
  listAllRoles(accessToken: string): Promise<RoleDetailView[]>;
  /** Users who hold a role, with the organisations in which they hold it. */
  listRoleMembers(accessToken: string, id: string): Promise<RoleMemberView[]>;
  createRole(accessToken: string, request: CreateRoleRequest): Promise<{ id: string }>;
  setRoleCapabilities(accessToken: string, id: string, request: SetRoleCapabilitiesRequest): Promise<void>;
  deleteRole(accessToken: string, id: string): Promise<void>;

  // Organisations
  /** The org tree. Archived nodes are excluded unless `includeArchived` is set. */
  /** The server's parent-type → creatable-child-types table, scoped to this caller. */
  getOrganisationPlacementRules(accessToken: string): Promise<OrganisationPlacementRules>;
  /**
   * Put one organisation's children in a chosen order. Ids left out keep their relative order and sort
   * after the ones named, so omitting a sibling that appeared since you fetched the list doesn't
   * reshuffle it.
   */
  reorderChildOrganisations(accessToken: string, parentId: string, orderedChildIds: string[]): Promise<void>;
  /**
   * Correct an organisation's type. Platform-only, and refused (400) when it would strand something:
   * a parent that can't contain the new type, children that would no longer fit, dials the new type
   * can't hold, or a licence pool/grant only the old type could carry.
   */
  changeOrganisationType(accessToken: string, id: string, type: string): Promise<void>;
  listOrganisations(accessToken: string, includeArchived?: boolean): Promise<OrganisationSummary[]>;
  getOrganisation(accessToken: string, id: string): Promise<OrganisationDetail>;
  createOrganisation(accessToken: string, request: CreateOrganisationRequest): Promise<{ id: string }>;
  updateOrganisation(accessToken: string, id: string, request: UpdateOrganisationRequest): Promise<void>;
  /** Whether an org is safe to hard-delete, and what is blocking it — drives delete vs. archive. */
  getOrganisationDeletionInfo(accessToken: string, id: string): Promise<OrganisationDeletionInfo>;
  /** Archive an org and its whole subtree (reversible): hides it from normal listings. */
  archiveOrganisation(accessToken: string, id: string): Promise<void>;
  /** Restore a previously archived org and its subtree. */
  unarchiveOrganisation(accessToken: string, id: string): Promise<void>;
  /** Permanently delete a genuinely empty org. Throws ApiError(409) when it has data (archive instead). */
  deleteOrganisation(accessToken: string, id: string): Promise<void>;
  /** An organisation's instance entitlements (partner pools or licensee grants). */
  getEntitlements(accessToken: string, organisationId: string): Promise<OrganisationEntitlements>;
  /** Upsert an organisation's cap for a definition (null = unlimited). */
  setEntitlement(accessToken: string, organisationId: string, request: SetEntitlementRequest): Promise<void>;
  /** Export an organisation, its sub-organisations and their members as one portable file (KEY-227).
   * Needs org.members.manage — the payload is every member email in the subtree. */
  exportOrganisation(accessToken: string, id: string): Promise<ExportedOrganisationTree>;
  /** What importing this file would do, worked out without writing anything. Needs org.import. */
  previewOrganisationImport(
    accessToken: string,
    request: ImportOrganisationRequest,
  ): Promise<OrganisationImportPreviewResult>;
  /** Apply the import. Needs org.import (platform-only). */
  importOrganisation(accessToken: string, request: ImportOrganisationRequest): Promise<OrganisationImportReport>;

  // Roles, members, users
  listRoles(accessToken: string): Promise<RoleSummary[]>;
  onboardPerson(accessToken: string, request: OnboardPersonRequest): Promise<OnboardPersonResult>;
  inviteInstanceMember(accessToken: string, instanceId: string, request: InviteInstanceMemberRequest): Promise<InviteInstanceMemberResult>;
  /** Renders the "dial.access-granted" email for the invite flow, without inviting anyone. */
  previewInviteInstanceMemberEmail(accessToken: string, instanceId: string, request: PreviewInviteEmailRequest): Promise<MemberEmailPreview>;
  /** The user directory. Archived accounts are excluded unless `includeArchived` is set. */
  listUsers(accessToken: string, includeArchived?: boolean): Promise<UserSummary[]>;
  /** KEY-895: one page of the directory matching a name or email, within the caller's remit (and a level). */
  searchUsers(accessToken: string, query: PeopleSearchQuery): Promise<PeopleSearchResult>;
  /** A user's footprint — drives the choice between a permanent delete and an archive. */
  getUserUsage(accessToken: string, userId: string): Promise<UserUsage>;
  /** Archive a user (reversible): blocks sign-in and hides them from the active directory. */
  archiveUser(accessToken: string, userId: string): Promise<void>;
  /** Restore a previously archived user. */
  restoreUser(accessToken: string, userId: string): Promise<void>;
  /** Change a user's email, which is also their login (KEY-626). Platform-only (org.manage). Throws
   *  ApiError(400) for an address that isn't one, one another account holds, or a guest account. */
  changeUserEmail(accessToken: string, userId: string, email: string): Promise<void>;
  /** Permanently delete a user. Throws ApiError(400) when the user has a footprint (archive instead). */
  deleteUser(accessToken: string, userId: string): Promise<void>;
  /** Start impersonating a user — returns a short-lived access token scoped to that user. */
  impersonate(accessToken: string, userId: string): Promise<ImpersonationResponse>;
  listMembers(accessToken: string, organisationId: string): Promise<OrgMemberSummary[]>;
  addMember(accessToken: string, organisationId: string, request: AddOrgMemberRequest): Promise<AddOrgMemberResult>;
  setMemberRole(accessToken: string, organisationId: string, userId: string, request: UpdateOrgMemberRequest): Promise<void>;
  /** KEY-928: which people may build their own dials, separate from their role. Takes effect at their next sign-in. */
  setMemberBuilder(accessToken: string, organisationId: string, userId: string, request: SetMemberBuilderRequest): Promise<void>;
  removeMember(accessToken: string, organisationId: string, userId: string): Promise<void>;

  // System settings
  listSettings(accessToken: string): Promise<SettingView[]>;
  updateSetting(accessToken: string, key: string, request: UpdateSettingRequest): Promise<void>;
  /** Claude models available to the configured API key; empty when AI isn't configured. */
  listAiModels(accessToken: string): Promise<AiModelOption[]>;

  // User feedback → Linear. Submit is available to any signed-in user in either app; the list/detail/
  // mutate calls require the feedback.manage capability (admin feedback page).
  /** `attachments` are pasted screenshots / picked documents, posted as multipart alongside the text (KEY-261). */
  submitBugReport(accessToken: string, request: SubmitBugReportRequest, attachments?: File[]): Promise<void>;
  submitFeatureRequest(accessToken: string, request: SubmitFeatureRequestRequest, attachments?: File[]): Promise<void>;
  listFeedback(accessToken: string, query?: FeedbackQuery): Promise<FeedbackListResponse>;
  getFeedback(accessToken: string, id: string): Promise<UserFeedbackDetail>;
  updateFeedback(accessToken: string, id: string, request: UpdateFeedbackRequest): Promise<void>;
  archiveFeedback(accessToken: string, id: string): Promise<void>;
  unarchiveFeedback(accessToken: string, id: string): Promise<void>;

  // Feedback clarification threads (KEY-253). `getClarification` and `requestClarification` need
  // feedback.manage; the reply/read/list calls are for whoever is a party to the thread.
  getClarification(accessToken: string, id: string): Promise<FeedbackClarificationDetail>;
  requestClarification(accessToken: string, id: string, request: RequestClarificationRequest): Promise<void>;
  replyToClarification(accessToken: string, id: string, request: ReplyToClarificationRequest): Promise<void>;
  markClarificationRead(accessToken: string, id: string): Promise<void>;
  listMyFeedbackClarifications(accessToken: string): Promise<FeedbackClarificationListResponse>;

  // CRM (KEY-311). Reads need crm.read, writes crm.manage; every record call additionally requires the
  // caller's organisation to be the root org or a first-level Partner (403 otherwise). getCrmStatus is
  // un-gated by eligibility (it answers it) and 404s when the Features:Crm flag is off.
  getCrmStatus(accessToken: string): Promise<CrmStatusResponse>;

  // Billing (KEY-366). Reads need billing.read, mutations billing.manage. Unlike getCrmStatus this one is
  // mapped even when Features:Billing is off — it answers the flag rather than 404ing on it, so a disabled
  // module and an unreachable API stay distinguishable.
  getBillingStatus(accessToken: string): Promise<BillingStatusResponse>;
  listBillingPlans(accessToken: string, includeRetired?: boolean): Promise<BillingPlanListResponse>;
  createBillingPlan(accessToken: string, request: SaveBillingPlanRequest): Promise<{ id: string }>;
  updateBillingPlan(accessToken: string, planId: string, request: SaveBillingPlanRequest): Promise<void>;
  /**
   * The narrow price-only edit (KEY-420): `billing.manage` OR `partner.price.manage`, but only when the
   * caller's own organisation is this rung's owner AND the rung has opted into partner price editing.
   * Refuses a price that would undercut the rung's agreed minimum fee.
   */
  updateBillingPlanPrice(accessToken: string, planId: string, request: UpdatePlanPriceRequest): Promise<void>;
  setBillingPlanRetired(accessToken: string, planId: string, retired: boolean): Promise<void>;
  /** List or unlist a rung on the website and buy page; unlisted rungs still sell by campaign or link. */
  setBillingPlanShownOnWebsite(accessToken: string, planId: string, shown: boolean): Promise<void>;
  /**
   * Permanently removes a rung nothing was ever sold on (KEY-442). Rejects with a 409 whose message names
   * what to clear first when anything still references it — subscribers past or present, a tier
   * organisation, a campaign, or a sale.
   */
  deleteBillingPlan(accessToken: string, planId: string): Promise<void>;
  /** A plan's trial drip sequence and what it may schedule (KEY-433). */
  getTrialDrip(accessToken: string, planId: string): Promise<TrialDripStateResponse>;
  saveTrialDrip(accessToken: string, planId: string, request: SaveTrialDripRequest): Promise<void>;
  /** Redeems a drip email's one-click unsubscribe token (KEY-433). False when the token isn't ours. */
  unsubscribeMarketing(token: string): Promise<boolean>;
  getBillingDrift(accessToken: string): Promise<BillingDriftReport>;
  /** The lapsed-data dry run (KEY-667). Platform admins only; everyone else is refused. */
  getLapsedDataRetention(accessToken: string): Promise<LapsedDataRetentionReport>;
  /** Holds or releases one organisation's lapsed-data countdown (KEY-667). Platform admins only. */
  setRetentionHold(accessToken: string, organisationId: string, hold: boolean): Promise<void>;
  /** The dashboard's billing headline (KEY-563), scoped on billing.read. */
  getBillingDashboardSummary(accessToken: string): Promise<BillingDashboardSummary>;
  /** An organisation's plan, or null when it is on none (the API answers 204). */
  /**
   * The caller's OWN partner earnings for a window. Null when their organisation is not a partner.
   * Takes no organisation id: whose figures come back is decided from the caller's own scope.
   */
  /**
   * Whether the Publish site button can work — a token is configured, GitHub still accepts it, and how
   * long it has left. Asks GitHub, so it is a touch slower than the other admin GETs on this screen.
   */
  getSitePublishStatus(accessToken: string): Promise<SitePublishStatusView>;
  /** Asks the marketing site to rebuild. Throws with the reason when it cannot. */
  publishSite(accessToken: string): Promise<SitePublishStartedView>;
  /**
   * Queues a site publish for a moment in the future (KEY-505). Supersedes any publish already queued —
   * there is only ever one. Throws with the reason when the time or the environment will not do.
   */
  scheduleSitePublish(accessToken: string, request: ScheduleSitePublishRequest): Promise<ScheduleSitePublishResponse>;
  /** Withdraws the queued publish. Succeeds whether or not there was one. */
  cancelScheduledSitePublish(accessToken: string): Promise<void>;
  /** Uploads a logo for a partner's public page. Returns the URL to store against it. */
  uploadPartnerLogo(accessToken: string, file: File): Promise<SiteImageUploadedView>;
  /** Uploads a logo for a CRM enquiry source. Independent of the partner page's, like the accent colour. */
  uploadCrmSourceLogo(accessToken: string, file: File): Promise<SiteImageUploadedView>;
  getMyPartnerEarnings(accessToken: string): Promise<PartnerOwnEarningsView | null>;
  /** Our discount codes that work on the caller's OWN plans (KEY-664) — read-only, billing.read. */
  listMyPartnerDiscountCodes(accessToken: string): Promise<PartnerDiscountCodeView[]>;
  getOrgSubscription(accessToken: string, organisationId: string): Promise<OrgSubscriptionView | null>;
  /** AI generations spent this period, or null when no plan meters the organisation (the API answers 204). */
  getOrgAiUsage(accessToken: string, organisationId: string): Promise<AiUsageView | null>;
  /** What Claude cost us for one dial in a month (KEY-713). Platform admins only; `month` is yyyy-MM, default this month. */
  getInstanceAiCost(accessToken: string, instanceId: string, month?: string): Promise<AiCostView>;
  /** What Claude cost us for one organisation in a month, split by dial (KEY-713). Platform admins only. */
  getOrgAiCost(accessToken: string, organisationId: string, month?: string): Promise<AiCostView>;
  grantOrgSubscription(accessToken: string, organisationId: string, request: GrantSubscriptionRequest): Promise<GrantSubscriptionResult>;
  cancelOrgSubscription(accessToken: string, organisationId: string): Promise<void>;
  /** What this organisation has actually paid, most recent first. Empty when it has been comped. */
  listOrgPayments(accessToken: string, organisationId: string, limit?: number): Promise<OrgPaymentView[]>;
  listOrgAddOns(accessToken: string, organisationId: string): Promise<SubscriptionAddOnView[]>;
  setOrgAddOn(accessToken: string, organisationId: string, request: SetAddOnRequest): Promise<SubscriptionAddOnView>;
  removeOrgAddOn(accessToken: string, organisationId: string, addOnId: string): Promise<void>;
  /** What moving to another tier would do. Calculated only — asking changes nothing. */
  previewOrgPlanChange(
    accessToken: string, organisationId: string, request: PlanChangePreviewRequest,
  ): Promise<PlanChangePreviewView>;
  /**
   * Carries the move out at Stripe: immediately and invoiced prorated when the customer is gaining, at the
   * end of the paid period and with no credit when they are giving something up. Refuses a comp.
   */
  applyOrgPlanChange(
    accessToken: string, organisationId: string, request: PlanChangeApplyRequest,
  ): Promise<PlanChangeResultView>;
  listCrmCompanies(accessToken: string, query?: CrmCompaniesQuery): Promise<CrmCompanyListResponse>;
  getCrmCompany(accessToken: string, id: string): Promise<CrmCompanyDetailView>;
  createCrmCompany(accessToken: string, request: CreateCrmCompanyRequest): Promise<{ id: string }>;
  updateCrmCompany(accessToken: string, id: string, request: CreateCrmCompanyRequest): Promise<void>;
  archiveCrmCompany(accessToken: string, id: string): Promise<void>;
  restoreCrmCompany(accessToken: string, id: string): Promise<void>;
  listCrmPeople(accessToken: string, query?: CrmPeopleQuery): Promise<CrmPersonListResponse>;
  /** The tokens a bulk compose box may offer (KEY-426). */
  listCrmBulkEmailTokens(accessToken: string): Promise<CrmBulkEmailTokenView[]>;
  /**
   * One message to several opted-in contacts. Each recipient is written to separately, so nobody
   * learns who else received it; consent is re-checked server-side per person.
   */
  sendCrmBulkEmail(accessToken: string, request: SendCrmBulkEmailRequest): Promise<CrmBulkEmailResultView>;
  getCrmPerson(accessToken: string, id: string): Promise<CrmPersonDetailView>;
  createCrmPerson(accessToken: string, request: CreateCrmPersonRequest): Promise<{ id: string }>;
  updateCrmPerson(accessToken: string, id: string, request: UpdateCrmPersonRequest): Promise<void>;
  archiveCrmPerson(accessToken: string, id: string): Promise<void>;
  restoreCrmPerson(accessToken: string, id: string): Promise<void>;
  /** Null company detaches. Refused (400) across organisations. */
  linkCrmPersonCompany(accessToken: string, id: string, companyId: string | null): Promise<void>;
  /** Promote-to-company: create the company and move the person in, one step. Returns the company id. */
  promoteCrmPerson(accessToken: string, id: string, request: CreateCrmCompanyRequest): Promise<{ id: string }>;
  listCrmEnquiries(accessToken: string, query?: CrmEnquiriesQuery): Promise<CrmEnquiryListResponse>;
  getCrmEnquiry(accessToken: string, id: string): Promise<CrmEnquiryDetailView>;
  /**
   * Logs an enquiry that arrived by hand. Give `personId` for somebody already in the book, or
   * `person` for somebody who isn't — the server matches on email first, so `personCreated: false`
   * comes back when the address turned out to belong to an existing record.
   */
  createCrmEnquiry(accessToken: string, request: CreateCrmEnquiryRequest): Promise<CreateCrmEnquiryResponse>;
  changeCrmEnquiryStatus(accessToken: string, id: string, request: { status: CrmEnquiryStatus; closedReason?: string | null }): Promise<void>;
  /** Null unassigns. The assignee must be a member of the enquiry's owning organisation (400 otherwise). */
  assignCrmEnquiry(accessToken: string, id: string, userId: string | null): Promise<void>;
  /** Who this enquiry can be handed to — members of its organisation who can open the CRM. */
  listCrmAssignees(accessToken: string, enquiryId: string): Promise<CrmAssigneeListResponse>;
  /** The caller's OWN call-back diary (crm.read). There is no user id in this surface by design. */
  /** The call booked on an enquiry, or null when there isn't one (the API answers 204). */
  /** What converting this company would involve (crm.manage). */
  getCrmConversionPreview(accessToken: string, companyId: string): Promise<CrmConversionPreview>;
  /** Creates the organisation and invites the named people (crm.manage + org.edit). */
  convertCrmCompany(accessToken: string, companyId: string, request: ConvertCrmCompanyRequest): Promise<ConvertCrmCompanyResult>;
  /** Whether each active source can actually deliver the calls it promises (crm.read). */
  getCrmBookingReadiness(accessToken: string): Promise<CrmBookingReadinessResponse>;
  listCrmCalendarLinks(accessToken: string): Promise<CrmCalendarLinkListResponse>;
  connectCrmCalendar(accessToken: string, provider: CrmCalendarProviderDto): Promise<CrmCalendarConnectResponse>;
  syncCrmCalendar(accessToken: string, id: string): Promise<void>;
  disconnectCrmCalendar(accessToken: string, id: string): Promise<void>;
  listCrmAppointmentAgents(accessToken: string): Promise<CrmAgentOption[]>;
  createCrmAppointment(accessToken: string, request: SaveCrmAppointmentRequest): Promise<CrmAppointmentView>;
  updateCrmAppointment(accessToken: string, id: string, request: SaveCrmAppointmentRequest): Promise<CrmAppointmentView>;
  deleteCrmAppointment(accessToken: string, id: string): Promise<void>;
  getCrmTeamAvailability(accessToken: string, organisationId?: string | null): Promise<CrmTeamAvailabilityResponse>;
  getCrmCalendar(accessToken: string, query: { from: string; to: string; scope: CrmCalendarScope; organisationId?: string | null }): Promise<CrmCalendarResponse>;
  getCrmEnquiryBooking(accessToken: string, enquiryId: string): Promise<CrmBookingView | null>;
  cancelCrmEnquiryBooking(accessToken: string, enquiryId: string, reason: string | null): Promise<void>;
  getMyCrmAvailability(accessToken: string): Promise<CrmAvailabilityView>;
  addMyCrmAvailabilityWindow(accessToken: string, request: SaveCrmAvailabilityWindowRequest): Promise<{ id: string }>;
  removeMyCrmAvailabilityWindow(accessToken: string, id: string): Promise<void>;
  addMyCrmAvailabilityException(accessToken: string, request: SaveCrmAvailabilityExceptionRequest): Promise<{ id: string }>;
  removeMyCrmAvailabilityException(accessToken: string, id: string): Promise<void>;
  listCrmInteractions(accessToken: string, query?: CrmInteractionsQuery): Promise<CrmInteractionListResponse>;
  /** To-dos (KEY-425). Writes read crm.read — keeping your own list is part of doing the job. */
  listCrmTodos(accessToken: string, query?: { scope?: CrmTodoScope; includeDone?: boolean; page?: number; pageSize?: number }): Promise<CrmTodoListResponse>;
  createCrmTodo(accessToken: string, request: SaveCrmTodoRequest): Promise<{ id: string }>;
  updateCrmTodo(accessToken: string, id: string, request: SaveCrmTodoRequest): Promise<void>;
  setCrmTodoDone(accessToken: string, id: string, done: boolean): Promise<void>;
  deleteCrmTodo(accessToken: string, id: string): Promise<void>;
  getCrmTodoEmailPreferences(accessToken: string): Promise<CrmTodoEmailPreferences>;
  saveCrmTodoEmailPreferences(accessToken: string, preferences: CrmTodoEmailPreferences): Promise<void>;
  /** The CRM dashboard (KEY-430). A POST because reading it moves the "since last visit" cursor. */
  getCrmDashboard(accessToken: string): Promise<CrmDashboardResponse>;
  logCrmInteraction(accessToken: string, request: LogCrmInteractionRequest): Promise<{ id: string }>;
  /**
   * Files on contacts and interactions (KEY-476). Reads need crm.read, writes crm.manage. Uploading is
   * three calls, as it is for dial evidence: ask for a slot, PUT the bytes straight to storage with
   * `uploadDocumentBytes`, then confirm — until the confirm lands the file appears nowhere.
   * `getCrmAttachmentDownloadUrl` answers 404 while a file is still awaiting its malware scan.
   */
  requestCrmAttachmentUpload(accessToken: string, request: RequestCrmAttachmentUploadRequest): Promise<CrmAttachmentUploadTicket>;
  confirmCrmAttachmentUploaded(accessToken: string, id: string): Promise<void>;
  listCrmAttachments(accessToken: string, query: { personId?: string; interactionId?: string }): Promise<CrmAttachmentView[]>;
  getCrmAttachmentDownloadUrl(accessToken: string, id: string): Promise<CrmAttachmentDownloadTicket>;
  deleteCrmAttachment(accessToken: string, id: string): Promise<void>;
  /**
   * Actually sends an email to a CRM person (crm.manage) and records it on their timeline. Distinct
   * from `logCrmInteraction`, which only writes down correspondence that happened elsewhere.
   * 502 = the mail service refused; nothing was written to their history.
   */
  sendCrmEmail(accessToken: string, personId: string, request: SendCrmEmailRequest): Promise<SendCrmEmailResponse>;
  /** Sources: reads need crm.read (the enquiries tab names/filters by them); writes crm.manage. */
  /** Lead campaigns (KEY-432). Reads are crm.read; everything that writes is crm.manage. */
  listCrmCampaigns(accessToken: string, includeInactive?: boolean): Promise<CrmCampaignListResponse>;
  getCrmCampaign(accessToken: string, id: string): Promise<CrmCampaignDetailView>;
  listCrmCampaignEvents(accessToken: string): Promise<CrmCampaignEventListResponse>;
  /** The tiers this organisation may put behind a QR code — its own price list, nobody else's. */
  /** The tiers a campaign may be put behind, the ones it may not with a reason each, and whose price list
   * it is (KEY-440). `campaignId` names the campaign being EDITED, so the answer is the campaign owner's
   * list rather than the caller's — those differ for a platform admin editing a partner's campaign, and
   * the save has always been held to the former. Rejects with 404 for a campaign the caller cannot see,
   * exactly as for an id that names nothing. */
  listCrmCampaignPlans(accessToken: string, campaignId?: string): Promise<CampaignPlanOptionsView>;
  createCrmCampaign(accessToken: string, request: SaveCrmCampaignRequest): Promise<{ id: string }>;
  updateCrmCampaign(accessToken: string, id: string, request: SaveCrmCampaignRequest): Promise<void>;
  setCrmCampaignActive(accessToken: string, id: string, active: boolean): Promise<void>;
  /** Emails a campaign's link and QR code to up to 10 people, from the platform mailbox with the caller as
   * reply-to. crm.manage. Rejects 400 for a deactivated or dead-end campaign; skipped recipients come back
   * named with a reason rather than failing the send. */
  shareCrmCampaign(accessToken: string, id: string, request: ShareCrmCampaignRequest): Promise<ShareCrmCampaignResultView>;
  /** Renders that email without sending it. */
  previewCrmCampaignShare(
    accessToken: string, id: string, request: { subject: string | null; message: string | null },
  ): Promise<CrmCampaignSharePreviewView>;
  /** A campaign's trial emails (KEY-661): its plan's, and its own replacements and extras. 404 for a
   *  campaign the caller cannot see. */
  getCampaignTrialEmails(accessToken: string, campaignId: string): Promise<CampaignTrialEmailsResponse>;
  saveCampaignTrialEmails(accessToken: string, campaignId: string, request: SaveCampaignTrialEmailsRequest): Promise<void>;
  uploadCrmCampaignLogo(accessToken: string, file: File): Promise<SiteImageUploadedView>;
  /** Resolve a scanned key. Unauthenticated — the visitor has no account yet, that is the point.
   *  Throws ApiError(404) for an unknown, deactivated or dead-tiered key: never a fallback offer. */
  getPublicCampaign(publicKey: string): Promise<PublicCampaignView>;
  listCrmSources(accessToken: string, includeInactive?: boolean): Promise<CrmEnquirySourceListResponse>;
  /** The caller's own partner page (KEY-429). Null when their organisation has none. */
  getMyPartnerPage(accessToken: string): Promise<PartnerPageView | null>;
  /** Rewrites the words on the caller's own partner page. */
  saveMyPartnerPage(accessToken: string, request: SavePartnerPageRequest): Promise<void>;
  createCrmSource(accessToken: string, request: SaveCrmEnquirySourceRequest): Promise<{ id: string }>;
  updateCrmSource(accessToken: string, id: string, request: SaveCrmEnquirySourceRequest): Promise<void>;
  /** Rewrites a source's landing-page copy (KEY-429). Reachable by the source's own organisation. */
  updateCrmSourceCopy(accessToken: string, id: string, request: SaveCrmEnquirySourceCopyRequest): Promise<void>;
  deactivateCrmSource(accessToken: string, id: string): Promise<void>;
  reactivateCrmSource(accessToken: string, id: string): Promise<void>;

  /** The website's partner pages. All three need `site.publish`. */
  listSitePartners(accessToken: string): Promise<PartnerSiteProfileAdminListResponse>;
  listSitePartnerCandidates(accessToken: string): Promise<SitePartnerCandidateListResponse>;
  saveSitePartner(
    accessToken: string,
    slug: string,
    request: SavePartnerSiteProfileRequest,
  ): Promise<PartnerSiteProfileAdminView>;
  draftPartnerCopy(
    accessToken: string,
    request: DraftPartnerCopyRequest,
  ): Promise<PartnerCopyDraftView>;

  /** Partner payouts. All three need `billing.manage`. */
  listPartnerPayouts(accessToken: string): Promise<PartnerPayoutAccountListResponse>;
  startPartnerOnboarding(accessToken: string, organisationId: string): Promise<PartnerOnboardingLinkView>;
  attachPartnerAccount(
    accessToken: string,
    organisationId: string,
    stripeAccountId: string,
  ): Promise<PartnerPayoutAccountView>;
  refreshPartnerPayout(accessToken: string, organisationId: string): Promise<PartnerPayoutAccountView>;

  /** Affiliate commission terms (KEY-502/514, stage 1). Recording only — nothing here moves money.
   *  `getAffiliateStatus` is answered even with the module off, so the admin app can fail closed on a
   *  positive answer rather than on a request that failed for some other reason. */
  getAffiliateStatus(accessToken: string): Promise<AffiliateStatusResponse>;
  /** Every affiliate organisation in the caller's scope. `billing.read` OR `affiliate.terms.manage`. */
  listAffiliateOrganisations(accessToken: string): Promise<AffiliateOrganisationListResponse>;
  /** One organisation's settings, members and referable rungs in a single call — so the ceiling is never
   *  assembled client-side. 404 for an organisation outside the caller's scope (never 403). */
  getAffiliateOrganisation(accessToken: string, organisationId: string): Promise<AffiliateOrganisationDetail>;
  /** `billing.manage` OR `affiliate.terms.manage`. Rejects with ApiError(400) for a rate above the ceiling,
   *  the message naming the binding rung — and for `funding: "KeyDials"` from a caller holding only the
   *  narrow capability. */
  saveAffiliateOrgSettings(
    accessToken: string,
    organisationId: string,
    request: SaveAffiliateOrgSettingsRequest,
  ): Promise<void>;
  /** The caller's own referral links (KEY-514): empty for anybody who is not an affiliate, and empty (not an
   *  error) where the affiliate module is off in this environment. */
  getMyAffiliateLinks(accessToken: string): Promise<MyAffiliateLinkView[]>;
  /** Names the discount code one affiliate's link gives a buyer (null clears, inheriting the organisation's).
   *  ApiError(400) when it is not a live Key Dials discount code. */
  saveAffiliateDiscount(accessToken: string, profileId: string, discountCode: string | null): Promise<void>;
  /** The organisation-wide default for {@link saveAffiliateDiscount}. */
  saveAffiliateOrgDiscount(accessToken: string, organisationId: string, discountCode: string | null): Promise<void>;
  /** Enrol a member of the organisation. `org.members.manage`; the rate starts unset. */
  enrolAffiliate(accessToken: string, organisationId: string, userId: string): Promise<{ id: string }>;
  /** `billing.manage` OR `affiliate.terms.manage`. Same refusals as saveAffiliateOrgSettings. */
  saveAffiliateTerms(
    accessToken: string,
    profileId: string,
    request: SaveAffiliateTermsRequest,
  ): Promise<void>;
  /** Activate or deactivate an arrangement. `org.members.manage`. Never blanks the agreed rate. */
  setAffiliateActive(accessToken: string, profileId: string, isActive: boolean): Promise<void>;
  /** Names the rung affiliates enrolled here are comped onto (KEY-937); null clears it. `billing.manage` OR
   *  `affiliate.terms.manage`. Rejects with ApiError(400) for a rung the organisation may not hand out. */
  saveAffiliateCompPlan(accessToken: string, organisationId: string, planId: string | null): Promise<void>;
  /** Sets up (or finishes setting up) one affiliate's free dial (KEY-937). Idempotent. `org.members.manage`. */
  setUpAffiliateComp(accessToken: string, profileId: string): Promise<AffiliateCompResult>;
  partnerPayoutReport(accessToken: string, from?: string, to?: string): Promise<PartnerPayoutReport>;
  listSales(accessToken: string, from?: string, to?: string): Promise<SaleRecordListResponse>;
  refundSale(accessToken: string, saleId: string, amountPence?: number): Promise<RefundRecordView>;
  /** Our discount codes, newest first, read live from Stripe (KEY-642 follow-up). `billing.manage`. */
  listDiscountCodes(accessToken: string): Promise<DiscountCodeView[]>;
  /** Makes a code at Stripe. Throws ApiError(400) with a readable reason for anything malformed. `billing.manage`. */
  createDiscountCode(accessToken: string, request: CreateDiscountCodeRequest): Promise<DiscountCodeView>;
  /** Switches a code on or off. `billing.manage`. */
  setDiscountCodeActive(accessToken: string, id: string, active: boolean): Promise<DiscountCodeView>;
  /** Replaces a code's plan limits; empty lifts them. `billing.manage`. */
  setDiscountCodeLimits(accessToken: string, id: string, request: SetDiscountCodeLimitsRequest): Promise<DiscountCodeView>;
  partnerDashboardLink(accessToken: string, organisationId: string): Promise<PartnerDashboardLinkView>;
  /** Email templates (spec D13), crm.manage. A cross-tenant admin's "own" overrides are the platform defaults. */
  listCrmEmailTemplates(accessToken: string): Promise<EmailTemplateListResponse>;
  saveCrmEmailTemplate(accessToken: string, key: string, request: SaveEmailTemplateRequest): Promise<void>;
  resetCrmEmailTemplate(accessToken: string, key: string): Promise<void>;
  /** Public intake — no token: the enquiry page runs with no session at all. 404 = dead-end key. */
  /**
   * The tiers somebody can buy themselves. Unauthenticated: read by people with no account yet.
   *
   * `forPlanId` is the price the visitor arrived for — the id every "Get started" link already carries —
   * and narrows the list to that price's SELLER (KEY-421). Pass it wherever the buyer was sent by
   * somebody: a partner's page must lead to that partner's shop, not to Key Dials' catalogue and every
   * other specialist's beside it. Omit it for the general catalogue.
   *
   * `discountCode` is the code a buy link carries (KEY-642): the list is narrowed to the plans it works on,
   * still only those shown on the website. A code that works on none of them leaves the list unchanged.
   */
  listPurchasablePlans(forPlanId?: string | null, discountCode?: string | null): Promise<PurchasablePlanView[]>;
  /** The seller's public branding for a price, or null for Key Dials' own prices. Unauthenticated. */
  getPlanSeller(planId: string): Promise<PublicSellerBrandingView | null>;
  /** The End User Terms and Conditions a buyer must accept (KEY-618). Unauthenticated. */
  getTerms(): Promise<LegalDocumentView>;
  /**
   * What a discount code takes off a plan (KEY-642), or null when it would not apply — unknown, expired or
   * for other plans, all alike. Unauthenticated.
   */
  previewDiscountCode(code: string, planId: string): Promise<DiscountCodePreviewView | null>;
  /** The discount code an affiliate's referral link gives (KEY-514), for the buy page to pre-fill. Null for a
   *  code naming no live affiliate, or one with no discount. Anonymous. */
  getReferralDiscountCode(referralCode: string): Promise<string | null>;
  /** Opens a Stripe Checkout Session and returns where to send the buyer. Unauthenticated. */
  startPublicCheckout(request: StartCheckoutRequest): Promise<CheckoutStartedView>;
  /** In-app upgrade for an organisation that already exists. */
  startOrgCheckout(accessToken: string, organisationId: string, request: StartCheckoutRequest): Promise<CheckoutStartedView>;
  /**
   * Resubscribing after a card-free trial expired (KEY-431). Distinct from {@link startOrgCheckout},
   * which needs billing.manage — a customer's own Organisation Administrator does not hold it, so this is
   * the only route out of the "trial ended" screen for the person actually looking at it. Throws
   * ApiError(409) when the subscription is not an expired trial.
   */
  reactivateSubscription(accessToken: string, organisationId: string, request: StartCheckoutRequest): Promise<CheckoutStartedView>;
  /** A link into Stripe's portal, or null when the organisation has never been billed through Stripe. */
  getOrgPortalLink(accessToken: string, organisationId: string): Promise<string | null>;
  /** The organisation's family pack (KEY-651), or null when it has none to show — the card then stays hidden. */
  getFamily(accessToken: string, organisationId: string): Promise<FamilyView | null>;
  /** Invites a relative and emails them the link. ApiError(400) carries a refusal worded for the buyer. */
  inviteFamilyMember(
    accessToken: string, organisationId: string, request: InviteFamilyMemberRequest): Promise<FamilyInviteSentView>;
  /** A fresh link for an invitation not yet accepted; the old one stops working. */
  resendFamilyInvite(accessToken: string, organisationId: string, seatId: string): Promise<FamilyInviteSentView>;
  /** Withdraws an invitation not yet accepted, freeing its place. */
  revokeFamilyInvite(accessToken: string, organisationId: string, seatId: string): Promise<void>;
  /** What a family invitation link says (anonymous). Null for a link that is unknown, withdrawn or out of date. */
  getFamilyInvite(token: string): Promise<FamilyInviteView | null>;
  /**
   * Accepts a family invitation (anonymous): sets up the relative's own organisation, plan and dials for the
   * invited address. ApiError(404) for a dead link; ApiError(400) carries anything else, worded for them.
   */
  claimFamilyInvite(token: string, request: ClaimFamilyInviteRequest): Promise<FamilyInviteClaimedView>;
  /** The rungs the caller may switch their OWN plan to (KEY-651), or null when it can't be switched from here. */
  getPlanSwitchOptions(accessToken: string, organisationId: string): Promise<PlanSwitchOptionsView | null>;
  /** Switches the caller's own plan to one of {@link getPlanSwitchOptions}'s options. ApiError(400) explains a refusal. */
  switchPlan(accessToken: string, organisationId: string, planId: string): Promise<PlanChangeResultView>;
  /** Chooses the caller's OWN organisation's Milo tier (KEY-716): requests a month from `miloStatus().tiers`, or 0 for none.
   * billing.read, own organisation only. ApiError(400) explains a refusal (a tier the plan doesn't sell, a card-free trial). */
  setOwnMiloTier(accessToken: string, organisationId: string, requests: number): Promise<SubscriptionAddOnView>;
  /** Buys 300 more Milo requests for the caller's OWN organisation (KEY-717), charged to the card on file; the credits
   * roll over. billing.read, own organisation only. ApiError(400) explains a refusal. */
  buyOwnMiloTopUp(accessToken: string, organisationId: string): Promise<MiloTopUpReceipt>;
  /** The same purchase made by an administrator for an organisation in their scope (billing.manage). On a comp it is simply granted. */
  buyMiloTopUp(accessToken: string, organisationId: string): Promise<MiloTopUpReceipt>;
  getPublicEnquirySource(publicKey: string): Promise<PublicEnquirySourceView>;
  submitPublicEnquiry(publicKey: string, request: SubmitPublicEnquiryRequest): Promise<SubmitPublicEnquiryResponse>;
  verifyPublicEnquiry(enquiryToken: string, code: string): Promise<VerifyPublicEnquiryResponse>;
  /** Call-back slots for a VERIFIED enquiry token; 404 (thrown) when the token isn't one. */
  getPublicCallbackSlots(enquiryToken: string): Promise<CallbackSlotsResponse>;
  /**
   * Claims a slot. `customerTimeZoneId` is the visitor's own IANA zone, so their confirmation email reads
   * back the time the page showed them rather than the agent organisation's. Omit it and the server falls
   * back to the organisation's zone, exactly as it did before.
   */
  bookPublicCallback(enquiryToken: string, startUtc: string, customerTimeZoneId?: string | null): Promise<BookCallbackResponse>;

  /**
   * Sets (or clears, with null) the organisation a cross-org request is scoped into, sent as the
   * `X-KeyDials-Org` header on every authenticated call until changed (KEY-132). The Workspace sets this to
   * the open dial's owning org so its nested reads/writes resolve; the Dashboard clears it and instead
   * passes a per-card org to {@link getInstance}. A per-call org argument always overrides this.
   */
  setActiveOrg(organisationId: string | null): void;

  /**
   * Sets (or clears, with null) the instance role a checker has asked to be evaluated as, sent as the
   * `X-KeyDials-Checking-Role` header on every authenticated call until changed (KEY-498). Exactly one of
   * "Admin" | "Editor" | "Reviewer" | "Reader"; anything else is a 400 from the server rather than a silent
   * fall back to the caller's own role.
   *
   * The server honours it only inside a checking subtree and only downwards from a real Admin, and reports
   * what it actually evaluated as on `InstanceDetail.simulatedRole` — render the toggle from THAT, not from
   * what was set here, or the UI can claim a role the server never applied.
   *
   * Global, exactly like {@link setActiveOrg}: the Workspace sets it for the dial it has open and MUST clear
   * it on unmount, or the Dashboard's other checking dials come back simulated too.
   */
  setCheckingRole(role: string | null): void;

  // Dial instances
  /** Allocate a published definition to a target organisation (platform admin). */
  allocateInstance(accessToken: string, request: AllocateInstanceRequest): Promise<{ id: string }>;
  listInstances(accessToken: string): Promise<InstanceSummary[]>;
  /** The archived instances the caller may oversee, for the admin "show archived" view (instance.manage). */
  listArchivedInstances(accessToken: string): Promise<InstanceSummary[]>;
  /** Archive a running instance (reversible): hides it from listings and frees its licence. Cascades to a group's children. */
  archiveInstance(accessToken: string, id: string): Promise<void>;
  /** Restore a previously archived instance (and, for a group, its children). */
  unarchiveInstance(accessToken: string, id: string): Promise<void>;
  /** The cross-org "My Dials" portfolio: every dial the caller can read across all their orgs + the org tree (KEY-131). */
  listMyInstances(accessToken: string): Promise<PortfolioResponse>;
  /** Fetch one instance. Pass `organisationId` to scope the request into a non-active accessible org (KEY-132). */
  getInstance(accessToken: string, id: string, organisationId?: string): Promise<InstanceDetail>;
  /** Preview-as (KEY-302): the detail exactly as `userId` receives it — same pruning, same withheld
   * rollups. Member-management authority enforced server-side; read-only by construction. */
  previewInstanceAs(accessToken: string, id: string, userId: string): Promise<InstanceDetail>;
  /** Rename a running instance (KEY-214). Name is trimmed + length-checked (1–200) server-side; caller must be
   * an instance-wide Admin or hold instance.manage. */
  renameInstance(accessToken: string, id: string, name: string): Promise<void>;
  // Instance-first group views: the caller's own groups assembled from instances they already own.
  /** The caller's own group views. */
  listMyGroups(accessToken: string): Promise<InstanceGroupSummary[]>;
  /** Create a group view from existing instances; returns its rolled-up detail. */
  createGroup(accessToken: string, request: CreateInstanceGroupRequest): Promise<InstanceGroupDetail>;
  /** A group view's rolled-up detail (one slice per accessible member). */
  getGroup(accessToken: string, id: string): Promise<InstanceGroupDetail>;
  /** Rename / re-strategy a group view; returns its updated detail. */
  updateGroup(accessToken: string, id: string, request: UpdateInstanceGroupRequest): Promise<InstanceGroupDetail>;
  /** Delete a group view (its member instances are untouched). */
  deleteGroup(accessToken: string, id: string): Promise<void>;
  /** Add an instance the caller can access to a group view; returns the updated detail. */
  addGroupMember(accessToken: string, id: string, instanceId: string): Promise<InstanceGroupDetail>;
  /** Remove a member from a group view. */
  removeGroupMember(accessToken: string, id: string, instanceId: string): Promise<void>;
  /** Reorder a group view's members to the given instance-id order. */
  reorderGroupMembers(accessToken: string, id: string, orderedInstanceIds: string[]): Promise<void>;
  /** The consolidated by-area view: each segment of the shared definition aggregated across members. */
  getGroupConsolidated(accessToken: string, id: string): Promise<InstanceGroupConsolidated>;
  /** The customisation editor view: gate result, effective envelope, headroom, resolved tree, disabled nodes (Phase C). */
  getInstanceCustomisation(accessToken: string, id: string): Promise<InstanceCustomisationView>;
  /** Add a bespoke section/segment/item to an instance; returns the generated key. */
  addInstanceSection(accessToken: string, id: string, body: AddNodeBody): Promise<{ key: string }>;
  addInstanceSegment(accessToken: string, id: string, body: AddNodeBody): Promise<{ key: string }>;
  addInstanceItem(accessToken: string, id: string, body: AddNodeBody): Promise<{ key: string }>;
  /** Re-word a custom node, or re-word/disable/enable a base node (`nodeType` required for a base node). */
  patchInstanceNode(accessToken: string, id: string, key: string, body: PatchNodeBody): Promise<void>;
  /** Move a custom item to another action point (segment). */
  moveInstanceItem(accessToken: string, id: string, key: string, body: MoveNodeBody): Promise<void>;
  /** Remove a custom node (base nodes can only be disabled). */
  deleteInstanceNode(accessToken: string, id: string, key: string): Promise<void>;
  /** KEY-754: show this dial's hidden parts to the caller alone for `minutes` (1–60, default 15). Customisers only. */
  revealHiddenParts(accessToken: string, id: string, minutes?: number): Promise<{ expiresAtUtc: string }>;
  /** KEY-754: end the caller's reveal early ("Hide again"). */
  endHiddenReveal(accessToken: string, id: string): Promise<void>;
  /** Export the instance's bespoke (custom-overlay) items as a portable file. */
  exportInstanceItems(accessToken: string, id: string): Promise<ExportedInstanceItems>;
  /** Import items, replacing the instance's existing items in the action points the file covers. */
  importInstanceItems(accessToken: string, id: string, payload: ExportedInstanceItems): Promise<ImportItemsReport>;
  /** Export a whole running instance — overlay, statuses, attributes and history — for another server. */
  exportInstance(accessToken: string, id: string): Promise<ExportedInstance>;
  /** Recreate an exported instance in the chosen organisation, against the dial of the same name here. */
  importInstance(accessToken: string, body: ImportInstanceRequest): Promise<ImportInstanceReport>;
  /** Whether the AI document → items import is enabled on this server. */
  itemImportStatus(accessToken: string): Promise<{ enabled: boolean }>;

  /** KEY-PEOPLE: whether a dial's people list (and the Person field type) is on in this environment. */
  peopleStatus(accessToken: string): Promise<{ enabled: boolean }>;
  /** KEY-684: whether push notifications are switched on here (Features:PushNotifications), so the subscribe
   * menu offers "Push to my phone". Says nothing about whether the caller has a phone registered — a Push
   * opt-in without one is refused by setSubscription with a message saying so. */
  pushStatus(accessToken: string): Promise<{ enabled: boolean }>;

  // ---- Milo, the assistant (KEY-704) ----
  /** Whether Milo is on here (Features:Milo), and where the pool a turn would spend stands: the dial's
   * organisation's when instanceId names a dial the caller can open, otherwise the caller's own. */
  miloStatus(accessToken: string, instanceId?: string | null): Promise<MiloStatus>;
  /** Say something to Milo. No conversationId starts a conversation; the reply carries the id to carry on with.
   * One request from the household's pool per call. 400 (ApiError) when the pool is spent or there is no Milo
   * pack; 409 when the conversation is at its turn limit; 404 when it has expired. */
  miloTurn(accessToken: string, body: MiloTurnRequest, conversationId?: string | null): Promise<MiloTurnResponse>;
  /** Run the write Milo proposed. Spends no request. 409 when nothing is waiting. */
  miloConfirm(accessToken: string, conversationId: string, actionId: string, choiceId?: string | null): Promise<MiloTurnResponse>;
  /** Drop the write Milo proposed. */
  miloDecline(accessToken: string, conversationId: string, actionId: string): Promise<MiloTurnResponse>;
  /** The conversation as shown: the words on both sides, tool traffic left out. 404 when expired or not the caller's. */
  miloConversation(accessToken: string, conversationId: string): Promise<MiloConversation>;
  /** KEY-LASTTIME: what a field held before it was last cleared. 404 (ApiError) when never cleared or not visible. */
  previousAttributeValue(accessToken: string, instanceId: string, scope: { target: string; sectionKey: string; segmentKey?: string; itemKey?: string; attributeKey: string }): Promise<PreviousAttributeValue>;
  /** KEY-LOOKBACK: the dial as it stood at `at` (an ISO instant; the future clamps to now). */
  instanceAsOf(accessToken: string, instanceId: string, at: string): Promise<InstanceSnapshot>;
  /** KEY-889: the dial at a later instant if nothing is done. 404 while Look ahead is off. */
  instanceAhead(accessToken: string, instanceId: string, at: string): Promise<InstanceForecast>;
  /** KEY-LOOKBACK: the days something visible changed, for the timeline's ticks. Both bounds optional ISO instants. */
  instanceChangeDays(accessToken: string, instanceId: string, range?: { from?: string; to?: string }): Promise<InstanceChangeDays>;
  /** KEY-RESETTIME: the dial's scheduled resets. Empty while the reset sweep is in dry run. */
  instanceResets(accessToken: string, instanceId: string): Promise<InstanceResets>;
  /** KEY-RESETTIME: moves one reset's day and time on this dial, or with `recurrence: null` restores the default. */
  setInstanceResetTiming(accessToken: string, instanceId: string, request: SetInstanceResetTimingRequest): Promise<InstanceReset>;
  /** The dial's people in display order. 404 when the flag is off or the caller cannot open the dial. */
  listInstancePeople(accessToken: string, instanceId: string): Promise<InstancePerson[]>;
  addInstancePerson(accessToken: string, instanceId: string, request: AddInstancePersonRequest): Promise<InstancePerson>;
  updateInstancePerson(accessToken: string, instanceId: string, personId: string, request: UpdateInstancePersonRequest): Promise<InstancePerson>;
  removeInstancePerson(accessToken: string, instanceId: string, personId: string): Promise<void>;
  reorderInstancePeople(accessToken: string, instanceId: string, personIds: string[]): Promise<void>;
  /** KEY-752: the dial's contacts in display order. 404 when the people flag is off or the caller cannot open the dial. */
  listInstanceContacts(accessToken: string, instanceId: string): Promise<InstanceContact[]>;
  addInstanceContact(accessToken: string, instanceId: string, request: InstanceContactRequest): Promise<InstanceContact>;
  updateInstanceContact(accessToken: string, instanceId: string, contactId: string, request: InstanceContactRequest): Promise<InstanceContact>;
  removeInstanceContact(accessToken: string, instanceId: string, contactId: string): Promise<void>;
  reorderInstanceContacts(accessToken: string, instanceId: string, contactIds: string[]): Promise<void>;
  /** KEY-917: invites sent to the dial's contacts, newest first — one contact's when `contactId` is given. */
  listContactInvites(accessToken: string, instanceId: string, contactId?: string): Promise<ContactInvite[]>;
  /** KEY-917: re-send a contact's invite over everywhere they are Responsible person. */
  resendContactInvite(accessToken: string, instanceId: string, contactId: string): Promise<ContactInvite>;
  /** KEY-916: every allocation on the dial the caller can see, ended ones included, newest first. */
  listAllocations(accessToken: string, instanceId: string): Promise<NodeAssignmentView[]>;
  /** KEY-916: allocate a section, action point or item to a member or a contact. */
  allocateNode(accessToken: string, instanceId: string, request: AssignNodeRequest): Promise<NodeAssignmentView>;
  /** KEY-916: take an allocation back (a dial admin's call once somebody has it). */
  withdrawAllocation(accessToken: string, instanceId: string, assignmentId: string): Promise<void>;
  /** KEY-916: send a contact's allocation invite again. */
  resendAllocation(accessToken: string, instanceId: string, assignmentId: string): Promise<ContactInvite>;
  /** Upload a document against an instance and poll the AI analysis to completion: extracted candidate
   * items with suggested segment mappings, ready for the review UI. `jobId` rides alongside the result
   * so a later confirm can pass it as `bulkAddInstanceItems`'s `sourceJobId` to resolve any kept photos.
   * Throws ApiError on failure (400 bad file, 403 no customise rights, 503 AI not configured, 502
   * upstream, 504 poll timeout). */
  analyzeInstanceDocument(
    accessToken: string,
    id: string,
    file: File,
    options?: { onPoll?: (elapsedMs: number) => void },
  ): Promise<DocumentItemAnalysisResult & { jobId: string }>;
  /** Additively create reviewed items across segments (the document-import confirm step). `sourceJobId`
   * is the analysis job any item's `images` reference — required whenever any item carries images. */
  bulkAddInstanceItems(
    accessToken: string, id: string, items: BulkAddItem[], statusDimensionKey?: string, sourceJobId?: string,
  ): Promise<BulkAddItemsReport>;
  /** The users who may be added to this instance: the members of its owning organisation. */
  listAssignableInstanceMembers(accessToken: string, instanceId: string): Promise<UserSummary[]>;
  /**
   * Where the organisation paying for this dial stands on its user allowance (KEY-474/KEY-487). 404 for a
   * caller who may not manage this dial's members: a seat position is commercial information about the
   * paying organisation. `enforced: false` means nobody is counting — a host without billing, or a plan
   * with no seat cap — and the numbers are absent.
   */
  getInstanceSeats(accessToken: string, instanceId: string): Promise<InstanceSeatsView>;
  /**
   * The dial's grants plus the section/segment names to label their scopes (KEY-578). 404 for anyone who may
   * not manage its members, like `listAssignableInstanceMembers`.
   */
  listInstanceMembers(accessToken: string, instanceId: string): Promise<InstanceMembersView>;
  /** Add or update an instance member's role (optionally scoped to a section/segment). Upsert. */
  addInstanceMember(accessToken: string, instanceId: string, request: AddInstanceMemberRequest): Promise<AddInstanceMemberResponse>;
  /** Renders the "dial.access-granted" email exactly as adding this member would send it, without granting anything. */
  previewInstanceMemberEmail(accessToken: string, instanceId: string, request: PreviewMemberEmailRequest): Promise<MemberEmailPreview>;
  /** Remove a member entirely (all their grants) from an instance. */
  removeInstanceMember(accessToken: string, instanceId: string, userId: string): Promise<void>;
  /** Remove one specific grant (by id) a user holds on an instance. */
  removeInstanceMemberGrant(accessToken: string, instanceId: string, grantId: string): Promise<void>;
  /** Who may set each status in this dial (definition roles + what applies here). */
  getInstanceStatusRoles(accessToken: string, instanceId: string): Promise<InstanceStatusRolesView>;
  /** Who may perform each configurable action in this dial (KEY-298) — the action-matrix sibling of
   * getInstanceStatusRoles, with the same read/manage split. */
  getInstanceActionRoles(accessToken: string, instanceId: string): Promise<InstanceActionRolesView>;
  /** Override (or revert, with roles: null) who may perform actions in this dial only. */
  setInstanceActionRoles(accessToken: string, instanceId: string, entries: SetInstanceActionRoleEntry[]): Promise<void>;
  /** One member's per-attribute exceptions (KEY-299 fine-tune). Member-management authority. */
  getMemberAttributeAccess(accessToken: string, instanceId: string, userId: string): Promise<MemberAttributeAccessView>;
  setMemberAttributeAccess(accessToken: string, instanceId: string, userId: string, entries: SetMemberAttributeAccessEntry[]): Promise<void>;
  /** Narrow/widen who may set given statuses, for this dial only. Dial admins; Admin is always kept. */
  setInstanceStatusRoles(accessToken: string, instanceId: string, entries: SetInstanceStatusRoleEntry[]): Promise<void>;
  /** Change a segment's status (optional review date + note). Throws ApiError(403) if not permitted. */
  changeSegmentStatus(accessToken: string, id: string, request: ChangeStatusRequest): Promise<void>;
  /** Set or clear a whole section's status (KEY-479). Refused unless the definition offers it. */
  changeSectionStatus(accessToken: string, id: string, request: ChangeSectionStatusRequest): Promise<void>;
  /** Dial links: re-evaluate every link rule of a group instance from current state. 404s where the flag is off. */
  recomputeInstanceLinks(accessToken: string, id: string): Promise<void>;
  /** A dial's segment status history. Pass `organisationId` to scope the request into a non-active
   * accessible org, exactly as {@link getInstance} does (KEY-132/KEY-221). */
  getInstanceHistory(accessToken: string, id: string, organisationId?: string): Promise<SegmentStatusEvent[]>;
  /** Whether the AI insight layer is enabled on this server (KEY-163; local-only for now). */
  insightsStatus(accessToken: string): Promise<{ enabled: boolean }>;
  /** KEY-531: which dial workspace this person gets, and whether the choice is offered at all. One call for
   * both — the toggle is hidden when `enabled` is false, and `mode` means nothing then. `chosen` is false
   * when they are on the server's default rather than a pick of their own. */
  workspaceMode(accessToken: string): Promise<WorkspaceModeView>;
  /** KEY-736: the caller's connected calendars, and whether the feature is on at all. */
  calendarConnections(accessToken: string): Promise<CalendarConnectionList>;
  /** KEY-736: the consent URL to send the browser to for Google or Outlook. */
  connectCalendar(accessToken: string, provider: "google" | "microsoft"): Promise<{ authorizationUrl: string }>;
  /** KEY-736: add a calendar by its iCal link. The server reads it once and refuses a bad one with the reason. */
  addIcsCalendar(accessToken: string, url: string, label?: string | null): Promise<CalendarConnectionView>;
  /** KEY-822: sign in to Apple iCloud or another CalDAV account. `server` is "icloud", "fastmail", "yahoo" or an https
   * address; `password` an app-specific one. Refused with the reason (a 400), e.g. when the password is wrong. */
  addCalDavCalendar(accessToken: string, server: string, username: string, password: string, label?: string | null): Promise<CalendarConnectionView>;
  syncCalendar(accessToken: string, id: string): Promise<CalendarConnectionView>;
  /** KEY-818: the calendars in one of my accounts, live from the provider, and which are shown. */
  calendarChoices(accessToken: string, id: string): Promise<CalendarChoiceList>;
  /** KEY-818: show these of the account's calendars; syncs before answering. 400 with the reason when refused. */
  chooseCalendars(accessToken: string, id: string, calendarIds: string[]): Promise<CalendarConnectionView>;
  /** KEY-819: let dials add appointments. Google and Outlook answer with a consent URL to send the browser to; CalDAV
   * with the connection, done. */
  allowCalendarWrites(accessToken: string, id: string): Promise<{ authorizationUrl: string | null; connection: CalendarConnectionView | null }>;
  stopCalendarWrites(accessToken: string, id: string): Promise<CalendarConnectionView>;
  setWriteCalendar(accessToken: string, id: string, calendarId: string): Promise<CalendarConnectionView>;
  /** KEY-821: the words that send an appointment Milo adds to this account. */
  setCalendarKeywords(accessToken: string, id: string, keywords: string[]): Promise<CalendarConnectionView>;
  /** KEY-817: "Sync calendars" — every one of mine, now. 429 when asked again within half a minute. */
  syncAllCalendars(accessToken: string): Promise<{ connections: CalendarConnectionView[] }>;
  /** KEY-817: called when a dial of days is opened; starts a background sync of each stale calendar on it. */
  refreshInstanceCalendars(accessToken: string, instanceId: string): Promise<{ started: number }>;
  disconnectCalendar(accessToken: string, id: string): Promise<void>;
  /** KEY-823: my private feed of what I added "just on a dial", for calendars Key Dials can't write to. */
  calendarFeed(accessToken: string): Promise<CalendarFeedView>;
  /** KEY-823: make a new feed link, revoking any old one. */
  resetCalendarFeed(accessToken: string): Promise<CalendarFeedLink>;
  revokeCalendarFeed(accessToken: string): Promise<void>;
  /** KEY-767: whole days, or the times of day less the hidden ones. Dial admins only. */
  setDaysLayout(accessToken: string, instanceId: string, allDay: boolean, hiddenSlots: string[]): Promise<void>;
  /** KEY-736: which people's calendars a dial shows. */
  instanceCalendarSources(accessToken: string, instanceId: string): Promise<InstanceCalendarSourceList>;
  addInstanceCalendarSource(accessToken: string, instanceId: string, connectionId: string, shareDetails: boolean): Promise<void>;
  setInstanceCalendarShare(accessToken: string, instanceId: string, sourceId: string, shareDetails: boolean): Promise<void>;
  removeInstanceCalendarSource(accessToken: string, instanceId: string, sourceId: string): Promise<void>;
  /** KEY-820: add an appointment to my own calendar from a day on this dial. Answers with its item key, once read back. */
  addCalendarAppointment(accessToken: string, instanceId: string, request: AddCalendarAppointmentRequest): Promise<{ itemKey: string }>;
  /** KEY-821: change one of my own appointments on this dial; answers with its item key as the dial now shows it. */
  changeCalendarAppointment(accessToken: string, instanceId: string, itemKey: string, request: AddCalendarAppointmentRequest): Promise<{ itemKey: string }>;
  /** KEY-821: delete one of my own appointments from my calendar. */
  deleteCalendarAppointment(accessToken: string, instanceId: string, itemKey: string): Promise<void>;
  setWorkspaceMode(accessToken: string, mode: WorkspaceMode): Promise<WorkspaceModeView>;
  /** Names or icons on the workspace tab strips. Not behind the dynamic-workspace flag. */
  tabStripMode(accessToken: string): Promise<{ mode: TabStripMode }>;
  setTabStripMode(accessToken: string, mode: TabStripMode): Promise<{ mode: TabStripMode }>;
  /** KEY-755: the caller's app theme id, or null when never picked. The one /api/me sub-route a PIN session may
   * also read (the tablet takes the unlocking person's theme); a guest session is refused it. */
  /** The stored theme and the themes offered here (`available`: a preview theme only where its flag is on). */
  myTheme(accessToken: string): Promise<{ theme: string | null; available?: string[] }>;
  /** KEY-755: an id the server doesn't know is refused with a 400. */
  setMyTheme(accessToken: string, theme: string): Promise<{ theme: string }>;
  /** Which guided-help tours this person has finished or dismissed. Never call this from a PIN or guest
   * session: InstanceScopeMiddleware admits only `/api/me` and `/api/me/notifications` for those, so it 403s
   * — and a confined session is never offered a tour in the first place. */
  tours(accessToken: string): Promise<TourCompletion[]>;
  recordTour(accessToken: string, tourId: string, outcome: TourOutcome): Promise<TourCompletion>;
  /** Run on-demand AI analysis of an instance (KEY-163). 404 if not readable/flag off; 409 if the org hasn't opted in. */
  analyseInstance(accessToken: string, id: string): Promise<InstanceInsights>;
  /** The open version upgrade a dial admin should see on this instance (Phase 2b), or null when there's none. */
  getPendingUpgrade(accessToken: string, instanceId: string): Promise<PendingUpgradeView | null>;
  /** Accept an offered optional upgrade — it will apply on its effective date. */
  acceptUpgrade(accessToken: string, instanceId: string, pendingId: string): Promise<void>;
  /** Decline an offered optional upgrade; the instance stays put (it may be re-offered later). */
  declineUpgrade(accessToken: string, instanceId: string, pendingId: string): Promise<void>;
  /** Apply an offered optional upgrade right now rather than waiting for its effective date (KEY-240). Same
   *  purge/reconcile as the scheduled apply and just as irreversible. Throws ApiError(409) when the upgrade is
   *  mandatory, the dial is a group child, the dial drifted to another version, or the group cascade would clear
   *  data in child dials (whose own admins get no notice of their own). */
  applyUpgradeNow(accessToken: string, instanceId: string, pendingId: string): Promise<void>;
  /** Save the dial admin's structure map for an open upgrade (KEY-805), replacing any saved one; `[]` clears it. Returns
   *  what it still leaves unanswered. Throws ApiError(400) with a `StructureRemapRefusal` for invalid entries, ApiError(409)
   *  with one when the data can't follow (a target holds its own), and ApiError(403) for anyone but the dial admin. */
  saveUpgradeStructureMap(accessToken: string, instanceId: string, pendingId: string, entries: StructureRemapEntry[]): Promise<StructureRemapSaved>;
  /** Download the data an upgrade would clear (statuses, history, attributes, documents of removed nodes). */
  exportUpgradeAtRisk(accessToken: string, instanceId: string, targetVersionId: string): Promise<AtRiskExport>;
  /** Toggle an organisation's AI-insights opt-in (KEY-163). When applyToDescendants is true the same
   *  value is applied to the org and every sub-organisation beneath it (KEY-174). */
  setOrganisationAiInsights(accessToken: string, organisationId: string, enabled: boolean, applyToDescendants?: boolean): Promise<void>;
  /** Set (or clear) an organisation's share restrictions (KEY-546) — who may hand its dials out, per
   *  channel. Platform-only (org.manage). A null field means inherit from the nearest ancestor that states
   *  one; there is no cascade, so a sub-organisation created later inherits rather than starting open. */
  setOrganisationSharePolicy(accessToken: string, organisationId: string, request: SetSharePolicyRequest): Promise<void>;
  /** An organisation's branding (KEY-718/719/731): its logos, what it inherits, and its logo policy. */
  getOrganisationBranding(accessToken: string, organisationId: string): Promise<OrganisationBrandingView>;
  /** Upload (or replace) one of an organisation's logos. Throws ApiError carrying the server's reason. */
  uploadOrganisationLogo(accessToken: string, organisationId: string, kind: OrganisationLogoKind, file: File): Promise<{ reference: string; url: string | null }>;
  /** Clear one of an organisation's logos, so the nearest ancestor's applies again. */
  clearOrganisationLogo(accessToken: string, organisationId: string, kind: OrganisationLogoKind): Promise<void>;
  /** Set (or clear, null = inherit) who below an organisation may change dial logos (KEY-718). */
  setOrganisationLogoPolicy(accessToken: string, organisationId: string, policy: ChildLogoPolicy | null): Promise<void>;
  /** Set the rungs a tier organisation sells (KEY-614; several since KEY-625) — the whole set, replacing
   *  what it sold. Platform-only (org.manage). Affects new buyers only — existing customers keep what they
   *  bought. Throws ApiError(400) for a rung it may not sell, or rungs of more than one family. */
  setOrganisationTierBinding(accessToken: string, organisationId: string, billingPlanIds: string | string[]): Promise<void>;
  /** Change one item's status along one dimension (itemised dials). Throws ApiError(403) if not permitted. */
  changeItemStatus(accessToken: string, id: string, request: ChangeItemStatusRequest): Promise<void>;
  /** Set (or clear, with an empty value) one declared node-attribute value on an instance node (KEY-161). */
  setNodeAttribute(accessToken: string, id: string, request: SetNodeAttributeRequest): Promise<void>;
  /** A dial's item status history (itemised dials). Pass `organisationId` to scope into a non-active
   * accessible org, exactly as {@link getInstance} does (KEY-132/KEY-221). */
  getInstanceItemHistory(accessToken: string, id: string, organisationId?: string): Promise<ItemStatusEvent[]>;
  /** A dial's activity log (KEY-464): section, action-point, item and field changes in one trail, newest
   * first. Both scope keys are optional and cumulative — neither for the whole dial, `sectionKey` for a
   * section and everything under it, both for one action point. */
  getInstanceActivity(
    accessToken: string,
    id: string,
    scope?: { sectionKey?: string | null; segmentKey?: string | null },
    organisationId?: string,
    /** Look back: only what happened after `since` (ISO), and each field row's before and after. */
    options?: { since?: string; includeValues?: boolean },
  ): Promise<InstanceActivityEvent[]>;

  // Segment conversations
  /** List a conversation's messages (oldest first) for a segment, or one item when `itemKey` is set. */
  listMessages(accessToken: string, instanceId: string, sectionKey: string, segmentKey: string, itemKey?: string): Promise<MessageDto[]>;
  /** The segment's own thread plus a transcript of every item thread within it. */
  listSegmentConversations(accessToken: string, instanceId: string, sectionKey: string, segmentKey: string): Promise<SegmentConversations>;
  /** A read-only transcript of everything said under a section, or across the whole dial when `sectionKey`
   * is omitted (KEY-559): newest first, each message with the node it was said on. 404 when the reader can
   * see no part of that scope. */
  listScopeTranscript(accessToken: string, instanceId: string, sectionKey?: string | null, limit?: number): Promise<ScopeTranscript>;
  /** Post a message to a segment conversation (created on first use). Returns the created message. */
  postMessage(accessToken: string, instanceId: string, request: PostMessageRequest): Promise<MessageDto>;
  /** Soft-delete a message — dial administrators only (KEY-582). It stays in every read as a tombstone
   * naming who deleted it and when. There is no edit call: a message is immutable once sent. */
  deleteMessage(accessToken: string, instanceId: string, messageId: string): Promise<void>;

  // Notification subscriptions (the caller's own opt-ins for an instance)
  listSubscriptions(accessToken: string, instanceId: string): Promise<NotificationSubscriptionDto[]>;
  setSubscription(accessToken: string, instanceId: string, request: SetSubscriptionRequest): Promise<void>;

  // In-app notification centre
  listNotifications(accessToken: string): Promise<NotificationDto[]>;
  markNotificationRead(accessToken: string, id: string): Promise<void>;
  markAllNotificationsRead(accessToken: string): Promise<void>;
  /** KEY-710: mark one notification done — it leaves the list (distinct from read). Idempotent; 404 if not the caller's. */
  dismissNotification(accessToken: string, id: string): Promise<void>;
  /** KEY-710: mark every notification in the caller's list done ("Clear all"). */
  dismissAllNotifications(accessToken: string): Promise<void>;

  // "What's New" release notes — published updates the caller hasn't dismissed, filtered to this app
  listUnreadReleases(accessToken: string): Promise<ReleaseNoteDto[]>;
  /** The full release history for this app (newest first), each flagged with whether the caller has
   * dismissed it — powers the "view all / dismissed updates" history view. */
  listAllReleases(accessToken: string): Promise<ReleaseHistoryDto[]>;
  /** Record "Don't show this again" for a release. Idempotent; unknown keys are ignored. */
  dismissRelease(accessToken: string, key: string): Promise<void>;
  /** Platform admins only: show a release to everyone (public) or to platform admins only. 403 for anyone else. */
  setReleasePublic(accessToken: string, key: string, isPublic: boolean): Promise<void>;

  // Saved dashboard views (KEY-134) — personal, cross-org
  listSavedViews(accessToken: string): Promise<SavedView[]>;
  createSavedView(accessToken: string, request: SaveViewRequest): Promise<SavedView>;
  updateSavedView(accessToken: string, id: string, request: SaveViewRequest): Promise<SavedView>;
  setSavedViewDefault(accessToken: string, id: string): Promise<void>;
  deleteSavedView(accessToken: string, id: string): Promise<void>;

  // Segment documents (direct-to-Blob via SAS)
  /** Reserve an upload slot: returns a document id + a short-lived SAS URL to PUT the bytes to. */
  requestDocumentUpload(accessToken: string, instanceId: string, request: RequestDocumentUploadRequest): Promise<DocumentUploadTicket>;
  /** PUT the file bytes straight to the SAS URL (no API auth — the SAS authorises it). */
  uploadDocumentBytes(uploadUrl: string, file: File): Promise<void>;
  /** Confirm the bytes landed; fires the upload-status-automation rule (auto-sets item status). Idempotent. */
  confirmDocumentUploaded(accessToken: string, instanceId: string, documentId: string): Promise<void>;
  listDocuments(accessToken: string, instanceId: string, sectionKey?: string, segmentKey?: string): Promise<DialDocument[]>;
  /** One sorted, paged page of the dial file browser, each row carrying its resolved location labels.
   *
   * Deliberately separate from {@link listDocuments}, which stays UNPAGINATED: the workspace resolves every
   * file-backed attribute's chips against the whole array that call returns, so paging it would blank them.
   * Both routes answer through one shared visibility filter, so the two can never disagree about what a
   * caller may see — only about how much of it arrives at a time. */
  browseDocuments(
    accessToken: string,
    instanceId: string,
    options?: {
      sectionKey?: string | null;
      segmentKey?: string | null;
      sort?: DocumentBrowseSort;
      dir?: "asc" | "desc";
      page?: number;
      pageSize?: number;
      /** Exact, case-insensitive tag labels; several OR together. Sent as a repeated `tag` parameter. */
      tags?: string[] | null;
      /** Free-text substring, matched against the filename or the tag label. ANDs with `tags`. */
      q?: string | null;
      /** A Group Key Dial's rolled-up Files tab (KEY-643): its own files plus every subdial's, each row naming
       * its dial, and the page carrying `dials`. Must not be combined with `sectionKey`/`segmentKey` (400). */
      includeChildren?: boolean;
      /** With `includeChildren`, narrows to that one dial of the group. */
      dialId?: string | null;
    },
  ): Promise<DocumentBrowsePage>;
  /** Every version of the file the given document belongs to, newest first (includes quarantined ones). */
  listDocumentVersions(accessToken: string, instanceId: string, documentId: string): Promise<DialDocument[]>;
  getDocumentDownloadUrl(accessToken: string, instanceId: string, documentId: string): Promise<DocumentDownloadTicket>;
  /** Zip selected (or, when `documentIds` is omitted, every visible Clean) document into one download.
   * Throws ApiError(400) with the plan's file count/size in its message when the selection is over the cap.
   *
   * `scope` narrows to a section/segment server-side, which is how "select all in this section" works over a
   * paged browser: pass the scope instead of the ids, and the server resolves the set itself. Pass both to
   * intersect them. It also carries the browse page's `tags`/`q`, so a zip taken beside a filtered table
   * matches what that table is showing rather than the whole scope.
   *
   * On a group's rolled-up table pass `includeChildren` (and `dialId` when narrowed) on every zip, ids or not —
   * see {@link DocumentZipRequest.includeChildren}. */
  downloadDocumentsZip(
    accessToken: string,
    instanceId: string,
    documentIds?: string[],
    scope?: {
      sectionKey?: string | null;
      segmentKey?: string | null;
      tags?: string[] | null;
      q?: string | null;
      includeChildren?: boolean;
      dialId?: string | null;
    },
  ): Promise<Blob>;
  /** This dial's own upload tags (KEY-499), oldest first. Also carried on the instance detail, which is
   * where the workspace reads them from; this route exists for callers that hold no detail. */
  listDocumentTags(accessToken: string, instanceId: string): Promise<DocumentTag[]>;
  /** Add an upload tag to this dial. Needs the same permission as uploading here. Throws ApiError(400) with
   * a human-readable reason for a blank/overlong label, a built-in name, a case-insensitive duplicate, or
   * the per-dial cap. */
  createDocumentTag(accessToken: string, instanceId: string, request: { label: string }): Promise<DocumentTag>;
  /** Withdraw a tag from this dial's picker (the documentVersions bar). Files already filed under it keep
   * the label — nothing is rewritten. */
  deleteDocumentTag(accessToken: string, instanceId: string, tagId: string): Promise<void>;
  /** Delete a document for good — metadata and bytes (instance-admin only; 403 otherwise). */
  deleteDocument(accessToken: string, instanceId: string, documentId: string): Promise<void>;
  /** Restore an earlier version as a new current version (instance-admin only; 403 otherwise). */
  rollbackDocument(accessToken: string, instanceId: string, documentId: string): Promise<void>;
  /** Switch the upload-status-automation rule on/off for one segment or item of an instance. */
  setUploadAutomation(accessToken: string, instanceId: string, request: SetUploadAutomationSettingRequest): Promise<void>;

  /** Upload (or replace) this instance's central hub logo — a dial admin action from the user app. Returns a
   * storage reference + a short-lived display URL. Requires the definition + instance gates and instance Admin. */
  uploadInstanceCentralImage(accessToken: string, instanceId: string, file: File): Promise<{ reference: string; url: string | null }>;
  /** Clear this instance's central logo override, reverting to the definition's image. */
  clearInstanceCentralImage(accessToken: string, instanceId: string): Promise<void>;

  // --- Kiosk auto-lock + PIN re-authentication (KEY-268) ---

  /** Set this dial's shared-device lock gate and idle-timeout override. A null/omitted override inherits
   * the definition's timeout. Requires the definition master switch and dial-admin authority. */
  setInstanceAutoLock(accessToken: string, instanceId: string, enabled: boolean, timeoutSecondsOverride?: number | null): Promise<void>;

  // --- Schedule time zone (KEY-276) ---

  /** Every time zone the server can resolve, for the dial's time-zone picker. */
  listTimeZones(accessToken: string): Promise<TimeZoneOption[]>;
  /** Set the IANA time zone this dial's schedules are read in; null clears it back to UTC. Requires
   * dial-admin authority, and an id the server can't resolve is a 400. */
  setInstanceTimeZone(accessToken: string, instanceId: string, timeZoneId: string | null): Promise<void>;
  /** Turns the hide-empty filter on or off for a dial (KEY-457). Dial-admin only. */
  setInstanceHideEmpty(accessToken: string, instanceId: string, enabled: boolean): Promise<void>;
  /** Splits a dial's section status edges per action point, or restores the single arc (KEY-494).
   * Dial-admin only. */
  setInstanceSectionEdgeSplit(accessToken: string, instanceId: string, enabled: boolean): Promise<void>;
  /** Puts Milo away on a dial for the whole household (KEY-715): the introduction's "No thanks". Anyone on the dial. */
  dismissMilo(accessToken: string, instanceId: string): Promise<void>;
  /** Puts Milo away on a dial, or brings him back (KEY-715). Dial-admin only. */
  setInstanceMiloHidden(accessToken: string, instanceId: string, hidden: boolean): Promise<void>;
  /** Records the caller's own hide-empty choice. Self-service — the server takes the user from the token. */
  setHideEmptyForMe(accessToken: string, instanceId: string, hideEmpty: boolean): Promise<void>;
  /** KEY-592: the caller answered the first-visit "follow this dial?" prompt, either way — stops it coming back.
   * Following itself goes through setSubscription. */
  answerFollowPrompt(accessToken: string, instanceId: string): Promise<void>;
  /** KEY-814: the caller closed the dial's welcome page; it stays closed until a later version changes it. */
  markWelcomeSeen(accessToken: string, instanceId: string): Promise<void>;
  /** Lock the device down to one dial: swaps a full session for a capability-free ticket and REVOKES the
   * refresh token passed in, so the session behind the lock screen can't be resumed. */
  lock(accessToken: string, instanceId: string, refreshToken: string | null): Promise<LockResponse>;
  /** Who can unlock this dial. Only callable with a lock ticket for the same dial — pass that ticket. */
  pinCandidates(lockTicket: string, instanceId: string): Promise<PinCandidate[]>;
  /** The dial face a locked screen displays: the normal InstanceDetail with members, attribute values and
   * every "who changed it" stripped server-side. Lock ticket only, and only for its own dial. Throws
   * ApiError(404) once auto-lock is switched off, which is the signal to stop showing the dial. */
  lockFace(lockTicket: string, instanceId: string): Promise<InstanceDetail>;
  /** Try an unlock PIN. The dial comes from the ticket, never the arguments. Throws ApiError(401) on any
   * failure — wrong PIN, no PIN, lockout, no grant — with no way to tell them apart. */
  pinVerify(lockTicket: string, userId: string, pin: string): Promise<PinVerifyResponse>;
  /** The caller's own PIN state, for the setup screen. Full session only. */
  pinStatus(accessToken: string): Promise<PinStatusResponse>;
  /** Set or replace the caller's own unlock PIN. Full session only; throws ApiError(400) with the server's
   * wording for a PIN that's the wrong length, non-numeric, or too guessable. */
  setPin(accessToken: string, pin: string): Promise<void>;
  /** Clear the caller's own unlock PIN. */
  clearPin(accessToken: string): Promise<void>;

  // Guest access (KEY-297)
  /** Open an invite link: asks the server to email a one-time code to the invited inbox. Always resolves —
   * a dead link answers exactly like a live one, so nothing can be probed from here. */
  guestLand(linkToken: string): Promise<void>;
  /** Exchange the link token + emailed code for an instance-bound guest session. Throws ApiError(401) on
   * any failure — dead link, wrong code, expired invite — with no way to tell them apart. */
  guestVerify(linkToken: string, code: string): Promise<GuestSessionResponse>;
  /** Invite an outsider to this instance (member-management authority). The result carries the raw link
   * token exactly once. Throws ApiError(409) when the email already has a real account here. */
  inviteGuest(accessToken: string, instanceId: string, request: InviteGuestRequest): Promise<InviteGuestResult>;
  listGuests(accessToken: string, instanceId: string): Promise<GuestGrantView[]>;
  /** Revoke a guest grant and every session it minted. */
  revokeGuest(accessToken: string, instanceId: string, grantId: string): Promise<void>;

  // Feeds (KEY-308). Member-management authority throughout, enforced server-side.
  /** Mint a connect code on your own dial. The raw code comes back once and is never retrievable again. */
  mintFeedCode(accessToken: string, instanceId: string, request: MintFeedCodeRequest): Promise<MintedFeedCode>;
  listFeedCodes(accessToken: string, instanceId: string): Promise<FeedCodeView[]>;
  revokeFeedCode(accessToken: string, instanceId: string, codeId: string): Promise<void>;
  /** Redeem a code against the dial you intend to feed from. Creates no access — it opens a draft and
   * tells you who the counterparty is. Every unusable code throws ApiError(400) with one message. */
  redeemFeedCode(accessToken: string, code: string, sourceInstanceId: string): Promise<RedeemedFeed>;
  /** Every feed touching this dial, both directions. */
  listFeeds(accessToken: string, instanceId: string): Promise<FeedView[]>;
  /** Replace what a feed discloses — the whole set, not a delta. Narrowing applies at once; widening
   * returns the feed to the recipient for fresh consent. */
  setFeedScope(accessToken: string, feedId: string, scopes: FeedScopeEntry[]): Promise<void>;
  offerFeed(accessToken: string, feedId: string, expiresAtUtc: string | null): Promise<void>;
  /** Accept an offer. The only call in the flow that creates access. */
  acceptFeed(accessToken: string, feedId: string): Promise<void>;
  declineFeed(accessToken: string, feedId: string): Promise<void>;
  /** End a feed. Either side may, with no counter-consent: the access goes, the record stays. */
  revokeFeed(accessToken: string, feedId: string): Promise<void>;
  listFeedEvents(accessToken: string, feedId: string): Promise<FeedEventView[]>;

  // Feed mappings — Mode B's authoring side (KEY-358/KEY-359). Nothing propagates yet.
  /** What this feed lets you name in a rule, pruned to what it discloses. 404s once the feed stops. */
  getFeedWiring(accessToken: string, feedId: string): Promise<FeedWiring>;
  /** Every wire into this dial, each with its health recomputed server-side on this read. */
  listFeedMappings(accessToken: string, instanceId: string): Promise<FeedMappingView[]>;
  /** Draw a wire. A refusal throws ApiError(400) whose `problems` name which end is wrong. */
  createFeedMapping(accessToken: string, instanceId: string, request: SaveFeedMappingRequest): Promise<FeedMappingView>;
  updateFeedMapping(accessToken: string, mappingId: string, request: SaveFeedMappingRequest): Promise<FeedMappingView>;
  deleteFeedMapping(accessToken: string, mappingId: string): Promise<void>;
  /** Pause or resume one wire, independently of stopping the feed. */
  pauseFeedMapping(accessToken: string, mappingId: string, paused: boolean): Promise<void>;

  // Mode B at rest (KEY-361).
  /** Everything a feed is waiting on you for, on this dial — pending and gate-blocked alike. */
  listFeedProposals(accessToken: string, instanceId: string): Promise<FeedProposalView[]>;
  /** Recompute from scratch and return what is waiting. The catch-up: a wire authored after the fact, or
   * a source that moved while write-back was switched off, produces no trigger of its own. */
  recomputeFeedProposals(accessToken: string, instanceId: string): Promise<FeedProposalView[]>;
  /** Accept a change or turn it down. A refusal is remembered, so the same change is not asked twice. */
  decideFeedProposal(accessToken: string, proposalId: string, accept: boolean): Promise<void>;
  /** What stopping this feed would leave behind. Readable from either side. */
  feedRevokeImpact(accessToken: string, feedId: string): Promise<FeedRevokeImpact>;
}

/** A message in a segment conversation. For a deleted message, `body` is empty and `isDeleted` is true, with
 * `deletedAtUtc` and the deleter's name alongside so it renders as a tombstone (KEY-582); the author and
 * `createdAtUtc` are still carried — that something was said, and by whom, survives its removal. */
export interface MessageDto {
  id: string;
  parentMessageId: string | null;
  body: string;
  authorUserId: string;
  authorEmail: string;
  authorDisplayName: string | null;
  createdAtUtc: string;
  editedAtUtc: string | null;
  isDeleted: boolean;
  /** "Public" (everyone on the action point) or "Restricted" (KEY-285 — the named people, plus dial
   * admins and Key Dials support). Absent on older payloads, which only ever carried public messages. */
  visibility?: "Public" | "Restricted";
  /** Everyone a restricted message names, the sender included; absent on a public one. */
  recipients?: MessageRecipient[] | null;
  /** When the message was deleted; null or absent while live (KEY-582). */
  deletedAtUtc?: string | null;
  /** Who deleted it — a dial administrator. Null while live, or when the deleter's account is gone. */
  deletedByEmail?: string | null;
  deletedByDisplayName?: string | null;
}

/** One person named on a restricted message. */
export interface MessageRecipient {
  userId: string;
  email: string;
  displayName: string | null;
}

export interface PostMessageRequest {
  sectionKey: string;
  segmentKey: string;
  body: string;
  parentMessageId?: string | null;
  /** Item this message belongs to (itemised dials); omit/"" for a segment-level thread. */
  itemKey?: string;
  /** Naming recipients IS choosing restricted mode (KEY-285) — there is no separate flag, so a mode
   * control and a recipient list can never disagree. Omit/empty for a public message. Ignored on a
   * reply to a restricted message, which the server forces to its parent's exact audience. */
  recipientUserIds?: string[];
}

/** One item's discussion thread within a segment (its key + messages). */
export interface ItemConversation {
  itemKey: string;
  messages: MessageDto[];
}

/** The whole discussion picture for one action point: its own thread plus a transcript of item threads. */
export interface SegmentConversations {
  segmentMessages: MessageDto[];
  itemThreads: ItemConversation[];
}

/** One message in a section or whole-dial transcript, with the node it was said on. `itemKey` is "" for the
 * action point's own thread. */
export interface TranscriptMessage {
  /** KEY-567: the dial it was said on — the dial asked for, or on a Group Key Dial's whole-dial transcript,
   * one of its child dials. Children of one group often share a definition, so the keys below only identify
   * a node together with this. */
  instanceId: string;
  instanceName: string;
  sectionKey: string;
  segmentKey: string;
  itemKey: string;
  message: MessageDto;
}

/** KEY-567: the part of one child dial's structure a group transcript's messages were said under, in the
 * child's own order and words — only the nodes a returned message sits on. */
export interface TranscriptChildDial {
  instanceId: string;
  sections: {
    key: string;
    label: string;
    showMnemonic: boolean;
    segments: { key: string; name: string; items: { key: string; name: string }[] }[];
  }[];
}

/** A section or whole-dial transcript (KEY-559): newest first, at most 300 messages. `truncated` is counted
 * over what this reader may see, so restricted messages they weren't addressed on never read as "more".
 * On a Group Key Dial the whole-dial transcript also holds its child dials' discussion (KEY-567), and
 * `childDials` names where in each child it was said; nearest level first, then by name. */
export interface ScopeTranscript {
  messages: TranscriptMessage[];
  truncated: boolean;
  childDials?: TranscriptChildDial[] | null;
}

/** A notification preference. Scope: both keys null = whole instance; section only; or section+segment.
 * `muted` true means this row suppresses an opt-in inherited from a broader scope. */
export interface NotificationSubscriptionDto {
  sectionKey: string | null;
  segmentKey: string | null;
  /** "InApp" | "Email" | "Sms" | "Push". Push (KEY-684) reaches the account's registered phones via the mobile
   * app; it is only offered when {@link ApiClient.pushStatus} says it is on. */
  channel: string;
  muted: boolean;
  /** KEY-592: set on a whole-dial row that belongs to a Group Key Dial ABOVE this one. Following a group follows
   * its children, so the row is broader than this dial's own whole-dial scope; a whole-dial mute here drops this
   * one child. Read-only from here — it is changed on the group. */
  inheritedFromInstanceId?: string | null;
  /** That group's name, for saying where the follow comes from. */
  inheritedFromName?: string | null;
}

/** Set one (scope, channel) preference. `mode`: "Subscribe" opts in here, "Mute" suppresses an inherited
 * opt-in here, "None" clears any row so the scope simply inherits from broader scopes. */
export type SubscriptionMode = "Subscribe" | "Mute" | "None";

export interface SetSubscriptionRequest {
  sectionKey?: string | null;
  segmentKey?: string | null;
  channel: string;
  mode: SubscriptionMode;
}

/** An in-app notification (the notification centre). */
export interface NotificationDto {
  id: string;
  dialInstanceId: string;
  sectionKey: string | null;
  segmentKey: string | null;
  type: string;
  summary: string;
  isRead: boolean;
  createdAtUtc: string;
}

/** One change within a "What's New" release. `category` is "New" | "Improved" | "Fixed". */
export interface ReleaseNoteItemDto {
  category: string;
  description: string;
}

/** A "What's New" release surfaced to the client (newest first). `publishedOn` is an ISO date ("yyyy-MM-dd").
 * `isPublic` is false for a release only platform admins are served — everyone else only ever gets public ones. */
export interface ReleaseNoteDto {
  key: string;
  title: string;
  publishedOn: string;
  items: ReleaseNoteItemDto[];
  isPublic: boolean;
}

/** A "What's New" release in the full history view (newest first). Same as {@link ReleaseNoteDto} plus
 * `dismissed`, which is true when the caller has already dismissed it. */
export interface ReleaseHistoryDto extends ReleaseNoteDto {
  dismissed: boolean;
}

/** Options for {@link createApiClient}. */
export interface ApiClientOptions {
  /**
   * Identifies the calling app, sent as the `X-KeyDials-Client` header on every authenticated request
   * ("web-user" / "web-admin"; the phone app sends "mobile"). The feedback feature uses it to tag submissions with their source app.
   */
  clientApp?: string;
  /**
   * Called when an authenticated request comes back 401 (KEY-604): a session that has quietly lapsed, most often
   * an access token that has simply aged out. Resolve with a fresh access token and the request is retried once
   * with it; resolve null when the session cannot be renewed and the caller gets the 401 as before. Never asked
   * about `/api/auth/*`, where a 401 is an answer ("wrong PIN") and not an expired session.
   */
  onUnauthorized?: (rejectedToken: string) => Promise<string | null>;
  /**
   * Called when an authenticated request comes back 402 (KEY-637): the account's card-free trial has ended and the
   * server has closed the app behind it. The server answers this on every gated route, so it can arrive from any
   * page — a dial that was open when the trial ran out, as much as a sign-in. Told BEFORE the caller sees the
   * error, in the same tick, so the app can take over the screen without the page first printing a refusal.
   * The call still rejects with the 402 afterwards, exactly as it did before this hook existed.
   *
   * `code` is the problem document's machine-readable reason (KEY-911): `"trial-ended"` or `"subscription-ended"`,
   * or null when the body carried none — so the app can word its screen before /api/me has been re-read.
   */
  onPaymentRequired?: (path: string, code: string | null) => void;
}

// --- CRM (KEY-311, "Switchboard" Phase 1): companies, people, enquiries, interaction logs ---

export type CrmEnquiryStatus = "New" | "Contacted" | "Qualified" | "Converted" | "Closed";
export type CrmPersonSource = "Manual" | "WebEnquiry" | "Campaign";
export type CrmInteractionType = "Call" | "Email" | "Meeting" | "Note";
export type CrmInteractionDirection = "None" | "Inbound" | "Outbound";

/** Whether the CRM is on offer to this caller — the org-type half of the gate (root org + first-level
 * partners only); the crm.read capability is the other half. Drives admin nav visibility. */
export interface CrmStatusResponse {
  eligible: boolean;
  /** The organisation the verdict was judged against, so a refusal can name it. */
  organisationName?: string | null;
  organisationType?: string | null;
  /**
   * Whether this caller may create, edit or deactivate enquiry sources — Top Co only (KEY-357).
   *
   * Take this from the server; do NOT infer it from `organisationType`. A Top Co admin focused into a
   * partner reports `"Partner"` here while legitimately being able to manage that partner's sources,
   * so client-side inference gets exactly the case the feature depends on wrong.
   */
  canManageSources?: boolean;
  /** Whether the effective organisation owns any source at all, active or inactive. Drives the nav rule. */
  hasSource?: boolean;
}

/**
 * Whether billing is switched on in this environment, and how far it is wired up (KEY-366).
 *
 * Consume this FAIL-CLOSED, unlike {@link CrmStatusResponse}: hide billing whenever the answer is missing,
 * still loading, or the request failed. The CRM's fail-open reasoning does not carry over — a nav item
 * shown on a guess costs a partner a moment's confusion, whereas a billing page shown on a guess invites
 * someone to go looking for money that may not be there.
 *
 * Note `enabled` and `stripeConfigured` are separate answers to separate questions: the module flag and the
 * credentials are set independently, so "on but not yet connected" is a real and expected state.
 */
export interface BillingStatusResponse {
  /** The Features:Billing module flag for this environment. */
  enabled: boolean;
  /** A Stripe secret key is present, so the API can call Stripe. */
  stripeConfigured: boolean;
  /** A webhook signing secret is present, so Stripe can call the API. */
  webhookConfigured: boolean;
  /** True when the configured key is a live one. Null when there is no key to judge. */
  liveMode?: boolean | null;
  /** The caller holds billing.manage, so mutations may be offered. */
  canManage: boolean;
}

/**
 * The billing headline for the admin dashboard (KEY-563): how many organisations currently entitle, and
 * what came in over the trailing 30 days. Scoped on billing.read, independently of the platform stats
 * endpoint's org.read scope.
 */
export interface BillingDashboardSummary {
  /** Organisations whose subscription is Active, Trialing or PastDue. */
  activeOrTrialingSubscriptions: number;
  /** Gross amount taken across every sale paid in the trailing 30 days, in minor units. */
  revenuePence: number;
  /** The currency `revenuePence` is denominated in. */
  currency: string;
}

/**
 * One rung of the price list (KEY-367).
 *
 * The meter fields are split by whether anything enforces them. `instancesPerDefinition` is the only cap
 * the system actually applies; the rest are recorded because pricing and quoting need them and are refused
 * by nothing. Don't infer which is which here — read {@link PlanMeterView} from the same response, so the
 * label can never disagree with what the server does.
 */
export interface BillingPlanView {
  id: string;
  /** Personal | Commercial | PartnerPersonal | PartnerCommercial | EducationPupils | EducationTrusts */
  family: string;
  /** The admin-side name, unique within the ladder, and the key the rung is matched on. */
  tierName: string;
  /** What buyers see when it differs from `tierName` (KEY-615). Null means buyers see the tier name. */
  displayName?: string | null;
  /** Listed on the website and buy page. False = on sale only by campaign QR code or direct link. */
  shownOnWebsite?: boolean;
  /** Relatives a buyer may invite to dials of their own (KEY-651). Null for a rung that is not a family pack. */
  familySeats?: number | null;
  /** The plan card's sentence in place of "N people on each" (KEY-691). Null shows the people count. */
  cardLine?: string | null;
  displayOrder: number;
  /** The price as authored, in pence. Read it with `period` — it is NOT normalised to a month. */
  amountPence: number;
  /** Monthly | Annual. Monthly on the direct and education families, Annual on the partner ones. */
  period: string;
  currency: string;
  trialDays: number;
  /** Whether the trial takes no card up front (KEY-431). Only ever true alongside a trial. */
  cardFreeTrial: boolean;
  /** The enforced cap. Null means unlimited; 0 means none allowed. They are different. */
  instancesPerDefinition?: number | null;
  maxDefinitions?: number | null;
  usersPerUnit?: number | null;
  /** Instance | Definition | School — what `usersPerUnit` is counted against. */
  userAllowanceUnit: string;
  aiGenerationsPerMonth?: number | null;
  childDialsIncludedPerGroup?: number | null;
  extraDefinitionPence?: number | null;
  extraInstancePence?: number | null;
  extraFullUserPence?: number | null;
  extraViewerSeatPence?: number | null;
  extraChildDialPence?: number | null;
  /** One Milo pack (300 requests a month) on this rung, in the plan's own period (KEY-703). Null sells no Milo. */
  extraMiloPackPence?: number | null;
  /** The whole Milo ladder this rung sells, smallest first (KEY-716): the 300 pack, then the larger rungs. Empty sells no Milo. */
  miloTiers?: MiloTier[] | null;
  collectionMethod: string;
  isRetired: boolean;
  /** How many organisations are on this rung — what makes retiring it routine or a conversation. */
  subscriberCount: number;
  /** The partner whose price list this belongs to, or null for Key Dials' own catalogue. */
  ownerOrganisationId: string | null;
  /** Resolved for display — a price list attributed to a GUID is unreadable. */
  ownerOrganisationName: string | null;
  /**
   * The partner's agreed share of this rung, in basis points (2500 is a quarter). Null where no split
   * has been agreed. Recorded against a sale when money arrives; nothing pays it out yet.
   */
  partnerShareBasisPoints: number | null;
  /**
   * Whether this rung can be bought self-serve. Read it rather than inferring: a partner's rung has no
   * buy route yet, and the UI must not offer one the server would refuse.
   */
  isSelfServe: boolean;
  /**
   * The prebuilt dials this tier sells, and how many of each. Empty on a tier that sells an allowance to
   * build your own — which is every Key Dials rung today.
   */
  includedDefinitions: PlanIncludedDefinitionView[];
  /**
   * The agreed floor on what Key Dials keeps from one sale of this rung, in pence (KEY-419). Null means no
   * floor has been agreed. Purely advisory/reporting — nothing on the platform enforces it automatically.
   */
  minimumFeePence: number | null;
  /** Whether this rung's owning partner may reprice it themselves, via `updateBillingPlanPrice` (KEY-420). */
  partnerMayEditPrice: boolean;
  /** Size of the invoiced free-dial pool (KEY-420) — free to the customer, invoiced to the partner manually. */
  freeDialPoolInvoicedSize: number | null;
  /** Size of the truly-free dial pool (KEY-420) — no invoice, no reporting, ever. */
  freeDialPoolTrueFreeSize: number | null;
  /** How many of `freeDialPoolInvoicedSize` are left. Null when the pool does not exist. */
  freeDialPoolInvoicedRemaining: number | null;
  /** How many of `freeDialPoolTrueFreeSize` are left. Null when the pool does not exist. */
  freeDialPoolTrueFreeRemaining: number | null;
  /**
   * The tier organisation this rung's buyers are created under (KEY-659), matched on the tier — a tier's
   * monthly and annual rows name the same one. Null means none is set up, so a buyer lands directly under
   * the seller. Also null for a scoped caller on a rung they do not own.
   */
  landingOrganisationId?: string | null;
  /** Resolved for display alongside `landingOrganisationId`. */
  landingOrganisationName?: string | null;
}

/** One prebuilt dial a tier sells. */
export interface PlanIncludedDefinitionView {
  dialDefinitionId: string;
  /** Resolved for display; "(removed dial)" where the definition has gone. */
  name: string;
  instancesIncluded: number;
  displayOrder: number;
}

/** The server's own verdict on a meter. Label by this rather than deciding in the UI. */
export interface PlanMeterView {
  meter: string;
  label: string;
  isEnforced: boolean;
  note: string;
}

/** A partner's Stripe Connect account and where onboarding has got to (partner plans, phase 3). */
export interface PartnerPayoutAccountView {
  organisationId: string;
  organisationName: string;
  /** Null until onboarding has been started for them. */
  stripeAccountId: string | null;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  /** Stripe's own words for what is outstanding. Shown verbatim — paraphrasing sends people after the wrong document. */
  disabledReason: string | null;
  /** The only readiness test that counts. Read it rather than deriving one in the UI. */
  isReadyForPayouts: boolean;
  /** When Stripe was last asked. Null means never — a stale "ready" is the dangerous state. */
  lastCheckedAtUtc: string | null;
  /** Rungs on their price list. Zero means nothing to sell yet. */
  planCount: number;
  /** Stripe's account type. Null on accounts opened before it was recorded — all of which are Express. */
  accountType: string | null;
  /** Whether a sign-in link to their Stripe dashboard can be minted. Standard accounts have none. */
  hasHostedDashboard: boolean;
}

/**
 * Whose share an affiliate's commission comes out of (KEY-502/514).
 *
 * `"RungOwner"` is the partner where the rung has one, else Top Co — one value carrying the no-partner
 * fallback in its meaning. `"KeyDials"` is always Top Co, even on a partner's rung, and needs
 * `billing.manage` to set.
 */
export type CommissionFundingDto = "RungOwner" | "KeyDials";

/** Where a resolved figure came from, so the UI can label it rather than imply it was typed. */
export type TermsSourceDto = "None" | "Profile" | "OrganisationDefault" | "PlatformDefault";

export interface AffiliateStatusResponse {
  /** Whether Features:Affiliates is on in this environment. */
  enabled: boolean;
  /** billing.manage OR affiliate.terms.manage. */
  canManageTerms: boolean;
  /** org.members.manage — a different job from pricing, and gated separately. */
  canEnrol: boolean;
  /**
   * billing.manage only. Read it rather than inferring one from canManageTerms: the routes accept either
   * capability but refuse this VALUE from the narrower one, so the UI must hide the option rather than
   * offer a dead end.
   */
  canFundFromKeyDials: boolean;
}

export interface AffiliateOrganisationSummary {
  organisationId: string;
  organisationName: string;
  /** The nearest Partner ancestor, or null where the affiliate sits under Top Co and Key Dials covers it. */
  fundingPartnerId: string | null;
  fundingPartnerName: string | null;
  defaultCommissionBasisPoints: number | null;
  defaultFunding: CommissionFundingDto;
  /** The highest rate that could be agreed here today. Server-resolved — never compute one client-side. */
  ceilingBasisPoints: number;
  profileCount: number;
}

export interface AffiliateOrganisationListResponse {
  items: AffiliateOrganisationSummary[];
}

export interface AffiliateProfileView {
  id: string;
  userId: string;
  userName: string;
  email: string | null;
  /** What this affiliate states themselves. Null means they inherit. */
  ownCommissionBasisPoints: number | null;
  ownFunding: CommissionFundingDto | null;
  /**
   * The resolved rate, or null meaning NO RATE AGREED ANYWHERE — the affiliate earns nothing. Not zero:
   * the two are different facts.
   */
  commissionBasisPoints: number | null;
  funding: CommissionFundingDto;
  /** Rate and funding resolve independently, so these two can differ. */
  rateSource: TermsSourceDto;
  fundingSource: TermsSourceDto;
  isActive: boolean;
  /**
   * False where the person no longer holds a membership. The profile deliberately survives one being
   * removed (stage 4 freezes terms onto sales), so the list says so rather than looking live.
   */
  isStillAMember: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  /**
   * The code this affiliate hands out (KEY-514), e.g. "PEP-JSMITH-7K", carried on their links as `?ref=`.
   * Never changes once issued. Null only for a profile the server has not yet backfilled.
   */
  referralCode: string | null;
  /** Customer organisations this affiliate has referred. */
  referralCount: number;
  /** Commission booked to date, in pence — a ledger figure; the platform pays nothing out. */
  commissionPence: number;
  /** The discount code set on this affiliate; null inherits the organisation's. */
  ownBuyerDiscountCode: string | null;
  /** The discount code their link actually gives a buyer, or null for none. */
  buyerDiscountCode: string | null;
  /** The organisation holding this affiliate's free dial (KEY-937), once set up. */
  compOrganisationId: string | null;
  compOrganisationName: string | null;
  /** Where the free dial stands; null where none has been set up (no rung chosen, or set-up failed). */
  compStatus: AffiliateCompStatusDto | null;
}

/** Where an affiliate's free dial (KEY-937) stands. */
export type AffiliateCompStatusDto = "Active" | "Paused" | "PaysOwnWay";

/** A rung an affiliate organisation may comp its affiliates onto (KEY-937). */
export interface AffiliateCompPlanOption {
  planId: string;
  /** Family and public name, e.g. "Personal · Affiliate". */
  label: string;
  amountPence: number;
}

/** What a free-dial set-up run found or did (KEY-937). */
export interface AffiliateCompResult {
  outcome: "NoPlan" | "SetUp" | "Paused" | "PaysOwnWay";
  organisationId: string | null;
  dialsAllocated: number;
  dialsFailed: number;
}

/** One of the caller's own affiliate arrangements and where its link lands (KEY-514). */
export interface MyAffiliateLinkView {
  profileId: string;
  organisationName: string;
  referralCode: string;
  fundingPartnerName: string | null;
  /** The partner's published website page; null lands the link on the home page. */
  fundingPartnerPageSlug: string | null;
  isActive: boolean;
  referralCount: number;
  /** Commission booked to date, in pence (a ledger figure, not a payout). */
  commissionPence: number;
}

/** One rung this affiliate may earn on, and what a sale of it splits three ways at the current rate. */
export interface ReferableRungView {
  planId: string;
  tierName: string;
  family: string;
  amountPence: number;
  partnerShareBasisPoints: number | null;
  /** The rung that decides the ceiling — the one to look at when a rate is refused. */
  isBinding: boolean;
  affiliatePence: number;
  partnerPence: number;
  keyDialsPence: number;
}

export interface AffiliateOrganisationDetail {
  organisationId: string;
  organisationName: string;
  fundingPartnerId: string | null;
  fundingPartnerName: string | null;
  defaultCommissionBasisPoints: number | null;
  defaultFunding: CommissionFundingDto;
  platformDefaultCommissionBasisPoints: number | null;
  ceilingBasisPoints: number;
  /** The same figure under Key-Dials funding, so the preview answers instantly when the dropdown changes. */
  ceilingBasisPointsIfKeyDialsFunded: number;
  profiles: AffiliateProfileView[];
  referableRungs: ReferableRungView[];
  /**
   * The funding partner's published page on the website (`/partners/{slug}/`), where a referral link lands.
   * Null under Top Co or with no live page — the link then lands on the home page.
   */
  fundingPartnerPageSlug: string | null;
  /** The discount code every affiliate link here gives a buyer, unless the affiliate has their own. */
  defaultBuyerDiscountCode: string | null;
  /** The rung every affiliate enrolled here is comped onto (KEY-937); null hands out nothing. */
  compPlanId: string | null;
  /** The rungs this organisation may choose for `compPlanId`. */
  compPlanOptions: AffiliateCompPlanOption[];
}

export interface SaveAffiliateOrgSettingsRequest {
  defaultCommissionBasisPoints: number | null;
  /** Null keeps the organisation on "RungOwner". "KeyDials" requires billing.manage. */
  funding: CommissionFundingDto | null;
}

export interface SaveAffiliateTermsRequest {
  /** Null clears this affiliate's own rate, returning them to the organisation default. */
  commissionBasisPoints: number | null;
  funding: CommissionFundingDto | null;
}

export interface PartnerPayoutAccountListResponse {
  items: PartnerPayoutAccountView[];
}

/** Stripe's hosted onboarding. Single-use and short-lived — open it, never store it. */
export interface PartnerOnboardingLinkView {
  url: string;
}

/** A single-use sign-in to the partner's own Express dashboard. A credential: open it, never store it. */
export interface PartnerDashboardLinkView {
  url: string;
}

/** One partner's earnings over a window, and whether Stripe agrees it has moved. */
export interface PartnerEarningsView {
  organisationId: string;
  organisationName: string;
  stripeAccountId: string | null;
  saleCount: number;
  grossPence: number;
  /** Summed from what was frozen onto each sale — never recomputed from today's rate. */
  partnerSharePence: number;
  keyDialsSharePence: number;
  /** Sales attributed to them with no share ever agreed. A question for a person, not a zero. */
  salesWithNoAgreedShare: number;
  /** What Stripe says it moved, or null when it could not be asked. */
  transferredPence: number | null;
  /** Ours minus Stripe's. Positive means we believe they are owed more than has moved. */
  differencePence: number | null;
  /** Gross refunded to buyers in the window, on sales attributed to this partner. */
  refundedPence: number;
  /** What was actually taken back off them — less than their share of the refunds if a transfer wasn't reversed. */
  partnerClawbackPence: number;
  /** What they keep after clawbacks. `partnerSharePence` stays gross, to stay comparable with Stripe's transfers. */
  partnerNetSharePence: number;
}

/** One payment we took, and how much of it has since gone back. */
export interface SaleRecordView {
  /** The affiliate organisation this sale earned commission for (KEY-514), or null. */
  affiliateOrganisationName: string | null;
  /** That commission, in pence, frozen at the time of sale; null when none. */
  affiliatePence: number | null;
  id: string;
  stripeInvoiceId: string;
  organisationId: string;
  organisationName: string;
  planDescription: string;
  amountPence: number;
  currency: string;
  paidAtUtc: string;
  partnerOrganisationId: string | null;
  partnerOrganisationName: string | null;
  /** What the partner earned, frozen at the time of sale. Null when nobody did. */
  partnerSharePence: number | null;
  /** Already refunded. Equal to `amountPence` means there is nothing left to give back. */
  refundedPence: number;
}

/** What a partner can see about their own money. No Key Dials share — that is our figure, not theirs. */
export interface PartnerOwnEarningsView {
  organisationId: string;
  organisationName: string;
  fromUtc: string;
  toUtc: string;
  /** Their Stripe account, or null when nobody has opened one for them yet. */
  account: PartnerPayoutAccountView | null;
  saleCount: number;
  grossPence: number;
  /** What they earned, gross, from what was frozen onto each sale. */
  partnerSharePence: number;
  refundedPence: number;
  /** What was actually taken back off them when those refunds went through. */
  partnerClawbackPence: number;
  partnerNetSharePence: number;
  salesWithNoAgreedShare: number;
  /** What Stripe says it moved, or null when it could not be asked — never zero for "unknown". */
  transferredPence: number | null;
  differencePence: number | null;
  sales: PartnerOwnSaleView[];
}

export interface PartnerOwnSaleView {
  customerName: string;
  planDescription: string;
  amountPence: number;
  currency: string;
  paidAtUtc: string;
  partnerSharePence: number | null;
  refundedPence: number;
}

/** Whether a site build was requested. */
export interface SitePublishStartedView {
  started: boolean;
  /**
   * Days left on the publish token, when GitHub reported one. Present on a build that STARTED — the
   * point is to warn while everything still works.
   */
  tokenExpiresInDays?: number | null;
}

/** Whether the Publish site button can work, asked before anybody presses it. */
export interface SitePublishStatusView {
  configured: boolean;
  /** Whether GitHub still accepts the token. Null means we could not find out — treat as fine. */
  tokenValid?: boolean | null;
  /** Days until the token stops working, if it has an expiry. */
  tokenExpiresInDays?: number | null;
  /** A specific thing to go and fix, or null when there is nothing to say. */
  problem?: string | null;
  /**
   * The publish queued for later, if there is one (KEY-505). Carried here rather than on a route of its
   * own because the screen already asks this question on load.
   */
  scheduled?: ScheduledSitePublishView | null;
}

/** Queueing a site publish for later (KEY-505). */
export interface ScheduleSitePublishRequest {
  /**
   * The moment to publish, as a UTC instant. The admin types local wall-clock into a `datetime-local`
   * input and the browser converts — the server never guesses a zone.
   */
  scheduledForUtc: string;
}

/** A publish waiting for its moment. */
export interface ScheduledSitePublishView {
  id: string;
  /**
   * When it is due. It fires within five minutes AFTER this instant and never before it, which is why
   * the screen says "about 09:00" rather than promising the minute.
   */
  scheduledForUtc: string;
  /** Who queued it, for a screen two people share. */
  requestedByName?: string | null;
  requestedAtUtc: string;
  /**
   * How many pages were waiting for a build when it was queued. A hint rather than a guarantee — the
   * marker behind it is cleared by an anonymous route — so word it as one.
   */
  pagesWaitingAtScheduleTime: number;
  /** Why the last attempt did not go, when one has failed. */
  lastError?: string | null;
  attemptCount: number;
}

/** The queued publish, plus anything worth saying about it. */
export interface ScheduleSitePublishResponse {
  scheduled: ScheduledSitePublishView;
  /**
   * Today this means one thing: the publish token expires before the publish is due to run. Not a
   * refusal — the fix can happen any time between now and then.
   */
  warning?: string | null;
}

/** Where an uploaded image can be loaded from. */
export interface SiteImageUploadedView {
  url: string;
}

export interface SaleRecordListResponse {
  items: SaleRecordView[];
}

/** Money given back. `partnerSharePence` is what was actually clawed back, not what was owed. */
export interface RefundRecordView {
  id: string;
  stripeRefundId: string;
  saleRecordId: string | null;
  amountPence: number;
  currency: string;
  partnerOrganisationId: string | null;
  partnerSharePence: number;
  /** False on a partner sale means the partner kept their share and we absorbed it. */
  transferReversed: boolean;
  refundedAtUtc: string;
}

export interface PartnerPayoutReport {
  from: string;
  to: string;
  partners: PartnerEarningsView[];
  /** False means the cross-check is withheld entirely — the figures are our ledger alone. */
  stripeAsked: boolean;
}

export interface BillingPlanListResponse {
  plans: BillingPlanView[];
  meters: PlanMeterView[];
}

/** A dial to sell as part of a tier. */
export interface PlanIncludedDefinitionRequest {
  dialDefinitionId: string;
  /** Copies the buyer gets. At least one — to include none, leave the dial off the tier. */
  instancesIncluded: number;
}

export interface SaveBillingPlanRequest {
  /** Whose price list this rung joins. Null keeps it in Key Dials' own catalogue. */
  ownerOrganisationId?: string | null;
  /**
   * The prebuilt dials this tier sells. Written WHOLE — an omitted or empty list means the tier includes
   * no dials, never "leave the ones it has alone".
   */
  includedDefinitions?: PlanIncludedDefinitionRequest[];
  /** The partner's share in basis points. Only valid alongside an owner. */
  partnerShareBasisPoints?: number | null;
  family: string;
  tierName: string;
  /** The buyer-facing name (KEY-615). Sent whole like every other field: omitting it clears it. */
  displayName?: string | null;
  displayOrder: number;
  amountPence: number;
  period: string;
  trialDays: number;
  /** Whether the trial takes no card up front (KEY-431). Refused without a trial length. */
  cardFreeTrial: boolean;
  instancesPerDefinition?: number | null;
  maxDefinitions?: number | null;
  usersPerUnit?: number | null;
  userAllowanceUnit: string;
  aiGenerationsPerMonth?: number | null;
  childDialsIncludedPerGroup?: number | null;
  extraDefinitionPence?: number | null;
  extraInstancePence?: number | null;
  extraFullUserPence?: number | null;
  extraViewerSeatPence?: number | null;
  extraChildDialPence?: number | null;
  /** One Milo pack (300 requests a month) on this rung, in the plan's own period (KEY-703). Null sells no Milo. */
  extraMiloPackPence?: number | null;
  /** The LARGER Milo rungs on top of the 300 pack (KEY-716) — never the 300 itself. Sent whole; omitted leaves only the pack. */
  miloTiers?: MiloTier[] | null;
  collectionMethod: string;
  currency?: string;
  /** The agreed floor on what Key Dials keeps from one sale (KEY-419). Null means none agreed. */
  minimumFeePence?: number | null;
  /** Whether the owning partner may reprice this rung themselves (KEY-420). */
  partnerMayEditPrice?: boolean;
  /** The invoiced free-dial pool's size (KEY-420). Null means no such allowance. */
  freeDialPoolInvoicedSize?: number | null;
  /** The truly-free dial pool's size (KEY-420). Null means no such allowance. */
  freeDialPoolTrueFreeSize?: number | null;
  /**
   * Makes the rung a family pack (KEY-651): relatives a buyer may invite, each to their own copy of the included
   * dials. Null for an ordinary rung. Sent whole like every other field: omitting it clears it.
   */
  familySeats?: number | null;
  /**
   * The plan card's sentence in place of "N people on each" (KEY-691), e.g. "Share access with up to 3 people
   * and control exactly what they can see". Null or blank clears it. Sent whole: omitting it clears it.
   */
  cardLine?: string | null;
}

/** The narrow price-only edit behind `updateBillingPlanPrice` (KEY-420). */
export interface UpdatePlanPriceRequest {
  /** The new price, in pence, in whatever period the rung already authors. */
  amountPence: number;
}

/**
 * One scheduled email in a plan's trial drip sequence (KEY-433). Since KEY-665 a step normally has wording
 * of its own; a step from before then sends shared catalogue wording (`isShared`, with its `templateKey`),
 * and its `subject`/`bodyHtml` show that shared wording as the rung's owner has it, so it can be copied into
 * a plan-specific step. `bodyHtml` is null for a caller who can see the rung but not edit it.
 */
export interface TrialDripStepView {
  id: string;
  dayOffset: number;
  subject: string;
  bodyHtml: string | null;
  isShared: boolean;
  templateKey: string | null;
  templateDescription: string | null;
}

export interface TrialDripTemplateOption {
  key: string;
  description: string;
}

export interface TrialDripStateResponse {
  steps: TrialDripStepView[];
  templates: TrialDripTemplateOption[];
  /** The plan's trial length, so the editor can warn about a step scheduled beyond it. */
  trialDays: number;
  /** The merge fields a trial email may use, with samples for the preview. */
  tokens: EmailTokenView[];
  /** Whether bodies were included and a save would be accepted. */
  canEdit: boolean;
  /** Whether the trial takes no card. Trial emails go only to those trials (KEY-654); on any other plan they never send. */
  cardFreeTrial: boolean;
}

/**
 * One step of a save: either its own `subject` + `bodyHtml`, or a shared `templateKey` — never both.
 * `id` keeps an existing step (the send ledger is keyed by it, so a kept id is what stops a re-save
 * resending); omit it for a new step, including one copied from another plan.
 */
export interface SaveTrialDripStepRequest {
  id?: string | null;
  dayOffset: number;
  subject?: string | null;
  bodyHtml?: string | null;
  templateKey?: string | null;
}

/** The plan's whole sequence: steps given are kept or added, steps left out are removed. */
export interface SaveTrialDripRequest {
  steps: SaveTrialDripStepRequest[];
}

/**
 * One of a campaign's own trial emails (KEY-661). `replacesPlanStepId` names the plan email it sends
 * instead of; null is an extra. `replacementMissing` = it names a plan email that has since gone, so it
 * sends as an extra.
 */
export interface CampaignTrialEmailView {
  id: string;
  dayOffset: number;
  subject: string;
  bodyHtml: string;
  replacesPlanStepId: string | null;
  replacementMissing: boolean;
}

/** A campaign's trial emails: the plan's (read-only; bodies only on the campaign owner's own rung) and its own. */
export interface CampaignTrialEmailsResponse {
  billingPlanId: string;
  trialDays: number;
  planSteps: TrialDripStepView[];
  campaignSteps: CampaignTrialEmailView[];
  tokens: EmailTokenView[];
  /** Whether the campaign's plan takes no card. Trial emails go only to those trials (KEY-654). */
  cardFreeTrial: boolean;
}

export interface SaveCampaignTrialEmailRequest {
  /** Keep it for an existing email — the send ledger is keyed by it. Omit for a new one. */
  id?: string | null;
  dayOffset: number;
  subject: string;
  bodyHtml: string;
  replacesPlanStepId?: string | null;
}

/** The campaign's whole set; empty = "reset to the plan's emails". */
export interface SaveCampaignTrialEmailsRequest {
  steps: SaveCampaignTrialEmailRequest[];
}

export interface BillingDriftView {
  organisationId: string;
  organisationName: string;
  /**
   * A `DriftKind` name. The list grew past the five originally documented here and the frontend did not
   * notice, so four kinds rendered as raw C# to readers — web-admin's `DRIFT_KINDS` is now the checked
   * mirror of it, with a test that fails when the server adds another.
   *
   * CapDiffersFromPlan | MissingGrant | UsageOverCap | EntitlementWithoutSubscription | PlanMissing |
   * DefinitionsOverAllowance | AddOnNotBilled | SeatsOverAllowance | AiOverAllowance |
   * IncludedDialNotProvided | SaleWithNoAgreedShare | PartnerRungHasNoTier | TrialEndedButStillTrialing
   */
  kind: string;
  definitionName?: string | null;
  planValue?: number | null;
  actualValue?: number | null;
  usedInstances: number;
  detail: string;
  detectedAtUtc: string;
}

export interface BillingDriftReport {
  findings: BillingDriftView[];
  /** Null when the nightly job has never run — which is not the same as finding nothing. */
  lastRunAtUtc?: string | null;
  /** The meters this report cannot speak for. Show them, or an empty list reads as all-clear. */
  unmeasuredMeters: PlanMeterView[];
}

/** A tier somebody can buy themselves (KEY-368). */
/**
 * Whose shop the buy page is (the seller of the price the visitor arrived for): the name, logo and colour
 * they already show the public. Null from {@link ApiClient.getPlanSeller} means Key Dials' own price.
 */
export interface PublicSellerBrandingView {
  displayName: string;
  logoUrl: string | null;
  accentColour: string | null;
  /** Their partner page's slug, only when it is published — for a link back to it. */
  pageSlug: string | null;
}

export interface PurchasablePlanView {
  id: string;
  family: string;
  familyLabel: string;
  /** The key — pair a rung's monthly and annual prices on this. Show `displayName`. */
  tierName: string;
  /**
   * The name to show the buyer (KEY-615): the display name, or the tier name when there is none.
   * Optional only so a client built against an older API keeps rendering — fall back to `tierName`.
   */
  displayName?: string | null;
  amountPence: number;
  /** "Monthly" or "Annual" — never converted; the figure is as authored. */
  period: string;
  currency: string;
  /** Trial length in days. Zero means no trial; whether a card is taken up front is `cardFreeTrial`'s answer. */
  trialDays: number;
  maxDefinitions: number | null;
  instancesPerDefinition: number | null;
  usersPerUnit: number | null;
  /** Whether the price already includes VAT. Personal and Partner Personal are inclusive; everything else is ex-VAT. */
  taxInclusive: boolean;
  /** Whether the trial takes no card up front (KEY-431). The buy page words the trial around this. */
  cardFreeTrial: boolean;
  /**
   * The dials this rung hands over on purchase (KEY-616), in catalogue order — "4 Personal Estate Plan
   * dials". Empty for a build-your-own tier. Optional only for a client built against an older API.
   */
  includedDefinitions?: IncludedDefinitionView[];
  /** Relatives the buyer may invite to dials of their own (KEY-651); null unless the rung is a family pack. */
  familySeats?: number | null;
  /** The seller's sentence for the card (KEY-691), shown in place of "N people on each". */
  cardLine?: string | null;
}

/** One dial a plan hands over on purchase (KEY-616): what it is and how many copies. */
export interface IncludedDefinitionView {
  name: string;
  instances: number;
}

export interface StartCheckoutRequest {
  planId: string;
  email: string;
  /** The campaign whose QR code this came from (KEY-432). When set the tier is taken from the CAMPAIGN
   *  and `planId` is ignored — the key is printed on a poster, so the caller's plan id is not trusted. */
  campaignKey?: string | null;
  /** Whether they ticked the marketing box. Only ever grants consent, never withdraws it. */
  marketingOptIn?: boolean;
  /**
   * The End User Terms version the buyer accepted (KEY-618). The public checkout refuses anything but the
   * current version, with a message saying the terms changed.
   */
  termsVersion?: string | null;
  /** The buyer confirmed they are 18 or over. Required by the public checkout. */
  ageConfirmed?: boolean;
  /**
   * A discount code the buyer typed (KEY-642). Checked against the plan actually being bought; one that
   * does not apply refuses the checkout with a 400 rather than charging full price. Blank means none.
   */
  discountCode?: string | null;
  /**
   * The affiliate referral code the buyer arrived with (KEY-514), from `?ref=`. Credited when the purchase
   * completes; an unknown or dead code is ignored and never refuses the checkout.
   */
  affiliateCode?: string | null;
}

/** What a discount code takes off a plan (KEY-642). Only returned for a code that applies to it. */
export interface DiscountCodePreviewView {
  /** As Stripe holds it, upper-cased. */
  code: string;
  percentOff: number | null;
  amountOffPence: number | null;
  /** Stripe's word: "once", "repeating" or "forever". */
  duration: string;
  /** How long a "repeating" discount lasts; null otherwise. */
  durationInMonths: number | null;
}

/** Where a discount code stands (KEY-642 follow-up). The admin's own switch comes first. */
export type DiscountCodeStatus = "Active" | "Deactivated" | "Expired" | "UsedUp" | "CouponInvalid";

/**
 * A discount code as the admin Billing page lists it (KEY-642 follow-up) — read live from Stripe, which is
 * the only place codes are kept.
 */
export interface DiscountCodeView {
  /** Stripe's promotion code id (`promo_…`), which the admin actions address. */
  id: string;
  code: string;
  /** What invoices call the discount. */
  name: string | null;
  percentOff: number | null;
  amountOffPence: number | null;
  /** "once", "repeating" or "forever". */
  duration: string;
  durationInMonths: number | null;
  /** The last moment the code can be used; null for no deadline. */
  expiresAtUtc: string | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  active: boolean;
  status: DiscountCodeStatus | string;
  /** Plan families it is limited to; empty for all. */
  families: string[];
  /** Plans it is limited to; empty for all. */
  planIds: string[];
  /** A limit was set on the coupon in Stripe; this page cannot change it. */
  limitsOnCoupon: boolean;
  createdAtUtc: string | null;
}

/**
 * A discount code as a partner sees it beside their own plans (KEY-664). Read-only; no Stripe id or usage,
 * and `planIds` names only the caller's own plans.
 */
export interface PartnerDiscountCodeView {
  code: string;
  percentOff: number | null;
  amountOffPence: number | null;
  /** "once", "repeating" or "forever". */
  duration: string;
  durationInMonths: number | null;
  /** The last moment the code can be used; null for no deadline. */
  expiresAtUtc: string | null;
  /** Which of the caller's own plans it can be used on. */
  planIds: string[];
}

/** A new percentage discount code. Everything but its limits is fixed once made. */
export interface CreateDiscountCodeRequest {
  code: string;
  /** What invoices call it; defaults to the code. */
  name?: string | null;
  percentOff: number;
  duration: "once" | "repeating" | "forever";
  durationInMonths?: number | null;
  /** The last day it can be used, yyyy-MM-dd, read in London time. Null for no deadline. */
  lastDay?: string | null;
  maxRedemptions?: number | null;
  families?: string[];
  planIds?: string[];
}

/** A code's plan limits, replaced whole. Empty lifts that limit. */
export interface SetDiscountCodeLimitsRequest {
  families?: string[];
  planIds?: string[];
}

/**
 * One block of a legal document. Text carries only `**bold**`, `*italic*` and `[label](href)`; hrefs
 * starting with "/" are pages on the marketing site.
 */
export type LegalBlock =
  | { h: string }
  | { h3: string }
  | { p: string }
  | { ul: string[] }
  | { ol: string[] }
  | { table: { head: string[]; rows: string[][] } };

/** A published legal document (KEY-618), from the one versioned copy the site is also built from. */
export interface LegalDocumentView {
  key: string;
  /** Its path on the marketing site, e.g. "terms-and-conditions". */
  slug: string;
  title: string;
  /** The "last updated" date, yyyy-MM-dd — what an acceptance records. */
  version: string;
  blocks: LegalBlock[];
}

export interface CheckoutStartedView {
  url: string;
}

/** A payment this organisation actually made (KEY-368). */
export interface OrgPaymentView {
  paidAtUtc: string;
  amountPence: number;
  currency: string;
  /** The plan as it was named when this was sold — frozen, so a later rename can't rewrite history. */
  planDescription: string;
  stripeInvoiceId: string;
}

/** Capacity bought on top of a plan (KEY-402). */
/** How existing extras are handled when an organisation moves tier (KEY-403). */
export type AddOnCarryChoice = "Drop" | "CarryAll" | "PreserveCapacity";

export interface PlanChangePreviewRequest {
  planId: string;
  /** Defaults server-side to preserving capacity, which leaves the customer where they stand. */
  carryExtras?: AddOnCarryChoice;
}

export interface PlanChangeMeterView {
  meter: string;
  label: string;
  /**
   * Whether anything refuses an action on this meter's account. False for users and AI generations —
   * shown so a changed figure cannot imply a consequence it does not have.
   */
  isEnforced: boolean;
  note: string;
  dialDefinitionId: string | null;
  definitionName: string | null;
  includedNow: number | null;
  includedAfter: number | null;
  extrasNow: number;
  extrasAfter: number;
  /** Null means unlimited. */
  allowanceNow: number | null;
  allowanceAfter: number | null;
  /** Null when nothing counts this meter. */
  inUse: number | null;
  overBy: number;
}

export interface PlanChangeAddOnView {
  kind: string;
  kindLabel: string;
  dialDefinitionId: string | null;
  definitionName: string | null;
  quantityNow: number;
  /** Recomputed for the new tier, not copied across. */
  quantityAfter: number;
  unitPencePerMonthAfter: number | null;
  totalPencePerMonthAfter: number | null;
  /** False when the new tier authors no rate for this kind. Not the same as free. */
  isPricedOnNewPlan: boolean;
  /** Whether anything actually charges for this capacity. False for every add-on today. */
  isBilledByStripe: boolean;
}

export interface PlanChangePreviewView {
  organisationId: string;
  fromPlan: string;
  toPlan: string;
  carryExtras: AddOnCarryChoice;
  meters: PlanChangeMeterView[];
  extras: PlanChangeAddOnView[];
  /** Null when one of the carried kinds has no rate on the new tier, so no honest total exists. */
  extrasPencePerMonthAfter: number | null;
  currency: string;
  /** The consequences in the words they must be shown in. Built server-side so surfaces cannot diverge. */
  warnings: string[];
  wouldBeOverAllowance: boolean;
  /**
   * False for a Stripe-billed subscription: the comp path must not write our record while Stripe carries on
   * charging. Such a move goes through `applyOrgPlanChange` instead, which moves Stripe itself (KEY-415).
   */
  canApplyHere: boolean;
  cannotApplyReason: string | null;
}

export interface PlanChangeApplyRequest {
  planId: string;
  carryExtras?: AddOnCarryChoice;
}

/** What carrying out a plan change against Stripe actually did (KEY-415). */
export interface PlanChangeResultView {
  organisationId: string;
  /** True when it is already in force and invoiced prorated — an upgrade. */
  appliedNow: boolean;
  /** The moment of the call for an upgrade; the end of the paid period for a scheduled downgrade. */
  effectiveFromUtc: string;
  planAfterUtcBoundary: string;
  /**
   * Anything to say out loud: extras the new tier authors no rate for, capacity that stopped being billed.
   * Empty when the move was unremarkable.
   */
  notes: string[];
  preview: PlanChangePreviewView;
}

export interface SubscriptionAddOnView {
  id: string;
  /** "Definition" or "Instance". */
  kind: string;
  kindLabel: string;
  /** The dial an Instance add-on applies to; null for subscription-wide kinds. */
  dialDefinitionId: string | null;
  definitionName: string | null;
  quantity: number;
  /** For Milo (KEY-716): the rung held, in requests a month. Null on every other kind. */
  miloRequests?: number | null;
  /** True when no Stripe item bills this — granted by hand. */
  isComped: boolean;
  /**
   * Why this capacity is not being billed when it ought to be — a Stripe call that failed, or a tier with
   * no rate for the kind. Null when it is billed, or when the subscription is a comp and never would be.
   */
  billingNote: string | null;
}

/** Sets an add-on to an absolute quantity, not an amount to add. */
export interface SetAddOnRequest {
  kind: string;
  quantity: number;
  dialDefinitionId?: string | null;
  /** For Milo (KEY-716): which rung, in requests a month. Required with quantity 1; Milo is one rung, so quantity > 1 is refused. */
  miloRequests?: number | null;
}

/** A customer choosing their own Milo tier (KEY-716): requests a month from the plan's ladder, or 0 for none. */
export interface SetMiloTierRequest {
  requests: number;
}

/**
 * Where an organisation stands against its AI allowance this period (KEY-400).
 *
 * The only meter with a reset date, so it is the only one whose position needs one: "you have used all
 * twenty" is a dead end without "and you get twenty more on the 1st". Null `allowance` means unlimited.
 */
export interface AiUsageView {
  allowance: number | null;
  used: number;
  periodStartUtc: string;
  periodEndUtc: string;
}

/**
 * What Claude cost us for a dial or an organisation in one calendar month, UTC (KEY-713). Our cost, not the
 * customer's price: platform admins only. Money is in millionths of a US dollar.
 */
export interface AiCostView {
  fromUtc: string;
  toUtc: string;
  calls: number;
  /** Calls with no cost: made before tokens were recorded, or on an unpriced model. Nonzero = the cost is a floor. */
  unmeasuredCalls: number;
  costMicroUsd: number;
  /** By feature and model, costliest first. */
  lines: AiCostLine[];
  /** Organisation only: its dials, costliest first (top 25). */
  dials?: AiCostDial[] | null;
  /** Organisation only: cost not about any one dial (generation, blueprints, plan conversations). */
  notOnADialMicroUsd?: number | null;
}

export interface AiCostLine {
  /** The `AiFeature` name: Milo, DocumentItemAnalysis, DefinitionGeneration, … */
  feature: string;
  model: string | null;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  costMicroUsd: number;
}

export interface AiCostDial {
  instanceId: string;
  name: string;
  calls: number;
  costMicroUsd: number;
}

export interface OrgSubscriptionView {
  organisationId: string;
  planId: string;
  family: string;
  tierName: string;
  amountPence: number;
  period: string;
  /** Trialing | Active | PastDue | Canceled | TrialExpired */
  status: string;
  /** Card | Invoice | Comp — how this organisation pays, not the plan's authored billing path. */
  collectionMethod: string;
  instancesPerDefinition?: number | null;
  startedAtUtc: string;
  currentPeriodEndUtc?: string | null;
  trialEndsAtUtc?: string | null;
  cancelAtPeriodEnd: boolean;
  /** Whether Stripe is actually billing this, rather than it having been granted by hand. */
  isStripeBilled: boolean;
  /** Whether the current subscription's trial took no card up front (KEY-431). */
  cardFreeTrial: boolean;
  /** What the plan is called in public (KEY-620). Null shows family and tier. */
  displayName?: string | null;
  /**
   * The buyer's name when a relative's family pack pays for this organisation (KEY-651), so the card says so
   * instead of quoting a price nobody here is paying. Null otherwise.
   */
  sponsoredBy?: string | null;
  /**
   * Whether the lapsed-data countdown (KEY-667) applies to this organisation. While false, "nothing has been
   * deleted" stays an unqualified promise; while true, an ended plan's data is kept for a limited time.
   */
  dataRetentionOn?: boolean;
  /** The deletion date this organisation has been emailed (KEY-667), once the countdown has started. */
  dataDeletionScheduledAtUtc?: string | null;
}

/** The lapsed-data dry run (KEY-667, platform-only). Phase 1 deletes nothing; this shows what WOULD go. */
export interface LapsedDataRetentionReport {
  /** Whether the countdown is on — the platform retention setting holds a number. */
  enabled: boolean;
  platformDays: number | null;
  noticeDays: number[];
  /** The reserved phase-2 switch. Nothing reads it yet. */
  purgeEnabled: boolean;
  organisations: LapsedOrganisationRetentionRow[];
}

export interface LapsedOrganisationRetentionRow {
  organisationId: string;
  organisationName: string;
  organisationType: string;
  status: string;
  endedAtUtc: string;
  /** Effective period for this organisation — the longest of its dials' periods; null when off. */
  retentionDays: number | null;
  /** The emailed date once the countdown has started, else the projection. */
  dueAtUtc: string | null;
  scheduledDeletionAtUtc: string | null;
  retentionHold: boolean;
  /** Why the countdown does not apply to it; null when it does. */
  excludedReason: string | null;
  notices: { daysBefore: number; skipped: boolean; recordedAtUtc: string; deletionAtUtc: string }[];
  wouldDelete: {
    instances: number;
    documents: number;
    documentBytes: number;
    crmAttachments: number;
    crmAttachmentBytes: number;
    crmRecords: number;
  };
}

/** A family pack as its buyer sees it (KEY-651): places, who holds them, and whether another invitation can go. */
export interface FamilyView {
  planName: string;
  /** Places for relatives, not counting the buyer's own. */
  seatsTotal: number;
  /** Relatives who joined plus invitations still open. */
  seatsUsed: number;
  canInvite: boolean;
  /** Why not, in the buyer's words, when `canInvite` is false. */
  cannotInviteReason?: string | null;
  seats: FamilySeatView[];
}

export interface FamilySeatView {
  id: string;
  name: string;
  email: string;
  /** Invited | Joined | Expired */
  status: string;
  invitedAtUtc: string;
  expiresAtUtc?: string | null;
  joinedAtUtc?: string | null;
}

export interface InviteFamilyMemberRequest {
  name: string;
  email: string;
}

/** `emailSent` false: the invitation is saved but the email didn't go — send it again from the card. */
export interface FamilyInviteSentView {
  seat: FamilySeatView;
  emailSent: boolean;
}

/** What a family invitation link says to whoever opens it (anonymous). */
export interface FamilyInviteView {
  /** Open (can be accepted) | Joined (already accepted — sign in) | Ended (the buyer's plan has ended). */
  status: string;
  invitedBy: string;
  invitedName: string;
  /** The address the dial is set up for, and the one they sign in with. */
  email: string;
  planName: string;
  dials: string[];
}

export interface ClaimFamilyInviteRequest {
  /** The End User Terms version shown on the page (KEY-618). */
  termsVersion: string | null;
  ageConfirmed: boolean;
}

export interface FamilyInviteClaimedView {
  /** The address to sign in with. */
  email: string;
}

/**
 * The rungs a customer may move their own card-billed plan to (KEY-651 phase 2): the same shop and product line —
 * single ↔ family above all. The api-client returns null (a 204) when the plan can't be switched from web-user.
 */
export interface PlanSwitchOptionsView {
  currentPlanId: string;
  currentName: string;
  options: PlanSwitchOptionView[];
}

export interface PlanSwitchOptionView {
  planId: string;
  name: string;
  amountPence: number;
  /** Monthly | Annual */
  period: string;
  taxInclusive: boolean;
  familySeats?: number | null;
  includedDefinitions: IncludedDefinitionView[];
  /** True: changes now and Stripe settles the difference today. False: at the end of the period already paid for. */
  takesEffectNow: boolean;
  /** What the switch costs somebody else — relatives losing their places. Null when nothing. */
  warning?: string | null;
}

export interface GrantSubscriptionRequest {
  planId: string;
  /** Override the plan's own trial length. Omit to use it. */
  trialDays?: number | null;
  /**
   * Draws this comp from one of the plan's two free-dial pools (KEY-420): `"Invoiced"` or `"TrueFree"`.
   * Omit for an ordinary comp. An explicit election, not something inferred from who is calling.
   */
  freeDialPoolKind?: "Invoiced" | "TrueFree" | null;
}

/** What applying a plan did to one definition's entitlement. */
export interface PlanCapOutcome {
  /** NoSubscription | AlreadySet | Written | Refused */
  action: string;
  holderOrganisationId?: string | null;
  definitionId: string;
  maxInstances?: number | null;
  /** Always populated for `Refused` — show it, or the admin finds out when an allocation fails. */
  detail?: string | null;
}

export interface GrantSubscriptionResult {
  subscription: OrgSubscriptionView;
  entitlements: PlanCapOutcome[];
}

export interface CrmCompanyView {
  id: string;
  name: string;
  website: string | null;
  phone: string | null;
  isArchived: boolean;
  createdAtUtc: string;
}

export interface CrmCompanyDetailView extends CrmCompanyView {
  notes: string | null;
  people: CrmPersonView[];
  /** KEY-899: the customer organisation this company became, once converted. */
  convertedOrganisationId?: string | null;
  convertedOrganisationName?: string | null;
}

export interface CrmCompanyListResponse {
  items: CrmCompanyView[];
  totalCount: number;
}

export interface CreateCrmCompanyRequest {
  name: string;
  website?: string | null;
  phone?: string | null;
  notes?: string | null;
}

export interface CrmPersonView {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string;
  phone: string | null;
  jobTitle: string | null;
  companyId: string | null;
  companyName: string | null;
  source: CrmPersonSource;
  /** When they opted into marketing; null means they did not, and may not be bulk-emailed (KEY-426). */
  marketingConsentAtUtc: string | null;
  isArchived: boolean;
  createdAtUtc: string;
}

/** Somebody not yet in the book, filled in while logging their enquiry. */
export interface NewCrmEnquiryPerson {
  firstName: string;
  lastName: string | null;
  email: string;
  phone: string | null;
  jobTitle: string | null;
  companyId: string | null;
  /** Cross-tenant callers only, and only without a company — a company already picks the owner. */
  organisationId: string | null;
}

/** Exactly one of `personId` / `person` is used; `personId` wins if both are sent. */
export interface CreateCrmEnquiryRequest {
  personId?: string | null;
  message: string;
  person?: NewCrmEnquiryPerson | null;
}

export interface CreateCrmEnquiryResponse {
  id: string;
  personId: string;
  /** False when the email matched somebody already on file and the enquiry joined their record. */
  personCreated: boolean;
}

export interface CrmPersonDetailView extends CrmPersonView {
  marketingConsentAtUtc: string | null;
  notes: string | null;
  enquiries: CrmEnquiryView[];
}

export interface CrmPersonListResponse {
  items: CrmPersonView[];
  totalCount: number;
}

export interface CreateCrmPersonRequest {
  firstName: string;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  jobTitle?: string | null;
  companyId?: string | null;
  notes?: string | null;
}

export interface UpdateCrmPersonRequest {
  firstName: string;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  jobTitle?: string | null;
  notes?: string | null;
}

export interface CrmEnquiryView {
  id: string;
  personId: string;
  personName: string;
  personEmail: string;
  status: CrmEnquiryStatus;
  message: string;
  assignedToUserId: string | null;
  assignedToName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  /** Which page of the referring site the enquiry came from (KEY-390); `sourceId` says which site. */
  sourcePage?: string | null;
  /** A web enquiry that hasn't proved its email yet — listed only when explicitly asked for. */
  awaitingVerification: boolean;
  createdAtUtc: string;
  statusChangedAtUtc: string;
  organisationId: string;
  organisationName: string | null;
}

export interface CrmEnquiryDetailView {
  id: string;
  status: CrmEnquiryStatus;
  message: string;
  closedReason: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  /** Which page of the referring site the enquiry came from (KEY-390); `sourceId` says which site. */
  sourcePage?: string | null;
  awaitingVerification: boolean;
  createdAtUtc: string;
  statusChangedAtUtc: string;
  organisationId: string;
  organisationName: string | null;
  person: CrmPersonView;
  interactions: CrmInteractionView[];
}

/** Who takes the callback for enquiries from an entry point (spec D10). */
export type CrmBookingHandler = "DestinationOrg" | "TopCo" | "None";

/** One website that may send enquiries: its public link key, landing-page branding, and callback routing. */
export interface CrmEnquirySourceView {
  id: string;
  name: string;
  publicKey: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  bookingHandler: CrmBookingHandler;
  isActive: boolean;
  createdAtUtc: string;
  organisationId: string;
  organisationName: string | null;
}

export interface CrmEnquirySourceListResponse {
  items: CrmEnquirySourceView[];
}

/* --- Lead campaigns (KEY-432) --------------------------------------------------------------------
   A QR code per rep, the tier it sells, and what came back from it. Every outcome figure below is
   DERIVED from the buyer's subscription and the sales ledger when you ask for it — nothing about a
   sign-up's fate is stored on the campaign, so these numbers cannot drift from the truth. */

/** Where a sign-up got to. "Converted" includes PastDue: they did convert; `revenuePence` says whether
 *  the money actually arrived. */
export type CrmCampaignOutcome = "Unknown" | "Trialing" | "Converted" | "Lapsed" | "Cancelled";

export interface CrmCampaignStatsView {
  signups: number;
  trialing: number;
  converted: number;
  lapsed: number;
  cancelled: number;
  /** Lapsed sign-ups who ticked the marketing box and are not archived — the follow-up list. */
  contactable: number;
  /** Money in, less money refunded, in `currency` only. */
  revenuePence: number;
  currency: string;
  firstSignupAtUtc: string | null;
  lastSignupAtUtc: string | null;
}

export interface CrmCampaignView {
  id: string;
  name: string;
  /** Whose campaign this is: Key Dials', or the partner whose tier it sells. */
  ownerOrganisationId: string;
  ownerName: string;
  /** A partner owns it and sells their own tier through it. */
  isPartnerCampaign: boolean;
  publicKey: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  billingPlanId: string;
  planLabel: string;
  trialDays: number;
  cardFreeTrial: boolean;
  /** False when the tier has been retired or moved off self-serve since the QR code was printed —
   *  the link is live and leads nowhere, which is worth saying out loud. */
  planIsPurchasable: boolean;
  amountPence: number;
  currency: string;
  period: string;
  eventName: string | null;
  eventOnUtc: string | null;
  /** The zone the sign-up clock is read in. An event happens somewhere. */
  timeZoneId: string;
  isActive: boolean;
  createdAtUtc: string;
  stats: CrmCampaignStatsView;
}

export interface CrmCampaignListResponse {
  items: CrmCampaignView[];
  /** Answered by the server: the capability is held by people whose organisation doesn't qualify. */
  canManage: boolean;
}

export interface SaveCrmCampaignRequest {
  name: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  billingPlanId: string;
  eventName: string | null;
  eventOnUtc: string | null;
  timeZoneId: string;
  /** Honoured only for a cross-tenant caller creating a campaign on a partner's behalf. */
  organisationId?: string | null;
}

/** One person a campaign email goes to: picked from the CRM (`personId`) or typed in (`email`). */
export interface CrmCampaignShareRecipient {
  personId: string | null;
  email: string | null;
}

export interface ShareCrmCampaignRequest {
  recipients: CrmCampaignShareRecipient[];
  /** Blank uses the campaign's own heading. */
  subject: string | null;
  /** An optional personal note above the offer. Plain text — the server escapes it. */
  message: string | null;
}

export interface CrmCampaignShareSkip {
  recipient: string;
  reason: string;
}

export interface ShareCrmCampaignResultView {
  sentTo: string[];
  skipped: CrmCampaignShareSkip[];
}

/** The email exactly as the send would render it, with the QR code as a data URI. */
export interface CrmCampaignSharePreviewView {
  subject: string;
  html: string;
  /** Whose branding it wears. */
  brandName: string;
  isPartnerBrand: boolean;
}

export interface CrmCampaignSignupView {
  id: string;
  email: string;
  signedUpAtUtc: string;
  /** Already formatted in the campaign's own zone — the client is not asked to convert it. */
  localSignedUpAt: string;
  localHour: number;
  personId: string | null;
  personName: string | null;
  outcome: CrmCampaignOutcome;
  trialEndsAtUtc: string | null;
  contactable: boolean;
  revenuePence: number;
}

export interface CrmCampaignHourView {
  hour: number;
  signups: number;
}

export interface CrmCampaignDetailView {
  campaign: CrmCampaignView;
  signups: CrmCampaignSignupView[];
  /** All 24 hours, including the empty ones. */
  byHour: CrmCampaignHourView[];
}

/** One event, across every campaign run at it — two reps at one breakfast are one breakfast. */
export interface CrmCampaignEventView {
  eventName: string;
  eventOnUtc: string | null;
  campaigns: number;
  stats: CrmCampaignStatsView;
}

export interface CrmCampaignEventListResponse {
  items: CrmCampaignEventView[];
}

/** What somebody who scanned the QR code is shown. An allow-list: the campaign's owner, internal name
 *  and sign-up history are all deliberately absent. */
export interface PublicCampaignView {
  publicKey: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  plan: PurchasablePlanView;
}

/* --- The website's partner pages (KEY-389) ------------------------------------------------------
   Editing what www.keydials.com publishes about a partner. Gated on `site.publish`, which is platform
   only: a partner administrator does not get to publish their own organisation to the internet. */

/** The page's copy, exactly as the website renders it. Opaque to the API — see PartnerSiteProfile. */
export type PartnerSiteMarketing = Record<string, unknown>;

export interface PartnerSiteProfileAdminView {
  id: string;
  organisationId: string;
  organisationName: string | null;
  slug: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  enquiryPublicKey: string | null;
  isPublished: boolean;
  isSample: boolean;
  updatedAtUtc: string;
  /** When the marketing site last collected this page. Null means it has never been in a build. */
  lastBuiltAtUtc: string | null;
  /**
   * Whether what is live differs from what is saved. Best-effort: the marker moves when the site's build
   * collects the page, so treat it as "waiting", not as proof.
   */
  needsRebuild: boolean;
  marketing: PartnerSiteMarketing;
  /** The dial the page is about (KEY-616); null means the first dial bundled in the cheapest plan. */
  featuredDefinitionId?: string | null;
}

export interface PartnerSiteProfileAdminListResponse {
  items: PartnerSiteProfileAdminView[];
}

export interface SavePartnerSiteProfileRequest {
  organisationId: string;
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  enquiryPublicKey: string | null;
  isPublished: boolean;
  isSample: boolean;
  marketing: PartnerSiteMarketing;
  /** The dial the page is about (KEY-616), or null to feature the first bundled dial of the cheapest plan. */
  featuredDefinitionId?: string | null;
}

/** One enquiry key a page may route to. Inactive keys are listed, so a revoked destination is visible. */
export interface SitePartnerEnquirySourceView {
  publicKey: string;
  name: string;
  displayName: string;
  isActive: boolean;
  /** The key's own colour, or null when it has none and will borrow the published page's. */
  accentColour: string | null;
}

export interface SitePartnerCandidateView {
  organisationId: string;
  name: string;
  type: string;
  hasPage: boolean;
  enquirySources: SitePartnerEnquirySourceView[];
  /** The published dials the page may feature (KEY-616): allocated to the partner, or bundled in a rung they sell. */
  dials?: SitePartnerDialOptionView[];
  /** The partner's unretired rungs, cheapest first (KEY-617), for the audience cards' buy buttons. */
  plans?: SitePartnerPlanOptionView[];
}

export interface SitePartnerDialOptionView {
  id: string;
  name: string;
}

export interface SitePartnerPlanOptionView {
  id: string;
  name: string;
  amountPence: number;
  currency: string;
  period: string;
}

export interface SitePartnerCandidateListResponse {
  items: SitePartnerCandidateView[];
}

/** Ask Claude for a first draft of a partner's page, grounded in the dials that partner runs. */
export interface DraftPartnerCopyRequest {
  organisationId: string;
  /** What the dials cannot say — who buys this, why them. Optional; at most 4000 characters. */
  brief?: string | null;
}

export interface PartnerCopyDraftView {
  /** The copy, in the shape the editor holds. A draft: nothing is saved until a person saves it. */
  marketing: PartnerSiteMarketing;
  /** The dials it actually read, by name. Empty means it had only the brief to go on. */
  groundedIn: string[];
}

/** One value an email template may reference; the sample drives draft previews. */
export interface EmailTokenView {
  token: string;
  description: string;
  sample: string;
}

/** A system email as this organisation sees it: effective wording at their layer + override state. */
export interface EmailTemplateStateView {
  key: string;
  description: string;
  tokens: EmailTokenView[];
  subject: string;
  bodyHtml: string;
  isOverridden: boolean;
  isActive: boolean;
}

export interface EmailTemplateListResponse {
  items: EmailTemplateStateView[];
}

export interface SaveEmailTemplateRequest {
  subject: string;
  bodyHtml: string;
  isActive?: boolean;
}

// --- CRM public intake (KEY-313): the anonymous enquiry surface ------------------------------------

/** The public enquiry page's branding + consent facts. */
export interface PublicEnquirySourceView {
  displayName: string;
  introText: string | null;
  accentColour: string | null;
  logoUrl: string | null;
  retentionMonths: number;
}

export interface SubmitPublicEnquiryRequest {
  firstName: string;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  message: string;
  marketingOptIn: boolean;
  /** Honeypot — rendered invisibly and left empty by humans. */
  website?: string | null;
  /**
   * Which page of the referring site the visitor came from (KEY-390) — "sector:awaabs-law",
   * "partner:fieldstone-safety", "home". The enquiry source already says which SITE; this says
   * where in it. Capped at 200 characters by the API.
   */
  sourcePage?: string | null;
}

export interface SubmitPublicEnquiryResponse {
  enquiryToken: string;
}

export interface VerifyPublicEnquiryResponse {
  verified: boolean;
  bookingOffered: boolean;
}

/** organisationId is honoured only for cross-tenant callers issuing a key for a partner. */
export interface SaveCrmEnquirySourceRequest {
  name: string;
  displayName: string;
  introText?: string | null;
  accentColour?: string | null;
  logoUrl?: string | null;
  bookingHandler: CrmBookingHandler;
  organisationId?: string | null;
}

/**
 * A source's landing-page copy, and nothing else (KEY-429). Carries no name, booking handler or
 * organisation — a partner writes their own words; where the form files enquiries stays Key Dials'.
 */
export interface SaveCrmEnquirySourceCopyRequest {
  displayName: string;
  introText?: string | null;
  accentColour?: string | null;
  logoUrl?: string | null;
}

/** One "who is this for" block on a partner's page. */
export interface PartnerPageAudience {
  title: string;
  body: string;
}

/**
 * A partner's own view of their marketing page (KEY-429) — the words they may write, plus the state
 * they need to understand what a save will do. The hero dial, the dial cards and the price list are
 * generated and deliberately absent.
 */
export interface PartnerPageView {
  slug: string;
  displayName: string;
  introText: string;
  accentColour: string | null;
  logoUrl: string | null;
  eyebrow: string;
  headline: string;
  lede: string;
  reassurance: string;
  ctaLabel: string;
  dialsEyebrow: string;
  dialsHeading: string;
  fixedByDesignHeading: string;
  fixedByDesignBody: string;
  audiences: PartnerPageAudience[];
  closingHeadline: string;
  closingBody: string;
  /** Whether the page is on the public site at all. Key Dials' decision, shown for context. */
  isPublished: boolean;
  isSample: boolean;
  /** Saved changes not yet collected by a site build — the words here aren't on the internet yet. */
  waitingForBuild: boolean;
  updatedAtUtc: string;
  lastBuiltAtUtc: string | null;
}

/** The partner-writable half. No slug, no published flag, nothing generated. */
export interface SavePartnerPageRequest {
  displayName: string;
  introText: string;
  accentColour: string | null;
  logoUrl: string | null;
  eyebrow: string;
  headline: string;
  lede: string;
  reassurance: string;
  ctaLabel: string;
  dialsEyebrow: string;
  dialsHeading: string;
  fixedByDesignHeading: string;
  fixedByDesignBody: string;
  audiences: PartnerPageAudience[];
  closingHeadline: string;
  closingBody: string;
}

export interface CrmEnquiryListResponse {

  items: CrmEnquiryView[];
  totalCount: number;
}

// --- CRM call-back availability (KEY-314) ---
// Times are LOCAL wall-clock in the window's own zone, serialised "HH:mm:ss"; dates "yyyy-MM-dd".
// Nothing here is UTC: the conversion happens per occurrence server-side, so a 09:00 window stays
// 09:00 across a clock change.

export type CrmDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type CrmBookingStatus = "Confirmed" | "Cancelled" | "Completed";

/** Whether one source can deliver the call-back it advertises — see the CRM readiness endpoint. */
export interface CrmSourceReadiness {
  sourceId: string;
  sourceName: string;
  bookingHandler: CrmBookingHandler;
  poolOrganisationId: string;
  poolOrganisationName: string;
  /** True when the pool is your own organisation, so "set your hours" is a fix you can act on. */
  poolIsMine: boolean;
  /** People in that pool with any weekly hours. Zero is the blunt failure. */
  agentsWithHours: number;
  /** Slots the pool could offer right now. Zero WITH agents is the subtle one: hours exist, but
   *  none fall inside the lead time and booking window. */
  offerableSlots: number;
  /** The same count with the notice period set aside. Positive while `offerableSlots` is zero means the
   *  hours are real and in the window — the notice period is eating them, which is a different fix. */
  slotsIgnoringNotice: number;
}

export interface CrmBookingReadinessResponse {
  sources: CrmSourceReadiness[];
  windowDays: number;
  /** Hours of notice an enquirer must give. Named in the warning so it can be acted on. */
  noticeHours: number;
}

// --- The call-back calendar (KEY-318) ---

export type CrmCalendarEntryKind = "Availability" | "TimeOff" | "Booking" | "Appointment" | "ExternalBusy";
export type CrmCalendarScope = "Mine" | "Team";
export type CrmCalendarBookability = "Bookable" | "TooSoon" | "BeyondWindow";

// --- Linked external calendars (KEY-319) ---

export type CrmCalendarProviderDto = "Microsoft" | "Google";

export interface CrmCalendarLinkView {
  id: string;
  provider: CrmCalendarProviderDto;
  accountEmail: string;
  lastSyncedUtc: string | null;
  /** Why the last sync failed — usually withdrawn consent. Shown so a dead link isn't an empty diary. */
  lastSyncError: string | null;
  connectedAtUtc: string;
}

export interface CrmCalendarProviderOption {
  provider: CrmCalendarProviderDto;
  name: string;
  /** False when this deployment has no client credentials for the provider. */
  isConfigured: boolean;
}

export interface CrmCalendarLinkListResponse {
  links: CrmCalendarLinkView[];
  providers: CrmCalendarProviderOption[];
  syncEnabled: boolean;
}

export interface CrmCalendarConnectResponse {
  authorizationUrl: string;
}

// --- Manual appointments (KEY-323) ---

export interface CrmAppointmentView {
  id: string;
  agentUserId: string;
  agentName: string;
  title: string;
  notes: string | null;
  startUtc: string;
  endUtc: string;
  personId: string | null;
  personName: string | null;
}

export interface SaveCrmAppointmentRequest {
  title: string;
  startUtc: string;
  endUtc: string;
  /** Null means the caller — putting time in your own diary shouldn't require naming yourself. */
  agentUserId?: string | null;
  notes?: string | null;
  personId?: string | null;
}

/** Someone who can hold an appointment: a colleague who can actually see the CRM. */
export interface CrmAgentOption {
  userId: string;
  name: string;
  isMe: boolean;
}

export interface CrmCalendarEntryView {
  kind: CrmCalendarEntryKind;
  /** The row behind the entry, when there is one to act on. Null for availability. */
  entryId: string | null;
  userId: string;
  userName: string;
  startUtc: string;
  endUtc: string;
  allDay: boolean;
  /** The reason on time off, or who the call is with on a booking. */
  label: string | null;
  /** Judged per entry: the notice period belongs to the hours, so two blocks can differ. */
  bookability: CrmCalendarBookability;
}

export interface CrmCalendarOrganisationOption {
  id: string;
  name: string;
  isMine: boolean;
}

export interface CrmCalendarResponse {
  entries: CrmCalendarEntryView[];
  /** The horizon is global, so it's stated once; the notice period rides on each entry instead. */
  bookableUntilUtc: string;
  /** The organisation's default notice period, for wording the explanation. */
  defaultNoticeHours: number;
  primaryTimeZoneId: string;
  /** True only with `crm.calendar.team` AND somebody else actually keeping a diary. */
  canSeeTeam: boolean;
  /** Which organisation is shown. */
  organisationId: string;
  organisationName: string;
  /** Organisations the caller may switch to. Empty without cross-tenant rights — the ordinary case. */
  organisations: CrmCalendarOrganisationOption[];
}

// --- Converting a won company into a tenant (KEY-315, spec D12) ---

export interface CrmConversionCandidate {
  personId: string;
  name: string;
  email: string;
  jobTitle: string | null;
}

/** The material for the wizard's decisions — nothing here has happened yet. */
export interface CrmConversionPreview {
  companyId: string;
  companyName: string;
  suggestedOrganisationName: string;
  people: CrmConversionCandidate[];
  /** Organisation types this caller may create; a scoped admin gets the consumer tiers only. */
  allowedTypes: string[];
  openEnquiryCount: number;
  convertedOrganisationId: string | null;
  convertedOrganisationName: string | null;
}

/** Only the people named in invitePersonIds become members — nothing copies silently. */
export interface ConvertCrmCompanyRequest {
  organisationName: string;
  organisationType: string;
  parentId?: string | null;
  invitePersonIds: string[];
  inviteRoleName: string;
}

export interface ConvertCrmCompanyResult {
  organisationId: string;
  invitedCount: number;
  /** Anyone who couldn't be invited, with the reason — a partial result stays visible. */
  skippedPeople: string[];
}

/** One offered slot, as instants — render in the reader's own zone. */
export interface CallbackSlotView {
  startUtc: string;
  endUtc: string;
}

/** An empty list is a legitimate answer: nobody free, or this entry point offers no call-backs. */
export interface CallbackSlotsResponse {
  slots: CallbackSlotView[];
  displayTimeZoneId: string;
  slotMinutes: number;
}

/** slotTaken is the race — someone booked it between the offer and the click; re-offer, don't error. */
export interface BookCallbackResponse {
  booked: boolean;
  slotTaken: boolean;
  startUtc: string | null;
  agentName: string | null;
}

export interface CrmBookingView {
  id: string;
  agentUserId: string;
  agentDisplayName: string;
  startUtc: string;
  endUtc: string;
  displayTimeZoneId: string;
  status: CrmBookingStatus;
  cancelledReason: string | null;
}

export interface CrmAvailabilityWindowView {
  /** Hours of warning these hours need. Null means the organisation's default. */
  minimumLeadHours: number | null;
  /** Who gets the call when several people are free. Lower wins; 0 means no preference. */
  preference: number;
  id: string;
  dayOfWeek: CrmDayOfWeek;
  startLocal: string;
  endLocal: string;
  timeZoneId: string;
}

export interface CrmAvailabilityExceptionView {
  id: string;
  date: string;
  startLocal: string | null;
  endLocal: string | null;
  reason: string | null;
}

/** isBookable is "am I in the call-back pool?" — true once at least one window exists. */
export interface CrmTeamAvailabilityRow {
  userId: string;
  name: string;
  email: string | null;
  windows: CrmAvailabilityWindowView[];
  /** Time off still ahead of them — a count; the dates belong on the calendar. */
  upcomingTimeOff: number;
  /** Holding any hours at all, which is what puts somebody in the booking pool. */
  isBookable: boolean;
}

export interface CrmTeamAvailabilityResponse {
  people: CrmTeamAvailabilityRow[];
  defaultNoticeHours: number;
  organisationId: string;
  organisationName: string;
  organisations: CrmCalendarOrganisationOption[];
}

export interface CrmAvailabilityView {
  windows: CrmAvailabilityWindowView[];
  exceptions: CrmAvailabilityExceptionView[];
  /** The organisation's default notice period, so a blank field isn't a mystery number. */
  defaultNoticeHours: number;
  isBookable: boolean;
}

export interface SaveCrmAvailabilityWindowRequest {
  /** Null keeps the organisation's default. */
  minimumLeadHours?: number | null;
  /** Lower wins. 0 (the default) is "no preference". */
  preference?: number;
  dayOfWeek: CrmDayOfWeek;
  startLocal: string;
  endLocal: string;
  timeZoneId: string;
}

/** Null times block the whole day. */
export interface SaveCrmAvailabilityExceptionRequest {
  date: string;
  startLocal?: string | null;
  endLocal?: string | null;
  reason?: string | null;
}

/** Someone an enquiry can be handed to: a crm.read holder in its owning organisation. */
export interface CrmAssigneeView {
  userId: string;
  name: string;
  email: string | null;
}

export interface CrmAssigneeListResponse {
  items: CrmAssigneeView[];
}

/** EmailBody is stored as submitted; render it through the shared rich-text sanitiser, never raw. */
export interface CrmInteractionView {
  id: string;
  personId: string;
  enquiryId: string | null;
  type: CrmInteractionType;
  direction: CrmInteractionDirection;
  occurredAtUtc: string;
  durationMinutes: number | null;
  summary: string;
  emailSubject: string | null;
  emailBody: string | null;
  loggedByUserId: string;
  loggedByName: string | null;
  createdAtUtc: string;
  /** Files filed with this entry (KEY-476). Empty on the dashboard's activity strip, which is a glance. */
  attachments: CrmAttachmentView[];
}

/**
 * A file on a contact's record (KEY-476). `interactionId` and the two fields beside it are set when the
 * file was filed with one logged conversation, and null when it belongs to the contact as a whole.
 * `status` is the antivirus lifecycle: only `Clean` can be downloaded.
 */
export interface CrmAttachmentView {
  id: string;
  personId: string;
  interactionId: string | null;
  interactionType: CrmInteractionType | null;
  interactionOccurredAtUtc: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: "Pending" | "Clean" | "Quarantined";
  uploadedByUserId: string;
  uploadedByName: string | null;
  uploadedAtUtc: string;
}

/** Declare a file before uploading it. Omit `interactionId` to file it against the contact alone. */
export interface RequestCrmAttachmentUploadRequest {
  personId: string;
  interactionId?: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** A reserved attachment plus the short-lived URL to PUT its bytes to. */
export interface CrmAttachmentUploadTicket {
  attachmentId: string;
  uploadUrl: string;
}

/** A short-lived URL to download a clean attachment. */
export interface CrmAttachmentDownloadTicket {
  downloadUrl: string;
}

/** One to-do (KEY-425). */
export interface CrmTodoView {
  id: string;
  title: string;
  notes: string | null;
  dueAtUtc: string;
  /** False means "some time that day": show a date, not a time, and never call it late. */
  isTimed: boolean;
  assignedToUserId: string;
  assignedToName: string;
  personId: string | null;
  personName: string | null;
  enquiryId: string | null;
  completedAtUtc: string | null;
  completedByName: string | null;
  createdByUserId: string;
  createdAtUtc: string;
}

export interface CrmTodoListResponse {
  items: CrmTodoView[];
  totalCount: number;
}

export interface SaveCrmTodoRequest {
  title: string;
  notes: string | null;
  dueAtUtc: string;
  isTimed: boolean;
  /** Null means "mine". */
  assignedToUserId: string | null;
  personId: string | null;
  enquiryId: string | null;
}

/**
 * The reminder emails a person controls for themselves, and when each arrives.
 *
 * Every timing is nullable and null is a real answer, distinct from 0:
 * - `digestHour` — null means whatever the environment is configured for (07:00 by default).
 * - `nudgeLeadMinutes` — null means **after** the time passes, which is what the chaser has always
 *   done. A number means that many minutes before instead.
 * - `appointmentLeadMinutes` — null means the standard 15.
 *
 * The server normalises an hour outside 0–23, or a lead that isn't one it offers, back to null rather
 * than rejecting the save — so read the response back if you care what stuck.
 */
export interface CrmTodoEmailPreferences {
  digestEnabled: boolean;
  nudgeEnabled: boolean;
  digestHour: number | null;
  nudgeLeadMinutes: number | null;
  /** Off unless asked for, unlike the two to-do switches. */
  appointmentRemindersEnabled: boolean;
  appointmentLeadMinutes: number | null;
}

/** Mine, or everybody's. */
export type CrmTodoScope = "Mine" | "Team";

/**
 * The CRM at a glance (KEY-430). `newEnquiriesSinceLastVisit` is measured against the cursor the same
 * call then moves on, so an immediate refresh honestly reads zero; `recentEnquiries` is always
 * populated so the panel never looks broken rather than up to date.
 */
export interface CrmDashboardResponse {
  overdueTodos: CrmTodoView[];
  todaysTodos: CrmTodoView[];
  newEnquiriesSinceLastVisit: number;
  lastVisitUtc: string | null;
  recentEnquiries: CrmEnquiryView[];
  recentInteractions: CrmInteractionView[];
  upcomingAppointments: CrmAppointmentView[];
}

export interface CrmInteractionListResponse {
  items: CrmInteractionView[];
  totalCount: number;
}

export interface LogCrmInteractionRequest {
  personId: string;
  enquiryId?: string | null;
  type: CrmInteractionType;
  direction: CrmInteractionDirection;
  occurredAtUtc: string;
  durationMinutes?: number | null;
  summary: string;
  emailSubject?: string | null;
  emailBody?: string | null;
}

/**
 * An email to actually send to a CRM person (KEY-379).
 *
 * `body` is PLAIN TEXT, not HTML. The API has no HTML sanitiser, and the admin app's `sanitizeRichText`
 * only guards what is rendered back into the timeline — it cannot guard what leaves for somebody's
 * mailbox. The server escapes this into the HTML part of the message.
 *
 * `personId` repeats the one in the path and must match it.
 */
export interface SendCrmEmailRequest {
  personId: string;
  subject: string;
  body: string;
}

/** The timeline entry the send wrote, and the address it actually went to. */
export interface SendCrmEmailResponse {
  interactionId: string;
  toEmail: string;
}

export interface CrmCompaniesQuery {
  search?: string;
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CrmPeopleQuery {
  search?: string;
  companyId?: string;
  includeArchived?: boolean;
  /** Only people who opted into marketing — the pool a bulk send may draw from (KEY-426). */
  consentedOnly?: boolean;
  /** One of name|email|company|source|added|marketing (KEY-657). Omit for the default (name). */
  sort?: string;
  /** Reverses `sort`. Ignored when `sort` is omitted. */
  desc?: boolean;
  page?: number;
  pageSize?: number;
}

/** A token the bulk compose box may use. */
export interface CrmBulkEmailTokenView {
  token: string;
  description: string;
  sample: string;
}

/** Somebody the send deliberately left out, and why. */
export interface CrmBulkEmailSkip {
  personId: string;
  name: string;
  reason: string;
}

/** What actually happened. Every omission comes back named and explained. */
export interface CrmBulkEmailResultView {
  sent: number;
  skipped: CrmBulkEmailSkip[];
}

export interface SendCrmBulkEmailRequest {
  personIds: string[];
  subject: string;
  /** Plain text; may contain {{person.firstName}} and friends. */
  body: string;
}

export interface CrmEnquiriesQuery {
  /** Additive (KEY-655): every status named is included, unioned. Empty/undefined means no filter. */
  status?: CrmEnquiryStatus[];
  assignedToUserId?: string;
  personId?: string;
  sourceId?: string;
  includeUnverified?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CrmInteractionsQuery {
  personId?: string;
  enquiryId?: string;
  page?: number;
  pageSize?: number;
}

function buildCrmCompaniesQuery(query?: CrmCompaniesQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.includeArchived) params.set("includeArchived", "true");
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildCrmPeopleQuery(query?: CrmPeopleQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.companyId) params.set("companyId", query.companyId);
  if (query.includeArchived) params.set("includeArchived", "true");
  if (query.consentedOnly) params.set("consentedOnly", "true");
  if (query.sort) params.set("sort", query.sort);
  if (query.desc) params.set("desc", "true");
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildCrmEnquiriesQuery(query?: CrmEnquiriesQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  // Repeated keys (?status=New&status=Contacted) — the additive filter (KEY-655).
  for (const s of query.status ?? []) params.append("status", s);
  if (query.assignedToUserId) params.set("assignedToUserId", query.assignedToUserId);
  if (query.personId) params.set("personId", query.personId);
  if (query.sourceId) params.set("sourceId", query.sourceId);
  if (query.includeUnverified) params.set("includeUnverified", "true");
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildCrmInteractionsQuery(query?: CrmInteractionsQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  if (query.personId) params.set("personId", query.personId);
  if (query.enquiryId) params.set("enquiryId", query.enquiryId);
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Builds the `?…` query string for the admin feedback list. */
function buildFeedbackQuery(query?: FeedbackQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.status) params.set("status", query.status);
  if (query.source) params.set("source", query.source);
  if (query.includeArchived) params.set("includeArchived", "true");
  if (query.includeExpired) params.set("includeExpired", "true");
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function createApiClient(baseUrl: string, options?: ApiClientOptions): ApiClient {
  const root = baseUrl.replace(/\/+$/, "");
  const clientApp = options?.clientApp;

  // The organisation a cross-org request is scoped into (KEY-132), sent as X-KeyDials-Org. A per-call org
  // argument overrides it. Harmless on user-scoped endpoints (they ignore tenant scope).
  let activeOrgId: string | null = null;
  // The role a checker is looking at a checking dial as (KEY-498), sent as X-KeyDials-Checking-Role. Same
  // lifecycle and the same global reach as activeOrgId above — which is why the Workspace clears it on the
  // way out: left set, it would render every other checking dial's card on the Dashboard simulated too.
  let checkingRole: string | null = null;

  async function postJson<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${root}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new ApiError(res.status, `${path} failed: ${res.status}`);
    }
    return (await res.json()) as T;
  }

  // The device's IANA zone ("Europe/London"), sent as X-KeyDials-TimeZone so a dial set up from here keeps this
  // clock rather than UTC (the server ignores one it can't resolve).
  const deviceZone = browserTimeZone();

  /**
   * Headers for a hand-rolled `multipart/form-data` upload. A `FormData` body can't go through
   * `authed` (which JSON-encodes), so these uploads build their own `fetch` — and must still carry
   * the same identity/scoping headers `authed` attaches. In particular `X-KeyDials-Org` (KEY-132):
   * without it a cross-org upload is tenant-filtered against the caller's home org and the target
   * instance/definition isn't found (404). No `Content-Type`: the browser sets the multipart boundary.
   * `orgId` follows `authed`'s rule: undefined means the active org, if any; null means no org header at all (KEY-560).
   */
  function uploadHeaders(accessToken: string, orgId?: string | null): Record<string, string> {
    const scopeOrg = orgId === undefined ? activeOrgId : orgId;
    return {
      Authorization: `Bearer ${accessToken}`,
      ...(clientApp ? { "X-KeyDials-Client": clientApp } : {}),
      ...(deviceZone ? { "X-KeyDials-TimeZone": deviceZone } : {}),
      ...(scopeOrg ? { "X-KeyDials-Org": scopeOrg } : {}),
      // KEY-498: uploads are role-gated like everything else (the DocumentUpload action), so a simulated
      // Reader must be refused one here exactly as they are on the JSON paths.
      ...(checkingRole ? { "X-KeyDials-Checking-Role": checkingRole } : {}),
    };
  }

  /**
   * One logo upload, for the two places that brand something. Goes through uploadHeaders like every
   * other multipart post here — a hand-rolled fetch that forgot X-KeyDials-Org is how an upload ends up
   * tenant-filtered against the caller's home organisation and 404s (KEY-132).
   */
  async function uploadLogo(accessToken: string, path: string, file: File): Promise<SiteImageUploadedView> {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${root}${path}`, {
      method: "POST",
      headers: uploadHeaders(accessToken),
      body: form,
    });
    if (!res.ok) {
      throw await apiFailure(res, "Uploading the logo failed");
    }
    return (await res.json()) as SiteImageUploadedView;
  }

  /**
   * Submits feedback as `multipart/form-data` — the text fields as form fields plus 0..N `attachments`
   * file parts (KEY-261). It goes through `uploadHeaders`, not a hand-rolled `{ Authorization }`: dropping
   * `X-KeyDials-Client` here would strip the source-app tag every piece of feedback is attributed by (and
   * a bespoke header set is exactly what caused the cross-org upload 404s in KEY-132).
   */
  async function submitFeedback(
    accessToken: string,
    kind: "bug" | "feature",
    fields: Record<string, string | undefined>,
    attachments?: File[],
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) {
        form.append(key, value);
      }
    }
    for (const file of attachments ?? []) {
      form.append("attachments", file, file.name);
    }
    const res = await fetch(`${root}/api/feedback/${kind}`, {
      method: "POST",
      headers: uploadHeaders(accessToken),
      body: form,
    });
    if (!res.ok) {
      throw await apiFailure(res, "Sending your feedback failed");
    }
  }

  /** Authenticated request. Returns parsed JSON, or undefined for 204/empty responses. */
  async function authed<T>(method: string, path: string, accessToken: string, body?: unknown, orgId?: string | null, retried = false): Promise<T> {
    // Undefined means "the active org, if any"; null means "no org at all" (KEY-560). A customer author's calls are always in
    // the organisation they signed in to, and the server refuses one re-pointed elsewhere, so they must not inherit the org
    // a dial left active.
    const scopeOrg = orgId === undefined ? activeOrgId : orgId;
    const res = await fetch(`${root}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(clientApp ? { "X-KeyDials-Client": clientApp } : {}),
        ...(deviceZone ? { "X-KeyDials-TimeZone": deviceZone } : {}),
        ...(scopeOrg ? { "X-KeyDials-Org": scopeOrg } : {}),
        ...(checkingRole ? { "X-KeyDials-Checking-Role": checkingRole } : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401 && !retried && options?.onUnauthorized && !path.startsWith("/api/auth/")) {
      const renewed = await options.onUnauthorized(accessToken);
      if (renewed) {
        return authed<T>(method, path, renewed, body, orgId, true);
      }
    }
    if (!res.ok) {
      // Surface the server's message (minimal APIs return a JSON-encoded string for BadRequest).
      let detail = `${method} ${path} failed: ${res.status}`;
      let problems: string[] | undefined;
      let requirements: FirstRunRequirement[] | undefined;
      let parsedBody: unknown;
      try {
        const raw = (await res.text()).trim();
        if (raw) {
          if (raw.startsWith("[")) {
            // A list of named problems rather than one sentence. Kept as a list AND joined, so a caller
            // that renders them individually can, and one that doesn't still shows something readable.
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
              problems = parsed as string[];
              detail = problems.join(" · ");
            } else {
              detail = raw;
            }
          } else if (raw.startsWith("{")) {
            // An ASP.NET problem document ({ detail, title, … }). Before this it fell through to the
            // branch below and the WHOLE JSON became the message — which a screen then printed at the
            // user. Its extensions matter too: the first-run refusal (KEY-434) carries what is missing.
            const body = JSON.parse(raw) as {
              detail?: string;
              title?: string;
              message?: string;
              requirements?: FirstRunRequirement[];
            };
            parsedBody = body;
            detail = body.detail || body.title || body.message || raw;
            if (Array.isArray(body.requirements) && body.requirements.length > 0) {
              requirements = body.requirements;
            }
          } else {
            detail = raw.startsWith('"') && raw.endsWith('"') ? (JSON.parse(raw) as string) : raw;
          }
        }
      } catch {
        // keep the default detail
      }
      if (res.status === 402) {
        // The trial gate (KEY-637). Announced after the body is read — so it can carry the reason (KEY-911) — but
        // still immediately before the throw, with nothing awaited between: a listener's state change and the
        // caller's catch land in the same tick, and the page never gets a render with the refusal on it.
        const code = (parsedBody as { code?: unknown } | undefined)?.code;
        options?.onPaymentRequired?.(path, typeof code === "string" ? code : null);
      }
      throw new ApiError(res.status, detail, problems, requirements, parsedBody);
    }
    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return undefined as T;
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  const homeOrg = (options?: HomeOrgOption): null | undefined => (options?.homeOrg ? null : undefined);

  const abortError = () => new DOMException("Generation was cancelled.", "AbortError");

  /** Waits between polls, or rejects with an AbortError the moment the caller gives up. */
  function waitOrAbort(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }
      const onAbort = () => {
        clearTimeout(timer);
        reject(abortError());
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  }

  // Async generation: start a background job, then poll it. Encapsulated here so call sites keep their existing
  // ApiError-aware handling — a failed job is re-thrown as an ApiError with the 502/503 the server chose.
  async function startGenerationJob(accessToken: string, request: GenerateDefinitionRequest, signal?: AbortSignal): Promise<GenerationJobAccepted> {
    const res = await fetch(`${root}/api/definitions/generate-jobs`, {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      throw await apiFailure(res, "AI generation failed");
    }
    return (await res.json()) as GenerationJobAccepted;
  }

  async function pollGenerationJob(accessToken: string, jobId: string, signal?: AbortSignal): Promise<GenerationJobStatus> {
    const res = await fetch(`${root}/api/definitions/generate-jobs/${jobId}`, {
      signal,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      throw await apiFailure(res, "Checking generation status failed");
    }
    return (await res.json()) as GenerationJobStatus;
  }

  // A planning conversation turn runs on a worker like a generation does, so the same shape applies: post,
  // then poll until it stops running. Encapsulated here so call sites just await a reply.
  async function pollPlanConversation(accessToken: string, conversationId: string): Promise<PlanConversation> {
    const res = await fetch(`${root}/api/definitions/plan-conversations/${conversationId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      throw await apiFailure(res, "Checking the conversation failed");
    }
    return (await res.json()) as PlanConversation;
  }

  async function awaitPlanTurn(accessToken: string, conversationId: string): Promise<PlanConversation> {
    // A turn is one Claude call, so seconds not minutes — but poll patiently rather than racing it.
    const deadline = Date.now() + 3 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const conversation = await pollPlanConversation(accessToken, conversationId);
      if (conversation.turnStatus === "Running") {
        continue;
      }
      if (conversation.turnStatus === "Failed") {
        throw new ApiError(conversation.failureStatusCode ?? 502, conversation.error || "The conversation failed.");
      }
      return conversation;
    }
    throw new ApiError(504, "That reply is taking longer than expected. Try sending it again.");
  }

  return {
    async health() {
      const res = await fetch(`${root}/healthz`);
      if (!res.ok) {
        throw new ApiError(res.status, `Health check failed: ${res.status}`);
      }
      return (await res.json()) as HealthResponse;
    },

    async requestCode(request) {
      // Best-effort: a 202 has an empty body and a 429 is swallowed, so the UI shows the same
      // "code is on its way" message either way. Never reveals whether the email exists.
      try {
        await fetch(`${root}/api/auth/request-code`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        });
      } catch {
        // network error — still surface the uniform message to the user
      }
    },

    verifyCode: (request) => postJson<TokenResponse>("/api/auth/verify-code", request),

    refresh: (refreshToken) => postJson<TokenResponse>("/api/auth/refresh", { refreshToken }),

    async logout(refreshToken) {
      const res = await fetch(`${root}/api/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        throw new ApiError(res.status, `Logout failed: ${res.status}`);
      }
    },

    // Through `authed` (KEY-604) so a lapsed access token is renewed and retried like any other call — this is the
    // read that decides, on load, whether there is a session at all. No org header: /api/me is user-scoped.
    me: (accessToken) => authed<MeResponse>("GET", "/api/me", accessToken, undefined, null),

    async getProfile(accessToken) {
      const res = await fetch(`${root}/api/me/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, `Fetching your account failed: ${res.status}`);
      }
      return (await res.json()) as ProfileView;
    },

    async updateProfile(accessToken, request) {
      const res = await fetch(`${root}/api/me/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!res.ok) {
        // The 400s here are worth reading out loud — they say which field is wrong and why.
        throw new ApiError(res.status, (await res.text()) || `Saving your account failed: ${res.status}`);
      }
    },

    async renameMyOrganisation(accessToken, name) {
      const res = await fetch(`${root}/api/me/organisation/name`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        throw new ApiError(res.status, (await res.text()) || `Renaming your organisation failed: ${res.status}`);
      }
    },

    async getAiFeatures(accessToken) {
      const res = await fetch(`${root}/api/definitions/ai-features`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, `AI features lookup failed: ${res.status}`);
      }
      return (await res.json()) as AiFeaturesStatus;
    },

    async extractSourceDocument(accessToken, file, orgId) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/definitions/extract-document`, {
        method: "POST",
        headers: uploadHeaders(accessToken, orgId),
        body: form,
      });
      if (!res.ok) {
        // Surface the server's message (unsupported type / unreadable / too large) so the UI can show it.
        throw await apiFailure(res, "Reading the document failed");
      }
      return (await res.json()) as ExtractedSourceDocument;
    },

    // The three downloads below go through uploadHeaders, not a hand-rolled { Authorization }, so they
    // carry X-KeyDials-Org and X-KeyDials-Client like every other authenticated call. Dropping the org
    // header tenant-filters the request against the caller's home org and 404s a cross-org read.
    async downloadDefinitionTemplate(accessToken, options) {
      const query = new URLSearchParams();
      if (options?.items !== undefined) query.set("items", String(options.items));
      if (options?.scales) query.set("scales", options.scales);
      if (options?.checks !== undefined) query.set("checks", String(options.checks));
      if (options?.details !== undefined) query.set("details", String(options.details));
      const suffix = query.size > 0 ? `?${query}` : "";
      const res = await fetch(`${root}/api/definitions/template${suffix}`, {
        headers: uploadHeaders(accessToken),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Downloading the template failed");
      }
      return await res.blob();
    },

    async downloadDialTemplate(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/template`, {
        headers: uploadHeaders(accessToken),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Downloading the dial template failed");
      }
      return await res.blob();
    },

    async downloadDefinitionsBackup(accessToken, scope) {
      const res = await fetch(`${root}/api/definitions/backup?scope=${scope}`, {
        headers: uploadHeaders(accessToken),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Backing up the definitions failed");
      }
      return await res.blob();
    },

    async downloadDefinitionBundle(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/export-bundle`, {
        headers: uploadHeaders(accessToken),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Exporting the group failed");
      }
      return await res.blob();
    },

    getSitePublishStatus: (t) => authed<SitePublishStatusView>("GET", "/api/admin/site/publish", t),
    publishSite: (t) => authed<SitePublishStartedView>("POST", "/api/admin/site/publish", t),
    scheduleSitePublish: (t, request) =>
      authed<ScheduleSitePublishResponse>("POST", "/api/admin/site/publish/schedule", t, request),
    cancelScheduledSitePublish: (t) => authed<void>("DELETE", "/api/admin/site/publish/schedule", t),

    async uploadPartnerLogo(accessToken, file) {
      return await uploadLogo(accessToken, "/api/admin/site/logo", file);
    },

    async uploadCrmSourceLogo(accessToken, file) {
      return await uploadLogo(accessToken, "/api/crm/sources/logo", file);
    },

    async uploadCrmCampaignLogo(accessToken, file) {
      return await uploadLogo(accessToken, "/api/crm/campaigns/logo", file);
    },

    async previewDefinitionsRestore(accessToken, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/definitions/restore/preview`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Reading the backup failed");
      }
      return (await res.json()) as DefinitionRestorePreviewResult;
    },

    async restoreDefinitions(accessToken, file, selections) {
      const form = new FormData();
      form.append("file", file);
      form.append("selections", JSON.stringify(selections));
      const res = await fetch(`${root}/api/definitions/restore`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Restoring the backup failed");
      }
      return (await res.json()) as DefinitionRestoreApplyResult;
    },

    async importDefinitionSpreadsheet(accessToken, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/definitions/import-spreadsheet`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Reading the spreadsheet failed");
      }
      return (await res.json()) as SpreadsheetImportResult;
    },

    async previewReimport(accessToken, id, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/definitions/${id}/reimport/preview`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Reading the update failed");
      }
      return (await res.json()) as ReimportPreviewResult;
    },

    async applyReimport(accessToken, id, file, removeAbsent) {
      const form = new FormData();
      form.append("file", file);
      form.append("removeAbsent", removeAbsent ? "true" : "false");
      const res = await fetch(`${root}/api/definitions/${id}/reimport/apply`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Applying the update failed");
      }
    },

    async startPlanConversation(accessToken, request) {
      const res = await fetch(`${root}/api/definitions/plan-conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(request),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Couldn't start the conversation");
      }
      const { id } = (await res.json()) as { id: string };
      return awaitPlanTurn(accessToken, id);
    },

    async sendPlanConversationMessage(accessToken, conversationId, message) {
      const res = await fetch(`${root}/api/definitions/plan-conversations/${conversationId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Couldn't send that message");
      }
      return awaitPlanTurn(accessToken, conversationId);
    },

    getPlanConversation(accessToken, conversationId) {
      return pollPlanConversation(accessToken, conversationId);
    },

    async generateDefinitionAsync(accessToken, request, options) {
      const { jobId } = await startGenerationJob(accessToken, request, options?.signal);
      const startedAt = Date.now();
      // Generation runs server-side off the request thread, so there's no inbound timeout to beat — just poll
      // until it finishes. A generous cap avoids an unbounded loop if a worker is wedged.
      const intervalMs = 2000;
      const deadline = startedAt + 6 * 60 * 1000; // 6 minutes
      while (Date.now() < deadline) {
        await waitOrAbort(intervalMs, options?.signal);
        const status = await pollGenerationJob(accessToken, jobId, options?.signal);
        if (status.status === "Succeeded") {
          if (!status.definition) {
            throw new ApiError(502, "Generation finished but returned no definition.");
          }
          return status.definition;
        }
        if (status.status === "Failed") {
          throw new ApiError(status.failureStatusCode ?? 502, status.error || "AI generation failed.");
        }
        options?.onPoll?.(Date.now() - startedAt);
      }
      throw new ApiError(504, "Generation is taking longer than expected. Please try again.");
    },

    async generateBlueprint(accessToken, request) {
      const res = await fetch(`${root}/api/definitions/blueprint`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(request),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Blueprint generation failed");
      }
      return (await res.json()) as GeneratedBlueprint;
    },

    async createDefinition(accessToken, definition) {
      const res = await fetch(`${root}/api/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(definition),
      });
      if (!res.ok) {
        throw await apiFailure(res, "Saving the draft failed");
      }
      return (await res.json()) as { id: string };
    },

    async listDefinitions(accessToken) {
      const res = await fetch(`${root}/api/definitions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, `List failed: ${res.status}`);
      }
      return (await res.json()) as DefinitionSummary[];
    },

    async getDefinition(accessToken, id, version) {
      const qs = version == null ? "" : `?version=${version}`;
      const res = await fetch(`${root}/api/definitions/${id}${qs}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, `Get failed: ${res.status}`);
      }
      return (await res.json()) as DefinitionDetail;
    },

    async listDefinitionVersions(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/versions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, `List versions failed: ${res.status}`);
      }
      return (await res.json()) as DefinitionVersionSummary[];
    },

    async uploadCentralImage(accessToken, id, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/definitions/${id}/central-image`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Image upload failed");
      }
      return (await res.json()) as { reference: string; url: string | null };
    },

    requestGuidanceVideoUpload: (accessToken, id, contentType, sizeBytes) =>
      authed<GuidanceVideoUploadTicket>("POST", `/api/definitions/${id}/guidance-video/upload-url`, accessToken, { contentType, sizeBytes }),
    requestGuidanceAudioUpload: (accessToken, id, contentType, sizeBytes) =>
      authed<GuidanceVideoUploadTicket>("POST", `/api/definitions/${id}/guidance-audio/upload-url`, accessToken, { contentType, sizeBytes }),

    requestGuidanceDocumentUpload: (accessToken, id, contentType, sizeBytes, fileName) =>
      authed<GuidanceDocumentUploadTicket>("POST", `/api/definitions/${id}/guidance-document/upload-url`, accessToken, { contentType, sizeBytes, fileName }),

    async uploadInstanceCentralImage(accessToken, instanceId, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/instances/${instanceId}/central-image`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Logo upload failed");
      }
      return (await res.json()) as { reference: string; url: string | null };
    },

    async publishDefinition(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/publish`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        // The publish gate explains exactly what's missing (e.g. an unpublished group child), so carry
        // the server's message through rather than showing a bare status code.
        throw await apiFailure(res, "Publish failed");
      }
    },

    // Checking (KEY-283). All three carry the server's message through: "send" reports the same integrity
    // gate as publish, and the other two report a lifecycle that moved under the user (another tab).
    async sendDefinitionForChecking(accessToken, id, audience) {
      // KEY-545: the body only goes when there is an audience to state. The server's parameter is optional
      // precisely so a bodyless POST still means "the whole organisation" — sending `{}` or a Content-Type
      // with no body would be a different, and worse, request.
      const res = await fetch(`${root}/api/definitions/${id}/checking`, {
        method: "POST",
        headers: audience
          ? { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
          : { Authorization: `Bearer ${accessToken}` },
        body: audience ? JSON.stringify({ audience }) : undefined,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Couldn't send this dial for checking");
      }
    },

    async revertDefinitionToDraft(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/checking/revert`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw await apiFailure(res, "Couldn't revert this dial to draft");
      }
    },

    async recreateCheckingDial(accessToken, id, audience) {
      const res = await fetch(`${root}/api/definitions/${id}/checking/recreate`, {
        method: "POST",
        headers: audience
          ? { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
          : { Authorization: `Bearer ${accessToken}` },
        body: audience ? JSON.stringify({ audience }) : undefined,
      });
      if (!res.ok) {
        throw await apiFailure(res, "Couldn't create a fresh test dial");
      }
    },

    // Organisations
    getOrganisationPlacementRules: (t) =>
      authed<OrganisationPlacementRules>("GET", "/api/admin/organisations/placement-rules", t),
    reorderChildOrganisations: (t, parentId, orderedChildIds) =>
      authed<void>("POST", `/api/admin/organisations/${parentId}/children/order`, t, { orderedChildIds }),
    changeOrganisationType: (t, id, type) =>
      authed<void>("POST", `/api/admin/organisations/${id}/type`, t, { type }),
    listOrganisations: (t, includeArchived) =>
      authed<OrganisationSummary[]>("GET", `/api/admin/organisations${includeArchived ? "?includeArchived=true" : ""}`, t),
    getOrganisation: (t, id) => authed<OrganisationDetail>("GET", `/api/admin/organisations/${id}`, t),
    createOrganisation: (t, request) => authed<{ id: string }>("POST", "/api/admin/organisations", t, request),
    updateOrganisation: (t, id, request) => authed<void>("PATCH", `/api/admin/organisations/${id}`, t, request),
    getOrganisationDeletionInfo: (t, id) =>
      authed<OrganisationDeletionInfo>("GET", `/api/admin/organisations/${id}/deletion-info`, t),
    archiveOrganisation: (t, id) => authed<void>("POST", `/api/admin/organisations/${id}/archive`, t),
    unarchiveOrganisation: (t, id) => authed<void>("POST", `/api/admin/organisations/${id}/unarchive`, t),
    deleteOrganisation: (t, id) => authed<void>("DELETE", `/api/admin/organisations/${id}`, t),
    getEntitlements: (t, orgId) => authed<OrganisationEntitlements>("GET", `/api/admin/organisations/${orgId}/entitlements`, t),
    setEntitlement: (t, orgId, request) => authed<void>("PUT", `/api/admin/organisations/${orgId}/entitlements`, t, request),
    exportOrganisation: (t, id) => authed<ExportedOrganisationTree>("GET", `/api/admin/organisations/${id}/export`, t),
    previewOrganisationImport: (t, request) =>
      authed<OrganisationImportPreviewResult>("POST", "/api/admin/organisations/import/preview", t, request),
    importOrganisation: (t, request) =>
      authed<OrganisationImportReport>("POST", "/api/admin/organisations/import", t, request),

    // Roles, members, users
    listRoles: (t) => authed<RoleSummary[]>("GET", "/api/admin/roles", t),
    onboardPerson: (t, request) => authed<OnboardPersonResult>("POST", "/api/admin/onboarding/person", t, request),
    inviteInstanceMember: (t, instanceId, request) => authed<InviteInstanceMemberResult>("POST", `/api/instances/${instanceId}/members/invite`, t, request),
    previewInviteInstanceMemberEmail: (t, instanceId, request) => authed<MemberEmailPreview>("POST", `/api/instances/${instanceId}/members/invite/preview-email`, t, request),
    listUsers: (t, includeArchived) =>
      authed<UserSummary[]>("GET", `/api/admin/users${includeArchived ? "?includeArchived=true" : ""}`, t),
    searchUsers: (t, query) => {
      const p = new URLSearchParams({ q: query.q });
      if (query.orgId) p.set("orgId", query.orgId);
      if (query.status) p.set("status", query.status);
      if (query.skip) p.set("skip", String(query.skip));
      if (query.take) p.set("take", String(query.take));
      return authed<PeopleSearchResult>("GET", `/api/admin/users/search?${p.toString()}`, t);
    },
    getUserUsage: (t, userId) => authed<UserUsage>("GET", `/api/admin/users/${userId}/usage`, t),
    archiveUser: (t, userId) => authed<void>("POST", `/api/admin/users/${userId}/archive`, t),
    restoreUser: (t, userId) => authed<void>("POST", `/api/admin/users/${userId}/restore`, t),
    changeUserEmail: (t, userId, email) => authed<void>("PUT", `/api/admin/users/${userId}/email`, t, { email }),
    deleteUser: (t, userId) => authed<void>("DELETE", `/api/admin/users/${userId}`, t),
    impersonate: (t, userId) => authed<ImpersonationResponse>("POST", `/api/admin/impersonation/${userId}`, t),
    listMembers: (t, orgId) => authed<OrgMemberSummary[]>("GET", `/api/admin/organisations/${orgId}/members`, t),
    addMember: (t, orgId, request) => authed<AddOrgMemberResult>("POST", `/api/admin/organisations/${orgId}/members`, t, request),
    setMemberRole: (t, orgId, userId, request) =>
      authed<void>("PUT", `/api/admin/organisations/${orgId}/members/${userId}`, t, request),
    setMemberBuilder: (t, orgId, userId, request) =>
      authed<void>("PUT", `/api/admin/organisations/${orgId}/members/${userId}/builder`, t, request),
    removeMember: (t, orgId, userId) => authed<void>("DELETE", `/api/admin/organisations/${orgId}/members/${userId}`, t),

    // Definition editing
    setDefinitionInstanceCustomisation: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/instance-customisation`, t, { value }),
    setDefinitionTemplate: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/is-template`, t, { value }),
    setDefinitionLogoOverride: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/logo-override`, t, { value }),
    setDefinitionLogoOverrideDefault: (t, id, value) =>
      authed<void>("PUT", `/api/definitions/${id}/logo-override-default`, t, { value }),
    setDefinitionRetention: (t, id, days) => authed<void>("PUT", `/api/definitions/${id}/retention`, t, { days }),
    setDefinitionSectionStatus: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/section-status`, t, { value }),
    setDefinitionMilo: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/milo`, t, { value }),
    setDefinitionLookBack: (t, id, value) => authed<void>("PUT", `/api/definitions/${id}/look-back`, t, { value }),
    setDefinitionDemoSource: (t, id, instanceId) =>
      authed<void>("PUT", `/api/definitions/${id}/demo-source`, t, { instanceId }),
    resetDemoInstance: (t, instanceId) => authed<{ id: string }>("POST", `/api/instances/${instanceId}/demo/reset`, t),
    setDefinitionSegmentNoun: (t, id, singular, plural, options) =>
      authed<void>("PUT", `/api/definitions/${id}/segment-noun`, t, { singular, plural }, homeOrg(options)),
    setDefinitionLabelOverrides: (t, id, overrides, options) =>
      authed<void>("PUT", `/api/definitions/${id}/label-overrides`, t, { overrides }, homeOrg(options)),
    setDefinitionAutoLock: (t, id, value, timeoutSeconds) =>
      authed<void>("PUT", `/api/definitions/${id}/auto-lock`, t, { value, timeoutSeconds }),
    setDefinitionRoleActions: (t, id, body) =>
      authed<void>("PUT", `/api/definitions/${id}/role-actions`, t, body),
    armReimport: (t, id) => authed<void>("POST", `/api/definitions/${id}/reimport-lock`, t, {}),
    cancelReimport: (t, id) => authed<void>("DELETE", `/api/definitions/${id}/reimport-lock`, t),
    listInstanceLogoOverrides: (t, definitionId) =>
      authed<InstanceLogoOverrideRow[]>("GET", `/api/definitions/${definitionId}/instances/logo-override`, t),
    setInstancesLogoOverride: (t, definitionId, instanceIds, enabled, includeSubdials) =>
      authed<SetInstancesLogoOverrideResult>(
        "PUT", `/api/definitions/${definitionId}/instances/logo-override`, t, { instanceIds, enabled, includeSubdials }),
    updateDefinition: (t, id, definition, options) => authed<void>("PUT", `/api/definitions/${id}`, t, definition, homeOrg(options)),
    updateDefinitionMetadata: (t, id, metadata) => authed<void>("PUT", `/api/definitions/${id}/metadata`, t, metadata),
    createDefinitionVersion: (t, id, options) => authed<{ id: string }>("POST", `/api/definitions/${id}/versions`, t, {}, homeOrg(options)),
    exportDefinition: (t, id) => authed<ExportedDefinition>("GET", `/api/definitions/${id}/export`, t),
    importDefinition: (t, payload) => authed<DefinitionImportResult>("POST", "/api/definitions/import", t, payload),
    duplicateDefinition: (t, id, body) =>
      authed<DefinitionDuplicateResult>("POST", `/api/definitions/${id}/duplicate`, t, body ?? {}),
    importDefinitionVersion: (t, id, payload, replaceDraft) =>
      authed<DefinitionVersionImportResult>(
        "POST",
        `/api/definitions/${id}/versions/import${replaceDraft ? "?replaceDraft=true" : ""}`,
        t,
        payload,
      ),
    deleteDefinition: (t, id, options) => authed<void>("DELETE", `/api/definitions/${id}`, t, undefined, homeOrg(options)),

    // Self-service authoring (KEY-560). Every call goes to the organisation the author signed in to: no org header, whatever
    // a dial left active.
    getAuthoringStatus: (t) => authed<AuthoringStatus>("GET", "/api/authoring/status", t, undefined, null),
    getAuthoringAllowance: (t) => authed<OrganisationAllowance>("GET", "/api/authoring/allowance", t, undefined, null),
    listAuthoringTemplates: (t) => authed<DefinitionTemplateSummary[]>("GET", "/api/authoring/templates", t, undefined, null),
    copyAuthoringTemplate: (t, templateId) =>
      authed<{ id: string }>("POST", `/api/authoring/templates/${templateId}/copy`, t, undefined, null),
    listMyDesigns: (t) => authed<AuthoredDesign[]>("GET", "/api/authoring/designs", t, undefined, null),
    getDefinitionReadiness: (t, id, purpose) =>
      authed<DefinitionReadiness>("GET", `/api/definitions/${id}/readiness?for=${purpose}`, t, undefined, null),

    async goLive(accessToken, id) {
      const res = await fetch(`${root}/api/definitions/${id}/go-live`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, ...(clientApp ? { "X-KeyDials-Client": clientApp } : {}) },
      });
      if (res.ok) {
        const body = (await res.json()) as { instanceId: string };
        return { ok: true, instanceId: body.instanceId };
      }
      // Every go-live answer is a GoLiveResponse; the two the builder shows resolve, so a checklist or "already live" isn't an exception.
      if (res.status === 400 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as
          | { instanceId?: string | null; message?: string | null; problems?: DefinitionProblem[] | null }
          | null;
        return {
          ok: false,
          status: res.status,
          message: body?.message ?? `Going live failed (HTTP ${res.status})`,
          problems: body?.problems ?? [],
          instanceId: body?.instanceId ?? null,
        };
      }
      throw await apiFailure(res, "Going live failed");
    },

    // Catalogue folders
    listDefinitionFolders: (t) => authed<DefinitionFolder[]>("GET", "/api/definitions/folders", t),
    createDefinitionFolder: (t, name, parentFolderId) =>
      authed<{ id: string }>("POST", "/api/definitions/folders", t, { name, parentFolderId: parentFolderId ?? null }),
    renameDefinitionFolder: (t, id, name) => authed<void>("PUT", `/api/definitions/folders/${id}`, t, { name }),
    moveDefinitionFolder: (t, id, parentFolderId) =>
      authed<void>("PUT", `/api/definitions/folders/${id}/parent`, t, { parentFolderId }),
    deleteDefinitionFolder: (t, id, moveContents) =>
      authed<void>("DELETE", `/api/definitions/folders/${id}${moveContents ? "?moveContents=true" : ""}`, t),
    moveDefinitionsToFolder: (t, definitionIds, folderId) =>
      authed<void>("POST", "/api/definitions/folders/move-definitions", t, { definitionIds, folderId }),

    // Status palettes
    listPalettes: (t) => authed<PaletteDetail[]>("GET", "/api/palettes", t),
    getPalette: (t, id) => authed<PaletteDetail>("GET", `/api/palettes/${id}`, t),
    createPalette: (t, request) => authed<{ id: string }>("POST", "/api/palettes", t, request),
    updatePalette: (t, id, request) => authed<void>("PUT", `/api/palettes/${id}`, t, request),
    deletePalette: (t, id) => authed<void>("DELETE", `/api/palettes/${id}`, t),

    // Dashboard / admin
    getStats: (t) => authed<DashboardStats>("GET", "/api/admin/stats", t),
    getDataToolsStatus: (t) => authed<DataToolsStatus>("GET", "/api/admin/dev/data-tools", t),
    resetData: (t, options) => authed<DataResetSummary>("POST", "/api/admin/dev/reset", t, options),
    getUserMemberships: (t, userId) => authed<UserMembershipView[]>("GET", `/api/admin/users/${userId}/memberships`, t),
    getUserInstances: (t, userId) => authed<UserInstanceView[]>("GET", `/api/admin/users/${userId}/instances`, t),

    // Roles & capabilities
    listCapabilities: (t) => authed<CapabilityView[]>("GET", "/api/admin/capabilities", t),
    listAllRoles: (t) => authed<RoleDetailView[]>("GET", "/api/admin/roles/all", t),
    listRoleMembers: (t, id) => authed<RoleMemberView[]>("GET", `/api/admin/roles/${id}/members`, t),
    createRole: (t, request) => authed<{ id: string }>("POST", "/api/admin/roles", t, request),
    setRoleCapabilities: (t, id, request) => authed<void>("PUT", `/api/admin/roles/${id}/capabilities`, t, request),
    deleteRole: (t, id) => authed<void>("DELETE", `/api/admin/roles/${id}`, t),

    // System settings
    listSettings: (t) => authed<SettingView[]>("GET", "/api/admin/settings", t),
    updateSetting: (t, key, request) =>
      authed<void>("PUT", `/api/admin/settings/${encodeURIComponent(key)}`, t, request),
    listAiModels: (t) => authed<AiModelOption[]>("GET", "/api/admin/settings/ai/models", t),

    // User feedback → Linear
    submitBugReport: (t, request, attachments) =>
      submitFeedback(t, "bug", {
        whatWereYouDoing: request.whatWereYouDoing,
        whatDidYouExpect: request.whatDidYouExpect,
        whatActuallyHappened: request.whatActuallyHappened,
        priority: request.priority,
      }, attachments),
    submitFeatureRequest: (t, request, attachments) =>
      submitFeedback(t, "feature", { description: request.description, priority: request.priority }, attachments),
    listFeedback: (t, query) => authed<FeedbackListResponse>("GET", `/api/feedback${buildFeedbackQuery(query)}`, t),
    getFeedback: (t, id) => authed<UserFeedbackDetail>("GET", `/api/feedback/${id}`, t),
    updateFeedback: (t, id, request) => authed<void>("PUT", `/api/feedback/${id}`, t, request),
    archiveFeedback: (t, id) => authed<void>("POST", `/api/feedback/${id}/archive`, t),
    unarchiveFeedback: (t, id) => authed<void>("POST", `/api/feedback/${id}/unarchive`, t),

    // Feedback clarification threads
    getClarification: (t, id) => authed<FeedbackClarificationDetail>("GET", `/api/feedback/${id}/clarification`, t),
    requestClarification: (t, id, request) => authed<void>("POST", `/api/feedback/${id}/clarify`, t, request),
    replyToClarification: (t, id, request) => authed<void>("POST", `/api/feedback/${id}/reply`, t, request),
    markClarificationRead: (t, id) => authed<void>("POST", `/api/feedback/${id}/clarify/read`, t),
    listMyFeedbackClarifications: (t) =>
      authed<FeedbackClarificationListResponse>("GET", "/api/me/feedback-clarifications", t),

    // --- Billing (KEY-366) ---
    getBillingStatus: (t) => authed<BillingStatusResponse>("GET", "/api/billing/status", t),
    listBillingPlans: (t, includeRetired) =>
      authed<BillingPlanListResponse>("GET", `/api/billing/plans${includeRetired === false ? "?includeRetired=false" : ""}`, t),
    createBillingPlan: (t, request) => authed<{ id: string }>("POST", "/api/billing/plans", t, request),
    updateBillingPlan: (t, planId, request) => authed<void>("PUT", `/api/billing/plans/${planId}`, t, request),
    updateBillingPlanPrice: (t, planId, request) =>
      authed<void>("PUT", `/api/billing/plans/${planId}/price`, t, request),
    setBillingPlanRetired: (t, planId, retired) =>
      authed<void>("POST", `/api/billing/plans/${planId}/retire?retired=${retired}`, t),
    setBillingPlanShownOnWebsite: (t, planId, shown) =>
      authed<void>("POST", `/api/billing/plans/${planId}/website?shown=${shown}`, t),
    deleteBillingPlan: (t, planId) => authed<void>("DELETE", `/api/billing/plans/${planId}`, t),
    getTrialDrip: (t, planId) => authed<TrialDripStateResponse>("GET", `/api/billing/plans/${planId}/trial-drip`, t),
    saveTrialDrip: (t, planId, request) => authed<void>("PUT", `/api/billing/plans/${planId}/trial-drip`, t, request),
    async unsubscribeMarketing(token) {
      const res = await fetch(`${root}/api/billing/public/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      return res.ok;
    },
    getBillingDrift: (t) => authed<BillingDriftReport>("GET", "/api/billing/drift", t),
    getLapsedDataRetention: (t) => authed<LapsedDataRetentionReport>("GET", "/api/admin/billing/retention", t),
    setRetentionHold: (t, organisationId, hold) =>
      authed<void>("PUT", `/api/admin/billing/retention/${organisationId}/hold`, t, { hold }),
    getBillingDashboardSummary: (t) => authed<BillingDashboardSummary>("GET", "/api/billing/dashboard-summary", t),
    getMyPartnerEarnings: (t) =>
      authed<PartnerOwnEarningsView | null>("GET", "/api/billing/my-partner/earnings", t),
    listMyPartnerDiscountCodes: (t) =>
      authed<PartnerDiscountCodeView[]>("GET", "/api/billing/my-partner/discount-codes", t),
    getOrgSubscription: (t, organisationId) =>
      authed<OrgSubscriptionView | null>("GET", `/api/admin/organisations/${organisationId}/subscription`, t),
    getOrgAiUsage: (t, organisationId) =>
      authed<AiUsageView | null>("GET", `/api/admin/organisations/${organisationId}/ai-usage`, t),
    getInstanceAiCost: (t, instanceId, month) =>
      authed<AiCostView>("GET", `/api/admin/ai-cost/instances/${instanceId}${month ? `?month=${month}` : ""}`, t),
    getOrgAiCost: (t, organisationId, month) =>
      authed<AiCostView>("GET", `/api/admin/ai-cost/organisations/${organisationId}${month ? `?month=${month}` : ""}`, t),
    grantOrgSubscription: (t, organisationId, request) =>
      authed<GrantSubscriptionResult>("PUT", `/api/admin/organisations/${organisationId}/subscription`, t, request),
    cancelOrgSubscription: (t, organisationId) =>
      authed<void>("DELETE", `/api/admin/organisations/${organisationId}/subscription`, t),
    listOrgPayments: (t, organisationId, limit) =>
      authed<OrgPaymentView[]>(
        "GET", `/api/admin/organisations/${organisationId}/payments${limit ? `?limit=${limit}` : ""}`, t),
    listOrgAddOns: (t, organisationId) =>
      authed<SubscriptionAddOnView[]>("GET", `/api/admin/organisations/${organisationId}/add-ons`, t),
    setOrgAddOn: (t, organisationId, request) =>
      authed<SubscriptionAddOnView>("PUT", `/api/admin/organisations/${organisationId}/add-ons`, t, request),
    removeOrgAddOn: (t, organisationId, addOnId) =>
      authed<void>("DELETE", `/api/admin/organisations/${organisationId}/add-ons/${addOnId}`, t),
    previewOrgPlanChange: (t, organisationId, request) =>
      authed<PlanChangePreviewView>(
        "POST", `/api/admin/organisations/${organisationId}/subscription/plan-change/preview`, t, request),
    applyOrgPlanChange: (t, organisationId, request) =>
      authed<PlanChangeResultView>(
        "POST", `/api/admin/organisations/${organisationId}/subscription/plan-change`, t, request),

    // --- CRM (KEY-311) ---
    getCrmStatus: (t) => authed<CrmStatusResponse>("GET", "/api/crm/status", t),
    listCrmCompanies: (t, query) => authed<CrmCompanyListResponse>("GET", `/api/crm/companies${buildCrmCompaniesQuery(query)}`, t),
    getCrmCompany: (t, id) => authed<CrmCompanyDetailView>("GET", `/api/crm/companies/${id}`, t),
    createCrmCompany: (t, request) => authed<{ id: string }>("POST", "/api/crm/companies", t, request),
    updateCrmCompany: (t, id, request) => authed<void>("PUT", `/api/crm/companies/${id}`, t, request),
    archiveCrmCompany: (t, id) => authed<void>("POST", `/api/crm/companies/${id}/archive`, t),
    restoreCrmCompany: (t, id) => authed<void>("POST", `/api/crm/companies/${id}/restore`, t),
    listCrmPeople: (t, query) => authed<CrmPersonListResponse>("GET", `/api/crm/people${buildCrmPeopleQuery(query)}`, t),
    listCrmBulkEmailTokens: (t) => authed<CrmBulkEmailTokenView[]>("GET", "/api/crm/bulk-email/tokens", t),
    sendCrmBulkEmail: (t, request) => authed<CrmBulkEmailResultView>("POST", "/api/crm/bulk-email", t, request),
    getCrmPerson: (t, id) => authed<CrmPersonDetailView>("GET", `/api/crm/people/${id}`, t),
    createCrmPerson: (t, request) => authed<{ id: string }>("POST", "/api/crm/people", t, request),
    updateCrmPerson: (t, id, request) => authed<void>("PUT", `/api/crm/people/${id}`, t, request),
    archiveCrmPerson: (t, id) => authed<void>("POST", `/api/crm/people/${id}/archive`, t),
    restoreCrmPerson: (t, id) => authed<void>("POST", `/api/crm/people/${id}/restore`, t),
    linkCrmPersonCompany: (t, id, companyId) => authed<void>("POST", `/api/crm/people/${id}/link-company`, t, { companyId }),
    promoteCrmPerson: (t, id, request) => authed<{ id: string }>("POST", `/api/crm/people/${id}/promote`, t, request),
    listCrmEnquiries: (t, query) => authed<CrmEnquiryListResponse>("GET", `/api/crm/enquiries${buildCrmEnquiriesQuery(query)}`, t),
    getCrmEnquiry: (t, id) => authed<CrmEnquiryDetailView>("GET", `/api/crm/enquiries/${id}`, t),
    createCrmEnquiry: (t, request) => authed<CreateCrmEnquiryResponse>("POST", "/api/crm/enquiries", t, request),
    changeCrmEnquiryStatus: (t, id, request) => authed<void>("POST", `/api/crm/enquiries/${id}/status`, t, request),
    assignCrmEnquiry: (t, id, userId) => authed<void>("POST", `/api/crm/enquiries/${id}/assign`, t, { userId }),
    listCrmAssignees: (t, enquiryId) => authed<CrmAssigneeListResponse>("GET", `/api/crm/enquiries/${enquiryId}/assignees`, t),
    getCrmConversionPreview: (t, companyId) => authed<CrmConversionPreview>("GET", `/api/crm/companies/${companyId}/conversion`, t),
    convertCrmCompany: (t, companyId, request) => authed<ConvertCrmCompanyResult>("POST", `/api/crm/companies/${companyId}/convert`, t, request),
    getCrmBookingReadiness: (t) => authed<CrmBookingReadinessResponse>("GET", "/api/crm/booking-readiness", t),
    listCrmCalendarLinks: (t) => authed<CrmCalendarLinkListResponse>("GET", "/api/crm/calendar-links", t),
    connectCrmCalendar: (t, provider) => authed<CrmCalendarConnectResponse>("POST", `/api/crm/calendar-links/${provider}/connect`, t),
    syncCrmCalendar: (t, id) => authed<void>("POST", `/api/crm/calendar-links/${id}/sync`, t),
    disconnectCrmCalendar: (t, id) => authed<void>("DELETE", `/api/crm/calendar-links/${id}`, t),
    listCrmAppointmentAgents: (t) => authed<CrmAgentOption[]>("GET", "/api/crm/appointments/agents", t),
    createCrmAppointment: (t, request) => authed<CrmAppointmentView>("POST", "/api/crm/appointments", t, request),
    updateCrmAppointment: (t, id, request) => authed<CrmAppointmentView>("PUT", `/api/crm/appointments/${id}`, t, request),
    deleteCrmAppointment: (t, id) => authed<void>("DELETE", `/api/crm/appointments/${id}`, t),
    getCrmTeamAvailability: (t, organisationId) => authed<CrmTeamAvailabilityResponse>(
      "GET", `/api/crm/availability/team${organisationId ? `?organisationId=${encodeURIComponent(organisationId)}` : ""}`, t),
    getCrmCalendar: (t, q) => authed<CrmCalendarResponse>(
      "GET",
      `/api/crm/calendar?from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}&scope=${q.scope}`
        + (q.organisationId ? `&organisationId=${encodeURIComponent(q.organisationId)}` : ""),
      t),
    getCrmEnquiryBooking: async (t, enquiryId) =>
      (await authed<CrmBookingView | undefined>("GET", `/api/crm/enquiries/${enquiryId}/booking`, t)) ?? null,
    cancelCrmEnquiryBooking: (t, enquiryId, reason) =>
      authed<void>("POST", `/api/crm/enquiries/${enquiryId}/booking/cancel`, t, { reason }),
    getMyCrmAvailability: (t) => authed<CrmAvailabilityView>("GET", "/api/crm/availability/mine", t),
    addMyCrmAvailabilityWindow: (t, request) => authed<{ id: string }>("POST", "/api/crm/availability/mine/windows", t, request),
    removeMyCrmAvailabilityWindow: (t, id) => authed<void>("DELETE", `/api/crm/availability/mine/windows/${id}`, t),
    addMyCrmAvailabilityException: (t, request) => authed<{ id: string }>("POST", "/api/crm/availability/mine/exceptions", t, request),
    removeMyCrmAvailabilityException: (t, id) => authed<void>("DELETE", `/api/crm/availability/mine/exceptions/${id}`, t),
    listCrmInteractions: (t, query) => authed<CrmInteractionListResponse>("GET", `/api/crm/interactions${buildCrmInteractionsQuery(query)}`, t),
    listCrmTodos: (t, query) => {
      const params = new URLSearchParams();
      if (query?.scope) params.set("scope", query.scope);
      if (query?.includeDone) params.set("includeDone", "true");
      if (query?.page) params.set("page", String(query.page));
      if (query?.pageSize) params.set("pageSize", String(query.pageSize));
      const qs = params.toString();
      return authed<CrmTodoListResponse>("GET", `/api/crm/todos${qs ? `?${qs}` : ""}`, t);
    },
    createCrmTodo: (t, request) => authed<{ id: string }>("POST", "/api/crm/todos", t, request),
    updateCrmTodo: (t, id, request) => authed<void>("PUT", `/api/crm/todos/${id}`, t, request),
    setCrmTodoDone: (t, id, done) => authed<void>("POST", `/api/crm/todos/${id}/done?done=${done}`, t),
    deleteCrmTodo: (t, id) => authed<void>("DELETE", `/api/crm/todos/${id}`, t),
    getCrmTodoEmailPreferences: (t) => authed<CrmTodoEmailPreferences>("GET", "/api/crm/todos/email-preferences", t),
    saveCrmTodoEmailPreferences: (t, preferences) => authed<void>("PUT", "/api/crm/todos/email-preferences", t, preferences),
    getCrmDashboard: (t) => authed<CrmDashboardResponse>("POST", "/api/crm/dashboard", t),
    logCrmInteraction: (t, request) => authed<{ id: string }>("POST", "/api/crm/interactions", t, request),
    requestCrmAttachmentUpload: (t, request) =>
      authed<CrmAttachmentUploadTicket>("POST", "/api/crm/attachments/upload-url", t, request),
    confirmCrmAttachmentUploaded: (t, id) => authed<void>("POST", `/api/crm/attachments/${id}/confirm`, t),
    listCrmAttachments: (t, query) => {
      const params = new URLSearchParams();
      if (query.personId) params.set("personId", query.personId);
      if (query.interactionId) params.set("interactionId", query.interactionId);
      return authed<CrmAttachmentView[]>("GET", `/api/crm/attachments?${params.toString()}`, t);
    },
    getCrmAttachmentDownloadUrl: (t, id) =>
      authed<CrmAttachmentDownloadTicket>("GET", `/api/crm/attachments/${id}/download-url`, t),
    deleteCrmAttachment: (t, id) => authed<void>("DELETE", `/api/crm/attachments/${id}`, t),
    sendCrmEmail: (t, personId, request) =>
      authed<SendCrmEmailResponse>("POST", `/api/crm/people/${personId}/send-email`, t, request),
    listCrmCampaigns: (t, includeInactive) =>
      authed<CrmCampaignListResponse>("GET", `/api/crm/campaigns${includeInactive ? "?includeInactive=true" : ""}`, t),
    getCrmCampaign: (t, id) => authed<CrmCampaignDetailView>("GET", `/api/crm/campaigns/${id}`, t),
    listCrmCampaignEvents: (t) => authed<CrmCampaignEventListResponse>("GET", "/api/crm/campaigns/events", t),
    listCrmCampaignPlans: (t, campaignId) => authed<CampaignPlanOptionsView>(
      "GET",
      campaignId ? `/api/crm/campaigns/plans?campaignId=${encodeURIComponent(campaignId)}` : "/api/crm/campaigns/plans",
      t),
    createCrmCampaign: (t, request) => authed<{ id: string }>("POST", "/api/crm/campaigns", t, request),
    updateCrmCampaign: (t, id, request) => authed<void>("PUT", `/api/crm/campaigns/${id}`, t, request),
    setCrmCampaignActive: (t, id, active) =>
      authed<void>("POST", `/api/crm/campaigns/${id}/${active ? "reactivate" : "deactivate"}`, t),
    shareCrmCampaign: (t, id, request) =>
      authed<ShareCrmCampaignResultView>("POST", `/api/crm/campaigns/${id}/share`, t, request),
    previewCrmCampaignShare: (t, id, request) =>
      authed<CrmCampaignSharePreviewView>("POST", `/api/crm/campaigns/${id}/share/preview`, t, request),
    getCampaignTrialEmails: (t, campaignId) =>
      authed<CampaignTrialEmailsResponse>("GET", `/api/crm/campaigns/${campaignId}/trial-emails`, t),
    saveCampaignTrialEmails: (t, campaignId, request) =>
      authed<void>("PUT", `/api/crm/campaigns/${campaignId}/trial-emails`, t, request),
    async getPublicCampaign(publicKey) {
      const res = await fetch(`${root}/api/crm/campaign/${encodeURIComponent(publicKey)}`);
      if (!res.ok) throw await apiFailure(res, "This sign-up link isn't valid");
      return (await res.json()) as PublicCampaignView;
    },
    listCrmSources: (t, includeInactive) =>
      authed<CrmEnquirySourceListResponse>("GET", `/api/crm/sources${includeInactive ? "?includeInactive=true" : ""}`, t),
    createCrmSource: (t, request) => authed<{ id: string }>("POST", "/api/crm/sources", t, request),
    updateCrmSource: (t, id, request) => authed<void>("PUT", `/api/crm/sources/${id}`, t, request),
    updateCrmSourceCopy: (t, id, request) => authed<void>("PUT", `/api/crm/sources/${id}/copy`, t, request),
    deactivateCrmSource: (t, id) => authed<void>("POST", `/api/crm/sources/${id}/deactivate`, t),
    reactivateCrmSource: (t, id) => authed<void>("POST", `/api/crm/sources/${id}/reactivate`, t),
    async getMyPartnerPage(t) {
      // A 404 is the designed answer for "Key Dials has not made you one" — a state, not a fault.
      try {
        return await authed<PartnerPageView>("GET", "/api/site/my-page", t);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    saveMyPartnerPage: (t, request) => authed<void>("PUT", "/api/site/my-page", t, request),
    listSitePartners: (t) =>
      authed<PartnerSiteProfileAdminListResponse>("GET", "/api/admin/site/partners", t),
    listSitePartnerCandidates: (t) =>
      authed<SitePartnerCandidateListResponse>("GET", "/api/admin/site/partner-candidates", t),
    saveSitePartner: (t, slug, request) =>
      authed<PartnerSiteProfileAdminView>("PUT", `/api/admin/site/partners/${encodeURIComponent(slug)}`, t, request),
    draftPartnerCopy: (t, request) =>
      authed<PartnerCopyDraftView>("POST", "/api/admin/site/partner-copy", t, request),
    listPartnerPayouts: (t) =>
      authed<PartnerPayoutAccountListResponse>("GET", "/api/billing/partner-payouts", t),
    startPartnerOnboarding: (t, organisationId) =>
      authed<PartnerOnboardingLinkView>("POST", `/api/billing/partner-payouts/${organisationId}/onboarding`, t),
    attachPartnerAccount: (t, organisationId, stripeAccountId) =>
      authed<PartnerPayoutAccountView>(
        "POST", `/api/billing/partner-payouts/${organisationId}/attach`, t, { stripeAccountId }),
    refreshPartnerPayout: (t, organisationId) =>
      authed<PartnerPayoutAccountView>("POST", `/api/billing/partner-payouts/${organisationId}/refresh`, t),
    partnerDashboardLink: (t, organisationId) =>
      authed<PartnerDashboardLinkView>(
        "POST", `/api/billing/partner-payouts/${organisationId}/dashboard-link`, t),
    getAffiliateStatus: (t) => authed<AffiliateStatusResponse>("GET", "/api/affiliates/status", t),
    listAffiliateOrganisations: (t) =>
      authed<AffiliateOrganisationListResponse>("GET", "/api/affiliates/organisations", t),
    getAffiliateOrganisation: (t, organisationId) =>
      authed<AffiliateOrganisationDetail>("GET", `/api/affiliates/${organisationId}`, t),
    saveAffiliateOrgSettings: (t, organisationId, request) =>
      authed<void>("PUT", `/api/affiliates/${organisationId}/settings`, t, request),
    async getMyAffiliateLinks(t) {
      try {
        return (await authed<{ items: MyAffiliateLinkView[] }>("GET", "/api/affiliates/me", t)).items;
      } catch (err) {
        // The module is off here, so the route does not exist: nobody has links.
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enrolAffiliate: (t, organisationId, userId) =>
      authed<{ id: string }>("POST", `/api/affiliates/${organisationId}/profiles`, t, { userId }),
    saveAffiliateDiscount: (t, profileId, discountCode) =>
      authed<void>("PUT", `/api/affiliates/profiles/${profileId}/discount`, t, { discountCode }),
    saveAffiliateOrgDiscount: (t, organisationId, discountCode) =>
      authed<void>("PUT", `/api/affiliates/${organisationId}/discount`, t, { discountCode }),
    saveAffiliateTerms: (t, profileId, request) =>
      authed<void>("PUT", `/api/affiliates/profiles/${profileId}/terms`, t, request),
    setAffiliateActive: (t, profileId, isActive) =>
      authed<void>("PUT", `/api/affiliates/profiles/${profileId}/active`, t, { isActive }),
    saveAffiliateCompPlan: (t, organisationId, planId) =>
      authed<void>("PUT", `/api/affiliates/${organisationId}/comp-plan`, t, { planId }),
    setUpAffiliateComp: (t, profileId) =>
      authed<AffiliateCompResult>("POST", `/api/affiliates/profiles/${profileId}/comp`, t),
    listSales: (t, from, to) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const suffix = q.toString();
      return authed<SaleRecordListResponse>("GET", `/api/billing/sales${suffix ? `?${suffix}` : ""}`, t);
    },
    refundSale: (t, saleId, amountPence) =>
      authed<RefundRecordView>("POST", `/api/billing/sales/${saleId}/refund`, t, { amountPence: amountPence ?? null }),
    listDiscountCodes: (t) => authed<DiscountCodeView[]>("GET", "/api/billing/discount-codes", t),
    createDiscountCode: (t, request) => authed<DiscountCodeView>("POST", "/api/billing/discount-codes", t, request),
    setDiscountCodeActive: (t, id, active) =>
      authed<DiscountCodeView>(
        "POST", `/api/billing/discount-codes/${encodeURIComponent(id)}/active?active=${active}`, t),
    setDiscountCodeLimits: (t, id, request) =>
      authed<DiscountCodeView>("PUT", `/api/billing/discount-codes/${encodeURIComponent(id)}/limits`, t, request),
    partnerPayoutReport: (t, from, to) => {
      const query = new URLSearchParams();
      if (from) query.set("from", from);
      if (to) query.set("to", to);
      const suffix = query.toString();
      return authed<PartnerPayoutReport>(
        "GET", `/api/billing/partner-payouts/report${suffix ? `?${suffix}` : ""}`, t);
    },
    listCrmEmailTemplates: (t) => authed<EmailTemplateListResponse>("GET", "/api/crm/templates", t),
    saveCrmEmailTemplate: (t, key, request) => authed<void>("PUT", `/api/crm/templates/${encodeURIComponent(key)}`, t, request),
    resetCrmEmailTemplate: (t, key) => authed<void>("DELETE", `/api/crm/templates/${encodeURIComponent(key)}`, t),
    // The buy surface (KEY-368): unauthenticated by design, same as public intake below.
    async listPurchasablePlans(forPlanId, discountCode) {
      const q = new URLSearchParams();
      if (forPlanId) q.set("plan", forPlanId);
      if (discountCode) q.set("code", discountCode);
      const query = q.toString() ? `?${q.toString()}` : "";
      const res = await fetch(`${root}/api/billing/public/plans${query}`);
      if (!res.ok) throw await apiFailure(res, "Couldn't load the plans");
      return (await res.json()) as PurchasablePlanView[];
    },

    async getTerms() {
      const res = await fetch(`${root}/api/billing/public/terms`);
      if (!res.ok) throw await apiFailure(res, "Couldn't load the terms");
      return (await res.json()) as LegalDocumentView;
    },

    async getPlanSeller(planId) {
      const res = await fetch(`${root}/api/billing/public/plans/${encodeURIComponent(planId)}/seller`);
      if (res.status === 204) return null;
      if (!res.ok) throw await apiFailure(res, "Couldn't load the seller");
      return (await res.json()) as PublicSellerBrandingView;
    },
    async previewDiscountCode(code, planId) {
      const res = await fetch(
        `${root}/api/billing/public/discount-codes/${encodeURIComponent(code)}?plan=${encodeURIComponent(planId)}`,
      );
      // 404 is the designed answer for every code that would not apply.
      if (res.status === 404) return null;
      if (!res.ok) throw await apiFailure(res, "Couldn't check that code");
      return (await res.json()) as DiscountCodePreviewView;
    },
    async getReferralDiscountCode(referralCode) {
      const res = await fetch(`${root}/api/billing/public/referrals/${encodeURIComponent(referralCode)}`);
      if (res.status === 404) return null;
      if (!res.ok) throw await apiFailure(res, "Couldn't check that link");
      return ((await res.json()) as { discountCode: string | null }).discountCode;
    },
    async startPublicCheckout(request) {
      const res = await fetch(`${root}/api/billing/public/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!res.ok) throw await apiFailure(res, "Couldn't start your purchase");
      return (await res.json()) as CheckoutStartedView;
    },
    startOrgCheckout: (t, organisationId, request) =>
      authed<CheckoutStartedView>("POST", `/api/admin/organisations/${organisationId}/checkout`, t, request),
    reactivateSubscription: (t, organisationId, request) =>
      authed<CheckoutStartedView>("POST", `/api/admin/organisations/${organisationId}/reactivate`, t, request),
    async getOrgPortalLink(t, organisationId) {
      // authed() already yields undefined on 204, which is how a comped or never-billed organisation
      // reports having nothing to manage — not an error.
      const result = await authed<{ url: string } | undefined>(
        "POST", `/api/admin/organisations/${organisationId}/portal`, t);
      return result?.url ?? null;
    },
    async getFamily(t, organisationId) {
      // 204 (undefined) is "no pack to show", not an error.
      return (await authed<FamilyView | undefined>("GET", `/api/admin/organisations/${organisationId}/family`, t)) ?? null;
    },
    inviteFamilyMember: (t, organisationId, request) =>
      authed<FamilyInviteSentView>("POST", `/api/admin/organisations/${organisationId}/family/invites`, t, request),
    resendFamilyInvite: (t, organisationId, seatId) =>
      authed<FamilyInviteSentView>(
        "POST", `/api/admin/organisations/${organisationId}/family/invites/${seatId}/resend`, t),
    async revokeFamilyInvite(t, organisationId, seatId) {
      await authed<void>("DELETE", `/api/admin/organisations/${organisationId}/family/invites/${seatId}`, t);
    },
    async getFamilyInvite(token) {
      const res = await fetch(`${root}/api/billing/public/family/${encodeURIComponent(token)}`);
      if (res.status === 404) return null;
      if (!res.ok) throw await apiFailure(res, "Couldn't open this invitation");
      return (await res.json()) as FamilyInviteView;
    },
    setOwnMiloTier: (t, organisationId, requests) =>
      authed<SubscriptionAddOnView>("PUT", `/api/admin/organisations/${organisationId}/subscription/milo`, t, { requests }),
    buyOwnMiloTopUp: (t, organisationId) =>
      authed<MiloTopUpReceipt>("POST", `/api/admin/organisations/${organisationId}/subscription/milo/top-up`, t),
    buyMiloTopUp: (t, organisationId) =>
      authed<MiloTopUpReceipt>("POST", `/api/admin/organisations/${organisationId}/milo/top-ups`, t),
    async getPlanSwitchOptions(t, organisationId) {
      return (await authed<PlanSwitchOptionsView | undefined>(
        "GET", `/api/admin/organisations/${organisationId}/subscription/switch-options`, t)) ?? null;
    },
    switchPlan: (t, organisationId, planId) =>
      authed<PlanChangeResultView>("POST", `/api/admin/organisations/${organisationId}/subscription/switch`, t, { planId }),
    async claimFamilyInvite(token, request) {
      const res = await fetch(`${root}/api/billing/public/family/${encodeURIComponent(token)}/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!res.ok) throw await apiFailure(res, "Couldn't accept the invitation");
      return (await res.json()) as FamilyInviteClaimedView;
    },

    // Public intake: unauthenticated by design, and routed through apiFailure so the visitor sees the
    // server's actual validation message rather than a status code.
    async getPublicEnquirySource(publicKey) {
      const res = await fetch(`${root}/api/crm/enquire/${encodeURIComponent(publicKey)}`);
      if (!res.ok) throw await apiFailure(res, "This enquiry link isn't valid");
      return (await res.json()) as PublicEnquirySourceView;
    },
    async submitPublicEnquiry(publicKey, request) {
      const res = await fetch(`${root}/api/crm/enquire/${encodeURIComponent(publicKey)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!res.ok) throw await apiFailure(res, "Sending your enquiry failed");
      return (await res.json()) as SubmitPublicEnquiryResponse;
    },
    async getPublicCallbackSlots(enquiryToken) {
      const res = await fetch(`${root}/api/crm/enquiries/${encodeURIComponent(enquiryToken)}/slots`);
      if (!res.ok) throw await apiFailure(res, "Couldn't load the available times");
      return (await res.json()) as CallbackSlotsResponse;
    },
    async bookPublicCallback(enquiryToken, startUtc, customerTimeZoneId) {
      const res = await fetch(`${root}/api/crm/enquiries/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enquiryToken, startUtc, customerTimeZoneId: customerTimeZoneId ?? null }),
      });
      if (!res.ok) throw await apiFailure(res, "Booking that time failed");
      return (await res.json()) as BookCallbackResponse;
    },
    async verifyPublicEnquiry(enquiryToken, code) {
      const res = await fetch(`${root}/api/crm/enquiries/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enquiryToken, code }),
      });
      if (!res.ok) throw await apiFailure(res, "Checking the code failed");
      return (await res.json()) as VerifyPublicEnquiryResponse;
    },

    setActiveOrg(organisationId) {
      activeOrgId = organisationId;
    },

    setCheckingRole(role) {
      checkingRole = role;
    },

    // Dial instances
    allocateInstance: (t, request) => authed<{ id: string }>("POST", "/api/instances", t, request),
    listInstances: (t) => authed<InstanceSummary[]>("GET", "/api/instances", t),
    listArchivedInstances: (t) => authed<InstanceSummary[]>("GET", "/api/instances/archived", t),
    archiveInstance: (t, id) => authed<void>("POST", `/api/instances/${id}/archive`, t),
    unarchiveInstance: (t, id) => authed<void>("POST", `/api/instances/${id}/unarchive`, t),
    listMyInstances: (t) => authed<PortfolioResponse>("GET", "/api/me/instances", t),
    getInstance: (t, id, organisationId) => authed<InstanceDetail>("GET", `/api/instances/${id}`, t, undefined, organisationId),
    previewInstanceAs: (t, id, userId) => authed<InstanceDetail>("GET", `/api/instances/${id}/preview-as/${userId}`, t),
    renameInstance: (t, id, name) => authed<void>("PUT", `/api/instances/${id}/name`, t, { name }),
    // Instance-first group views
    listMyGroups: (t) => authed<InstanceGroupSummary[]>("GET", "/api/me/groups", t),
    createGroup: (t, request) => authed<InstanceGroupDetail>("POST", "/api/me/groups", t, request),
    getGroup: (t, id) => authed<InstanceGroupDetail>("GET", `/api/me/groups/${id}`, t),
    updateGroup: (t, id, request) => authed<InstanceGroupDetail>("PUT", `/api/me/groups/${id}`, t, request),
    deleteGroup: (t, id) => authed<void>("DELETE", `/api/me/groups/${id}`, t),
    addGroupMember: (t, id, instanceId) => authed<InstanceGroupDetail>("POST", `/api/me/groups/${id}/members`, t, { instanceId }),
    removeGroupMember: (t, id, instanceId) => authed<void>("DELETE", `/api/me/groups/${id}/members/${instanceId}`, t),
    reorderGroupMembers: (t, id, orderedInstanceIds) => authed<void>("POST", `/api/me/groups/${id}/members/order`, t, { orderedInstanceIds }),
    getGroupConsolidated: (t, id) => authed<InstanceGroupConsolidated>("GET", `/api/me/groups/${id}/consolidated`, t),
    getInstanceCustomisation: (t, id) => authed<InstanceCustomisationView>("GET", `/api/instances/${id}/customisation`, t),
    addInstanceSection: (t, id, body) => authed<{ key: string }>("POST", `/api/instances/${id}/sections`, t, body),
    addInstanceSegment: (t, id, body) => authed<{ key: string }>("POST", `/api/instances/${id}/segments`, t, body),
    addInstanceItem: (t, id, body) => authed<{ key: string }>("POST", `/api/instances/${id}/items`, t, body),
    patchInstanceNode: (t, id, key, body) => authed<void>("PATCH", `/api/instances/${id}/nodes/${key}`, t, body),
    moveInstanceItem: (t, id, key, body) => authed<void>("POST", `/api/instances/${id}/nodes/${key}/move`, t, body),
    deleteInstanceNode: (t, id, key) => authed<void>("DELETE", `/api/instances/${id}/nodes/${key}`, t),
    revealHiddenParts: (t, id, minutes) =>
      authed<{ expiresAtUtc: string }>("POST", `/api/instances/${id}/hidden/reveal`, t, minutes === undefined ? {} : { minutes }),
    endHiddenReveal: (t, id) => authed<void>("DELETE", `/api/instances/${id}/hidden/reveal`, t),
    exportInstanceItems: (t, id) => authed<ExportedInstanceItems>("GET", `/api/instances/${id}/items/export`, t),
    importInstanceItems: (t, id, payload) => authed<ImportItemsReport>("POST", `/api/instances/${id}/items/import`, t, payload),
    exportInstance: (t, id) => authed<ExportedInstance>("GET", `/api/instances/${id}/export-instance`, t),
    importInstance: (t, body) => authed<ImportInstanceReport>("POST", "/api/instances/import-instance", t, body),

    itemImportStatus: (t) => authed<{ enabled: boolean }>("GET", "/api/instances/item-import/status", t),

    peopleStatus: (t) => authed<{ enabled: boolean }>("GET", "/api/instances/people/status", t),
    pushStatus: (t) => authed<{ enabled: boolean }>("GET", "/api/instances/push/status", t),
    miloStatus: (t, instanceId) => authed<MiloStatus>("GET", instanceId ? `/api/milo/status?instanceId=${instanceId}` : "/api/milo/status", t),
    miloTurn: (t, body, conversationId) =>
      authed<MiloTurnResponse>("POST", conversationId ? `/api/milo/conversations/${conversationId}/turns` : "/api/milo/turns", t, body),
    miloConfirm: (t, conversationId, actionId, choiceId) =>
      authed<MiloTurnResponse>("POST", `/api/milo/conversations/${conversationId}/actions/${actionId}/confirm`, t, choiceId ? { choiceId } : undefined),
    miloDecline: (t, conversationId, actionId) =>
      authed<MiloTurnResponse>("POST", `/api/milo/conversations/${conversationId}/actions/${actionId}/decline`, t),
    miloConversation: (t, conversationId) => authed<MiloConversation>("GET", `/api/milo/conversations/${conversationId}`, t),
    previousAttributeValue: (t, id, scope) => {
      const q = new URLSearchParams({ target: scope.target, sectionKey: scope.sectionKey, segmentKey: scope.segmentKey ?? "", itemKey: scope.itemKey ?? "", attributeKey: scope.attributeKey });
      return authed<PreviousAttributeValue>("GET", `/api/instances/${id}/nodes/attribute/previous?${q.toString()}`, t);
    },
    instanceAsOf: (t, id, at) => authed<InstanceSnapshot>("GET", `/api/instances/${id}/as-of?at=${encodeURIComponent(at)}`, t),
    instanceAhead: (t, id, at) => authed<InstanceForecast>("GET", `/api/instances/${id}/ahead?at=${encodeURIComponent(at)}`, t),
    instanceChangeDays: (t, id, range) => {
      const q = new URLSearchParams();
      if (range?.from) q.set("from", range.from);
      if (range?.to) q.set("to", range.to);
      const qs = q.toString();
      return authed<InstanceChangeDays>("GET", `/api/instances/${id}/change-days${qs ? `?${qs}` : ""}`, t);
    },
    instanceResets: (t, id) => authed<InstanceResets>("GET", `/api/instances/${id}/resets`, t),
    setInstanceResetTiming: (t, id, request) => authed<InstanceReset>("PUT", `/api/instances/${id}/resets`, t, request),
    listInstancePeople: (t, id) => authed<InstancePerson[]>("GET", `/api/instances/${id}/people`, t),
    addInstancePerson: (t, id, request) => authed<InstancePerson>("POST", `/api/instances/${id}/people`, t, request),
    updateInstancePerson: (t, id, personId, request) => authed<InstancePerson>("PUT", `/api/instances/${id}/people/${personId}`, t, request),
    removeInstancePerson: (t, id, personId) => authed<void>("DELETE", `/api/instances/${id}/people/${personId}`, t),
    reorderInstancePeople: (t, id, personIds) => authed<void>("PUT", `/api/instances/${id}/people/order`, t, { personIds }),
    listInstanceContacts: (t, id) => authed<InstanceContact[]>("GET", `/api/instances/${id}/contacts`, t),
    addInstanceContact: (t, id, request) => authed<InstanceContact>("POST", `/api/instances/${id}/contacts`, t, request),
    updateInstanceContact: (t, id, contactId, request) => authed<InstanceContact>("PUT", `/api/instances/${id}/contacts/${contactId}`, t, request),
    removeInstanceContact: (t, id, contactId) => authed<void>("DELETE", `/api/instances/${id}/contacts/${contactId}`, t),
    reorderInstanceContacts: (t, id, contactIds) => authed<void>("PUT", `/api/instances/${id}/contacts/order`, t, { contactIds }),
    listContactInvites: (t, id, contactId) => authed<ContactInvite[]>(
      "GET", `/api/instances/${id}/contacts/invites${contactId ? `?contactId=${encodeURIComponent(contactId)}` : ""}`, t),
    resendContactInvite: (t, id, contactId) => authed<ContactInvite>("POST", `/api/instances/${id}/contacts/${contactId}/invite`, t),
    listAllocations: (t, id) => authed<NodeAssignmentView[]>("GET", `/api/instances/${id}/allocations`, t),
    allocateNode: (t, id, request) => authed<NodeAssignmentView>("POST", `/api/instances/${id}/allocations`, t, request),
    withdrawAllocation: (t, id, assignmentId) => authed<void>("POST", `/api/instances/${id}/allocations/${assignmentId}/withdraw`, t),
    resendAllocation: (t, id, assignmentId) => authed<ContactInvite>("POST", `/api/instances/${id}/allocations/${assignmentId}/resend`, t),

    async analyzeInstanceDocument(accessToken, id, file, options) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/instances/${id}/item-analysis-jobs`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        // Surface the server's message (unsupported type / too large / no customise rights).
        throw await apiFailure(res, "Analysing the document failed");
      }
      const { jobId } = (await res.json()) as { jobId: string };

      // Analysis runs server-side off the request thread (same job/poll pattern as AI generation) — poll
      // until it finishes, with a generous cap to avoid an unbounded loop if a worker is wedged.
      const startedAt = Date.now();
      const intervalMs = 2000;
      const deadline = startedAt + 6 * 60 * 1000; // 6 minutes
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const status = await authed<{
          status: string;
          result?: DocumentItemAnalysisResult | null;
          error?: string | null;
          failureStatusCode?: number | null;
        }>("GET", `/api/instances/${id}/item-analysis-jobs/${jobId}`, accessToken);
        if (status.status === "Succeeded") {
          if (!status.result) {
            throw new ApiError(502, "Analysis finished but returned no result.");
          }
          return { ...status.result, jobId };
        }
        if (status.status === "Failed") {
          throw new ApiError(status.failureStatusCode ?? 502, status.error || "Document analysis failed.");
        }
        options?.onPoll?.(Date.now() - startedAt);
      }
      throw new ApiError(504, "Analysis is taking longer than expected. Please try again.");
    },

    bulkAddInstanceItems: (t, id, items, statusDimensionKey, sourceJobId) =>
      authed<BulkAddItemsReport>("POST", `/api/instances/${id}/items/bulk-add`, t, { items, statusDimensionKey, sourceJobId }),
    listAssignableInstanceMembers: (t, id) => authed<UserSummary[]>("GET", `/api/instances/${id}/members/assignable`, t),
    getInstanceSeats: (t, id) => authed<InstanceSeatsView>("GET", `/api/instances/${id}/members/seats`, t),
    listInstanceMembers: (t, id) => authed<InstanceMembersView>("GET", `/api/instances/${id}/members`, t),
    addInstanceMember: (t, id, request) => authed<AddInstanceMemberResponse>("POST", `/api/instances/${id}/members`, t, request),
    previewInstanceMemberEmail: (t, id, request) => authed<MemberEmailPreview>("POST", `/api/instances/${id}/members/preview-email`, t, request),
    removeInstanceMember: (t, id, userId) => authed<void>("DELETE", `/api/instances/${id}/members/${userId}`, t),
    removeInstanceMemberGrant: (t, id, grantId) => authed<void>("DELETE", `/api/instances/${id}/members/grants/${grantId}`, t),
    getInstanceStatusRoles: (t, id) => authed<InstanceStatusRolesView>("GET", `/api/instances/${id}/status-roles`, t),
    setInstanceStatusRoles: (t, id, entries) => authed<void>("PUT", `/api/instances/${id}/status-roles`, t, { entries }),
    getInstanceActionRoles: (t, id) => authed<InstanceActionRolesView>("GET", `/api/instances/${id}/action-roles`, t),
    setInstanceActionRoles: (t, id, entries) => authed<void>("PUT", `/api/instances/${id}/action-roles`, t, { entries }),
    getMemberAttributeAccess: (t, id, userId) => authed<MemberAttributeAccessView>("GET", `/api/instances/${id}/members/${userId}/attribute-access`, t),
    setMemberAttributeAccess: (t, id, userId, entries) => authed<void>("PUT", `/api/instances/${id}/members/${userId}/attribute-access`, t, { entries }),
    changeSegmentStatus: (t, id, request) => authed<void>("POST", `/api/instances/${id}/status`, t, request),
    changeSectionStatus: (t, id, request) => authed<void>("POST", `/api/instances/${id}/sections/status`, t, request),
    recomputeInstanceLinks: (t, id) => authed<void>("POST", `/api/instances/${id}/links/recompute`, t),
    getInstanceHistory: (t, id, organisationId) => authed<SegmentStatusEvent[]>("GET", `/api/instances/${id}/history`, t, undefined, organisationId),
    insightsStatus: (t) => authed<{ enabled: boolean }>("GET", "/api/instances/insights/status", t),
    workspaceMode: (t) => authed<WorkspaceModeView>("GET", "/api/me/workspace-mode", t),
    calendarConnections: (t) => authed<CalendarConnectionList>("GET", "/api/me/calendar-connections", t),
    connectCalendar: (t, provider) => authed<{ authorizationUrl: string }>("POST", `/api/me/calendar-connections/${provider}/connect`, t),
    addIcsCalendar: (t, url, label) => authed<CalendarConnectionView>("POST", "/api/me/calendar-connections/ics", t, { url, label: label ?? null }),
    addCalDavCalendar: (t, server, username, password, label) =>
      authed<CalendarConnectionView>("POST", "/api/me/calendar-connections/caldav", t, { server, username, password, label: label ?? null }),
    syncCalendar: (t, id) => authed<CalendarConnectionView>("POST", `/api/me/calendar-connections/${id}/sync`, t),
    calendarChoices: (t, id) => authed<CalendarChoiceList>("GET", `/api/me/calendar-connections/${id}/calendars`, t),
    chooseCalendars: (t, id, calendarIds) =>
      authed<CalendarConnectionView>("PUT", `/api/me/calendar-connections/${id}/calendars`, t, { calendarIds }),
    allowCalendarWrites: (t, id) =>
      authed<{ authorizationUrl: string | null; connection: CalendarConnectionView | null }>("POST", `/api/me/calendar-connections/${id}/writes`, t),
    stopCalendarWrites: (t, id) => authed<CalendarConnectionView>("DELETE", `/api/me/calendar-connections/${id}/writes`, t),
    setCalendarKeywords: (t, id, keywords) =>
      authed<CalendarConnectionView>("PUT", `/api/me/calendar-connections/${id}/keywords`, t, { keywords }),
    setWriteCalendar: (t, id, calendarId) =>
      authed<CalendarConnectionView>("PUT", `/api/me/calendar-connections/${id}/writes/calendar`, t, { calendarId }),
    syncAllCalendars: (t) => authed<{ connections: CalendarConnectionView[] }>("POST", "/api/me/calendar-connections/sync", t),
    refreshInstanceCalendars: (t, instanceId) =>
      authed<{ started: number }>("POST", `/api/instances/${instanceId}/calendar-sources/refresh`, t),
    disconnectCalendar: (t, id) => authed<void>("DELETE", `/api/me/calendar-connections/${id}`, t),
    calendarFeed: (t) => authed<CalendarFeedView>("GET", "/api/me/calendar-feed", t),
    resetCalendarFeed: (t) => authed<CalendarFeedLink>("POST", "/api/me/calendar-feed", t),
    revokeCalendarFeed: (t) => authed<void>("DELETE", "/api/me/calendar-feed", t),
    setDaysLayout: (t, instanceId, allDay, hiddenSlots) => authed<void>("PUT", `/api/instances/${instanceId}/days-layout`, t, { allDay, hiddenSlots }),
    instanceCalendarSources: (t, instanceId) => authed<InstanceCalendarSourceList>("GET", `/api/instances/${instanceId}/calendar-sources`, t),
    addInstanceCalendarSource: (t, instanceId, connectionId, shareDetails) =>
      authed<void>("POST", `/api/instances/${instanceId}/calendar-sources`, t, { connectionId, shareDetails }),
    setInstanceCalendarShare: (t, instanceId, sourceId, shareDetails) =>
      authed<void>("PUT", `/api/instances/${instanceId}/calendar-sources/${sourceId}/share`, t, { shareDetails }),
    removeInstanceCalendarSource: (t, instanceId, sourceId) => authed<void>("DELETE", `/api/instances/${instanceId}/calendar-sources/${sourceId}`, t),
    addCalendarAppointment: (t, instanceId, request) =>
      authed<{ itemKey: string }>("POST", `/api/instances/${instanceId}/calendar-appointments`, t, request),
    changeCalendarAppointment: (t, instanceId, itemKey, request) =>
      authed<{ itemKey: string }>("PUT", `/api/instances/${instanceId}/calendar-appointments/${encodeURIComponent(itemKey)}`, t, request),
    deleteCalendarAppointment: (t, instanceId, itemKey) =>
      authed<void>("DELETE", `/api/instances/${instanceId}/calendar-appointments/${encodeURIComponent(itemKey)}`, t),
    setWorkspaceMode: (t, mode) => authed<WorkspaceModeView>("PUT", "/api/me/workspace-mode", t, { mode }),
    tabStripMode: (t) => authed<{ mode: TabStripMode }>("GET", "/api/me/tab-strip-mode", t),
    setTabStripMode: (t, mode) => authed<{ mode: TabStripMode }>("PUT", "/api/me/tab-strip-mode", t, { mode }),
    myTheme: (t) => authed<{ theme: string | null; available?: string[] }>("GET", "/api/me/theme", t),
    setMyTheme: (t, theme) => authed<{ theme: string }>("PUT", "/api/me/theme", t, { theme }),
    tours: (t) => authed<TourCompletion[]>("GET", "/api/me/tours", t),
    recordTour: (t, tourId, outcome) =>
      authed<TourCompletion>("POST", `/api/me/tours/${encodeURIComponent(tourId)}`, t, { outcome }),
    analyseInstance: (t, id) => authed<InstanceInsights>("POST", `/api/instances/${id}/insights`, t),
    previewUpgrade: (t, definitionId, body) =>
      authed<UpgradeDriftReport[]>("POST", `/api/definitions/${definitionId}/upgrade/preview`, t, body),
    queueUpgrade: (t, definitionId, body) =>
      authed<QueueUpgradeResult>("POST", `/api/definitions/${definitionId}/upgrade`, t, body),
    applyUpgrade: (t, definitionId, pendingId) =>
      authed<void>("POST", `/api/definitions/${definitionId}/upgrade/${pendingId}/apply`, t),
    cancelUpgrade: (t, definitionId, pendingId) =>
      authed<void>("DELETE", `/api/definitions/${definitionId}/upgrade/${pendingId}`, t),
    listPendingUpgrades: (t, definitionId) =>
      authed<PendingUpgradeAdminRow[]>("GET", `/api/definitions/${definitionId}/upgrade/pending`, t),
    getPendingUpgrade: (t, instanceId) => authed<PendingUpgradeView | null>("GET", `/api/instances/${instanceId}/upgrade`, t),
    acceptUpgrade: (t, instanceId, pendingId) => authed<void>("POST", `/api/instances/${instanceId}/upgrade/${pendingId}/accept`, t),
    declineUpgrade: (t, instanceId, pendingId) => authed<void>("POST", `/api/instances/${instanceId}/upgrade/${pendingId}/decline`, t),
    applyUpgradeNow: (t, instanceId, pendingId) => authed<void>("POST", `/api/instances/${instanceId}/upgrade/${pendingId}/apply`, t),
    saveUpgradeStructureMap: (t, instanceId, pendingId, entries) =>
      authed<StructureRemapSaved>("PUT", `/api/instances/${instanceId}/upgrade/${pendingId}/structure-map`, t, { entries }),
    exportUpgradeAtRisk: (t, instanceId, targetVersionId) =>
      authed<AtRiskExport>("GET", `/api/instances/${instanceId}/upgrade/export?targetVersionId=${targetVersionId}`, t),
    setOrganisationAiInsights: (t, organisationId, enabled, applyToDescendants = false) =>
      authed<void>("PUT", `/api/admin/organisations/${organisationId}/ai-insights`, t, { enabled, applyToDescendants }),
    setOrganisationSharePolicy: (t, organisationId, request) =>
      authed<void>("PUT", `/api/admin/organisations/${organisationId}/share-policy`, t, request),
    getOrganisationBranding: (t, organisationId) =>
      authed<OrganisationBrandingView>("GET", `/api/admin/organisations/${organisationId}/branding`, t),
    async uploadOrganisationLogo(accessToken, organisationId, kind, file) {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${root}/api/admin/organisations/${organisationId}/branding/${kind}-logo`, {
        method: "POST",
        headers: uploadHeaders(accessToken),
        body: form,
      });
      if (!res.ok) {
        // The server names the reason (type, size, a lock above) — show it rather than a bare status.
        const reason = (await res.text().catch(() => "")).replace(/^"|"$/g, "");
        throw new ApiError(res.status, reason || `Logo upload failed: ${res.status}`);
      }
      return (await res.json()) as { reference: string; url: string | null };
    },
    clearOrganisationLogo: (t, organisationId, kind) =>
      authed<void>("DELETE", `/api/admin/organisations/${organisationId}/branding/${kind}-logo`, t),
    setOrganisationLogoPolicy: (t, organisationId, policy) =>
      authed<void>("PUT", `/api/admin/organisations/${organisationId}/branding/logo-policy`, t, { policy }),
    setOrganisationTierBinding: (t, organisationId, billingPlanIds) => {
      const ids = Array.isArray(billingPlanIds) ? billingPlanIds : [billingPlanIds];
      return authed<void>("PUT", `/api/admin/organisations/${organisationId}/tier-binding`, t, { billingPlanIds: ids });
    },
    changeItemStatus: (t, id, request) => authed<void>("POST", `/api/instances/${id}/items/status`, t, request),
    setNodeAttribute: (t, id, request) => authed<void>("POST", `/api/instances/${id}/nodes/attribute`, t, request),
    getInstanceItemHistory: (t, id, organisationId) => authed<ItemStatusEvent[]>("GET", `/api/instances/${id}/items/history`, t, undefined, organisationId),
    getInstanceActivity: (t, id, scope, organisationId, options) => {
      const query = new URLSearchParams();
      if (options?.since) query.set("since", options.since);
      if (options?.includeValues) query.set("includeValues", "true");
      if (scope?.sectionKey) {
        query.set("sectionKey", scope.sectionKey);
        // A segment key only means anything inside its section, so it rides along or not at all.
        if (scope.segmentKey) {
          query.set("segmentKey", scope.segmentKey);
        }
      }
      const suffix = query.size ? `?${query}` : "";
      return authed<InstanceActivityEvent[]>("GET", `/api/instances/${id}/activity${suffix}`, t, undefined, organisationId);
    },
    listMessages: (t, id, sectionKey, segmentKey, itemKey) =>
      authed<MessageDto[]>("GET", `/api/instances/${id}/messages?sectionKey=${encodeURIComponent(sectionKey)}&segmentKey=${encodeURIComponent(segmentKey)}${itemKey ? `&itemKey=${encodeURIComponent(itemKey)}` : ""}`, t),
    listSegmentConversations: (t, id, sectionKey, segmentKey) =>
      authed<SegmentConversations>("GET", `/api/instances/${id}/messages/segment?sectionKey=${encodeURIComponent(sectionKey)}&segmentKey=${encodeURIComponent(segmentKey)}`, t),
    listScopeTranscript: (t, id, sectionKey, limit) => {
      const query = new URLSearchParams();
      if (sectionKey) {
        query.set("sectionKey", sectionKey);
      }
      if (limit !== undefined) {
        query.set("limit", String(limit));
      }
      const suffix = query.size ? `?${query}` : "";
      return authed<ScopeTranscript>("GET", `/api/instances/${id}/messages/transcript${suffix}`, t);
    },
    postMessage: (t, id, request) => authed<MessageDto>("POST", `/api/instances/${id}/messages`, t, request),
    deleteMessage: (t, id, messageId) => authed<void>("DELETE", `/api/instances/${id}/messages/${messageId}`, t),
    listSubscriptions: (t, id) => authed<NotificationSubscriptionDto[]>("GET", `/api/instances/${id}/subscriptions`, t),
    setSubscription: (t, id, request) => authed<void>("PUT", `/api/instances/${id}/subscriptions`, t, request),
    listNotifications: (t) => authed<NotificationDto[]>("GET", "/api/me/notifications", t),
    markNotificationRead: (t, id) => authed<void>("POST", `/api/me/notifications/${id}/read`, t),
    markAllNotificationsRead: (t) => authed<void>("POST", "/api/me/notifications/read-all", t),
    dismissNotification: (t, id) => authed<void>("POST", `/api/me/notifications/${id}/dismiss`, t),
    dismissAllNotifications: (t) => authed<void>("POST", "/api/me/notifications/dismiss-all", t),
    listUnreadReleases: (t) => authed<ReleaseNoteDto[]>("GET", "/api/whatsnew/unread", t),
    listAllReleases: (t) => authed<ReleaseHistoryDto[]>("GET", "/api/whatsnew/all", t),
    dismissRelease: (t, key) => authed<void>("POST", `/api/whatsnew/${encodeURIComponent(key)}/dismiss`, t),
    setReleasePublic: (t, key, isPublic) =>
      authed<void>("PUT", `/api/whatsnew/${encodeURIComponent(key)}/public`, t, { isPublic }),

    // Saved dashboard views (KEY-134)
    listSavedViews: (t) => authed<SavedView[]>("GET", "/api/me/views", t),
    createSavedView: (t, request) => authed<SavedView>("POST", "/api/me/views", t, request),
    updateSavedView: (t, id, request) => authed<SavedView>("PUT", `/api/me/views/${id}`, t, request),
    setSavedViewDefault: (t, id) => authed<void>("POST", `/api/me/views/${id}/default`, t),
    deleteSavedView: (t, id) => authed<void>("DELETE", `/api/me/views/${id}`, t),

    requestDocumentUpload: (t, id, request) =>
      authed<DocumentUploadTicket>("POST", `/api/instances/${id}/documents/upload-url`, t, request),
    async uploadDocumentBytes(uploadUrl, file) {
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!res.ok) {
        throw new ApiError(res.status, `Upload failed: ${res.status}`);
      }
    },
    confirmDocumentUploaded: (t, id, documentId) =>
      authed<void>("POST", `/api/instances/${id}/documents/${documentId}/confirm`, t),
    listDocuments: (t, id, section, segment) => {
      const qs = new URLSearchParams();
      if (section) qs.set("section", section);
      if (segment) qs.set("segment", segment);
      const q = qs.toString();
      return authed<DialDocument[]>("GET", `/api/instances/${id}/documents${q ? `?${q}` : ""}`, t);
    },
    browseDocuments: (t, id, options) => {
      const qs = new URLSearchParams();
      if (options?.sectionKey) qs.set("section", options.sectionKey);
      if (options?.segmentKey) qs.set("segment", options.segmentKey);
      if (options?.sort) qs.set("sort", options.sort);
      if (options?.dir) qs.set("dir", options.dir);
      if (options?.page != null) qs.set("page", String(options.page));
      if (options?.pageSize != null) qs.set("pageSize", String(options.pageSize));
      // `append`, not `set`: the server reads `tag` as a repeatable parameter, and several tags OR.
      for (const tag of options?.tags ?? []) qs.append("tag", tag);
      if (options?.q) qs.set("q", options.q);
      if (options?.includeChildren) qs.set("children", "true");
      if (options?.dialId) qs.set("dial", options.dialId);
      const query = qs.toString();
      return authed<DocumentBrowsePage>("GET", `/api/instances/${id}/documents/browse${query ? `?${query}` : ""}`, t);
    },
    listDocumentVersions: (t, id, documentId) =>
      authed<DialDocument[]>("GET", `/api/instances/${id}/documents/${documentId}/versions`, t),
    getDocumentDownloadUrl: (t, id, documentId) =>
      authed<DocumentDownloadTicket>("GET", `/api/instances/${id}/documents/${documentId}/download-url`, t),
    async downloadDocumentsZip(t, id, documentIds, scope) {
      const res = await fetch(`${root}/api/instances/${id}/documents/zip`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${t}`,
          "Content-Type": "application/json",
          ...(clientApp ? { "X-KeyDials-Client": clientApp } : {}),
          ...(activeOrgId ? { "X-KeyDials-Org": activeOrgId } : {}),
          ...(checkingRole ? { "X-KeyDials-Checking-Role": checkingRole } : {}),
        },
        body: JSON.stringify({
          documentIds: documentIds ?? null,
          sectionKey: scope?.sectionKey ?? null,
          segmentKey: scope?.segmentKey ?? null,
          tags: scope?.tags ?? null,
          q: scope?.q ?? null,
          // Never null: the server's flag is a plain bool, and a JSON null there fails the whole request.
          includeChildren: scope?.includeChildren ?? false,
          dialId: scope?.dialId ?? null,
        } satisfies DocumentZipRequest),
      });
      if (res.status === 400) {
        // The server's 400 body IS a DocumentZipPlan (not a {detail} problem response) when the selection
        // is over the cap — read the actual limit out of it rather than showing a generic failure.
        let plan: DocumentZipPlan | undefined;
        try {
          plan = (await res.clone().json()) as DocumentZipPlan;
        } catch {
          // not a plan body — fall through to the generic failure below
        }
        if (plan && !plan.withinLimits) {
          const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;
          throw new ApiError(
            400,
            `That selection is too large to zip (${plan.fileCount} files, ${mb(plan.totalBytes)}). ` +
              `Limit: ${plan.maxFiles} files or ${mb(plan.maxTotalBytes)}.`,
          );
        }
      }
      if (!res.ok) {
        throw await apiFailure(res, "Zip download failed");
      }
      return res.blob();
    },
    listDocumentTags: (t, id) => authed<DocumentTag[]>("GET", `/api/instances/${id}/documents/tags`, t),
    createDocumentTag: (t, id, request) =>
      authed<DocumentTag>("POST", `/api/instances/${id}/documents/tags`, t, request),
    deleteDocumentTag: (t, id, tagId) =>
      authed<void>("DELETE", `/api/instances/${id}/documents/tags/${tagId}`, t),
    deleteDocument: (t, id, documentId) => authed<void>("DELETE", `/api/instances/${id}/documents/${documentId}`, t),
    rollbackDocument: (t, id, documentId) =>
      authed<void>("POST", `/api/instances/${id}/documents/${documentId}/rollback`, t),
    setUploadAutomation: (t, id, request) =>
      authed<void>("PUT", `/api/instances/${id}/upload-automation`, t, request),
    clearInstanceCentralImage: (t, instanceId) =>
      authed<void>("DELETE", `/api/instances/${instanceId}/central-image`, t),

    // Kiosk auto-lock + PIN re-authentication (KEY-268)
    setInstanceAutoLock: (t, instanceId, enabled, timeoutSecondsOverride) =>
      authed<void>("PUT", `/api/instances/${instanceId}/auto-lock`, t, { enabled, timeoutSecondsOverride: timeoutSecondsOverride ?? null }),

    // Schedule time zone (KEY-276)
    listTimeZones: (t) => authed<TimeZoneOption[]>("GET", "/api/timezones", t),
    setInstanceTimeZone: (t, instanceId, timeZoneId) =>
      authed<void>("PUT", `/api/instances/${instanceId}/timezone`, t, { timeZoneId }),
    setInstanceHideEmpty: (t, instanceId, enabled) =>
      authed<void>("PUT", `/api/instances/${instanceId}/hide-empty`, t, { enabled }),
    setInstanceSectionEdgeSplit: (t, instanceId, enabled) =>
      authed<void>("PUT", `/api/instances/${instanceId}/section-edge-split`, t, { enabled }),
    dismissMilo: (t, instanceId) => authed<void>("POST", `/api/instances/${instanceId}/milo/dismiss`, t),
    setInstanceMiloHidden: (t, instanceId, hidden) =>
      authed<void>("PUT", `/api/instances/${instanceId}/milo-hidden`, t, { hidden }),
    setHideEmptyForMe: (t, instanceId, hideEmpty) =>
      authed<void>("PUT", `/api/instances/${instanceId}/hide-empty/me`, t, { hideEmpty }),
    answerFollowPrompt: (t, instanceId) =>
      authed<void>("POST", `/api/instances/${instanceId}/follow-prompt/me`, t),
    markWelcomeSeen: (t, instanceId) =>
      authed<void>("POST", `/api/instances/${instanceId}/welcome-seen`, t),
    lock: (t, instanceId, refreshToken) =>
      authed<LockResponse>("POST", "/api/auth/lock", t, { instanceId, refreshToken }),
    pinCandidates: (ticket, instanceId) =>
      authed<PinCandidate[]>("GET", `/api/instances/${instanceId}/pin-candidates`, ticket),
    lockFace: (ticket, instanceId) =>
      authed<InstanceDetail>("GET", `/api/instances/${instanceId}/lock-face`, ticket),
    pinVerify: (ticket, userId, pin) =>
      authed<PinVerifyResponse>("POST", "/api/auth/pin-verify", ticket, { userId, pin }),
    pinStatus: (t) => authed<PinStatusResponse>("GET", "/api/me/pin", t),
    setPin: (t, pin) => authed<void>("PUT", "/api/me/pin", t, { pin }),
    clearPin: (t) => authed<void>("DELETE", "/api/me/pin", t),

    // Guest access (KEY-297)
    async guestLand(linkToken) {
      try {
        // 202 with an empty body either way — like requestCode, a plain fetch, and failures stay quiet.
        await fetch(`${root}/api/guest/land`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: linkToken }),
        });
      } catch {
        // network error — the code screen's "ask for another code" path covers the retry
      }
    },
    guestVerify: (linkToken, code) => postJson<GuestSessionResponse>("/api/guest/verify", { token: linkToken, code }),
    inviteGuest: (t, id, request) => authed<InviteGuestResult>("POST", `/api/instances/${id}/guests`, t, request),
    listGuests: (t, id) => authed<GuestGrantView[]>("GET", `/api/instances/${id}/guests`, t),
    revokeGuest: (t, id, grantId) => authed<void>("DELETE", `/api/instances/${id}/guests/${grantId}`, t),

    // Feeds (KEY-308)
    mintFeedCode: (t, id, request) => authed<MintedFeedCode>("POST", `/api/instances/${id}/feed-codes`, t, request),
    listFeedCodes: (t, id) => authed<FeedCodeView[]>("GET", `/api/instances/${id}/feed-codes`, t),
    revokeFeedCode: (t, id, codeId) => authed<void>("DELETE", `/api/instances/${id}/feed-codes/${codeId}`, t),
    redeemFeedCode: (t, code, sourceInstanceId) =>
      authed<RedeemedFeed>("POST", "/api/feeds/redeem", t, { code, sourceInstanceId }),
    listFeeds: (t, id) => authed<FeedView[]>("GET", `/api/instances/${id}/feeds`, t),
    setFeedScope: (t, feedId, scopes) => authed<void>("PUT", `/api/feeds/${feedId}/scope`, t, { scopes }),
    offerFeed: (t, feedId, expiresAtUtc) => authed<void>("POST", `/api/feeds/${feedId}/offer`, t, { expiresAtUtc }),
    acceptFeed: (t, feedId) => authed<void>("POST", `/api/feeds/${feedId}/accept`, t, {}),
    declineFeed: (t, feedId) => authed<void>("POST", `/api/feeds/${feedId}/decline`, t, {}),
    revokeFeed: (t, feedId) => authed<void>("DELETE", `/api/feeds/${feedId}`, t),
    listFeedEvents: (t, feedId) => authed<FeedEventView[]>("GET", `/api/feeds/${feedId}/events`, t),

    // Feed mappings (KEY-358/KEY-359)
    getFeedWiring: (t, feedId) => authed<FeedWiring>("GET", `/api/feeds/${feedId}/wiring`, t),
    listFeedMappings: (t, id) => authed<FeedMappingView[]>("GET", `/api/instances/${id}/feed-mappings`, t),
    createFeedMapping: (t, id, request) =>
      authed<FeedMappingView>("POST", `/api/instances/${id}/feed-mappings`, t, request),
    updateFeedMapping: (t, mappingId, request) =>
      authed<FeedMappingView>("PUT", `/api/feed-mappings/${mappingId}`, t, request),
    deleteFeedMapping: (t, mappingId) => authed<void>("DELETE", `/api/feed-mappings/${mappingId}`, t),
    pauseFeedMapping: (t, mappingId, paused) =>
      authed<void>("POST", `/api/feed-mappings/${mappingId}/pause`, t, { paused }),
    listFeedProposals: (t, id) => authed<FeedProposalView[]>("GET", `/api/instances/${id}/feed-proposals`, t),
    recomputeFeedProposals: (t, id) =>
      authed<FeedProposalView[]>("POST", `/api/instances/${id}/feed-proposals/recompute`, t, {}),
    decideFeedProposal: (t, proposalId, accept) =>
      authed<void>("POST", `/api/feed-proposals/${proposalId}/decide`, t, { accept }),
    feedRevokeImpact: (t, feedId) => authed<FeedRevokeImpact>("GET", `/api/feeds/${feedId}/revoke-impact`, t),
  };
}

/** The browser's IANA time zone, or null where Intl can't say (very old engines, some test runners). */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}
